/* assets/comp-events.js
 * Ghostline Club - Competition Events UI
 * Driver + Judge + Admin portal (App Proxy friendly)
 *
 * Features:
 * - Bracket visual (columns + connector lines)
 * - Driver live polling
 * - Judge scoring (winner/OMT + notes) + optional judge display name
 * - Admin portal:
 *   - Non-admin: Request admin access (emails support@ghostlineclub.com)
 *   - Admin: My Events + Create Event (separate view)
 *   - Event manage page: status controls, bracket (always), judges, registrations
 *   - Bracket: Regenerate + Edit (pre-live only)
 *   - Live "Comp View": bracket + current battle + live judge decisions + popout judging
 */
;(() => {
  "use strict";

  const root = document.getElementById("glCompEventsApp");
  if (!root) return;

  // API base (typically your Shopify App Proxy path, e.g. /apps/gl-comp/api)
  let API = String(root.dataset.apiBase || "");
  while (API.endsWith("/")) API = API.slice(0, -1);

  const DRIVER_IMAGE_REFERENCE_PREFIX = "r2:driver-images/";
  function isOwnedDriverImageReference(value) {
    return /^r2:driver-images\/drivers\/[0-9a-f-]{36}\.(?:jpg|png|webp|gif)$/.test(String(value || "").trim());
  }

  function driverImageSrc(value) {
    const imageUrl = String(value || "").trim();
    if (!isOwnedDriverImageReference(imageUrl)) return imageUrl;
    const key = imageUrl.slice(DRIVER_IMAGE_REFERENCE_PREFIX.length);
    const encodedKey = key.split("/").map(encodeURIComponent).join("/");
    return `${API}/driver-images/${encodedKey}`;
  }

  let isAdmin = String(root.dataset.isAdmin || "false") === "true";
  const isLoggedIn = String(root.dataset.isLoggedIn || "false") === "true";
  const customerEmail = String(root.dataset.customerEmail || "").trim().toLowerCase();
  // Ask the backend to verify the customer against Shopify (admin tag OR
  // "Organizer Admin" metafield) and enroll them if eligible. We run this for
  // any logged-in non-admin rather than relying on a theme-side metafield hint,
  // because the exact metafield namespace/key may not be known to the theme.
  // The backend re-verifies against the Shopify Admin API, so this can never
  // escalate a non-eligible customer. Guarded to run at most once per browser
  // session to avoid repeat Admin API lookups for ordinary shoppers.
  async function claimOrganizerAdminOncePerSession() {
    // NOTE: intentionally do NOT skip when the client-side isAdmin hint is true.
    // That hint comes from the theme's metafield/tag check, which the backend
    // does not trust; if we skipped here, the metafield grant would never be
    // recorded and the subsequent /whoami would downgrade the user back to
    // non-admin. The backend short-circuits real (DB-backed) admins cheaply.
    if (!isLoggedIn) return;
    const SESSION_KEY = "glCeAdminClaimAttempted";
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (_) {
      // sessionStorage unavailable (private mode) — proceed without the guard.
    }
    try {
      await apiPost("/admin/claim-organizer-access", { email: customerEmail });
    } catch (err) {
      console.log("[AUTH] Organizer-admin claim failed.", err?.message || String(err));
    }
  }

  // Refresh admin status from backend (auth.whoami) to reflect DB-backed isAdmin
  async function refreshAdminStatus() {
    try {
      // Enroll via tag/metafield first (if eligible), so the subsequent whoami
      // reflects the freshly granted access.
      await claimOrganizerAdminOncePerSession();
      const data = await apiGet("/whoami");
      isAdmin = !!(data?.is_admin ?? data?.isAdmin);
      if (isLoggedIn) {
        if (isAdmin && !adminState.events.length && !Object.keys(adminState.series.byId).length) {
          try {
            await Promise.all([adminRefreshEvents(), adminRefreshSeries()]);
          } catch (_) {
            // The admin pane will expose the request failure when the user retries.
          }
        }
        renderAdmin();
      }
    } catch (err) {
      console.log("[AUTH] Failed to refresh admin status; using initial isAdmin value.", err?.message || String(err));
    }
  }
  
  // Get logo URL once at initialization (for use in popups)
  const LOGO_URL = (root?.dataset?.logoUrl || root?.getAttribute?.('data-logo-url') || '').trim();
  if (LOGO_URL) {
    console.log('Logo URL loaded:', LOGO_URL);
  } else {
    console.warn('Logo URL not found. Upload logo in theme editor or set data-logo-url attribute.');
  }

  // -----------------------------
  // Helpers
  // -----------------------------
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // Compact, keyboard-accessible help for advanced Admin-only controls.
  // The tooltip is revealed by both hover and focus-within (see CSS).
  function adminHelpIcon(id, text) {
    const tooltipId = `glCeHelp-${String(id).replace(/[^a-z0-9_-]/gi, "")}`;
    return `<span class="gl-ce-help">
      <button class="gl-ce-help__trigger" type="button" aria-label="Help: ${esc(text)}" aria-describedby="${esc(tooltipId)}">
        <span aria-hidden="true">i</span>
      </button>
      <span class="gl-ce-help__tooltip" id="${esc(tooltipId)}" role="tooltip">${esc(text)}</span>
    </span>`;
  }

  // -----------------------------
  // Timezone helpers (Luxon-backed)
  // -----------------------------
  // Mirror of `lib/event-time.js` for the storefront. Luxon is loaded via the
  // `<script src="luxon.min.js">` tag injected by `sections/comp-events.liquid`,
  // so `window.luxon.DateTime` is available before this file runs.
  const __luxon = (typeof window !== "undefined" && window.luxon) ? window.luxon : null;
  const LX_DateTime = __luxon ? __luxon.DateTime : null;
  if (!LX_DateTime) {
    // Hard failure surfaces in the browser console; date math falls back to
    // best-effort `new Date(...)` parsing below.
    console.error("[comp-events] Luxon failed to load — timezone math will be inaccurate.");
  }

  // Canonical list of IANA event timezones. Admin UI uses this for the
  // create/edit dropdowns. Saved data MUST use the IANA `value`.
  const EVENT_TIMEZONES = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Phoenix", label: "Arizona Time (MST, no DST)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "UTC", label: "UTC" },
  ];

  // LEGACY FALLBACK: events created before the timezone columns existed have
  // no `reg_close_tz`. The historical default for this app was Central, so we
  // interpret legacy wall-clock strings as America/Chicago.
  const LEGACY_DEFAULT_TIMEZONE = "America/Chicago";

  const HAS_ZONE_SUFFIX_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;
  const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;

  function isValidIanaTimezone(tz) {
    const s = String(tz || "").trim();
    if (!s || !LX_DateTime) return false;
    try {
      return LX_DateTime.local().setZone(s).isValid;
    } catch {
      return false;
    }
  }

  // Convert a wall-clock local datetime + an IANA timezone into a real UTC
  // ISO string. Accepts the combined "YYYY-MM-DDTHH:mm" form OR a separate
  // date + time pair. If the input already carries a `Z`/offset, it is
  // returned unchanged.
  function combineLocalDateTimeInZoneToUtc(localDateOrCombined, maybeTime, ianaTz) {
    const tz = String(ianaTz || "").trim();
    if (!tz || !LX_DateTime) return null;
    let combined;
    if (maybeTime != null && String(maybeTime).trim() !== "") {
      const d = String(localDateOrCombined || "").trim();
      const t = String(maybeTime || "").trim();
      if (!d || !t) return null;
      combined = `${d}T${t}`;
    } else {
      combined = String(localDateOrCombined || "").trim();
    }
    if (!combined) return null;
    if (HAS_ZONE_SUFFIX_RE.test(combined)) return combined;
    const m = combined.match(LOCAL_DATETIME_RE);
    if (!m) return null;
    const dt = LX_DateTime.fromObject(
      {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: Number(m[4]),
        minute: Number(m[5]),
        second: Number(m[6] || 0),
        millisecond: Number((m[7] || "0").padEnd(3, "0").slice(0, 3)),
      },
      { zone: tz }
    );
    if (!dt.isValid) return null;
    return dt.toUTC().toISO({ suppressMilliseconds: false });
  }

  // Format a stored UTC ISO timestamp for display in the event's IANA tz.
  // Example: "Sat, Jun 13, 2026, 7:00 PM CDT"
  function formatUtcInEventTimezone(utcIso, ianaTz, opts) {
    const s = String(utcIso || "").trim();
    if (!s || !LX_DateTime) return "";
    const tz = String(ianaTz || "").trim() || "UTC";
    let dt;
    if (HAS_ZONE_SUFFIX_RE.test(s)) {
      dt = LX_DateTime.fromISO(s, { setZone: true });
    } else {
      dt = LX_DateTime.fromISO(s, { zone: tz });
    }
    if (!dt.isValid) return "";
    const zoned = dt.setZone(tz);
    if (!zoned.isValid) return "";
    const fmt = (opts && opts.format) || "ccc, LLL d, yyyy, h:mm a ZZZZ";
    return zoned.toFormat(fmt);
  }

  // Hydrate an <input type="datetime-local"> from a stored UTC ISO, showing
  // the wall-clock in the event's IANA tz so admins see what they originally
  // entered.
  function utcIsoToLocalInputValueInTz(utcIso, ianaTz) {
    const s = String(utcIso || "").trim();
    if (!s || !LX_DateTime) return "";
    const tz = String(ianaTz || "").trim();
    let dt;
    if (HAS_ZONE_SUFFIX_RE.test(s)) {
      dt = LX_DateTime.fromISO(s, { setZone: true });
    } else {
      dt = LX_DateTime.fromISO(s, { zone: tz || "UTC" });
    }
    if (!dt.isValid) return "";
    const zoned = tz ? dt.setZone(tz) : dt.toLocal();
    if (!zoned.isValid) return "";
    return zoned.toFormat("yyyy-LL-dd'T'HH:mm");
  }

  // normalizeEventTimes — mirror of the server-side helper. Returns a copy
  // with `registrationClosesAtUtc`, `eventStartsAtUtc`, and `eventTimezone`
  // derived from the canonical UTC + tz fields, falling back to the legacy
  // Chicago default for pre-tz events.
  function normalizeEventTimes(ev) {
    if (!ev || typeof ev !== "object") return ev;
    const regCloseTz = String(ev.reg_close_tz || ev.eventTimezone || "").trim() || null;
    const startAtTz = String(ev.start_at_tz || ev.eventTimezone || "").trim() || null;
    const registrationClosesAtUtc = __pickUtcIso(
      ev.registrationClosesAtUtc,
      ev.reg_close_at,
      regCloseTz
    );
    const eventStartsAtUtc = __pickUtcIso(
      ev.eventStartsAtUtc,
      ev.start_at,
      startAtTz
    );
    return {
      ...ev,
      registrationClosesAtUtc,
      eventStartsAtUtc,
      eventTimezone: regCloseTz || startAtTz || null,
    };
  }

  function __pickUtcIso(preComputed, rawValue, ianaTz) {
    const pre = String(preComputed || "").trim();
    if (pre) {
      if (HAS_ZONE_SUFFIX_RE.test(pre)) return pre;
      return combineLocalDateTimeInZoneToUtc(pre, undefined, ianaTz || LEGACY_DEFAULT_TIMEZONE);
    }
    const raw = String(rawValue || "").trim();
    if (!raw) return null;
    if (HAS_ZONE_SUFFIX_RE.test(raw)) return raw;
    return combineLocalDateTimeInZoneToUtc(raw, undefined, ianaTz || LEGACY_DEFAULT_TIMEZONE);
  }

  // Pure: is registration currently open? Time-based only — uses the UTC
  // close timestamp, never browser local strings or display labels.
  //
  // Manual admin override: when `reg_close_override` is truthy on the row
  // (admin explicitly reopened registration after the deadline), the
  // time-based gate is bypassed and registration is treated as open.
  function isRegistrationOpen(ev, nowMs) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    if (!ev) return true;
    if (hasManualReopenOverride(ev)) return true;
    const normalized =
      ev.registrationClosesAtUtc != null ? ev : normalizeEventTimes(ev);
    const closeUtc = normalized.registrationClosesAtUtc;
    if (!closeUtc) return true;
    const closeMs = __isoToMs(closeUtc);
    if (!Number.isFinite(closeMs)) return true;
    return now < closeMs;
  }

  function getRegistrationStatus(ev, nowMs) {
    return isRegistrationOpen(ev, nowMs) ? "open" : "closed";
  }

  function hasManualReopenOverride(ev) {
    if (!ev) return false;
    const raw = ev.reg_close_override != null ? ev.reg_close_override : ev.regCloseOverride;
    if (raw == null) return false;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "number") return raw !== 0;
    const s = String(raw).trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  }

  function __isoToMs(iso) {
    const s = String(iso || "").trim();
    if (!s || !LX_DateTime) return NaN;
    const dt = HAS_ZONE_SUFFIX_RE.test(s)
      ? LX_DateTime.fromISO(s, { setZone: true })
      : LX_DateTime.fromISO(s, { zone: "utc" });
    return dt.isValid ? dt.toMillis() : NaN;
  }

  function normalizeStatus(s) {
    return String(s || "draft").trim().toLowerCase();
  }

  function eventStatusForUi(eventLike) {
    const stored = normalizeStatus(eventLike?.status_stored || eventLike?.stored_status);
    if (stored === "qualifying") return "qualifying";
    return normalizeStatus(eventLike?.status || eventLike?.computed_status || stored);
  }

  // Browser-local rendering (kept for non-event timestamps like "created at",
  // "first event at" — places where the viewer's local zone is what they want).
  function fmtDateLocalish(v) {
    if (!v) return "";
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return String(v);
    return d.toLocaleString();
  }

  // Render a stored UTC ISO timestamp in an event's saved IANA tz, e.g.
  // "Sat, Jun 13, 2026, 7:00 PM CDT". Thin wrapper around the shared
  // `formatUtcInEventTimezone` helper so every call site that already uses
  // `fmtDateInTz(ev.reg_close_at, ev.reg_close_tz)` keeps working.
  function fmtDateInTz(v, ianaTz) {
    if (!v) return "";
    const out = formatUtcInEventTimezone(v, ianaTz);
    if (out) return out;
    return fmtDateLocalish(v);
  }

  function toLocalInputValue(v) {
    if (!v) return "";
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return String(v);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;
  }

  // Build a "YYYY-MM-DDTHH:mm" string suitable for an
  // <input type="datetime-local"> that represents the same wall-clock instant
  // in the supplied IANA zone. Falls back to browser-local when no tz is set.
  function toLocalInputValueInTz(v, ianaTz) {
    if (!v) return "";
    const tz = String(ianaTz || "").trim();
    if (!tz) return toLocalInputValue(v);
    const out = utcIsoToLocalInputValueInTz(v, tz);
    return out || toLocalInputValue(v);
  }

  function parseMaybeJson(v) {
    if (v == null || v === "") return null;
    if (typeof v === "object") return v;
    try {
      return JSON.parse(String(v));
    } catch {
      return null;
    }
  }

  function eventDisplayName(ev, fallback = "Event") {
    const name = ev?.event_name != null ? String(ev.event_name).trim() : "";
    if (name) return name;
    const code = ev?.comp_code != null ? String(ev.comp_code).trim() : "";
    if (code) return code;
    return fallback;
  }

  function displayRoundName(round, match, totalRounds) {
    if (isConsolationMatch(match)) return "Third Place Match";
    const r = Number(round || 0);
    if (!Number.isFinite(r) || r <= 0) return "Round ?";
    if (totalRounds != null) {
      const last = Number(totalRounds);
      if (Number.isFinite(last) && last > 0) {
        if (r === last) return "Final Battle";
        if (r === last - 1) return "Semi-Final";
        if (r === last - 2) return "Quarter-Final";
        const teamsInRound = Math.pow(2, last - r + 1);
        if (Number.isFinite(teamsInRound) && teamsInRound >= 8) {
          return `Round of ${teamsInRound}`;
        }
      }
    }
    return `Round ${r}`;
  }

  function displayMatchNo(matchNo) {
    if (matchNo == null || matchNo === "") return "—";
    const n = Number(matchNo);
    // Third-place/consolation uses sentinel values internally; never expose them in UI.
    if (Number.isFinite(n) && (n === 999 || n === 9999)) return "1";
    if (Number.isFinite(n) && n >= 1000) {
      const r = n % 1000;
      return r === 0 ? "—" : String(r);
    }
    return String(matchNo);
  }

  function isConsolationMatch(m) {
    if (!m) return false;
    const normalizedFlag = (v) => {
      const raw = String(v ?? "").trim().toLowerCase();
      return raw === "1" || raw === "true" || raw === "yes";
    };
    const state = String(m.state || "").toLowerCase();
    const role = String(m.role || "").toLowerCase();
    const type = String(m.type || m.match_type || "").toLowerCase();
    const bracketType = String(m.bracket_type || "").toLowerCase();
    const side = String(m.side || "").toLowerCase();
    const label = String(m.label || m.name || m.round_name || "").toUpperCase();
    const matchNo = Number(m.match_no ?? m.matchNo ?? 0);

    return (
      state === "third" ||
      state === "third_place" ||
      state === "third-place" ||
      state === "consolation" ||
      role.includes("third") ||
      role.includes("consolation") ||
      type.includes("third") ||
      type.includes("consolation") ||
      bracketType.includes("third") ||
      bracketType.includes("consolation") ||
      (side === "center" && (state.includes("third") || role.includes("third") || label.includes("THIRD"))) ||
      label.includes("THIRD") ||
      label.includes("CONSOLATION") ||
      matchNo === 999 ||
      matchNo === 9999 ||
      m.is_third_place === true ||
      m.is_third_place === 1 ||
      normalizedFlag(m.is_third_place) ||
      normalizedFlag(m.isThirdPlace) ||
      normalizedFlag(m.is_consolation) ||
      normalizedFlag(m.isConsolation)
    );
  }

  function normalizeBracketRoundSelection(raw) {
    if (raw == null) return null;
    if (raw === "third-place") return "third-place";
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // Simple stable hash (FNV-1a) for change detection when server doesn't provide version
  function fnv1a(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
    }
    return ("0000000" + h.toString(16)).slice(-8);
  }

  function bracketSignature(br) {
    if (!br) return "";
    if (br.version) return String(br.version);

    const matches = Array.isArray(br.matches) ? br.matches : [];
    const slim = matches
      .map((m) => {
        const hasSlots = m && ("slot_a_driver_id" in m || "slot_b_driver_id" in m);
        const a = hasSlots ? (m.slot_a_driver_id || "") : (m.driver_a || "");
        const b = hasSlots ? (m.slot_b_driver_id || "") : (m.driver_b || "");
        return {
          id: m.id,
          round: Number(m.round || 0),
          match_no: Number(m.match_no || 0),
          battle_no: Number(m.battle_no || 1),
          is_third_place: !!m.is_third_place,
          a,
          b,
          al: m.driver_a_label || "",
          bl: m.driver_b_label || "",
          as: m.driver_a_seed ?? "",
          bs: m.driver_b_seed ?? "",
          w: m.winner || "",
          wl: m.winner_label || "",
          st: m.status || m.state || "",
          omt: Number(m.omt_count || 0),
        };
      })
      .sort((x, y) => (x.round - y.round) || (x.match_no - y.match_no) || String(x.id).localeCompare(String(y.id)));

    return fnv1a(JSON.stringify({ slim, podium: br.podium || null }));
  }

  /**
   * Improved Single-Elimination Tournament Bracket Generator and Progressor
   * 
   * This function generates and progresses a single-elimination tournament bracket
   * for any number of teams (up to 64). It automatically adjusts bracket size
   * to the next power of 2 (16, 32, 64) and handles BYE matches.
   * 
   * @param {Array<string|object>} teams - Array of team/driver identifiers (IDs or objects with id property)
   * @param {Object} options - Configuration options
   * @param {number} options.maxTeams - Maximum teams allowed (default: 64)
   * @param {boolean} options.autoProgress - Automatically progress through all rounds (default: false)
   * @param {Function} options.getWinner - Function to determine winner: (teamA, teamB, matchNo, round) => teamA | teamB | null
   * @param {Function} options.getTeamLabel - Function to get display label: (team) => string (default: uses team directly)
   * 
   * @returns {Object} Bracket structure with rounds, matchups, and progression data
   * 
   * Structure returned:
   * {
   *   bracketSize: number,        // Actual bracket size (power of 2)
   *   totalRounds: number,         // Total number of rounds
   *   rounds: [                    // Array of round data
   *     {
   *       round: number,           // Round number (1 = first round)
   *       roundName: string,       // Human-readable name (e.g., "Round of 16", "Finals")
   *       matchups: [              // Array of matchups in this round
   *         {
   *           matchNo: number,     // Match number within round
   *           teamA: string,       // Team A identifier (or "BYE")
   *           teamB: string,       // Team B identifier (or "BYE")
   *           winner: string|null,  // Winner identifier (null if not decided)
   *           isBye: boolean,      // True if one team is BYE
   *           isDecided: boolean   // True if winner is determined (BYE or decided)
   *         }
   *       ],
   *       winners: Array<string>,  // Array of winners advancing to next round
   *       isComplete: boolean,      // True if all matchups have winners
   *       isFinal: boolean          // True if this is the final round
   *     }
   *   ],
   *   champion: string|null,       // Champion (if tournament complete)
   *   isComplete: boolean          // True if tournament is finished
   * }
   * 
   * Example usage:
   *   const bracket = generateTournamentBracket(
   *     ["team1", "team2", "team3", ..., "team16"],
   *     {
   *       getWinner: (a, b, matchNo, round) => {
   *         // Your logic to determine winner
   *         return a; // or b, or null if not decided
   *       },
   *       getTeamLabel: (team) => team.name || team
   *     }
   *   );
   */
  function generateTournamentBracket(teams, options = {}) {
    // Validate input
    if (!Array.isArray(teams) || teams.length < 2) {
      throw new Error("Need at least 2 teams to generate bracket");
    }

    const maxTeams = Number(options.maxTeams || 64);
    if (teams.length > maxTeams) {
      throw new Error(`Maximum ${maxTeams} teams allowed, got ${teams.length}`);
    }

    // Helper: Get next power of 2
    const nextPow2 = (x) => {
      x = Math.max(1, Number(x) || 1);
      let p = 1;
      while (p < x) p <<= 1;
      return p;
    };

    // Helper: Get team ID from team (handles both strings and objects)
    const getTeamId = (team) => {
      if (team == null) return null;
      if (typeof team === "string") return team;
      if (typeof team === "object" && team.id != null) return String(team.id);
      return String(team);
    };

    // Helper: Get team label
    const getTeamLabel = options.getTeamLabel || ((team) => {
      if (team == null || team === "BYE") return "BYE";
      if (typeof team === "string") return team;
      if (typeof team === "object") {
        if (team.name) return team.name;
        if (team.label) return team.label;
        if (team.id) return String(team.id);
      }
      return String(team);
    });

    // Calculate bracket size (next power of 2)
    const bracketSize = nextPow2(teams.length);
    const totalRounds = Math.log2(bracketSize); // 4->2, 8->3 (round 1 = semis for 4, round 2 = finals)

    // Get round name
    const getRoundName = (round, totalRounds) => {
      return displayRoundName(round, null, totalRounds);
    };

    // Initialize teams array (pad with BYEs if needed)
    const teamIds = teams.map(getTeamId).filter(Boolean);
    const bracketTeams = [...teamIds];
    while (bracketTeams.length < bracketSize) {
      bracketTeams.push("BYE");
    }

    // Initialize rounds array
    const rounds = [];
    let currentRoundTeams = [...bracketTeams];
    let champion = null;
    let isComplete = false;

    // Generate each round
    for (let round = 1; round <= totalRounds; round++) {
      const isFinal = round === totalRounds;
      const matchups = [];
      const winners = [];

      // Create matchups for this round
      for (let i = 0; i < currentRoundTeams.length; i += 2) {
        const teamA = currentRoundTeams[i];
        const teamB = currentRoundTeams[i + 1] || "BYE";
        const matchNo = Math.floor(i / 2) + 1;

        const isBye = teamA === "BYE" || teamB === "BYE";
        let winner = null;
        let isDecided = false;

        // Determine winner
        if (isBye) {
          // BYE matches: winner is the non-BYE team
          winner = teamA === "BYE" ? teamB : teamA;
          isDecided = true;
        } else if (options.getWinner) {
          // Use custom winner function if provided
          winner = options.getWinner(teamA, teamB, matchNo, round);
          isDecided = winner != null;
        }

        matchups.push({
          matchNo,
          teamA,
          teamB,
          teamALabel: getTeamLabel(teamA),
          teamBLabel: getTeamLabel(teamB),
          winner,
          winnerLabel: winner ? getTeamLabel(winner) : null,
          isBye,
          isDecided,
        });

        // Collect winners for next round
        if (winner) {
          winners.push(winner);
        }
      }

      // Check if round is complete
      const isComplete = matchups.every(m => m.isDecided);

      // If final round and complete, set champion
      if (isFinal && isComplete && winners.length > 0) {
        champion = winners[0];
        isComplete = true;
      }

      rounds.push({
        round,
        roundName: getRoundName(round, totalRounds),
        matchups,
        winners: isComplete ? winners : [],
        isComplete,
        isFinal,
      });

      // If auto-progress is enabled and round is complete, prepare for next round
      if (options.autoProgress && isComplete && !isFinal) {
        currentRoundTeams = winners;
      } else if (!isComplete || isFinal) {
        // Stop if round is incomplete or we've reached finals
        break;
      } else {
        // Prepare for next round
        currentRoundTeams = winners;
      }
    }

    return {
      bracketSize,
      totalRounds,
      rounds,
      champion,
      isComplete: champion != null,
    };
  }

  /**
   * Progress a bracket to the next round
   * 
   * Takes an existing bracket structure and advances winners to the next round.
   * 
   * @param {Object} bracket - Existing bracket structure from generateTournamentBracket
   * @param {Function} getWinner - Function to determine winners for new round: (teamA, teamB, matchNo, round) => teamA | teamB | null
   * @returns {Object} Updated bracket structure
   */
  function progressBracketRound(bracket, getWinner) {
    if (!bracket || !Array.isArray(bracket.rounds) || bracket.rounds.length === 0) {
      throw new Error("Invalid bracket structure");
    }

    // Find the current incomplete round
    const currentRoundIndex = bracket.rounds.findIndex(r => !r.isComplete && !r.isFinal);
    if (currentRoundIndex === -1) {
      // All rounds complete or we're at finals
      return bracket;
    }

    const currentRound = bracket.rounds[currentRoundIndex];
    
    // Determine winners for current round
    const winners = [];
    for (const matchup of currentRound.matchups) {
      if (matchup.isDecided && matchup.winner) {
        winners.push(matchup.winner);
      } else if (getWinner) {
        const winner = getWinner(matchup.teamA, matchup.teamB, matchup.matchNo, currentRound.round);
        if (winner) {
          matchup.winner = winner;
          matchup.winnerLabel = matchup.teamA === winner ? matchup.teamALabel : matchup.teamBLabel;
          matchup.isDecided = true;
          winners.push(winner);
        }
      }
    }

    // Update current round
    currentRound.winners = winners;
    currentRound.isComplete = currentRound.matchups.every(m => m.isDecided);

    // If round is complete and not final, create next round
    if (currentRound.isComplete && !currentRound.isFinal) {
      const nextRound = currentRound.round + 1;
      const isFinal = nextRound === bracket.totalRounds;

      const matchups = [];
      for (let i = 0; i < winners.length; i += 2) {
        const teamA = winners[i];
        const teamB = winners[i + 1] || "BYE";
        const matchNo = Math.floor(i / 2) + 1;

        const isBye = teamA === "BYE" || teamB === "BYE";
        let winner = null;
        let isDecided = false;

        if (isBye) {
          winner = teamA === "BYE" ? teamB : teamA;
          isDecided = true;
        } else if (getWinner) {
          winner = getWinner(teamA, teamB, matchNo, nextRound);
          isDecided = winner != null;
        }

        matchups.push({
          matchNo,
          teamA,
          teamB,
          teamALabel: String(teamA === "BYE" ? "BYE" : teamA),
          teamBLabel: String(teamB === "BYE" ? "BYE" : teamB),
          winner,
          winnerLabel: winner ? String(winner) : null,
          isBye,
          isDecided,
        });
      }

      const nextRoundData = {
        round: nextRound,
        roundName: displayRoundName(nextRound, null, bracket.totalRounds),
        matchups,
        winners: matchups.filter(m => m.isDecided).map(m => m.winner),
        isComplete: matchups.every(m => m.isDecided),
        isFinal,
      };

      bracket.rounds.push(nextRoundData);

      // Update champion if final round is complete
      if (isFinal && nextRoundData.isComplete && nextRoundData.winners.length > 0) {
        bracket.champion = nextRoundData.winners[0];
        bracket.isComplete = true;
      }
    }

    return bracket;
  }

  function isRealParticipant(x) {
    const v = String(x || "").trim();
    if (!v) return false;
    if (v === "BYE") return false;
    if (v === "TBD") return false; // defensive for legacy payloads
    return true;
  }
  function matchHasTwoDrivers(m) {
    // Slot-truth first (new schema / correct contract)
    if (m && ("slot_a_driver_id" in m || "slot_b_driver_id" in m)) {
      return isRealParticipant(m?.slot_a_driver_id) && isRealParticipant(m?.slot_b_driver_id);
    }
    // Legacy fallback
    return isRealParticipant(m?.driver_a) && isRealParticipant(m?.driver_b);
  }
  const tbdHint = "Waiting for winners to advance";

  function pickCurrentMatchId(br) {
    const matches = Array.isArray(br?.matches) ? br.matches : [];
    if (!matches.length) return "";

    const ordered = matches
      .slice()
      .sort(
        (a, b) =>
          Number(a.round || 0) - Number(b.round || 0) ||
          Number(a.match_no || 0) - Number(b.match_no || 0)
      );
    const normalizeStatus = (m) => String(m?.status || "").trim().toLowerCase();
    const isCompleted = (m) => {
      const s = normalizeStatus(m);
      return !!m?.winner || s === "complete" || s === "completed";
    };
    const isPlayableUndecided = (m) => !isCompleted(m) && matchHasTwoDrivers(m);

    // Terminal rule: once final is complete, keep it as the displayed current match
    // so UI does not fall backward to third place on polling/resync.
    const mainMatches = ordered.filter((m) => !isConsolationMatch(m));
    const finalRound = mainMatches.length
      ? Math.max(...mainMatches.map((m) => Number(m.round || 0)).filter(Number.isFinite))
      : null;
    const finalMatch = Number.isFinite(finalRound)
      ? mainMatches
          .filter((m) => Number(m.round || 0) === Number(finalRound))
          .sort((a, b) => Number(a.match_no || 0) - Number(b.match_no || 0))[0] || null
      : null;
    if (finalMatch && isCompleted(finalMatch)) {
      return String(finalMatch.id || "");
    }

    // Progression rule: when both 3rd-place and final are playable, third place runs first.
    const thirdPlayable = ordered.find((m) => isConsolationMatch(m) && isPlayableUndecided(m)) || null;
    const finalPlayable = finalMatch && isPlayableUndecided(finalMatch);
    if (thirdPlayable && finalPlayable) {
      return String(thirdPlayable.id || "");
    }

    for (const m of ordered) {
      if (isConsolationMatch(m)) continue;
      if (!isPlayableUndecided(m)) continue;
      return String(m.id || "");
    }

    for (const m of ordered) {
      if (!isPlayableUndecided(m)) continue;
      return String(m.id || "");
    }

    return "";
  }

  function getViewMode() {
    // Support hash-based mode for popout, e.g. #glce_view=judging&event_id=...
    const q = new URLSearchParams(window.location.search || "");
    const h = String(window.location.hash || "").replace(/^#/, "");
    const hp = new URLSearchParams(h);
    return {
      view: q.get("glce_view") || hp.get("glce_view") || "",
      event_id: q.get("glce_event_id") || hp.get("glce_event_id") || "",
      code: q.get("glce_code") || hp.get("glce_code") || "",
    };
  }

  // -----------------------------
  // API helpers
  // -----------------------------
  let adminState;
  const EVENT_ACCESS_SESSION_STORAGE_KEY = "glCeEventAccessSession";

  function clearStoredStaffSession() {
    try { window.localStorage.removeItem(EVENT_ACCESS_SESSION_STORAGE_KEY); } catch (_) {}
  }

  function persistStaffSession(session) {
    if (!session?.token || !session?.event_id) {
      clearStoredStaffSession();
      return;
    }
    try {
      window.localStorage.setItem(EVENT_ACCESS_SESSION_STORAGE_KEY, JSON.stringify({
        token: String(session.token),
        event_id: String(session.event_id),
        expires_at: session.expires_at || null,
      }));
    } catch (_) {}
  }

  function activeStaffSession() {
    const session = adminState?.series?.staffSession;
    if (session?.expires_at && Date.parse(String(session.expires_at)) <= Date.now()) {
      adminState.series.staffSession = null;
      clearStoredStaffSession();
      return null;
    }
    return session?.token && session?.event_id ? session : null;
  }

  function withStaffSessionToken(path) {
    const session = activeStaffSession();
    const rawPath = String(path || "");
    if (!session) return rawPath;
    const eventId = encodeURIComponent(String(session.event_id));
    const scoped = new RegExp(`^/admin/events/${eventId}(?:/|\\?|$)`);
    const list = /^\/admin\/events(?:\?|$)/;
    if (!scoped.test(rawPath) && !list.test(rawPath)) return rawPath;
    const separator = rawPath.includes("?") ? "&" : "?";
    return `${rawPath}${separator}staff_token=${encodeURIComponent(session.token)}`;
  }

  function hasAdminPortalAccess() {
    return isAdmin || !!activeStaffSession();
  }

  function buildApiGetCandidates(path) {
    const rawPath = String(path || "").startsWith("/") ? String(path) : `/${String(path || "")}`;
    const normalizedPath = withStaffSessionToken(rawPath);
    const out = [];
    const seen = new Set();
    const push = (base) => {
      const b = String(base || "").trim().replace(/\/+$/, "");
      if (!b) return;
      const url = `${b}${normalizedPath}`;
      if (seen.has(url)) return;
      seen.add(url);
      out.push(url);
    };

    push(API);
    push("/apps/comp-events/api");

    if (String(API || "").includes("/api")) {
      push(String(API).replace(/\/api(?:\/|$)/, "/"));
    } else {
      push(`${String(API || "").replace(/\/+$/, "")}/api`);
    }

    return out;
  }

  function buildApiWriteCandidates(path) {
    const rawPath = String(path || "").startsWith("/") ? String(path) : `/${String(path || "")}`;
    const normalizedPath = withStaffSessionToken(rawPath);
    const out = [];
    const seen = new Set();
    const push = (base) => {
      const b = String(base || "").trim().replace(/\/+$/, "");
      if (!b) return;
      const url = `${b}${normalizedPath}`;
      if (seen.has(url)) return;
      seen.add(url);
      out.push(url);
    };

    // Prefer configured API base first, then common app-proxy forms.
    push(API);
    push("/apps/comp-events/api");
    push("/api");
    push("");

    if (String(API || "").includes("/api")) {
      push(String(API).replace(/\/api(?:\/|$)/, "/"));
    } else {
      push(`${String(API || "").replace(/\/+$/, "")}/api`);
    }

    return out;
  }

  async function apiGet(path) {
    const candidates = buildApiGetCandidates(path);
    let lastErr = null;
    const primaryUrl = candidates[0] || "";

    for (let i = 0; i < candidates.length; i++) {
      const requestUrl = candidates[i];
      try {
        if (i > 0) {
          console.warn("[API/GET] Retrying with fallback base", { path, requestUrl, attempt: i + 1 });
        }
        const res = await fetch(requestUrl, {
          method: "GET",
          headers: { accept: "application/json" },
          credentials: "same-origin",
        });

        const contentLength = res.headers.get("content-length");
        if (contentLength === "0" || res.status === 204) {
          throw new Error(`Empty response from ${path} (status: ${res.status})`);
        }

        const contentType = res.headers.get("content-type") || "";
        const isJsonResponse =
          contentType.includes("application/json") || contentType.includes("text/json");
        let data = null;
        if (isJsonResponse) {
          data = await res.json().catch((e) => {
            throw new Error(`Failed to parse JSON from ${path}: ${e.message}`);
          });
        } else if (!res.ok) {
          const raw = await res.text().catch(() => "");
          if (raw) {
            try {
              data = JSON.parse(raw);
            } catch (_) {
              data = { ok: false, error: raw };
            }
          }
        } else {
          throw new Error(`Expected JSON but got ${contentType || "unknown content-type"} from ${path}`);
        }

        if (!res.ok || data?.ok === false) {
          const apiErr = new Error(data?.message || data?.error || `Request failed (${res.status})`);
          // Do not mask real API errors by trying alternate bases.
          apiErr.__glCeStopFallback = true;
          apiErr.__glCeApiPayload = data || { ok: false, error: `Request failed (${res.status})` };
          apiErr.__glCeStatus = res.status;
          throw apiErr;
        }

        if (i > 0) {
          console.info("[API/GET] Fallback base succeeded", { path, requestUrl, primaryUrl });
        }
        return data;
      } catch (err) {
        lastErr = err;
        if (err && err.__glCeStopFallback) {
          throw err;
        }
        if (i === 0 && candidates.length > 1) {
          console.warn("[API/GET] Primary base failed; trying fallbacks", {
            path,
            requestUrl,
            error: err?.message || String(err),
          });
        }
        if (i < candidates.length - 1) {
          continue;
        }
      }
    }

    throw lastErr || new Error(`Request failed for ${path}`);
  }

  async function apiPost(path, body) {
    const candidates = buildApiWriteCandidates(path);
    let lastErr = null;
    const primaryUrl = candidates[0] || "";

    for (let i = 0; i < candidates.length; i++) {
      const requestUrl = candidates[i];
      try {
        if (i > 0) {
          console.warn("[API/POST] Retrying with fallback base", { path, requestUrl, attempt: i + 1 });
        }
        const res = await fetch(requestUrl, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(body ?? {}),
        });

        // Check if response is empty (but allow 204 No Content)
        const contentLength = res.headers.get("content-length");
        if (contentLength === "0" && res.status !== 204) {
          const e = new Error(`Empty response from ${path} (status: ${res.status})`);
          e.__glCeInvalidJson = true;
          e.__glCeStopFallback = true;
          throw e;
        }

        // For 204 No Content, return success
        if (res.status === 204) {
          return { ok: true };
        }

        // Check content type
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json") && !contentType.includes("text/json")) {
          const text = await res.text().catch(() => "");
          const snippet = text ? text.slice(0, 120).replace(/\s+/g, " ").trim() : "";
          const looksLikeHtml = /<!doctype|<html/i.test(snippet);
          console.error("[API/POST] Non-JSON response", {
            path,
            requestUrl,
            status: res.status,
            contentType,
            snippet,
            looksLikeHtml,
          });
          const e = new Error(
            !text
              ? `Empty or non-JSON response from ${path} (status: ${res.status}, content-type: ${contentType || "missing"})`
              : looksLikeHtml
                ? `Got HTML instead of JSON from ${path} (status: ${res.status}). The worker route may not be deployed or the proxy is serving a fallback page.`
                : `Expected JSON but got ${contentType} from ${path} (status: ${res.status})`
          );
          e.__glCeInvalidJson = true;
          e.__glCeStopFallback = true;
          e.__glCeStatus = res.status;
          throw e;
        }

        const data = await res.json().catch(async (parseErr) => {
          // Re-read body as text so we can show what came back. The body has already
          // been consumed by res.json(), so clone the response on the next pass.
          const err = new Error(
            `Failed to parse JSON from ${path} (status: ${res.status}): ${parseErr.message}`
          );
          err.__glCeInvalidJson = true;
          err.__glCeStopFallback = true;
          err.__glCeStatus = res.status;
          console.error("[API/POST] JSON parse failed", {
            path,
            requestUrl,
            status: res.status,
            contentType,
            error: parseErr.message,
          });
          throw err;
        });

        if (!res.ok || data?.ok === false) {
          const apiErr = new Error(data?.error || `Request failed (${res.status})`);
          // Real API errors should not trigger base fallback retries.
          apiErr.__glCeStopFallback = true;
          throw apiErr;
        }

        if (i > 0) {
          console.info("[API/POST] Fallback base succeeded", { path, requestUrl, primaryUrl });
        }
        return data;
      } catch (err) {
        lastErr = err;
        if (err && err.__glCeStopFallback) {
          throw err;
        }
        if (i === 0 && candidates.length > 1) {
          console.warn("[API/POST] Primary base failed; trying fallbacks", {
            path,
            requestUrl,
            error: err?.message || String(err),
          });
        }
        if (i < candidates.length - 1) {
          continue;
        }
      }
    }

    throw lastErr || new Error(`Request failed for ${path}`);
  }

  function isInvalidJsonResponseError(err) {
    const msg = String(err?.message || err || "");
    return !!err?.__glCeInvalidJson ||
      msg.includes("Expected JSON but got") ||
      msg.includes("Failed to parse JSON") ||
      msg.includes("Empty or non-JSON response") ||
      msg.includes("Empty response from");
  }

  function isTransientNetworkError(err) {
    const msg = String(err?.message || err || "").toLowerCase();
    return (
      msg.includes("networkerror") ||
      msg.includes("failed to fetch") ||
      msg.includes("load failed") ||
      msg.includes("timeout") ||
      msg.includes("temporary") ||
      msg.includes("http 5")
    );
  }

  function friendlyJudgeNetworkError(err, fallback = "A temporary network error occurred.") {
    const msg = String(err?.message || err || "").trim();
    if (!msg) return fallback;
    if (isInvalidJsonResponseError(err) || isTransientNetworkError(err)) {
      return `${fallback} Please retry or resync.`;
    }
    return msg;
  }

  async function apiWriteJson(method, path, body) {
    const candidates = buildApiWriteCandidates(path);
    let lastErr = null;
    const sendBody = !(method === "DELETE" && body == null);

    for (let i = 0; i < candidates.length; i++) {
      const requestUrl = candidates[i];
      try {
        const headers = { accept: "application/json" };
        if (sendBody) headers["content-type"] = "application/json";
        const res = await fetch(requestUrl, {
          method,
          headers,
          body: sendBody ? JSON.stringify(body ?? {}) : undefined,
          credentials: "same-origin",
        });
        if (res.status === 204) return { ok: true };
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json") && !contentType.includes("text/json")) {
          if (res.ok) return { ok: true };
          const e = new Error(`Expected JSON but got ${contentType || "unknown content type"} from ${path}`);
          e.__glCeInvalidJson = true;
          e.__glCeStopFallback = true;
          throw e;
        }
        const data = await res.json().catch((parseErr) => {
          const err = new Error(`Failed to parse JSON from ${path}: ${parseErr.message}`);
          err.__glCeInvalidJson = true;
          err.__glCeStopFallback = true;
          throw err;
        });
        if (!res.ok || data?.ok === false) {
          const apiErr = new Error(data?.error || `Request failed (${res.status})`);
          apiErr.__glCeStopFallback = true;
          throw apiErr;
        }
        return data;
      } catch (err) {
        lastErr = err;
        if (err && err.__glCeStopFallback) throw err;
        if (i < candidates.length - 1) continue;
      }
    }
    throw lastErr || new Error(`Request failed for ${path}`);
  }

  async function apiDelete(path, body = null) {
    return apiWriteJson("DELETE", path, body);
  }

  async function apiPatch(path, body) {
    return apiWriteJson("PATCH", path, body);
  }

  async function uploadSeriesDriverImage(seriesId, driverId, file) {
    if (!file || !Number(file.size)) return null;
    const form = new FormData();
    form.append("image", file);
    const res = await fetch(
      `${API}/admin/series/${encodeURIComponent(seriesId)}/drivers/${encodeURIComponent(driverId)}/image`,
      { method: "POST", headers: { accept: "application/json" }, credentials: "same-origin", body: form }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) throw new Error(data?.error || `Image upload failed (${res.status})`);
    return data.driver;
  }

  function isPastEventDate(value) {
    const ts = __isoToMs(value);
    if (!Number.isFinite(ts)) {
      const fallback = Date.parse(String(value || ""));
      return Number.isFinite(fallback) && fallback < Date.now();
    }
    return ts < Date.now();
  }

  function showConfirmModal({
    title = "Confirm Action",
    message = "Are you sure?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    danger = false,
    mountEl = null,
  } = {}) {
    return new Promise((resolve) => {
      const modal = document.createElement("div");
      modal.className = "gl-ce-modal";
      modal.style.display = "flex";
      modal.style.alignItems = "center";
      modal.style.justifyContent = "center";
      modal.style.position = "fixed";
      modal.style.inset = "0";
      modal.style.zIndex = "9999";
      modal.innerHTML = `
        <div class="gl-ce-modal__inner gl-ce-card">
          <div class="gl-ce-row gl-ce-row-between">
            <h3 class="gl-ce-h2">${esc(title)}</h3>
          </div>
          <div class="gl-ce-muted" style="margin-top:8px; white-space:pre-line;">${esc(message)}</div>
          <div class="gl-ce-row" style="margin-top:16px; justify-content:flex-end;">
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="confirm-cancel" type="button">${esc(cancelText)}</button>
            <button class="gl-ce-btn ${danger ? "gl-ce-btn-danger" : ""}" data-action="confirm-ok" type="button">${esc(confirmText)}</button>
          </div>
        </div>
      `;

      const cleanup = (result) => {
        modal.remove();
        resolve(result);
      };

      modal.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (!btn) {
          if (e.target === modal) cleanup(false);
          return;
        }
        if (btn.dataset.action === "confirm-ok") cleanup(true);
        if (btn.dataset.action === "confirm-cancel") cleanup(false);
      });

      const mountTarget = mountEl && mountEl.isConnected ? mountEl : document.body;
      mountTarget.appendChild(modal);
    });
  }

  // -----------------------------
  // UI scaffold
  // -----------------------------
  const requestedView = getViewMode();
  if (!API && !["spectator", "spectator_live"].includes(requestedView.view) && !(requestedView.view === "bracket_popout" && requestedView.code)) {
    root.innerHTML = `<div class="gl-ce-card">Missing <code>data-api-base</code>.</div>`;
    return;
  }

  const mode = requestedView;

  // Public spectator view intentionally runs before the sign-in gate. It only
  // consumes the redacted /public/spectator contract and never falls back to
  // any admin endpoint.
  function spectatorBracketData(payload) {
    const matches = Array.isArray(payload?.bracket?.matches) ? payload.bracket.matches : [];
    return {
      matches: matches.map((match) => ({
        ...match,
        id: `spectator-${Number(match.round || 1)}-${Number(match.match_no || 1)}-${match.is_third_place ? "third" : "main"}`,
        state: match.is_third_place ? "third" : "",
      })),
    };
  }

  function spectatorPayloadSignature(payload) {
    // The public contract is generated in a deterministic field order. Its
    // serialized representation includes every visible spectator datum while
    // excluding transport-only state, so an unchanged poll cannot replace the
    // bracket DOM and reset its pan/zoom measurement.
    return JSON.stringify(payload || null);
  }

  function spectatorCurrentMatchId(payload) {
    const current = payload?.bracket?.current_battle;
    if (!current) return "";
    return `spectator-${Number(current.round || 1)}-${Number(current.match_no || 1)}-${current.is_third_place ? "third" : "main"}`;
  }

  function spectatorImageSrc(value) {
    const source = String(value || "").trim();
    if (!source) return "";
    const storedImageKey = source.match(/^r2:driver-images\/(drivers\/[0-9a-f-]{36}\.(?:jpg|png|webp|gif))$/i)?.[1];
    if (storedImageKey) {
      // Series driver uploads are stored as an R2 reference. Resolve those
      // through the configured Worker route; external URLs remain unchanged.
      const base = String(API || "/apps/comp-events/api").replace(/\/+$/, "");
      return `${base}/driver-images/${encodeURIComponent(storedImageKey)}`;
    }
    try {
      const url = new URL(source, window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : "";
    } catch (_) {
      return "";
    }
  }

  function spectatorAccountGate(mountEl, portalName = "Spectator") {
    if (mountEl) mountEl.innerHTML = `<div class="gl-ce-card"><h2 class="gl-ce-h">${esc(portalName)}</h2><p class="gl-ce-sub">Create an account or sign in to access ${esc(portalName)} events.</p><div class="gl-ce-row gl-ce-row-wrap"><a class="gl-ce-btn" href="/account/register">Create account</a><a class="gl-ce-btn gl-ce-btn-ghost" href="/account/login">Sign in</a></div></div>`;
  }

  function renderSpectatorView(initialCode = "", mountEl = root, options = {}) {
    if (!isLoggedIn) {
      spectatorAccountGate(mountEl);
      return;
    }
    const mount = mountEl?.isConnected ? mountEl : root;
    let payload = null;
    const liveOnly = !!options.liveOnly;
    let activeTab = liveOnly ? "live" : "drivers";
    let pollTimer = null;
    let loading = false;
    let fanVotePayload = null;
    let fanVoteLoading = false;
    let fanVoteError = "";
    let fanVoteSubmittingPromptId = "";
    let lastPayloadSignature = "";
    let spectatorReveal = null;
    let spectatorRevealPhase = "";
    let spectatorRevealTimer = null;
    let spectatorRevealKey = "";
    const POLL_MS = liveOnly ? 1000 : 5000;
    const SPECTATOR_DECISIONS_HOLD_MS = 3000;
    const SPECTATOR_RESULT_REVEAL_MS = 5000;

    mount.innerHTML = `
      <div class="gl-ce-wrap gl-ce-spectator">
        <section class="gl-ce-card gl-ce-spectator-header">
          <h1 class="gl-ce-h" id="glCeSpectatorTitle">${liveOnly ? "Live Battles" : "Spectator View"}</h1>
          <p class="gl-ce-sub" id="glCeSpectatorMeta">${liveOnly ? "Connecting to the live battle board…" : "Enter an event code to follow the action."}</p>
          ${liveOnly ? "" : `<form class="gl-ce-spectator-lookup" id="glCeSpectatorLookup">
            <label class="gl-ce-sr-only" for="glCeSpectatorCode">Event code</label>
            <input id="glCeSpectatorCode" maxlength="128" autocomplete="off" placeholder="Event code" value="${esc(initialCode)}">
            <button class="gl-ce-btn" type="submit">View event</button>
          </form>`}
        </section>
        <nav class="gl-ce-tabs gl-ce-spectator-tabs" id="glCeSpectatorTabs" aria-label="Spectator event sections" ${liveOnly ? "hidden" : ""}></nav>
        <main class="gl-ce-card gl-ce-spectator-content" id="glCeSpectatorContent" aria-live="polite"></main>
      </div>
    `;
    const lookup = mount.querySelector("#glCeSpectatorLookup");
    const codeInput = mount.querySelector("#glCeSpectatorCode");
    const tabs = mount.querySelector("#glCeSpectatorTabs");
    const content = mount.querySelector("#glCeSpectatorContent");
    const title = mount.querySelector("#glCeSpectatorTitle");
    const meta = mount.querySelector("#glCeSpectatorMeta");

    const hasBracket = () => Array.isArray(payload?.bracket?.matches) && payload.bracket.matches.length > 0;
    const status = () => String(payload?.event?.status || "").toLowerCase();
    const isManualSpectator = () => payload?.event?.bracket_mode === "manual";
    const tabList = () => [
      ["drivers", "Registered Drivers", !liveOnly],
      ["qualifying", "Qualifying", ["qualifying", "live", "complete", "completed"].includes(status())],
      ["bracket", "Bracket", hasBracket()],
      ["live", "Live Battles", (liveOnly || status() === "live") && !!payload?.bracket?.current_battle && !isManualSpectator()],
      ["votes", "Fan Votes", true],
      ["results", "Event Results", !!payload?.bracket?.podium || ["complete", "completed"].includes(status())],
    ];

    function renderTabs() {
      const available = tabList().filter(([, , visible]) => visible);
      if (!available.some(([id]) => id === activeTab)) activeTab = available[0]?.[0] || "drivers";
      tabs.innerHTML = available.map(([id, label]) =>
        `<button class="gl-ce-tab ${id === activeTab ? "active" : ""}" data-spectator-tab="${id}" type="button">${esc(label)}</button>`
      ).join("");
    }

    function renderDrivers() {
      const roster = Array.isArray(payload?.roster) ? payload.roster : [];
      if (!roster.length) return `<div class="gl-ce-muted">No registered drivers are available yet.</div>`;
      return `<div class="gl-ce-spectator-roster">${roster.map((driver) => {
        const image = spectatorImageSrc(driver.image_url);
        return `<article class="gl-ce-spectator-driver">
          ${image ? `<img src="${esc(image)}" alt="" loading="lazy">` : `<div class="gl-ce-spectator-driver-placeholder" aria-hidden="true">🏁</div>`}
          <h3>${esc(driver.name || "Driver")}</h3>
          <div class="gl-ce-muted">${driver.driver_number ? `#${esc(driver.driver_number)}` : ""}${driver.city ? `${driver.driver_number ? " · " : ""}${esc(driver.city)}` : ""}</div>
          ${(driver.car || driver.hp != null) ? `<div class="gl-ce-spectator-driver-detail">${esc([driver.car, driver.hp != null ? `${driver.hp} HP` : ""].filter(Boolean).join(" · "))}</div>` : ""}
        </article>`;
      }).join("")}</div>`;
    }

    function renderQualifying() {
      const rows = Array.isArray(payload?.qualifying?.leaderboard) ? payload.qualifying.leaderboard : [];
      const current = payload?.qualifying?.current_run;
      const config = qualifyingDisplayConfig(payload?.qualifying);
      const scoreBySlot = (slot) => qualifyingScoreBySlot(current, slot);
      const submittedScores = Array.isArray(current?.scores) ? current.scores.length : 0;
      const currentRun = current ? `<section class="gl-ce-qual-popout gl-ce-spectator-qualifying-live" aria-label="Live qualifying run">
        <div class="gl-ce-spectator-live-label">CURRENT RUN</div>
        <div class="gl-ce-qual-row"><span>Driver:</span><strong>${esc(current.driver || "Driver")}</strong></div>
        <div class="gl-ce-qual-row"><span>#:</span><strong>${esc(current.driver_number || "—")}</strong></div>
        <div class="gl-ce-qual-row"><span>Run:</span><strong>${esc(current.run_no || "—")}</strong></div>
        <div class="gl-ce-qual-scores">
          ${[1, 2, 3].map((slot) => `<div class="gl-ce-qual-row"><span>${esc(config.roleForSlot(slot))}:</span><strong>${esc(formatQualifyingScoreValue(scoreBySlot(slot)))}</strong></div>`).join("")}
        </div>
        <div class="gl-ce-qual-row gl-ce-qual-total"><span>${esc(config.resultLabel)}:</span><strong>${esc(formatQualifyingScoreValue(current.avg_score))}</strong></div>
        <div class="gl-ce-spectator-vote-count">${esc(submittedScores)} of 3 judges scored${String(current.status || "").toLowerCase() === "locked" ? " · Final" : ""}</div>
      </section>` : "";
      return `${currentRun}
        ${rows.length ? `<div class="gl-ce-tablewrap"><table class="gl-ce-table gl-ce-qualifying-leaderboard-table"><thead><tr><th>Position</th><th>Driver</th><th>#</th><th>Run 1</th><th>Run 2</th><th>Best</th></tr></thead><tbody>
          ${rows.map((row) => `<tr><td>${row.rank ? `P${esc(row.rank)}` : "—"}</td><td>${esc(row.name || "Driver")}</td><td>${esc(row.driver_number || "—")}</td><td>${spectatorQualifyingScore((row.runs || []).find((run) => Number(run.run_no) === 1)?.avg_score)}</td><td>${spectatorQualifyingScore((row.runs || []).find((run) => Number(run.run_no) === 2)?.avg_score)}</td><td>${spectatorQualifyingScore(row.best_score)}</td></tr>`).join("")}
        </tbody></table></div>` : `<div class="gl-ce-muted">Qualifying results are not available yet.</div>`}`;
    }

    function spectatorQualifyingScore(value) {
      return value == null ? "—" : esc(formatQualifyingScoreValue(value));
    }

    function spectatorBattleDriverCard(profile, fallbackName) {
      const driver = profile || {};
      const details = [
        ["Driver Number", driver.driver_number],
        ["City", driver.city],
        ["Car", driver.car],
        ["HP", driver.hp == null ? null : `${driver.hp}`],
      ].filter(([, value]) => value != null && String(value).trim() !== "");
      return `<article class="gl-ce-spectator-battle-driver">
        <h3>${esc(driver.name || fallbackName || "Driver")}</h3>
        ${details.length ? `<div class="gl-ce-spectator-battle-driver-meta">${details.map(([label, value]) => `<div><strong>${esc(label)}:</strong> ${esc(value)}</div>`).join("")}</div>` : `<div class="gl-ce-muted">Driver details unavailable.</div>`}
      </article>`;
    }

    function renderLive() {
      const battle = spectatorReveal?.battle || payload?.bracket?.current_battle;
      if (!battle) return `<div class="gl-ce-muted">No live battle is active.</div>`;
      const bracketMatches = Array.isArray(payload?.bracket?.matches) ? payload.bracket.matches : [];
      const totalRounds = bracketMatches
        .filter((match) => !isConsolationMatch(match))
        .reduce((highest, match) => Math.max(highest, Number(match?.round || 0)), 0);
      const roundLabel = displayRoundName(battle.round, battle, totalRounds || null);
      const matchLabel = displayMatchNo(battle.match_no);
      const tally = Array.isArray(battle.tally) ? battle.tally : [];
      const judges = Array.isArray(battle.judges) ? battle.judges : [];
      const votedCount = judges.filter((judge) => judge?.decision != null && String(judge.decision).trim() !== "").length;
      const isReveal = spectatorRevealPhase === "result";
      const revealLabel = battle.is_omt_reveal ? "OMT — ONE MORE TIME" : battle.winner ? `WINNER: ${battle.winner_label || battle.winner}` : "CURRENT BATTLE";
      const event = payload?.event || {};
      const logo = spectatorImageSrc(event.logo_url);
      const hideJudges = String(event.bracket_mode || "") === "manual";
      const judgeRows = hideJudges ? "" : (judges.length ? judges.map((judge) => {
        const decision = String(judge?.decision || "");
        const left = decision === "driver_a" ? (judge.decision_label || battle.driver_a) : decision === "omt" ? "OMT" : "";
        const right = decision === "driver_b" ? (judge.decision_label || battle.driver_b) : decision === "omt" ? "OMT" : "";
        const side = decision === "omt" ? "omt" : decision ? "winner" : "waiting";
        return `<div class="gl-ce-spectator-judge-row ${side}">
          <div class="gl-ce-spectator-judge-decision left">${esc(left)}</div>
          <div class="gl-ce-spectator-judge-field"><strong>JUDGE ${esc(judge.slot)}</strong>${judge.name ? ` (${esc(judge.name)})` : ""}<div class="gl-ce-spectator-judge-waiting">${decision ? "" : "Waiting"}</div></div>
          <div class="gl-ce-spectator-judge-decision right">${esc(right)}</div>
        </div>`;
      }).join("") : `<div class="gl-ce-muted">Awaiting judge assignments.</div>`);
      return `<div class="gl-ce-spectator-live gl-ce-spectator-live-panel">
        <header class="gl-ce-spectator-live-event">
          ${logo ? `<img src="${esc(logo)}" alt="" class="gl-ce-spectator-live-logo">` : ""}
          <div><h2>${esc(event.name || "Live Battles")}</h2><p>${esc(roundLabel)} · Match ${esc(matchLabel)} · Battle ${esc(battle.battle_no || 1)}</p></div>
        </header>
        <div class="gl-ce-spectator-live-label">${esc(revealLabel)}</div>
        <div class="gl-ce-spectator-battle-drivers">
          ${spectatorBattleDriverCard(battle.driver_a_profile, battle.driver_a)}
          ${spectatorBattleDriverCard(battle.driver_b_profile, battle.driver_b)}
        </div>
        ${hideJudges ? "" : `<div class="gl-ce-spectator-judge-rows">${judgeRows}</div>
        <div class="gl-ce-spectator-vote-count">${esc(votedCount)} of ${esc(judges.length || 3)} judges voted</div>`}
        ${isReveal ? `<div class="gl-ce-spectator-reveal ${spectatorReveal?.kind === "omt" ? "omt" : ""}">
          <div class="gl-ce-spectator-reveal-title">${spectatorReveal?.kind === "omt" ? "OMT" : "WINNER"}</div>
          <div class="gl-ce-spectator-reveal-name">${esc(spectatorReveal?.kind === "omt" ? "ONE MORE TIME" : (battle.winner_label || battle.winner || ""))}</div>
          <div class="gl-ce-spectator-reveal-tally">${tally.map((item) => `<span>${esc(item.label || item.option)} <b>${esc(item.count || 0)}</b></span>`).join("")}</div>
          <div class="gl-ce-spectator-reveal-next">Next battle in 5 seconds...</div>
        </div>` : ""}
      </div>`;
    }

    function renderResults() {
      const podium = payload?.bracket?.podium;
      if (!podium) return `<div class="gl-ce-muted">Official results will appear when the event is complete.</div>`;
      const place = (value) => {
        if (!value) return null;
        if (typeof value === "object") return { label: value.label || "TBD" };
        return { label: String(value) };
      };
      const podiumForDisplay = {
        first: place(podium.first),
        second: place(podium.second),
        third: place(podium.third),
      };
      return `${podiumMarkup(podiumForDisplay)}${finalResultsMarkup(payload?.bracket?.final_results, payload?.bracket?.final_results_pending)}`;
    }

    function renderFanVotes() {
      if (fanVoteLoading) {
        content.innerHTML = `<div class="gl-ce-spectator-placeholder"><h2>Fan Votes</h2><p class="gl-ce-muted">Loading fan votes…</p></div>`;
        return;
      }
      if (fanVoteError) {
        content.innerHTML = `<div class="gl-ce-spectator-placeholder"><h2>Fan Votes</h2><p class="gl-ce-warn">${esc(fanVoteError)}</p></div>`;
        return;
      }
      const prompts = Array.isArray(fanVotePayload?.prompts) ? fanVotePayload.prompts : [];
      if (!prompts.length) {
        content.innerHTML = `<div class="gl-ce-spectator-placeholder"><h2>Fan Votes</h2><p>There are no fan vote prompts for this event yet.</p></div>`;
        return;
      }
      const drivers = Array.isArray(fanVotePayload?.drivers) ? fanVotePayload.drivers : [];
      const driversById = new Map(drivers.map((driver) => [String(driver.registration_id || ""), driver]));
      content.innerHTML = `<div class="gl-ce-fan-votes"><h2 class="gl-ce-h">Fan Votes</h2>${prompts.map((prompt) => {
        const open = !!prompt.is_open;
        const eligibleIds = new Set(Array.isArray(prompt.eligible_driver_registration_ids) ? prompt.eligible_driver_registration_ids.map(String) : []);
        const eligibleDrivers = drivers.filter((driver) => eligibleIds.has(String(driver.registration_id || "")));
        const myVoteId = String(prompt.my_vote?.driver_registration_id || "");
        const myDriver = myVoteId ? driversById.get(myVoteId) : null;
        const state = open ? "" : `<div class="gl-ce-muted">This vote is closed.</div>`;
        const results = Array.isArray(prompt.results) ? prompt.results : null;
        const resultsHtml = results ? `<div class="gl-ce-spectator-tally" style="margin-top:12px;">${results.map((item) => `<div class="gl-ce-spectator-tally-item"><span>${esc(item.driver || "Driver")}${item.driver_number ? ` · #${esc(item.driver_number)}` : ""}</span><b>${esc(item.votes || 0)}</b></div>`).join("")}</div>` : "";
        const driversHtml = `<div class="gl-ce-spectator-roster" style="margin-top:12px;">${eligibleDrivers.map((driver) => {
          const driverId = String(driver.registration_id || "");
          const image = driverImageSrc(driver.image_url);
          const selected = driverId === myVoteId;
          const disabled = !isLoggedIn || !!myVoteId || !open || fanVoteSubmittingPromptId === String(prompt.id);
          return `<article class="gl-ce-spectator-driver ${selected ? "gl-ce-fan-vote-choice-selected" : ""}">
            ${image ? `<img src="${esc(image)}" alt="" loading="lazy">` : `<div class="gl-ce-spectator-driver-placeholder" aria-hidden="true">🏁</div>`}
            <h3>${esc(driver.name || "Driver")}</h3>
            <div class="gl-ce-muted">${driver.driver_number ? `#${esc(driver.driver_number)}` : ""}${driver.city ? `${driver.driver_number ? " · " : ""}${esc(driver.city)}` : ""}</div>
            ${selected
              ? `<div class="gl-ce-muted" style="margin:10px;"><b>Your recorded selection</b></div>`
              : `<div style="margin:10px;"><button class="gl-ce-btn gl-ce-btn-sm" data-action="spectator-fan-vote" data-prompt-id="${esc(prompt.id)}" data-driver-id="${esc(driverId)}" type="button" ${disabled ? "disabled" : ""}>${fanVoteSubmittingPromptId === String(prompt.id) ? "Submitting…" : "Vote for this driver"}</button></div>`}
          </article>`;
        }).join("") || `<div class="gl-ce-muted">No eligible drivers are available for this prompt.</div>`}</div>`;
        const login = !isLoggedIn ? `<div class="gl-ce-muted" style="margin-top:10px;">Sign in to cast your one fan vote.</div>` : "";
        const recorded = myVoteId ? `<div class="gl-ce-muted" style="margin-top:10px;">Your vote is recorded for <b>${esc(myDriver?.name || "the selected driver")}</b>.</div>` : "";
        return `<section class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;"><h3 class="gl-ce-h2">${esc(prompt.title)}</h3>${prompt.public_note ? `<p>${esc(prompt.public_note)}</p>` : ""}${state}${driversHtml}${login}${recorded}${resultsHtml}</section>`;
      }).join("")}</div>`;
    }

    async function loadFanVotes() {
      const code = String(payload?.event?.code || codeInput.value || "").trim();
      if (!code || fanVoteLoading) return;
      fanVoteLoading = true;
      fanVoteError = "";
      renderFanVotes();
      try {
        fanVotePayload = await apiGet(`/public/fan-vote/prompts?code=${encodeURIComponent(code)}`);
      } catch (error) {
        fanVoteError = error?.message || "Unable to load fan votes.";
      } finally {
        fanVoteLoading = false;
        if (activeTab === "votes") renderFanVotes();
      }
    }

    function renderContent() {
      if (!payload) {
        content.innerHTML = `<div class="gl-ce-muted">Enter the event code shown by the organizer.</div>`;
        return;
      }
      if (activeTab === "drivers") content.innerHTML = renderDrivers();
      else if (activeTab === "qualifying") content.innerHTML = renderQualifying();
      else if (activeTab === "live") content.innerHTML = renderLive();
      else if (activeTab === "votes") {
        if (!fanVotePayload && !fanVoteLoading) loadFanVotes();
        else renderFanVotes();
      }
      else if (activeTab === "results") content.innerHTML = renderResults();
      else if (activeTab === "bracket") {
        content.innerHTML = `<div class="gl-ce-row gl-ce-row-between"><h2 class="gl-ce-h">Bracket</h2><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="spectator-open-bracket" type="button">Pop-Out</button></div><div id="glCeSpectatorBracket"></div>`;
        renderBracketVisual(content.querySelector("#glCeSpectatorBracket"), spectatorBracketData(payload), {
          title: payload.event?.name || "Bracket", live: status() === "live", lastUpdated: new Date().toLocaleTimeString(),
          currentMatchId: spectatorCurrentMatchId(payload), showPopOut: false, showBracketControls: true,
        });
      }
    }

    function render() {
      title.textContent = payload?.event?.name || "Spectator View";
      meta.textContent = payload ? `${payload.event?.code || ""}${payload.event?.venue ? ` · ${payload.event.venue}` : ""}${payload.event?.status ? ` · ${payload.event.status}` : ""}` : "Enter an event code to follow the action.";
      renderTabs();
      renderContent();
    }

    function shouldPoll() {
      return (liveOnly || !document.hidden) && !!payload && ["qualifying", "bracket", "live"].includes(activeTab) && !["complete", "completed"].includes(status());
    }
    function schedulePoll() {
      clearTimeout(pollTimer);
      // The server retains a completed battle for the full 3s decisions plus
      // 5s result sequence. Poll the
      // embedded Live Battles tab each second so it cannot miss the final
      // judge decision and reveal between ordinary spectator refreshes.
      const delay = activeTab === "live" ? 1000 : POLL_MS;
      if (shouldPoll()) pollTimer = setTimeout(() => load(codeInput.value, true), delay);
    }
    function beginSpectatorReveal(reveal) {
      const battle = reveal?.battle;
      if (!battle) return;
      // `live_reveal.battle` is an immutable public snapshot. Identity is the
      // completed battle (OMT snapshots already pin reveal_battle_no), not
      // expires_at, so a lock timestamp appearing one poll later cannot restart
      // the 3s+5s sequence.
      const key = `${battle.round || ""}:${battle.match_no || ""}:${battle.battle_no || ""}:${battle.winner || ""}:${reveal.kind || ""}`;
      if (key === spectatorRevealKey) return;
      spectatorRevealKey = key;
      spectatorReveal = reveal;
      const hasDecisions = (Array.isArray(battle.judges) ? battle.judges : []).some(
        (judge) => judge?.decision != null && String(judge.decision).trim() !== ""
      );
      spectatorRevealPhase = hasDecisions ? "decisions" : "result";
      clearTimeout(spectatorRevealTimer);
      const finishReveal = () => {
        spectatorReveal = null;
        spectatorRevealPhase = "";
        spectatorRevealKey = "";
        if (String(payload?.event?.status || "").toLowerCase() === "complete") activeTab = "results";
        if (activeTab === "live" || activeTab === "results") render();
      };
      const startResultPhase = () => {
        spectatorRevealPhase = "result";
        if (activeTab === "live") renderContent();
        spectatorRevealTimer = setTimeout(finishReveal, SPECTATOR_RESULT_REVEAL_MS);
      };
      // Admin awards have no judge rows to show; skip the 3s "Waiting" hold
      // so spectators see the 5s WINNER overlay immediately.
      if (hasDecisions) {
        spectatorRevealTimer = setTimeout(startResultPhase, SPECTATOR_DECISIONS_HOLD_MS);
      } else {
        spectatorRevealTimer = setTimeout(finishReveal, SPECTATOR_RESULT_REVEAL_MS);
      }
    }
    async function load(code, silent = false) {
      const normalized = String(code || "").trim();
      if (!normalized || loading) return;
      loading = true;
      if (!silent) content.innerHTML = `<div class="gl-ce-muted">Loading event…</div>`;
      try {
        const nextPayload = await apiGet(`/public/spectator?code=${encodeURIComponent(normalized)}`);
        const nextPayloadSignature = spectatorPayloadSignature(nextPayload);
        const unchanged = silent && nextPayloadSignature === lastPayloadSignature;
        beginSpectatorReveal(nextPayload?.bracket?.live_reveal);
        payload = nextPayload;
        lastPayloadSignature = nextPayloadSignature;
        codeInput.value = payload?.event?.code || normalized;
        if (liveOnly && payload?.event?.bracket_mode === "manual") activeTab = "bracket";
        if (!silent && !liveOnly) history.replaceState(null, "", `${location.pathname}${location.search}#glce_view=spectator&glce_code=${encodeURIComponent(codeInput.value)}`);
        // Avoid rebuilding the active bracket on every polling interval. A
        // rebuild replaces the pan/zoom viewport and causes the visible flash.
        if (!unchanged) render();
      } catch (error) {
        if (!payload || !silent) content.innerHTML = `<div class="gl-ce-warn">${esc(error?.message || "Unable to load this event.")}</div>`;
      } finally {
        loading = false;
        schedulePoll();
      }
    }
    lookup?.addEventListener("submit", (event) => { event.preventDefault(); load(codeInput.value); });
    tabs.addEventListener("click", (event) => {
      const button = event.target.closest("[data-spectator-tab]");
      if (!button) return;
      activeTab = button.dataset.spectatorTab;
      render();
      if (activeTab === "votes") loadFanVotes();
      schedulePoll();
    });
    mount.addEventListener("click", async (event) => {
      const action = event.target.closest("[data-action]")?.dataset.action;
      if (action === "spectator-open-bracket") {
        const code = String(payload?.event?.code || codeInput.value || "").trim();
        if (code) window.open(`${location.origin}${location.pathname}${location.search}#glce_view=bracket_popout&glce_code=${encodeURIComponent(code)}`, "spectatorBracketPopup");
        return;
      }
      if (action !== "spectator-fan-vote") return;
      if (!isLoggedIn) {
        toast("Please sign in to cast a fan vote.", "err");
        return;
      }
      const button = event.target.closest("button");
      const promptId = String(button?.dataset.promptId || "");
      const driverId = String(button?.dataset.driverId || "");
      const prompt = (fanVotePayload?.prompts || []).find((item) => String(item.id) === promptId);
      const driver = (fanVotePayload?.drivers || []).find((item) => String(item.registration_id) === driverId);
      if (!prompt?.is_open || prompt?.my_vote || !driverId || !driver) return;
      const confirmed = await showConfirmModal({
        title: "Cast fan vote",
        message: `Vote for ${driver.name || "this driver"}? You can only vote once for this prompt.`,
        confirmText: "Cast Vote",
        mountEl: content,
      });
      if (!confirmed) return;
      fanVoteSubmittingPromptId = promptId;
      renderFanVotes();
      try {
        const code = String(payload?.event?.code || codeInput.value || "").trim();
        await apiPost(`/public/fan-vote/prompts/${encodeURIComponent(promptId)}/vote?code=${encodeURIComponent(code)}`, {
          driver_registration_id: driverId,
        });
        await loadFanVotes();
        toast("Your fan vote has been recorded.", "ok");
      } catch (error) {
        toast(error?.message || "Unable to record your vote.", "err");
        await loadFanVotes();
      } finally {
        fanVoteSubmittingPromptId = "";
        if (activeTab === "votes") renderFanVotes();
      }
    });
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && shouldPoll()) load(codeInput.value, true);
      else schedulePoll();
    });
    if (initialCode) load(initialCode);
  }

  if (mode.view === "spectator") {
    if (!API) API = "/apps/comp-events/api";
    renderSpectatorView(mode.code);
    return;
  }

  // This share route is code-gated and uses only the redacted spectator API.
  // It does not read admin or judge credentials.
  if (mode.view === "spectator_live" && mode.code) {
    if (!API) API = "/apps/comp-events/api";
    renderSpectatorView(mode.code, root, { liveOnly: true });
    return;
  }

  // If popout mode, render ONLY that view (no tabs)
  if (mode.view === "judging" && mode.event_id) {
    // Ensure API is set (might not be in popout window)
    if (!API) {
      API = String(root.dataset.apiBase || "/apps/comp-events/api");
      while (API.endsWith("/")) API = API.slice(0, -1);
    }
    
    root.innerHTML = `
      <div class="gl-ce-wrap gl-ce-popout">
        <div class="gl-ce-card">
          <div class="gl-ce-row gl-ce-row-between">
            <div>
              <h2 class="gl-ce-h">Live Judging (Pop-out)</h2>
              <div class="gl-ce-muted" style="font-size:12px;">Event ID: ${esc(mode.event_id)}</div>
            </div>
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="popout-close" type="button">Close</button>
          </div>
          <div id="glCePopJudging"></div>
        </div>
        <div class="gl-ce-output" id="glCeOutput" aria-live="polite"></div>
      </div>
    `;

    const outputEl = root.querySelector("#glCeOutput");
    const mount = root.querySelector("#glCePopJudging");

    if (!mount) {
      console.error("Pop-out: #glCePopJudging element not found");
      root.innerHTML = `<div class="gl-ce-card"><div class="gl-ce-muted">Error: Mount element not found.</div></div>`;
      return;
    }

    // Show initial loading state
    mount.innerHTML = `<div class="gl-ce-muted" style="padding:20px; text-align:center;">Loading live judging data...</div>`;

    function toast(msg, kind = "info") {
      if (!outputEl) return;
      outputEl.innerHTML = `<div class="gl-ce-msg gl-ce-msg-${kind}">${esc(msg)}</div>`;
      clearTimeout(toast._t);
      toast._t = setTimeout(() => {
        outputEl.innerHTML = "";
      }, 5500);
    }

    let pollTimer = null;
    let lastPollHadError = false;
    const ACTIVE_POLL_MS = 2000;
    const ERROR_RETRY_MS = 9000;

    function renderPopoutError(message, { recoverable = true, code = "" } = {}) {
      const safeMessage = String(message || "Unable to load live judging data.");
      const safeCode = String(code || "").trim();
      mount.innerHTML = `<div style="padding:20px; background:rgba(255,0,0,0.1); border-radius:6px; border:1px solid rgba(255,0,0,0.3);">
        <div style="font-weight:bold; margin-bottom:8px;">${recoverable ? "Temporary issue" : "Action required"}</div>
        ${safeCode ? `<div style="font-size:11px; opacity:0.72; margin-bottom:6px;">Code: ${esc(safeCode)}</div>` : ""}
        <div style="font-size:14px; opacity:0.92;">${esc(safeMessage)}</div>
        <div style="font-size:12px; opacity:0.75; margin-top:8px;">${recoverable ? "Judging will keep trying in the background. You can retry or resync now." : "Open the main event page to resolve this state, then retry."}</div>
        <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
          <button class="gl-ce-btn gl-ce-btn-sm" data-action="popout-retry" type="button">Retry</button>
          ${recoverable ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="popout-resync" type="button">Resync Match</button>` : `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="popout-return" type="button">Return to Event</button>`}
        </div>
      </div>`;
    }

    function scheduleNextPoll() {
      clearTimeout(pollTimer);
      pollTimer = setTimeout(() => {
        poll().catch(() => {});
      }, lastPollHadError ? ERROR_RETRY_MS : ACTIVE_POLL_MS);
    }

    function requestParentResync() {
      if (!window.opener || window.opener.closed) {
        toast("Main event window is unavailable for resync.", "err");
        return;
      }
      try {
        window.opener.postMessage(
          { type: "judge-resync-request", event_id: String(mode.event_id || "") },
          window.location.origin
        );
      } catch (err) {
        console.error("Pop-out resync request failed:", err);
      }
    }

    async function getPopoutEventStatus() {
      const eventsData = await apiGet("/admin/events");
      const event = (eventsData?.events || []).find((ev) => String(ev?.id) === String(mode.event_id));
      return normalizeStatus(event?.status);
    }

    async function poll() {
      try {
        if (!mode.event_id) {
          mount.innerHTML = `<div class="gl-ce-muted" style="padding:20px;">Error: No event ID in URL.</div>`;
          lastPollHadError = true;
          scheduleNextPoll();
          return;
        }

        const eventStatus = await getPopoutEventStatus();
        if (eventStatus === "qualifying") {
          const data = await apiGet(
            `/admin/events/${encodeURIComponent(mode.event_id)}/qualifying/current-run?include_locked=1`
          );
          renderQualifyingPopout(mount, data);
          lastPollHadError = false;
          scheduleNextPoll();
          return;
        }

        const data = await apiGet(
          `/admin/events/${encodeURIComponent(mode.event_id)}/comp-view?popout=1&view=judging`
        );

        if (!data) {
          renderPopoutError("No active match found", { recoverable: true, code: "NO_ACTIVE_MATCH" });
          lastPollHadError = true;
          scheduleNextPoll();
          return;
        }

        renderLiveJudgingOnly(mount, data, { showFinalStandingsOnComplete: true });
        lastPollHadError = false;
      } catch (e) {
        console.error("Pop-out poll error:", e);
        const apiState = e?.__glCeApiPayload;
        if (apiState && apiState.ok === false) {
          const recoverable = apiState.recoverable !== false;
          const msg = String(apiState.message || apiState.error || "Unable to load match state.");
          renderPopoutError(msg, { recoverable, code: apiState.code || "" });
          toast(msg, recoverable ? "info" : "err");
          lastPollHadError = true;
          scheduleNextPoll();
          return;
        }
        const msg = friendlyJudgeNetworkError(e, "Temporary error loading live judging data.");
        renderPopoutError(msg, { recoverable: true, code: "NETWORK_ERROR" });
        toast(msg, "err");
        lastPollHadError = true;
      }
      scheduleNextPoll();
    }

    function stop() {
      clearTimeout(pollTimer);
      pollTimer = null;
    }

    function start() {
      stop();
      poll().catch(() => {});
    }

    window.addEventListener("message", (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== "judge-resync-result") return;
      if (data.ok && data.payload) {
        if (data.view === "qualifying") {
          renderQualifyingPopout(mount, data.payload);
        } else {
          renderLiveJudgingOnly(mount, data.payload, { showFinalStandingsOnComplete: true });
        }
        lastPollHadError = false;
        toast("Resynced with main event window.", "ok");
      } else {
        const msg = String(data.error || "Resync failed.");
        renderPopoutError(msg, { recoverable: true, code: "RESYNC_FAILED" });
        toast(msg, "err");
        lastPollHadError = true;
      }
      scheduleNextPoll();
    });

    root.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === "popout-close") {
        window.close();
      } else if (action === "popout-return") {
        if (window.opener && !window.opener.closed && typeof window.opener.focus === "function") {
          window.opener.focus();
        }
      } else if (action === "popout-retry") {
        poll().catch(() => {});
      } else if (action === "popout-resync") {
        requestParentResync();
        poll().catch(() => {});
      }
    });

    window.addEventListener("focus", () => {
      poll().catch(() => {});
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        poll().catch(() => {});
      }
    });

    start();
    return;
  }

  // -----------------------------
  // BRACKET POP-OUT (hash-based)
  // Loads the full page so the normal comp-events.css applies, then mounts
  // just the bracket via renderBracketVisual and polls /comp-view for live
  // updates. This is the replacement for the old document.write + .toString()
  // popup that rendered as unstyled plain text.
  // -----------------------------
  if (mode.view === "bracket_popout" && (mode.event_id || mode.code)) {
    if (!API) {
      API = String(root.dataset.apiBase || "/apps/comp-events/api");
      while (API.endsWith("/")) API = API.slice(0, -1);
    }

    root.innerHTML = `
      <div class="gl-ce-wrap gl-ce-popout">
        <div class="gl-ce-card">
          <div class="gl-ce-row gl-ce-row-between" style="margin-bottom:10px;">
            <div>
              <h2 class="gl-ce-h" id="glCeBracketPopoutTitle">Bracket</h2>
              <div class="gl-ce-muted" style="font-size:12px;">
                ${mode.code ? `Event code: ${esc(mode.code)}` : `Event ID: ${esc(mode.event_id)}`} ·
                <span id="glCeBracketPopoutStatus">Loading...</span>
              </div>
            </div>
            <div class="gl-ce-row" style="gap:8px;">
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="popout-retry" type="button">Refresh</button>
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="popout-close" type="button">Close</button>
            </div>
          </div>
          <div id="glCeBracketPopoutMount"></div>
        </div>
        <div class="gl-ce-output" id="glCeOutput" aria-live="polite"></div>
      </div>
    `;

    const mount = root.querySelector("#glCeBracketPopoutMount");
    const statusEl = root.querySelector("#glCeBracketPopoutStatus");
    const titleEl = root.querySelector("#glCeBracketPopoutTitle");
    const outputEl = root.querySelector("#glCeOutput");

    if (!mount) {
      console.error("Bracket pop-out: mount element not found");
      return;
    }

    mount.innerHTML = `<div class="gl-ce-muted" style="padding:20px; text-align:center;">Loading bracket...</div>`;

    function popToast(msg, kind = "info") {
      if (!outputEl) return;
      outputEl.innerHTML = `<div class="gl-ce-msg gl-ce-msg-${kind}">${esc(msg)}</div>`;
      clearTimeout(popToast._t);
      popToast._t = setTimeout(() => { outputEl.innerHTML = ""; }, 4500);
    }

    let pollTimer = null;
    let lastSig = "";
    let pollInFlight = false;
    const ACTIVE_POLL_MS = 2000;

    function renderBracketError(msg) {
      mount.innerHTML = `
        <div style="max-width:900px;margin:24px auto;padding:16px;border:1px solid #f0b4b4;border-radius:8px;background:#fff0f0;color:#6a1a1a;">
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;">Unable to load bracket</div>
          <div style="font-size:14px;">${esc(String(msg || "Unknown error"))}</div>
          <div style="margin-top:10px;">
            <button class="gl-ce-btn gl-ce-btn-sm" data-action="popout-retry" type="button">Retry</button>
          </div>
        </div>`;
    }

    async function pollOnce() {
      if (pollInFlight) return;
      pollInFlight = true;
      try {
        const data = mode.code
          ? await apiGet(`/public/spectator?code=${encodeURIComponent(mode.code)}`)
          : await apiGet(`/admin/events/${encodeURIComponent(mode.event_id)}/comp-view`);
        if (!data || data.ok === false) {
          renderBracketError(String(data?.message || data?.error || "Comp view returned an error."));
          if (statusEl) statusEl.textContent = "Error";
          return;
        }
        const bracket = mode.code ? spectatorBracketData(data) : (data.bracket || data);
        if (!bracket || !Array.isArray(bracket.matches) || bracket.matches.length === 0) {
          renderBracketError("Bracket has no matches yet. Generate the bracket first.");
          if (statusEl) statusEl.textContent = "No bracket";
          return;
        }
        // Update title from event metadata if we can find it.
        const evName = mode.code
          ? (data?.event?.name || "Bracket")
          : (data?.event?.event_name || data?.event?.organizer || data?.event_name);
        if (evName && titleEl) titleEl.textContent = String(evName);

        // Cheap signature: re-render only when something visible actually
        // changed (current match, winners, battle/omt counts).
        const matchSig = bracket.matches
          .map((m) => [
            m.id || "", m.round || 1, m.match_no || 1, m.is_third_place ? 1 : 0,
            m.side || "", m.position_in_side_round ?? "",
            m.driver_a || "", m.driver_b || "", m.driver_a_label || "", m.driver_b_label || "",
            m.winner || "", m.winner_label || "", m.status || "", m.battle_no || 1, m.omt_count || 0,
          ].join(":"))
          .join(",");
        const currentMatchId = mode.code ? spectatorCurrentMatchId(data) : String(data.current_match_id || "");
        const sig = `${currentMatchId}::${matchSig}`;
        if (sig === lastSig) {
          if (statusEl) statusEl.textContent = `Live · last update: ${new Date().toLocaleTimeString()}`;
          return;
        }
        lastSig = sig;

        // Mount the visual bracket using the SAME renderer the parent uses.
        // The popout loaded the regular comp-events.css, so all .gl-ce-bracket*
        // styles apply automatically — no stylesheet plumbing needed.
        // showBracketControls:true gives the pop-out its own Reset / + / -
        // / Current Match toolbar so the user can pan/zoom the bracket
        // inside the pop-out window the same way as the main views.
        // showPopOut:false hides the redundant "Pop-Out" button (we're
        // already in the pop-out window).
        renderBracketVisual(mount, bracket, {
          title: evName || "Bracket",
          live: true,
          lastUpdated: new Date().toLocaleTimeString(),
          currentMatchId,
          max_drivers: bracket.max_drivers || data?.event?.max_drivers || 32,
          showPopOut: false,
          showBracketControls: true,
          eventId: bracket.event_id || mode.event_id || "",
        });

        if (statusEl) statusEl.textContent = `Live · last update: ${new Date().toLocaleTimeString()}`;
      } catch (err) {
        console.error("Bracket pop-out poll error:", err);
        renderBracketError(err?.message || "Network error.");
        if (statusEl) statusEl.textContent = "Error";
      } finally {
        pollInFlight = false;
      }
    }

    function scheduleNext() {
      clearTimeout(pollTimer);
      pollTimer = setTimeout(() => { pollOnce().then(scheduleNext).catch(scheduleNext); }, ACTIVE_POLL_MS);
    }

    // First load + start polling loop.
    pollOnce().then(scheduleNext).catch(scheduleNext);

    // Refresh on opener postMessage (used by the parent when the admin clicks
    // Pop-Out again, so a stale window snaps back to current state).
    window.addEventListener("message", (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === "bracket-popout-refresh" && String(data.event_id || "") === String(mode.event_id)) {
        lastSig = "";
        pollOnce().catch(() => {});
        popToast("Refreshed.", "ok");
      }
    });

    root.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === "popout-close") {
        window.close();
      } else if (action === "popout-retry") {
        lastSig = "";
        pollOnce().catch(() => {});
      }
    });

    window.addEventListener("focus", () => {
      pollOnce().catch(() => {});
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") pollOnce().catch(() => {});
    });

    return;
  }

  // Logged-out behavior: grey out everything and show sign-in message
  if (!isLoggedIn) {
    root.innerHTML = `
      <div class="gl-ce-wrap gl-ce-disabled">
        <div class="gl-ce-card">
          <h2 class="gl-ce-h">Competition Events</h2>
          <p class="gl-ce-sub">Please sign in to access the Competition Events panel.</p>
        </div>
        <div class="gl-ce-tabs">
          <button class="gl-ce-tab is-active" data-tab="spectator" type="button">Spectator</button>
          <button class="gl-ce-tab" data-tab="driver" type="button">Driver</button>
          <button class="gl-ce-tab" data-tab="judge" type="button">Judge</button>
          <button class="gl-ce-tab" data-tab="admin" type="button">Admin</button>
        </div>
        <div class="gl-ce-panes">
          <section class="gl-ce-pane is-active" data-pane="spectator"></section>
          <section class="gl-ce-pane" data-pane="driver"></section>
          <section class="gl-ce-pane" data-pane="judge"></section>
          <section class="gl-ce-pane" data-pane="admin"></section>
        </div>
        <div class="gl-ce-output" id="glCeOutput" aria-live="polite"></div>
      </div>
    `;
    // Render empty panes for logged-out users
    const driverPane = root.querySelector('.gl-ce-pane[data-pane="driver"]');
    const judgePane = root.querySelector('.gl-ce-pane[data-pane="judge"]');
    const adminPane = root.querySelector('.gl-ce-pane[data-pane="admin"]');
    const spectatorPane = root.querySelector('.gl-ce-pane[data-pane="spectator"]');
    if (driverPane) spectatorAccountGate(driverPane, "Driver");
    if (judgePane) spectatorAccountGate(judgePane, "Judge");
    if (adminPane) spectatorAccountGate(adminPane, "Admin");
    if (!API) API = "/apps/comp-events/api";
    if (spectatorPane) renderSpectatorView("", spectatorPane);
    root.querySelectorAll(".gl-ce-pane").forEach((pane) => {
      pane.style.display = pane.dataset.pane === "spectator" ? "block" : "none";
    });
    root.addEventListener("click", (event) => {
      const tab = event.target.closest("button[data-tab]")?.dataset.tab;
      if (!tab) return;
      root.querySelectorAll(".gl-ce-tab[data-tab]").forEach((button) => button.classList.toggle("is-active", button.dataset.tab === tab));
      root.querySelectorAll(".gl-ce-pane").forEach((pane) => {
        const active = pane.dataset.pane === tab;
        pane.classList.toggle("is-active", active);
        pane.style.display = active ? "block" : "none";
      });
    });
    return;
  }

  root.innerHTML = `
    <div class="gl-ce-wrap">
      <div class="gl-ce-tabs">
        <button class="gl-ce-tab" data-tab="spectator" type="button">Spectator</button>
        <button class="gl-ce-tab" data-tab="driver" type="button">Driver</button>
        <button class="gl-ce-tab" data-tab="judge" type="button">Judge</button>
        <button class="gl-ce-tab" data-tab="admin" type="button">Admin</button>
      </div>

      <div class="gl-ce-panes">
        <section class="gl-ce-pane" data-pane="spectator"></section>
        <section class="gl-ce-pane" data-pane="driver"></section>
        <section class="gl-ce-pane" data-pane="judge"></section>
        <section class="gl-ce-pane" data-pane="admin"></section>
      </div>

      <div class="gl-ce-output" id="glCeOutput" aria-live="polite"></div>
    </div>
  `;

  const outputEl = root.querySelector("#glCeOutput");
  const tabs = [...root.querySelectorAll(".gl-ce-tab")];
  const panes = [...root.querySelectorAll(".gl-ce-pane")];
  const spectatorPane = root.querySelector('.gl-ce-pane[data-pane="spectator"]');
  let spectatorPortalInitialized = false;

  function toast(msg, kind = "info") {
    if (!outputEl) return;
    outputEl.innerHTML = `<div class="gl-ce-msg gl-ce-msg-${kind}">${esc(msg)}</div>`;
    clearTimeout(toast._t);
    toast._t = setTimeout(() => {
      outputEl.innerHTML = "";
    }, 5500);
  }

  function setTab(name) {
    tabs.forEach((b) => b.classList.toggle("is-active", b.dataset.tab === name));
    panes.forEach((p) => {
      const on = p.dataset.pane === name;
      p.classList.toggle("is-active", on);
      p.style.display = on ? "block" : "none";
    });
  }

  function openSpectatorPortal() {
    if (!spectatorPortalInitialized && spectatorPane) {
      renderSpectatorView("", spectatorPane);
      spectatorPortalInitialized = true;
    }
    setTab("spectator");
  }

  // Default tab
  setTab(isAdmin ? "admin" : "driver");

  // -----------------------------
  // BRACKET VISUAL RENDER
  // -----------------------------
  function computeNextKey(round, matchNo) {
    return `${round + 1}:${Math.ceil(matchNo / 2)}`;
  }

  function getBracketLayout(el) {
    if (!el) return "legacy";
    if (el.dataset && el.dataset.layout) return String(el.dataset.layout);
    if (el.classList && el.classList.contains("gl-ce-bracket-converging")) return "converging";
    if (el.classList && el.classList.contains("gl-ce-bracket-ltr")) return "ltr";
    return "legacy";
  }

  function findBracketRootFromInner(inner) {
    return inner?.closest?.(".gl-ce-bracket") || null;
  }

  function drawBracketLines(inner) {
    if (!inner) return;

    const svg = inner.querySelector(".gl-ce-bracket-lines");
    const container = inner.querySelector(".gl-ce-bracket-container");
    if (!svg) return;
    const bracketRoot = findBracketRootFromInner(inner);
    if (getBracketLayout(bracketRoot) === "ltr") {
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      return;
    }
    
    // Check if this is traditional bracket format
    const isTraditional = inner.closest(".gl-ce-bracket-traditional");
    
    if (isTraditional && container) {
      // Traditional converging bracket lines
      const w = container.scrollWidth || container.getBoundingClientRect().width || 0;
      const h = Math.max(container.scrollHeight || 0, container.getBoundingClientRect().height || 0);
      // The SVG lives in .gl-ce-bracket-inner while point coordinates are
      // container-relative. Anchor it to the container so connector paths do
      // not drift when the bracket receives viewport padding or is panned.
      svg.style.inset = "auto";
      svg.style.left = `${container.offsetLeft || 0}px`;
      svg.style.top = `${container.offsetTop || 0}px`;
      svg.setAttribute("width", String(Math.ceil(w)));
      svg.setAttribute("height", String(Math.ceil(h)));
      svg.setAttribute("viewBox", `0 0 ${Math.ceil(w)} ${Math.ceil(h)}`);
      
      const matchEls = [...inner.querySelectorAll(".gl-ce-bracket-match-traditional[data-round][data-matchno]")];
      const map = new Map();
      for (const el of matchEls) {
        const r = Number(el.dataset.round || 0);
        const n = Number(el.dataset.matchno || 0);
        map.set(`${r}:${n}`, el);
      }
      
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      const containerRect = container.getBoundingClientRect();
      
      const pt = (el, side) => {
        const r = el.getBoundingClientRect();
        const x = side === "left" ? r.right - containerRect.left : r.left - containerRect.left;
        const y = r.top - containerRect.top + r.height / 2;
        return { x, y };
      };
      
      const makePath = (d, cls) => {
        const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
        p.setAttribute("d", d);
        p.setAttribute("class", cls);
        return p;
      };
      
      for (const el of matchEls) {
        const r = Number(el.dataset.round || 0);
        const n = Number(el.dataset.matchno || 0);
        if (!r || !n) continue;
        
        const next = map.get(computeNextKey(r, n));
        if (!next) continue;
        
        // Determine if match is in left or right half
        const isLeft = el.closest(".gl-ce-bracket-left");
        const isRight = el.closest(".gl-ce-bracket-right");
        const nextIsLeft = next.closest(".gl-ce-bracket-left");
        const nextIsRight = next.closest(".gl-ce-bracket-right");
        const nextIsCenter = next.closest(".gl-ce-bracket-center");
        
        if (isLeft && (nextIsLeft || nextIsCenter)) {
          const a = pt(el, "right");
          const b = pt(next, "left");
          const midX = a.x + Math.max(30, (b.x - a.x) / 2);
          const d = `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
          svg.appendChild(makePath(d, "gl-ce-bracket-line"));
        } else if (isRight && (nextIsRight || nextIsCenter)) {
          const a = pt(el, "left");
          const b = pt(next, "right");
          const midX = a.x - Math.max(30, (a.x - b.x) / 2);
          const d = `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
          svg.appendChild(makePath(d, "gl-ce-bracket-line"));
        }
      }
    } else {
      // Original column-based bracket lines
      const cols = inner.querySelector(".gl-ce-bracket-cols");
      if (!cols) return;

      const w = cols.scrollWidth || cols.getBoundingClientRect().width || 0;
      const h = Math.max(cols.scrollHeight || 0, cols.getBoundingClientRect().height || 0);

      svg.setAttribute("width", String(Math.ceil(w)));
      svg.setAttribute("height", String(Math.ceil(h)));
      svg.setAttribute("viewBox", `0 0 ${Math.ceil(w)} ${Math.ceil(h)}`);

      const matchEls = [...inner.querySelectorAll(".gl-ce-bracket-match[data-round][data-matchno]")];
      const map = new Map();
      for (const el of matchEls) {
        const r = Number(el.dataset.round || 0);
        const n = Number(el.dataset.matchno || 0);
        map.set(`${r}:${n}`, el);
      }

      while (svg.firstChild) svg.removeChild(svg.firstChild);

      const innerRect = inner.getBoundingClientRect();

      const pt = (el, side) => {
        const r = el.getBoundingClientRect();
        const x = side === "left" ? r.left - innerRect.left : r.right - innerRect.left;
        const y = r.top - innerRect.top + r.height / 2;
        return { x, y };
      };

      const makePath = (d, cls) => {
        const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
        p.setAttribute("d", d);
        p.setAttribute("class", cls);
        return p;
      };

      for (const el of matchEls) {
        const r = Number(el.dataset.round || 0);
        const n = Number(el.dataset.matchno || 0);
        if (!r || !n) continue;

        const next = map.get(computeNextKey(r, n));
        if (!next) continue;

        const a = pt(el, "right");
        const b = pt(next, "left");
        const midX = a.x + Math.max(20, (b.x - a.x) / 2);

        const d = `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
        svg.appendChild(makePath(d, "gl-ce-bracket-line"));
      }
    }
  }

  function podiumMarkup(podiumData) {
    const first = podiumData.first || null;
    const second = podiumData.second || null;
    const third = podiumData.third || null;
    return `
      <div class="gl-ce-podium">
        <div class="gl-ce-podium-title">
          <h3 class="gl-ce-h2" style="margin: 0; text-align: center;">🏆 Final Standings</h3>
        </div>
        <div class="gl-ce-podium-container">
          <div class="gl-ce-podium-place gl-ce-podium-second">
            <div class="gl-ce-podium-medal">🥈</div>
            <div class="gl-ce-podium-label">2nd Place</div>
            <div class="gl-ce-podium-name">${second ? esc(second.label) : "—"}</div>
          </div>
          <div class="gl-ce-podium-place gl-ce-podium-first">
            <div class="gl-ce-podium-medal">🥇</div>
            <div class="gl-ce-podium-label">1st Place</div>
            <div class="gl-ce-podium-name">${first ? esc(first.label) : "—"}</div>
          </div>
          <div class="gl-ce-podium-place gl-ce-podium-third">
            <div class="gl-ce-podium-medal">🥉</div>
            <div class="gl-ce-podium-label">3rd Place</div>
            <div class="gl-ce-podium-name">${third ? esc(third.label) : "—"}</div>
          </div>
        </div>
      </div>
    `;
  }

  // PHASE 4: Render podium display
  function renderPodium(mount, podiumData) {
    if (!mount) {
      console.warn("renderPodium: mount element not found");
      return;
    }

    if (!podiumData || (!podiumData.first && !podiumData.second && !podiumData.third)) {
      mount.innerHTML = "";
      return;
    }

    mount.innerHTML = podiumMarkup(podiumData);
  }

  // Shared helper: Build render columns with slots (spacers + matches) from flat match array
  // Uses backend-provided side and position_in_side_round (no UI math for grouping)
  function buildRenderColumns(matches, meta = {}) {
    // Prefer backend meta if present; totalRounds: prefer final_round, else from match data
    let base = meta.base || meta.bracketSize;
    let hasPlayIn = meta.hasPlayIn;
    let totalRounds = (meta.final_round != null && Number(meta.final_round) > 0) ? Number(meta.final_round) : (meta.totalRounds || meta.total_rounds);
    let N = meta.N;
    
    // Infer hasPlayIn from match data if not provided
    if (hasPlayIn === undefined || hasPlayIn === null) {
      // Check if round==1 matches have role 'play_in' or 'bye'
      const round1Matches = matches.filter(m => Number(m.round || 1) === 1);
      hasPlayIn = round1Matches.some(m => {
        const role = String(m.role || '').toLowerCase();
        return role === 'play_in' || role === 'bye' || m.isBye || m.is_play_in;
      });
    }
    
    // Infer base from match data if not provided
    if (!base) {
      // Find first non-play-in round (round 2 if hasPlayIn, else round 1)
      const firstBaseRound = hasPlayIn ? 2 : 1;
      const baseRoundMatches = matches.filter(m => Number(m.round || 1) === firstBaseRound && (m.side === 'left' || m.side === 'right'));
      
      if (baseRoundMatches.length > 0) {
        // Group by side and count matches per side
        const leftCount = baseRoundMatches.filter(m => m.side === 'left').length;
        const rightCount = baseRoundMatches.filter(m => m.side === 'right').length;
        const matchesPerSide = Math.max(leftCount, rightCount, 1);
        // Base = matches per side * 2 (each match has 2 slots)
        base = matchesPerSide * 2;
      } else {
        // Fallback: try to infer from round 1 if no round 2
        const round1Matches = matches.filter(m => Number(m.round || 1) === 1 && (m.side === 'left' || m.side === 'right'));
        if (round1Matches.length > 0) {
          const leftCount = round1Matches.filter(m => m.side === 'left').length;
          const rightCount = round1Matches.filter(m => m.side === 'right').length;
          const matchesPerSide = Math.max(leftCount, rightCount, 1);
          base = matchesPerSide * 2;
        } else {
          base = 16; // Ultimate fallback
        }
      }
    }
    
    // Infer totalRounds from match data if not provided (main draw only; finite rounds)
    if (!totalRounds) {
      const mainMatches = matches.filter((m) => !isConsolationMatch(m));
      const rounds = mainMatches.map(m => Number(m.round || 1)).filter(Number.isFinite);
      const maxRound = rounds.length ? Math.max(...rounds) : 0;
      totalRounds = maxRound > 0 ? maxRound : 1;
    }
    
    // Infer N from match data if not provided
    if (!N) {
      // Count unique non-null drivers from slot_*_driver_id (actual drivers)
      const uniqueDrivers = new Set();
      matches.forEach(m => {
        if (m.slot_a_driver_id && m.slot_a_driver_id !== 'BYE' && m.slot_a_driver_id !== 'TBD') uniqueDrivers.add(m.slot_a_driver_id);
        if (m.slot_b_driver_id && m.slot_b_driver_id !== 'BYE' && m.slot_b_driver_id !== 'TBD') uniqueDrivers.add(m.slot_b_driver_id);
      });
      N = uniqueDrivers.size || matches.length || base;
    }
    
    // Helper: Compute slot positions for a round using spacing algorithm
    const computeSlotsForRound = (roundNum, matchesInRound, isPlayIn = false) => {
      const slots = [];
      const baseRoundOffset = hasPlayIn ? 1 : 0;
      const baseRoundNumber = roundNum - baseRoundOffset;
      let lead, gap;
      if (isPlayIn && roundNum === 1) {
        lead = 0;
        gap = 1;
      } else {
        lead = Math.pow(2, baseRoundNumber - 1) - 1;
        gap = Math.pow(2, baseRoundNumber) - 1;
      }
      if (lead > 0) {
        slots.push({ type: 'spacer', size: lead });
      }
      for (let i = 0; i < matchesInRound; i++) {
        slots.push({ type: 'match', position: i, slotIndex: slots.length });
        if (i < matchesInRound - 1 && gap > 0) {
          slots.push({ type: 'spacer', size: gap });
        }
      }
      return slots;
    };
    
    // Helper: Map matches to slots based on position_in_side_round
    const mapMatchesToSlots = (slots, matches) => {
      const matchMap = new Map();
      matches.forEach(m => {
        const pos = (m.position_in_side_round != null)
          ? Number(m.position_in_side_round)
          : (m.match_no != null)
            ? Math.max(0, Number(m.match_no) - 1)
            : 0;
        matchMap.set(pos, m);
      });
      return slots.map(slot => {
        if (slot.type === 'spacer') return slot;
        const match = matchMap.get(slot.position);
        return match ? { type: 'match', match, slotIndex: slot.slotIndex } : slot;
      });
    };
    
    const tree = [];
    const matchesByRound = new Map();
    
    // Group matches by round and side (using backend-provided 'side' field)
    for (const m of matches) {
      if (isConsolationMatch(m)) continue; // third place is rendered in center section
      const r = Number(m.round || 1);
      if (!matchesByRound.has(r)) matchesByRound.set(r, { left: [], right: [] });
      const side = m.side;
      if (side === 'left' || side === 'right') {
        matchesByRound.get(r)[side].push(m);
      }
    }
    
    // Only show rounds that have actual matches
    const actualRounds = [...new Set(matches.filter((m) => !isConsolationMatch(m)).map(m => Number(m.round || 1)))].sort((a, b) => a - b);
    const roundsToShow = actualRounds.length > 0 ? actualRounds : Array.from({ length: totalRounds }, (_, i) => i + 1);
    
    // For each round, compute slots using spacing algorithm and map matches
    for (const r of roundsToShow) {
      const roundData = matchesByRound.get(r) || { left: [], right: [] };
      const leftMatches = roundData.left || [];
      const rightMatches = roundData.right || [];
      
      // Calculate matches per side (for slot computation)
      const matchesPerSide = Math.max(leftMatches.length, rightMatches.length);
      
      // Determine if this is Round 1 play-in
      const isPlayInRound = (r === 1 && hasPlayIn);
      
      // Compute slots for this round (shared lattice for left and right)
      const slotLattice = computeSlotsForRound(r, matchesPerSide, isPlayInRound);
      
      // Map matches to slots for left side
      const leftSlots = mapMatchesToSlots(slotLattice, leftMatches);
      
      // Map matches to slots for right side (same lattice, different matches)
      const rightSlots = mapMatchesToSlots(slotLattice, rightMatches);
      
      tree.push({
        round: r,
        leftSlots: leftSlots,
        rightSlots: rightSlots,
        isFinal: r === totalRounds,
        isSemi: r === totalRounds - 1,
        finalMatch: null
      });
    }
    
    return tree;
  }

  /**
   * Shared comparator for deterministic match ordering (round, match_no numeric/string, id).
   * Used by both matchesSorted and stableBracketId input ordering.
   */
  function compareMatches(a, b) {
    const r = (Number(a.round) || 1) - (Number(b.round) || 1);
    if (r !== 0) return r;
    const ma = a.match_no ?? a.matchNo ?? "";
    const mb = b.match_no ?? b.matchNo ?? "";
    const na = Number(ma), nb = Number(mb);
    if (na === na && nb === nb) {
      if (na !== nb) return na - nb;
    } else {
      const sc = String(ma).localeCompare(String(mb));
      if (sc !== 0) return sc;
    }
    const ia = a.id ?? "", ib = b.id ?? "";
    const ni = Number(ia), nj = Number(ib);
    if (ni === ni && nj === nj) return ni - nj;
    return String(ia).localeCompare(String(ib));
  }

  /**
   * UI ordering for judging/admin match lists:
   * - keep natural round/match order
   * - but in the same round, show consolation before final
   */
  function compareMatchesForJudgingDisplay(a, b) {
    const roundDiff = (Number(a?.round) || 1) - (Number(b?.round) || 1);
    if (roundDiff !== 0) return roundDiff;
    const aIsConsolation = isConsolationMatch(a);
    const bIsConsolation = isConsolationMatch(b);
    if (aIsConsolation !== bIsConsolation) {
      return aIsConsolation ? -1 : 1;
    }
    const matchNoDiff = (Number(a?.match_no) || 0) - (Number(b?.match_no) || 0);
    if (matchNoDiff !== 0) return matchNoDiff;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  }

  /**
   * Deterministic bracketId for same bracket JSON (Comp View + Popout DOM parity).
   * Expects matches to already be sorted with compareMatches (no internal re-sort).
   * State key uses explicit defaults so the string never contains "undefined" or "null".
   */
  function stableBracketId(data, opts, matchesSorted) {
    if (typeof window !== "undefined" && window.__GLCE_VERIFY_BRACKET_SORT && matchesSorted.length > 1) {
      let ok = true;
      for (let i = 0; i < matchesSorted.length - 1; i++) {
        if (compareMatches(matchesSorted[i], matchesSorted[i + 1]) > 0) {
          ok = false;
          break;
        }
      }
      if (!ok) console.warn("[stableBracketId] expected sorted matches; array is not ordered by compareMatches");
    }
    const rawEventKey = data.event_id || data.eventId || opts.eventId || data.id || "ev";
    const eventKey = String(rawEventKey).replace(/[^a-zA-Z0-9_-]/g, "_");
    const keyParts = matchesSorted.map(m => {
      const round = Number(m.round || 1);
      const matchNo = String(m.match_no ?? m.matchNo ?? "");
      const id = String(m.id ?? "");
      const tp = m.is_third_place ? 1 : 0;
      return `${round}:${matchNo}:${id}:${tp}`;
    });
    const stateStr = keyParts.join("|");
    let h = 5381;
    for (let i = 0; i < stateStr.length; i++) h = ((h << 5) + h) + stateStr.charCodeAt(i);
    const hash = (h >>> 0).toString(36);
    return `bracket-${eventKey}-${hash}`;
  }

  /**
   * Canonical renderer for the inward-facing Formula-Drift-style bracket.
   * Used by admin, comp view, driver view, and bracket pop-out when rendering "Full Bracket".
   * Returns { html, bracketId, meta } where meta is used for wireBracketControls.
   *
   * Layout rules (UI ONLY — no bracket data is reshaped here):
   *   - Bracket generation, seeding, winner advancement, BYE logic, and the
   *     3rd-place row all live in worker.js. This renderer ONLY splits and
   *     mirrors the already-generated `data.matches`; it MUST NOT re-seed
   *     or re-sort the bracket.
   *   - Match.side ("left" | "right" | "center") is emitted by
   *     worker.js#resolveLaneSide. Round 1 left half = first matchesInRound/2
   *     matches by match_no, right half = remainder. The UI uses m.side as
   *     authoritative and only falls back to a match_no <= floor(N/2) rule
   *     for legacy NULL-side rows (pre repairLegacyLaneMetadata).
   *   - The grand final is the highest-round main match with side === "center"
   *     (NOT role: "grand_final", which is only in the unused bracket-builder
   *     reference fixtures). It is rendered in the center column between the
   *     two converging sides.
   *   - The 3rd-place battle is the worker-generated row with
   *     is_third_place === true (state: "third", match_no: 9999,
   *     side: "center"), populated by worker.js#populateThirdPlaceIfReady
   *     from the TWO SEMIFINAL LOSERS — never the final loser, never random
   *     semi losers. UI must not derive, reseed, or substitute participants.
   *   - For bracket sizes < 4 (i.e. size 2) there is no 3rd-place row.
   */
  function renderBracketTraditionalConverging(data, opts = {}) {
    if (!data || (data.error && !data.matches)) {
      const maxDrivers = opts.max_drivers || data?.max_drivers || 32;
      const bracketSize = Math.pow(2, Math.ceil(Math.log2(maxDrivers)));
      const errorMsg = data?.error ? `<div class="gl-ce-warn" style="margin-bottom:10px;">Error: ${esc(data.error)}</div>` : "";
      // Empty/error state: NO pan-zoom viewport (nothing to zoom). Plain
      // bracket placeholder; toolbar isn't useful here.
      const html = `
        ${errorMsg}
        <div class="gl-ce-muted">Bracket structure exists (${bracketSize} slots). ${data?.error ? "API error - please refresh." : "Loading matches..."}</div>
        <div class="gl-ce-bracket" data-bracket>
          <div class="gl-ce-bracket-inner">
            <div class="gl-ce-muted" style="padding:20px; text-align:center;">
              ${data?.error ? "Unable to load bracket. Please check your connection and try refreshing." : "Bracket will appear here once drivers register."}
            </div>
          </div>
        </div>
      `;
      return { html, bracketId: null, meta: {} };
    }

    const matches = Array.isArray(data.matches) ? data.matches : [];
    if (!matches.length) {
      const html = `
        <div class="gl-ce-bracket" data-bracket>
          <div class="gl-ce-bracket-inner">
            <div class="gl-ce-muted" style="padding:20px; text-align:center;">No bracket generated yet</div>
          </div>
        </div>
      `;
      return { html, bracketId: null, meta: {} };
    }

    // Deterministic ordering for stable HTML and bracketId (shared comparator)
    const matchesSorted = [...matches].sort(compareMatches);
    if (typeof window !== "undefined" && window.__GLCE_VERIFY_BRACKET_SORT) {
      let ok = true;
      for (let i = 0; i < matchesSorted.length - 1; i++) {
        if (compareMatches(matchesSorted[i], matchesSorted[i + 1]) > 0) {
          ok = false;
          break;
        }
      }
      if (!ok) console.warn("[renderBracketHTML] matchesSorted is not ordered by compareMatches");
    }

    const byRound = new Map();
    for (const m of matchesSorted) {
      const r = Number(m.round || 1);
      if (!byRound.has(r)) byRound.set(r, []);
      byRound.get(r).push(m);
    }
    const rounds = [...byRound.keys()].sort((a, b) => a - b);
    
    const maxRoundFromRenderedMatches = matchesSorted.length
      ? Math.max(...matchesSorted.filter((m) => !isConsolationMatch(m)).map(m => Number(m?.round || 0)).filter(Number.isFinite))
      : 0;
    const declaredTotalRounds = Number(opts.finalRound != null ? opts.finalRound : opts.totalRounds);
    const labelTotalRounds = maxRoundFromRenderedMatches > 0
      ? maxRoundFromRenderedMatches
      : (Number.isFinite(declaredTotalRounds) && declaredTotalRounds > 0 ? declaredTotalRounds : null);

    // Filter by activeRound if specified
    const activeRound = normalizeBracketRoundSelection(opts.activeRound);
    const totalRoundsForFilter = labelTotalRounds ?? (rounds.length ? Math.max(...rounds) : 0);
    let renderedMatches = matchesSorted;
    if (activeRound === "third-place") {
      const filteredMatches = matchesSorted.filter((m) => isConsolationMatch(m));
      if (filteredMatches.length > 0) {
        renderedMatches = filteredMatches;
        // Rebuild byRound with only the selected consolation matches.
        byRound.clear();
        for (const m of filteredMatches) {
          const r = Number(m.round || 1);
          if (!byRound.has(r)) byRound.set(r, []);
          byRound.get(r).push(m);
        }
      }
    } else if (Number.isFinite(activeRound)) {
      // Strict tab behavior: show only the selected round's matches.
      // Finals tab should show finals only (no automatic consolation injection).
      const filteredMatches = matchesSorted.filter(
        (m) => Number(m.round || 1) === activeRound && !isConsolationMatch(m)
      );
      if (filteredMatches.length > 0) {
        renderedMatches = filteredMatches;
        // Rebuild byRound with only the active round for metadata consistency.
        byRound.clear();
        for (const m of filteredMatches) {
          const r = Number(m.round || 1);
          if (!byRound.has(r)) byRound.set(r, []);
          byRound.get(r).push(m);
        }
      } else {
        // Keep empty so selected-round fallback shows one clear message.
        renderedMatches = [];
        byRound.clear();
      }
    }
    // When no activeRound, show all rounds.

    // If a specific numeric round is selected but there are no main-draw matches
    // for that round yet (e.g. final not generated), show one clear fallback message.
    if (Number.isFinite(activeRound)) {
      const hasMainRoundMatch = renderedMatches.some((m) => !isConsolationMatch(m));
      if (!hasMainRoundMatch) {
        const html = `
          <div class="gl-ce-bracket" data-bracket>
            <div class="gl-ce-bracket-inner">
              <div class="gl-ce-muted" style="padding:20px; text-align:center;">No bracket generated yet</div>
            </div>
          </div>
        `;
        return { html, bracketId: null, meta: {} };
      }
    }

    const currentId = String(opts.currentMatchId || "");

    // PHASE 4.1: Zoom & Expand Controls
    const currentRound = opts.currentRound || (currentId ? (() => {
      const match = matchesSorted.find(m => String(m.id) === currentId);
      return match ? Number(match.round || 1) : null;
    })() : null);
    
    // Calculate bracket structure for traditional converging format
    // Calculate N (actual driver count) and base (highest power of 2 <= N) for play-in strategy
    const allMatches = Array.isArray(data.matches) ? data.matches : [];
    const actualDrivers = new Set([...allMatches.map(m => m.driver_a), ...allMatches.map(m => m.driver_b)].filter(d => isRealParticipant(d))).size;
    const maxDrivers = data.max_drivers || opts.max_drivers || 32;
    const N = actualDrivers > 0 ? actualDrivers : maxDrivers;
    
    // Calculate base (highest power of 2 <= N) for play-in strategy
    const highestPowerOfTwoLessOrEqual = (n) => {
      n = Math.max(2, Math.min(64, Number(n) || 2));
      if (n <= 2) return 2;
      return Math.pow(2, Math.floor(Math.log2(n)));
    };
    const base = highestPowerOfTwoLessOrEqual(N);
    const hasPlayIn = (N !== base);
    
    // Use base as bracketSize (not next power of 2)
    let bracketSize = base;
    
    // Calculate rounds - for powers of 2 use log2, otherwise calculate
    const calculateRounds = (size) => {
      if ((size & (size - 1)) === 0) {
        return Math.log2(size) + 1;
      }
      let rounds = 1;
      let remaining = size / 2;
      while (remaining > 2) {
        rounds++;
        remaining = Math.ceil(remaining / 2);
      }
      return rounds + 1;
    };

    // Template #2: columns by exact backend round structure
    const mainMatches = renderedMatches.filter((m) => (
      !isConsolationMatch(m) && String(m?.side || "").toLowerCase() !== "center"
    ));
    const roundGroups = new Map();
    for (const m of mainMatches) {
      const r = Number(m.round || 1);
      if (!roundGroups.has(r)) roundGroups.set(r, []);
      roundGroups.get(r).push(m);
    }
    const maxRound = mainMatches.length ? Math.max(...mainMatches.map(m => Number(m.round || 1)).filter(Number.isFinite)) : 0;
    // Use actual rendered rounds for user-facing labels; fallback to declared rounds when needed.
    const totalRounds = labelTotalRounds || (maxRound > 0 ? maxRound : (opts.totalRounds || calculateRounds(bracketSize)));
    const mainRenderedMatches = mainMatches;
    const roundMatchCounts = new Map();
    for (const m of mainRenderedMatches) {
      const r = Number(m.round || 1);
      roundMatchCounts.set(r, (roundMatchCounts.get(r) || 0) + 1);
    }
    const maxMatchesInRound = roundMatchCounts.size
      ? Math.max(...Array.from(roundMatchCounts.values()))
      : Math.max(1, Math.ceil(N / 2));
    const roundsVisible = Math.max(
      1,
      roundMatchCounts.size || (maxRound > 0 ? maxRound : totalRounds)
    );
    const denseField = N >= 32 || maxMatchesInRound >= 16;
    const mediumField = N >= 16 || maxMatchesInRound >= 8;
    const compactField = N <= 8 && roundsVisible <= 3;
    const colWidth = denseField ? 168 : (mediumField ? 188 : (compactField ? 230 : 208));
    const roundGap = denseField ? 12 : (mediumField ? 16 : 20);
    const matchGapBase = denseField ? 8 : (mediumField ? 10 : 12);
    const roundOffsetStep = denseField ? 8 : (mediumField ? 11 : 14);
    const containerPadX = denseField ? 12 : (mediumField ? 16 : 20);
    const containerPadY = denseField ? 12 : (mediumField ? 14 : 18);
    const bracketMinWidth = Math.max(
      compactField ? 460 : 620,
      Math.round((roundsVisible * colWidth) + ((roundsVisible - 1) * roundGap) + (containerPadX * 2))
    );
    const bracketStyleVars = [
      `--gl-ce-round-col-width:${colWidth}px`,
      `--gl-ce-round-gap:${roundGap}px`,
      `--gl-ce-match-gap-base:${matchGapBase}px`,
      `--gl-ce-round-offset-step:${roundOffsetStep}px`,
      `--gl-ce-bracket-pad-x:${containerPadX}px`,
      `--gl-ce-bracket-pad-y:${containerPadY}px`,
      `--gl-ce-bracket-min-width:${bracketMinWidth}px`,
    ].join("; ");

    const bracketId = stableBracketId(data, opts, matchesSorted);

    function renderMatchCard(m) {
      const aLabel = m.driver_a_label || m.driver_a || "TBD";
      const bLabel = m.driver_b_label || m.driver_b || "TBD";
      const a = esc(aLabel);
      const b = esc(bLabel);
      const hasTbd = (aLabel === "TBD" || bLabel === "TBD");
      const bothTbd = (aLabel === "TBD" && bLabel === "TBD");
      const showTbdHint = hasTbd && !bothTbd;
      const aSeed = m.driver_a_seed || null;
      const bSeed = m.driver_b_seed || null;
      const decided = !!m.winner;
      const isAWin = !opts.manualInteract && decided && String(m.winner) === String(m.driver_a);
      const isBWin = !opts.manualInteract && decided && String(m.winner) === String(m.driver_b);
      const isCurrent = currentId && String(m.id) === currentId;
      const st = String(m.status || "pending").toLowerCase();
      const matchStatus = (st === "ready" || st === "complete") ? st : "pending";
      const aId = occupiedManualDriverId(m, "a");
      const bId = occupiedManualDriverId(m, "b");
      const interactAttrs = opts.manualInteract
        ? ` data-no-pan="1" data-manual-interact="1" data-match-id="${esc(m.id)}"`
        : "";
      const roster = opts.manualInteract && Array.isArray(opts.manualDrivers) ? opts.manualDrivers : [];
      const slotSelect = (slot, currentId) => {
        if (!opts.manualInteract) return "";
        return `<select class="gl-ce-bracket-slot-select" data-no-pan="1" data-manual-select="1" data-match-id="${esc(m.id)}" data-manual-slot="${slot}" aria-label="Swap driver">
          <option value="">TBD</option>
          ${roster.map((driver) => `<option value="${esc(driver.id)}"${String(currentId || "") === String(driver.id) ? " selected" : ""}>${esc(manualDriverOptionLabel(driver))}</option>`).join("")}
        </select>`;
      };
    return `
  <div class="gl-ce-bracket-match-traditional ${decided ? "is-decided" : ""} ${isCurrent ? "is-current" : ""} status-${matchStatus}"
       data-id="${esc(m.id)}"
       data-round="${esc(m.round)}"
       data-matchno="${esc(m.match_no)}"${interactAttrs}>
    <div class="gl-ce-bracket-driver-box ${isAWin ? "is-winner" : ""}"${opts.manualInteract ? ` data-no-pan="1" data-manual-slot="a" data-match-id="${esc(m.id)}" data-driver-id="${esc(aId)}"` : ""}>
      <div class="gl-ce-bracket-driver-name">${a}</div>
      ${slotSelect("a", aId)}
      ${aSeed ? `<div class="gl-ce-bracket-seed">${aSeed}</div>` : ''}
    </div>
    <div class="gl-ce-bracket-vs">VS</div>
    <div class="gl-ce-bracket-driver-box ${isBWin ? "is-winner" : ""}"${opts.manualInteract ? ` data-no-pan="1" data-manual-slot="b" data-match-id="${esc(m.id)}" data-driver-id="${esc(bId)}"` : ""}>
      <div class="gl-ce-bracket-driver-name">${b}</div>
      ${slotSelect("b", bId)}
      ${bSeed ? `<div class="gl-ce-bracket-seed">${bSeed}</div>` : ''}
    </div>
    ${showTbdHint ? `<div class="gl-ce-muted" style="font-size:11px; margin-top:6px; line-height:1.25;">${esc(tbdHint)}</div>` : ""}
  </div>
`;
    }

    function renderConsolationPlaceholderCard() {
      return `
  <div class="gl-ce-bracket-match-traditional gl-ce-bracket-match-placeholder status-pending" data-placeholder="consolation">
    <div class="gl-ce-bracket-driver-box">
      <div class="gl-ce-bracket-driver-name">TBD</div>
    </div>
    <div class="gl-ce-bracket-vs">VS</div>
    <div class="gl-ce-bracket-driver-box">
      <div class="gl-ce-bracket-driver-name">TBD</div>
    </div>
    <div class="gl-ce-muted" style="font-size:11px; margin-top:6px; line-height:1.25;">Third Place Match appears after semifinals.</div>
  </div>
`;
    }

    const orderedRounds = [...roundGroups.keys()]
      .map((r) => Number(r))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    const orderedMaxRound = orderedRounds.length ? Math.max(...orderedRounds) : null;
    const allMainMatches = matchesSorted.filter((m) => !isConsolationMatch(m));
    const finalMainMatch = allMainMatches
      .filter((m) => !isConsolationMatch(m))
      .sort((a, b) => {
        const roundDiff = Number(b?.round || 0) - Number(a?.round || 0);
        if (roundDiff !== 0) return roundDiff;
        return Number(a?.match_no || 0) - Number(b?.match_no || 0);
      })
      .find((m, idx, arr) => {
        const side = String(m?.side || "").toLowerCase();
        if (side === "center") return true;
        // Fallback for legacy rows missing side metadata.
        return idx === 0 && arr.length > 0;
      }) || null;
    const finalRound = finalMainMatch ? Number(finalMainMatch.round || 0) : null;
    // Insert 3rd-place after semis using true final round metadata when available.
    // Round groups exclude center-side final, so using only grouped rounds can place
    // 3rd-place one column too early (after quarter-finals).
    const inferredFinalRound = (Number.isFinite(finalRound) && finalRound > 0)
      ? finalRound
      : orderedMaxRound;
    const semiRound = (Number.isFinite(inferredFinalRound) && inferredFinalRound >= 2)
      ? (inferredFinalRound - 1)
      : null;
    const thirdPlaceMatch = renderedMatches.find((m) => {
      const state = String(m?.state || "").toLowerCase();
      return isConsolationMatch(m) || state === "third";
    }) || null;
    const shouldRenderThirdOnly = activeRound === "third-place";
    const isSingleRoundFocus = activeRound !== null;

    // matchesOverride lets the inward-facing layout pass in left-half or
    // right-half slices of a round without rebuilding roundGroups.
    const renderRoundGroup = (round, matchesOverride) => {
      const sourceMatches = matchesOverride != null ? matchesOverride : (roundGroups.get(round) || []);
      const roundMatches = sourceMatches.slice().sort(compareMatches);
      if (!roundMatches.length) return "";
      const rawGap = 10 * Math.pow(2, Math.max(0, round - 1));
      const roundGapForGroup = Math.max(10, Math.min(220, Math.round(rawGap)));
      const roundOffset = Math.max(0, Math.min(160, Math.floor(roundGapForGroup / 2)));
      const slotsHtml = roundMatches.map(renderMatchCard).join("");
      return `
  <div class="gl-ce-bracket-round-group" data-round="${round}" style="--round-offset:${roundOffset}px; --gl-ce-match-gap:${roundGapForGroup}px; --match-gap:${roundGapForGroup}px;">
    <div class="gl-ce-bracket-round-title">${displayRoundName(round, null, totalRounds)}</div>
    <div class="gl-ce-bracket-matches">
      ${slotsHtml}
    </div>
  </div>
`;
    };

    // Authoritative side: prefer worker-emitted m.side, fall back to splitting
    // by match_no for legacy NULL-side rows. Mirrors worker.js#resolveLaneSide
    // so the UI never disagrees with the bracket data.
    const sideOfMatch = (m) => {
      const s = String(m?.side || "").toLowerCase();
      if (s === "left" || s === "right" || s === "center") return s;
      if (m?.is_third_place) return "center";
      const r = Number(m?.round || 0);
      const cnt = roundMatchCounts.get(r) || 0;
      const mn = Number(m?.match_no || 0);
      if (cnt > 0 && mn > 0) return mn <= Math.floor(cnt / 2) ? "left" : "right";
      return "left";
    };

    // Split non-center main matches into left/right halves grouped by round.
    // Exclude the detected final by id so legacy data with a missing side on
    // the final row never double-renders it inside a side half.
    const finalMatchId = finalMainMatch ? String(finalMainMatch.id) : null;
    const leftByRound = new Map();
    const rightByRound = new Map();
    for (const m of mainMatches) {
      if (finalMatchId && String(m.id) === finalMatchId) continue;
      const side = sideOfMatch(m);
      if (side === "center") continue;
      const r = Number(m.round || 1);
      const targetMap = side === "left" ? leftByRound : rightByRound;
      if (!targetMap.has(r)) targetMap.set(r, []);
      targetMap.get(r).push(m);
    }

    const thirdPlaceColumnHtml = thirdPlaceMatch ? `
  <div class="gl-ce-bracket-round-group gl-ce-bracket-third-place ${isSingleRoundFocus ? "gl-ce-bracket-standalone-center" : ""}" data-round="third-place" style="--round-offset:0px; --gl-ce-match-gap:18px; --match-gap:18px;">
    <div class="gl-ce-bracket-round-title">3RD PLACE MATCH</div>
    <div class="gl-ce-bracket-matches">
      ${renderMatchCard(thirdPlaceMatch)}
    </div>
  </div>
` : "";
    const finalColumnHtml = finalMainMatch ? `
  <div class="gl-ce-bracket-round-group gl-ce-bracket-final-section ${isSingleRoundFocus ? "gl-ce-bracket-standalone-center" : ""}" data-round="${finalRound}" data-matchno="${esc(finalMainMatch.match_no || 1)}" style="--round-offset:0px; --gl-ce-match-gap:18px; --match-gap:18px;">
    <div class="gl-ce-bracket-round-title">FINAL BATTLE</div>
    <div class="gl-ce-bracket-matches">
      ${renderMatchCard(finalMainMatch)}
    </div>
  </div>
` : "";

    // Champion card sits ABOVE the final once the final has a winner.
    // The actual winner determination lives in worker.js — this only mirrors it.
    const championWinnerLabel = (finalMainMatch && finalMainMatch.winner)
      ? (finalMainMatch.winner_label || finalMainMatch.winner)
      : null;
    const championWinnerSeed = (finalMainMatch && finalMainMatch.winner_seed) || null;
    const championHtml = championWinnerLabel ? `
  <div class="gl-ce-bracket-champion" data-bracket-champion>
    <div class="gl-ce-bracket-champion-label">Champion</div>
    <div class="gl-ce-bracket-champion-name">${esc(championWinnerLabel)}</div>
    ${championWinnerSeed ? `<div class="gl-ce-bracket-champion-seed">Seed ${esc(championWinnerSeed)}</div>` : ""}
  </div>
` : "";

    // Build the half columns. Right-side columns are reversed in DOM order so
    // R1 sits on the OUTSIDE (rightmost) and later rounds advance inward.
    const halfRoundsFor = (byRound) =>
      orderedRounds.filter((r) => byRound.has(r) && r !== finalRound);

    const renderHalfHtml = (byRound, halfClass) => {
      const roundsForHalf = halfRoundsFor(byRound);
      const cols = roundsForHalf
        .map((r) => renderRoundGroup(r, byRound.get(r)))
        .filter(Boolean);
      if (!cols.length) return "";
      if (halfClass === "right") cols.reverse();
      return `
  <div class="gl-ce-bracket-half gl-ce-bracket-${halfClass}">
    <div class="gl-ce-bracket-columns">
      ${cols.join("")}
    </div>
  </div>`;
    };

    // Decide which regions to render based on activeRound:
    //   - "third-place" tab: center column with only the 3rd place card.
    //   - numeric tab == finalRound: center column with champion + final + 3rd.
    //   - numeric tab (other round): both half columns of that round only.
    //   - null (full bracket): left half + center column + right half.
    const centerInnerHtml = `${championHtml}${finalColumnHtml}${thirdPlaceColumnHtml}`;
    const centerHtml = centerInnerHtml ? `
  <div class="gl-ce-bracket-center">
    ${centerInnerHtml}
  </div>` : "";

    let wrapperBodyHtml = "";
    if (shouldRenderThirdOnly) {
      wrapperBodyHtml = thirdPlaceColumnHtml ? `
  <div class="gl-ce-bracket-center">${thirdPlaceColumnHtml}</div>` : "";
    } else if (Number.isFinite(activeRound) && Number.isFinite(finalRound) && Number(activeRound) === Number(finalRound)) {
      wrapperBodyHtml = centerHtml;
    } else if (Number.isFinite(activeRound)) {
      const leftHalfHtml = renderHalfHtml(leftByRound, "left");
      const rightHalfHtml = renderHalfHtml(rightByRound, "right");
      wrapperBodyHtml = `${leftHalfHtml}${rightHalfHtml}`;
    } else {
      const leftHalfHtml = renderHalfHtml(leftByRound, "left");
      const rightHalfHtml = renderHalfHtml(rightByRound, "right");
      wrapperBodyHtml = `${leftHalfHtml}${centerHtml}${rightHalfHtml}`;
    }

    const showBracketControls = opts.showBracketControls !== false;
    // Toolbar (.gl-ce-bracket-toolbar) is INTENTIONALLY emitted OUTSIDE the
    // .gl-ce-bracket-viewport / .gl-ce-bracket-panzoom-content wrapper so the
    // zoom/translate transform applied to the bracket content NEVER moves,
    // hides, or scales these buttons. Pan/zoom is UI-only and does not touch
    // bracket data, generation, seeding, or advancement.
    const header = `
      <div class="gl-ce-row gl-ce-row-between gl-ce-bracket-topbar gl-ce-bracket-toolbar">
        <div class="gl-ce-muted">
          ${opts.title ? `<b>${esc(opts.title)}</b> • ` : ``}
          Live updates: <b>${opts.live ? "ON" : "OFF"}</b>
          ${opts.lastUpdated ? ` • Last update: ${esc(opts.lastUpdated)}` : ""}
        </div>
        ${showBracketControls ? `
          <div class="gl-ce-bracket-controls">
            ${opts.showCurrentMatchButton !== false ? `
            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-zoom-to-current" data-bracket-id="${bracketId}" title="Center the current match in the bracket" aria-label="Center the current match in the bracket">
              <span>📍</span> Current Match
            </button>
            ` : ""}
            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-expand" data-bracket-id="${bracketId}" title="Fit the complete bracket in this view" aria-label="Fit the complete bracket in this view">
              <span>⤢</span> Reset
            </button>
            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-zoom-in" data-bracket-id="${bracketId}" title="Zoom in on the bracket" aria-label="Zoom in on the bracket">
              <span>+</span>
            </button>
            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-zoom-out" data-bracket-id="${bracketId}" title="Zoom out to the full bracket" aria-label="Zoom out to the full bracket">
              <span>−</span>
            </button>
            ${opts.showPopOut !== false ? `
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-popout" data-bracket-id="${bracketId}" title="Open a full-screen, responsive bracket window" aria-label="Open a full-screen, responsive bracket window">
                <span>⛶</span> Pop-Out
              </button>
            ` : ''}
          </div>
        ` : ``}
      </div>
    `;

    // Pan/zoom viewport structure:
    //   .gl-ce-bracket-viewport          <- overflow:hidden, drag/wheel zoom target
    //     .gl-ce-bracket-panzoom-content <- single transform: translate3d() scale()
    //       .gl-ce-bracket               <- existing bracket markup (UNCHANGED)
    //         .gl-ce-bracket-inner
    //           <svg .gl-ce-bracket-lines> (hidden via CSS; kept for back-compat)
    //           .gl-ce-bracket-container
    // The bracket content can be larger than the viewport; the viewport
    // clips it and the surrounding card/page never grows wider as a result.
    const html = `
        ${header}
        ${opts.manualInteract ? renderManualPodiumBar(opts) : ""}
        <div class="gl-ce-bracket-viewport" data-bracket-viewport data-bracket-id="${bracketId}">
          <div class="gl-ce-bracket-panzoom-content" data-bracket-panzoom-content data-bracket-id="${bracketId}">
            <div class="gl-ce-bracket gl-ce-bracket-traditional gl-ce-bracket-converging ${isSingleRoundFocus ? "gl-ce-bracket-single-round-focus" : ""}" data-bracket data-bracket-id="${bracketId}" data-layout="converging" data-zoom="1" data-view-mode="full" data-driver-count="${esc(N)}" data-round-count="${esc(totalRounds)}" data-total-rounds="${esc(totalRounds)}" style="${bracketStyleVars}">
              <div class="gl-ce-bracket-inner">
                <svg class="gl-ce-bracket-lines" aria-hidden="true"></svg>
                <div class="gl-ce-bracket-container">
                  ${wrapperBodyHtml}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    return { html, bracketId, meta: { currentRound, totalRounds, rounds, matches: matchesSorted, currentId, layout: "converging" } };
  }

  function isManualCompView(payload, ev) {
    return String(payload?.event?.bracket_mode || ev?.bracket_mode || "") === "manual";
  }

  function occupiedManualDriverId(match, side) {
    const slotId = side === "a" ? match?.slot_a_driver_id : match?.slot_b_driver_id;
    if (slotId && slotId !== "BYE" && slotId !== "TBD") return String(slotId);
    const fallback = side === "a" ? match?.driver_a : match?.driver_b;
    const label = String((side === "a" ? match?.driver_a_label : match?.driver_b_label) || "");
    if (!fallback || fallback === "BYE" || fallback === "TBD") return "";
    if (label === "TBD" || label === "BYE") return "";
    return String(fallback);
  }

  function manualEligibleDriversList() {
    const fromDraft = adminState.manualBracket?.eligible_drivers;
    if (Array.isArray(fromDraft) && fromDraft.length) return fromDraft;
    return registrationRows(adminState.regs)
      .filter((row) => String(row.role || "driver").toLowerCase() === "driver");
  }

  function manualDriverOptionLabel(driver) {
    if (!driver) return "TBD";
    return `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.email || driver.id;
  }

  function manualPodiumPlaceId(podium, place) {
    const entry = podium?.[place];
    if (!entry) return "";
    if (typeof entry === "object") return String(entry.id || "");
    return String(entry);
  }

  function renderManualPodiumBar(opts) {
    const roster = Array.isArray(opts.manualDrivers) ? opts.manualDrivers : [];
    const podium = opts.podium || {};
    const options = (place) => {
      const current = manualPodiumPlaceId(podium, place);
      return `<option value="">TBD</option>${roster.map((driver) => `<option value="${esc(driver.id)}"${current === String(driver.id) ? " selected" : ""}>${esc(manualDriverOptionLabel(driver))}</option>`).join("")}`;
    };
    return `
      <div class="gl-ce-manual-podium" data-no-pan="1">
        <div class="gl-ce-manual-podium-slot">
          <label>1st</label>
          <select class="gl-ce-input" data-manual-podium="first" data-no-pan="1">${options("first")}</select>
        </div>
        <div class="gl-ce-manual-podium-slot">
          <label>2nd</label>
          <select class="gl-ce-input" data-manual-podium="second" data-no-pan="1">${options("second")}</select>
        </div>
        <div class="gl-ce-manual-podium-slot">
          <label>3rd</label>
          <select class="gl-ce-input" data-manual-podium="third" data-no-pan="1">${options("third")}</select>
        </div>
      </div>
    `;
  }

  /**
   * Shared bracket HTML entry point.
   * Keep this wrapper so all callers route through one canonical renderer.
   */
  function renderBracketHTML(data, opts = {}) {
    return renderBracketTraditionalConverging(data, opts);
  }

  function renderBracketVisual(mount, data, opts = {}) {
    if (!mount) {
      console.warn("renderBracketVisual: mount element not found");
      return;
    }
    if (!renderBracketVisual._mounts) renderBracketVisual._mounts = new Set();
    renderBracketVisual._mounts.add(mount);
    renderBracketVisual._lastMount = mount;
    let result;
    const normalizedActiveRound = normalizeBracketRoundSelection(opts.activeRound);
    const renderOpts = { ...opts, activeRound: normalizedActiveRound };
    try {
      result = renderBracketHTML(data, renderOpts);
    } catch (err) {
      console.error("Error rendering traditional bracket format:", err);
      mount.innerHTML = `
        <div class="gl-ce-card gl-ce-card-soft" style="padding:12px;">
          <div class="gl-ce-warn">
            Error rendering bracket: ${esc(err?.message || "Unknown error")}
          </div>
        </div>
      `;
      return;
    }
    mount.innerHTML = result.html;
    if (!result.bracketId) return;
    const { bracketId: bid, meta } = result;
    const inner = mount.querySelector(".gl-ce-bracket-inner");
    if (!inner) return;

    // Draw the visual progression wires after layout settles. This is strictly
    // presentational: card order, progression, seeding, BYEs, and match data all
    // remain backend-owned. The calculation uses the rendered card geometry, so
    // it is shared by Admin, Driver, spectator, and pop-out bracket views.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => drawBracketLines(inner));
    });

    if (!renderBracketVisual._boundResize) {
      renderBracketVisual._boundResize = true;
      let resizeTimer = null;
      const onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const mounts = renderBracketVisual._mounts;
          if (!mounts) return;
          for (const mnt of mounts) {
            if (!mnt || !mnt.isConnected) continue;
            const fitToFrame = mnt.__glCeFitToFrame;
            if (typeof fitToFrame === "function") {
              fitToFrame({ reason: "resize" });
            }
          }
        }, 120);
      };
      window.addEventListener("resize", onResize);
      window.addEventListener("orientationchange", onResize);
    }

    wireBracketControls(mount, bid, meta);
  }

  // ==========================================================================
  // Bracket Pan/Zoom Viewport System (UI-only)
  // --------------------------------------------------------------------------
  // wireBracketControls() initializes the embedded bracket viewport described
  // in renderBracketTraditionalConverging — like an embedded Google Map:
  //
  //   .gl-ce-bracket-toolbar           (sticky outside; Reset/+/-/Current/Pop-Out)
  //   .gl-ce-bracket-viewport          (overflow:hidden; wheel zoom + drag pan)
  //     .gl-ce-bracket-panzoom-content (single transform: translate3d() scale())
  //       .gl-ce-bracket               (existing bracket markup — UNCHANGED)
  //
  // Rules followed by this layer:
  //   * No bracket data, generation, qualifying, seeding, advancement, BYE,
  //     or 3rd-place logic is touched here. This is pure UI state.
  //   * The transform is applied to .gl-ce-bracket-panzoom-content ONLY.
  //     The outer card, the toolbar, and the page wrapper are never scaled.
  //   * The bracket content is allowed to be larger than the viewport; the
  //     viewport clips it and the parent card never grows wider as a result.
  //   * Mouse wheel only zooms while the pointer is inside the viewport;
  //     page scroll is preserved everywhere else.
  //   * Click-drag pans inside the viewport. Clicks on buttons / inputs /
  //     match controls still work because we skip dragging on those targets.
  //   * Fit-to-view is computed AFTER the bracket renders and re-runs on
  //     window resize / orientation change.
  //
  // External contract (used by the rest of the file — DO NOT CHANGE):
  //   mount.__glCeBracketCleanup  -> tear down all listeners on next render
  //   mount.__glCeFitToFrame      -> resize callback to refit the bracket
  //   mount.__glCeBracketId       -> current bracketId (used by delegated clicks)
  //   mount.__glCeBracketTransformState -> {scale, tx, ty, fitScale, ...}
  // ==========================================================================
  function wireBracketControls(mount, bracketId, opts = {}) {
    if (!mount) {
      console.warn(`wireBracketControls: mount element not provided`);
      return;
    }

    // renderBracketVisual replaces mount.innerHTML on every poll/update, which
    // detaches per-element listeners but leaves mount-level + document-level
    // listeners (and their stale closures) attached. Tear those down first.
    if (typeof mount.__glCeBracketCleanup === "function") {
      try { mount.__glCeBracketCleanup(); } catch (_) {}
      mount.__glCeBracketCleanup = null;
    }

    // Preserve scale/tx/ty across re-renders (poll updates every ~2s) so the
    // user's zoom/pan doesn't snap back every time the bracket re-mounts.
    const priorState = mount.__glCeBracketTransformState || null;

    mount.__glCeBracketWired = true;
    mount.__glCeBracketId = bracketId;
    mount.__glCeBracketMeta = opts;

    const viewport = mount.querySelector(
      `.gl-ce-bracket-viewport[data-bracket-id="${bracketId}"]`
    ) || mount.querySelector(".gl-ce-bracket-viewport");
    const panzoomContent = mount.querySelector(
      `.gl-ce-bracket-panzoom-content[data-bracket-id="${bracketId}"]`
    ) || mount.querySelector(".gl-ce-bracket-panzoom-content");
    const bracket = mount.querySelector(`[data-bracket][data-bracket-id="${bracketId}"]`)
      || mount.querySelector(`.gl-ce-bracket[data-bracket-id="${bracketId}"]`);

    if (!bracket) {
      // Bracket placeholder/empty-state — wire only the click handler so any
      // Pop-Out button keeps working; no pan/zoom needed.
      const clickOnlyHandler = (e) => {
        const btn = e.target.closest(`[data-action][data-bracket-id="${mount.__glCeBracketId}"]`);
        if (!btn) return;
        const action = btn.getAttribute("data-action");
        if (action === "bracket-current-battle-popout") popOutCurrentBattle();
      };
      mount.addEventListener("click", clickOnlyHandler);
      mount.__glCeBracketCleanup = () => {
        try { mount.removeEventListener("click", clickOnlyHandler); } catch (_) {}
      };
      return;
    }

    if (!viewport || !panzoomContent) {
      // Older/empty render paths (error states) won't have the pan/zoom
      // wrapper. Still wire the click handler so toolbar buttons work.
      const clickOnlyHandler = (e) => {
        const btn = e.target.closest(`[data-action][data-bracket-id="${mount.__glCeBracketId}"]`);
        if (!btn) return;
        const action = btn.getAttribute("data-action");
        if (action === "bracket-current-battle-popout") popOutCurrentBattle();
      };
      mount.addEventListener("click", clickOnlyHandler);
      mount.__glCeBracketCleanup = () => {
        try { mount.removeEventListener("click", clickOnlyHandler); } catch (_) {}
      };
      return;
    }
    
    // ------------------------------------------------------------------------
    // Transform state — single source of truth for the pan/zoom layer.
    // (No bracket data lives here. State is preserved across re-renders.)
    //
    // Min-scale model (Google-Maps style):
    //   ABS_MIN_SCALE is just a hard safety floor to avoid divide-by-zero or
    //   absurdly tiny renders. The EFFECTIVE minimum zoom is the calculated
    //   fit-to-view scale, written into transformState.minScale by
    //   fitBracketToView() on every fit/refit. That means once the bracket
    //   is fully visible, the user cannot zoom out any further (mouse wheel,
    //   pinch, or the - button all bottom out at fit). Reset always returns
    //   to fit. PAN_PAD is the slack the user can drag past content edges.
    // ------------------------------------------------------------------------
    const ABS_MIN_SCALE = 0.05;
    const MAX_SCALE = 3;
    const ZOOM_STEP = 0.15;
    const PAN_PAD = 80;
    const transformState = {
      scale: 1,
      fitScale: 1,
      tx: 0,
      ty: 0,
      fitTx: 0,
      fitTy: 0,
      contentW: 0,
      contentH: 0,
      minScale: ABS_MIN_SCALE,
      maxScale: MAX_SCALE,
    };
    mount.__glCeBracketTransformState = transformState;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    // ------------------------------------------------------------------------
    // Pan clamp — keep the bracket from being dragged into endless empty
    // space. When the content is smaller than the viewport on an axis (which
    // is the case at fit-to-view), lock that axis to its centered position.
    // Otherwise allow PAN_PAD slack past each edge so the user can comfortably
    // bring corner cards inside the viewport without floating them in the void.
    // ------------------------------------------------------------------------
    const clampPan = (tx, ty, scale) => {
      const vpW = viewport.clientWidth || viewport.getBoundingClientRect().width || 0;
      const vpH = viewport.clientHeight || viewport.getBoundingClientRect().height || 0;
      const contentW = transformState.contentW || 0;
      const contentH = transformState.contentH || 0;
      const scaledW = contentW * scale;
      const scaledH = contentH * scale;
      let nextTx = Number.isFinite(tx) ? tx : 0;
      let nextTy = Number.isFinite(ty) ? ty : 0;
      if (vpW > 0 && contentW > 0) {
        if (scaledW <= vpW) {
          nextTx = (vpW - scaledW) / 2;
        } else {
          const minTx = vpW - scaledW - PAN_PAD;
          const maxTx = PAN_PAD;
          nextTx = clamp(nextTx, minTx, maxTx);
        }
      }
      if (vpH > 0 && contentH > 0) {
        if (scaledH <= vpH) {
          nextTy = (vpH - scaledH) / 2;
        } else {
          const minTy = vpH - scaledH - PAN_PAD;
          const maxTy = PAN_PAD;
          nextTy = clamp(nextTy, minTy, maxTy);
        }
      }
      return { tx: nextTx, ty: nextTy };
    };

    // ------------------------------------------------------------------------
    // Apply transform — the ONLY place that writes a transform to the DOM.
    // Every transform write runs through clampPan so the bracket can never
    // be dragged or zoomed into endless blank space.
    // ------------------------------------------------------------------------
    const applyTransform = ({ animate = false } = {}) => {
      const s = clamp(Number(transformState.scale) || 1, transformState.minScale, transformState.maxScale);
      const rawTx = Number.isFinite(transformState.tx) ? transformState.tx : 0;
      const rawTy = Number.isFinite(transformState.ty) ? transformState.ty : 0;
      const { tx, ty } = clampPan(rawTx, rawTy, s);
      transformState.scale = s;
      transformState.tx = tx;
      transformState.ty = ty;
      panzoomContent.style.transition = animate ? "transform 0.25s ease" : "none";
      panzoomContent.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${s})`;
      // Keep the data-zoom attribute current for any CSS hooks/tests.
      bracket.setAttribute("data-zoom", String(s));
    };

    // ------------------------------------------------------------------------
    // Measure & fit — scrollWidth/Height are layout sizes (unaffected by
    // CSS transform). Do not rewrite style.transform here: forcing scale(1)
    // to measure flashes the bracket when the user is zoomed in.
    // ------------------------------------------------------------------------
    const measureContent = () => {
      const w = panzoomContent.scrollWidth || 0;
      const h = panzoomContent.scrollHeight || 0;
      return { w, h };
    };

    const fitBracketToView = ({ animate = false } = {}) => {
      if (!viewport || !panzoomContent) return false;
      // Skip while the viewport isn't laid out (display:none / off-screen);
      // the resize/visibility hooks will re-fit later when it is.
      if (viewport.offsetParent === null && viewport.getClientRects().length === 0) {
        return false;
      }
      const vpW = viewport.clientWidth || viewport.getBoundingClientRect().width || 0;
      const vpH = viewport.clientHeight || viewport.getBoundingClientRect().height || 0;
      const { w: contentW, h: contentH } = measureContent();
      if (!(vpW > 0) || !(vpH > 0) || !(contentW > 0) || !(contentH > 0)) {
        return false;
      }
      // Fit-to-view scale — never upscale past 1.0, but ALLOW the calculated
      // fit to be smaller than any prior minScale. The fit scale itself becomes
      // the new effective minimum zoom (Google-Maps semantics): mouse-wheel-out,
      // pinch-out, and the - button all bottom out at fit so the user can never
      // zoom past the point where the full bracket is visible.
      const rawFit = Math.min(vpW / contentW, vpH / contentH);
      const fitScale = Math.max(ABS_MIN_SCALE, Math.min(1, rawFit));
      const tx = (vpW - contentW * fitScale) / 2;
      const ty = (vpH - contentH * fitScale) / 2;
      transformState.contentW = contentW;
      transformState.contentH = contentH;
      transformState.fitScale = fitScale;
      transformState.fitTx = tx;
      transformState.fitTy = ty;
      // Dynamic min-zoom: lock the floor to the calculated fit scale so the
      // user can't zoom further out than the full bracket. Also widen the max
      // ceiling for very-wide brackets so pinch-in still feels useful when
      // fitScale is small (e.g. on phones).
      transformState.minScale = fitScale;
      transformState.maxScale = Math.max(MAX_SCALE, fitScale * 4);
      transformState.scale = fitScale;
      transformState.tx = tx;
      transformState.ty = ty;
      applyTransform({ animate });
      return true;
    };

    // ------------------------------------------------------------------------
    // Pointer-centered zoom — zoom toward (or around) a viewport-local point
    // so the content under the pointer stays under the pointer. Falls back
    // to viewport-center zoom when no point is provided (button +/-).
    // ------------------------------------------------------------------------
    const zoomAtPoint = (nextScaleRaw, point) => {
      const nextScale = clamp(nextScaleRaw, transformState.minScale, transformState.maxScale);
      const prevScale = transformState.scale || 1;
      const vpRect = viewport.getBoundingClientRect();
      const px = point ? (point.x - vpRect.left) : (vpRect.width / 2);
      const py = point ? (point.y - vpRect.top) : (vpRect.height / 2);
      // Content-local coords UNDER the point at current scale:
      const contentX = (px - transformState.tx) / prevScale;
      const contentY = (py - transformState.ty) / prevScale;
      transformState.tx = px - contentX * nextScale;
      transformState.ty = py - contentY * nextScale;
      transformState.scale = nextScale;
      // If we're back at (or below) fit, snap to the cached fit transform so
      // the bracket re-centers cleanly when the user zooms all the way out.
      if (nextScale <= transformState.fitScale + 0.001) {
        transformState.scale = transformState.fitScale;
        transformState.tx = transformState.fitTx;
        transformState.ty = transformState.fitTy;
      }
      applyTransform({ animate: !point });
    };

    // ------------------------------------------------------------------------
    // Wheel zoom — only inside the viewport. preventDefault to stop the page
    // from scrolling while the pointer is over the bracket.
    // ------------------------------------------------------------------------
    const handleWheel = (e) => {
      // Skip form controls so a dropdown/button scroll still works. Match
      // cards use data-no-pan to block drag-pan, but wheel zoom should still
      // work while the pointer is over a card.
      if (e.target && e.target.closest && e.target.closest("button, input, select, textarea, a")) {
        return;
      }
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : -1;
      const factor = 1 + (direction * ZOOM_STEP);
      zoomAtPoint(transformState.scale * factor, { x: e.clientX, y: e.clientY });
    };

    // ------------------------------------------------------------------------
    // Pointer-based pan — shared logic for mouse + touch via Pointer Events.
    // Skips drag start on buttons / inputs / match controls so winner-select
    // and other clicks inside the bracket keep working.
    // ------------------------------------------------------------------------
    let dragPointerId = null;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartTx = 0;
    let dragStartTy = 0;

    const isInteractiveTarget = (target) => {
      if (!target || !target.closest) return false;
      return !!target.closest("button, input, select, textarea, a, [data-no-pan], .gl-ce-bracket-controls");
    };

    const handlePointerDown = (e) => {
      if (e.button != null && e.button !== 0 && e.pointerType === "mouse") return;
      if (isInteractiveTarget(e.target)) return;
      dragPointerId = e.pointerId != null ? e.pointerId : 0;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragStartTx = transformState.tx;
      dragStartTy = transformState.ty;
      viewport.classList.add("is-dragging");
      try { viewport.setPointerCapture && viewport.setPointerCapture(e.pointerId); } catch (_) {}
      panzoomContent.style.transition = "none";
      e.preventDefault();
    };

    const handlePointerMove = (e) => {
      if (dragPointerId === null) return;
      if (e.pointerId != null && e.pointerId !== dragPointerId) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      transformState.tx = dragStartTx + dx;
      transformState.ty = dragStartTy + dy;
      applyTransform({ animate: false });
      e.preventDefault();
    };

    const endDrag = (e) => {
      if (dragPointerId === null) return;
      if (e && e.pointerId != null && e.pointerId !== dragPointerId) return;
      try { viewport.releasePointerCapture && viewport.releasePointerCapture(dragPointerId); } catch (_) {}
      dragPointerId = null;
      viewport.classList.remove("is-dragging");
    };

    // ------------------------------------------------------------------------
    // Pinch zoom on touch — track 2 active pointers and use their distance
    // delta. (Single-pointer touch already drags via handlePointerDown.)
    // ------------------------------------------------------------------------
    const activeTouchPointers = new Map();
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    let pinchCenter = { x: 0, y: 0 };

    const pinchDistance = () => {
      const pts = Array.from(activeTouchPointers.values());
      if (pts.length < 2) return 0;
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const pinchMidpoint = () => {
      const pts = Array.from(activeTouchPointers.values());
      if (pts.length < 2) return { x: 0, y: 0 };
      return { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    };

    const handleTouchPointerDown = (e) => {
      if (e.pointerType !== "touch") return;
      activeTouchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activeTouchPointers.size === 2) {
        // Cancel any in-progress single-pointer drag so it doesn't fight pinch.
        dragPointerId = null;
        viewport.classList.remove("is-dragging");
        pinchStartDistance = pinchDistance();
        pinchStartScale = transformState.scale;
        pinchCenter = pinchMidpoint();
      }
    };

    const handleTouchPointerMove = (e) => {
      if (e.pointerType !== "touch") return;
      if (!activeTouchPointers.has(e.pointerId)) return;
      activeTouchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activeTouchPointers.size === 2 && pinchStartDistance > 0) {
        const d = pinchDistance();
        if (d > 0) {
          const factor = d / pinchStartDistance;
          zoomAtPoint(pinchStartScale * factor, pinchCenter);
        }
        e.preventDefault();
      }
    };

    const handleTouchPointerUp = (e) => {
      if (e.pointerType !== "touch") return;
      activeTouchPointers.delete(e.pointerId);
      if (activeTouchPointers.size < 2) {
        pinchStartDistance = 0;
      }
    };

    // ------------------------------------------------------------------------
    // Toolbar action handlers
    // ------------------------------------------------------------------------
    const resetView = () => {
      fitBracketToView({ animate: true });
    };

    const zoomIn = () => {
      zoomAtPoint(transformState.scale + ZOOM_STEP);
    };

    const zoomOut = () => {
      zoomAtPoint(transformState.scale - ZOOM_STEP);
    };

    // Pan-to-current-match: bring the .is-current match (or one matching the
    // currentId passed from meta) into the center of the viewport WITHOUT
    // touching bracket data. Math is the inverse of the transform: we want
    // the match's content-local center to land at the viewport's pixel center.
    const centerCurrentMatch = ({ silent = false } = {}) => {
      const currentId = opts?.currentId || "";
      const match = bracket.querySelector(".gl-ce-bracket-match-traditional.is-current, .gl-ce-bracket-match.is-current")
        || (currentId ? (
            bracket.querySelector(`[data-id="${currentId}"]`)
            || bracket.querySelector(`[data-matchid="${currentId}"]`)
            || bracket.querySelector(`[data-match-id="${currentId}"]`)
          ) : null);
      if (!match) {
        if (!silent) toast(currentId ? "Current match not found in bracket." : "No current match to zoom to.", "info");
        return;
      }
      // Walk offsetParent chain up to panzoomContent so the resulting
      // coordinates are in content-local space (independent of transform).
      let x = 0, y = 0;
      let node = match;
      while (node && node !== panzoomContent) {
        x += node.offsetLeft || 0;
        y += node.offsetTop || 0;
        node = node.offsetParent;
      }
      const matchW = match.offsetWidth || 0;
      const matchH = match.offsetHeight || 0;
      const cx = x + matchW / 2;
      const cy = y + matchH / 2;
      // Keep current zoom but lift it slightly toward 1.0 for visibility.
      const targetScale = clamp(Math.max(transformState.scale, Math.min(1, transformState.fitScale * 1.5)), transformState.minScale, transformState.maxScale);
      const vpRect = viewport.getBoundingClientRect();
      transformState.scale = targetScale;
      transformState.tx = vpRect.width / 2 - cx * targetScale;
      transformState.ty = vpRect.height / 2 - cy * targetScale;
      applyTransform({ animate: true });
      // Flash highlight for ~3s for visual confirmation.
      bracket.querySelectorAll(".gl-ce-bracket-match-highlight").forEach((m) => m.classList.remove("gl-ce-bracket-match-highlight"));
      match.classList.add("gl-ce-bracket-match-highlight");
      setTimeout(() => { match.classList.remove("gl-ce-bracket-match-highlight"); }, 3000);
    };

    // ------------------------------------------------------------------------
    // Pop-out button (opens the same Shopify page with a hash flag so the
    // popup re-uses our CSS and renders a fresh viewport with its own pan/zoom).
    // ------------------------------------------------------------------------
    const popOutBracket = () => {
      const eventId = String(
        adminState.selectedId
          || adminState.comp?.payload?.bracket?.event_id
          || adminState.comp?.payload?.event_id
          || adminState.bracket?.event_id
          || driverState?.bracket?.event_id
          || ""
      );
      if (!eventId) {
        toast("No event selected to pop out. Open a comp first.", "err");
        return;
      }
      const baseUrl = `${location.origin}${location.pathname}${location.search}`;
      const popoutUrl = `${baseUrl}#glce_view=bracket_popout&glce_event_id=${encodeURIComponent(eventId)}`;
      if (popOutBracket._popup && !popOutBracket._popup.closed) {
        try { popOutBracket._popup.focus(); } catch (_) {}
        try {
          popOutBracket._popup.postMessage(
            { type: "bracket-popout-refresh", event_id: eventId },
            window.location.origin
          );
        } catch (_) {}
        return;
      }
      const availableWidth = window.screen?.availWidth || 1600;
      const availableHeight = window.screen?.availHeight || 1000;
      const popupWidth = Math.min(availableWidth, Math.max(960, Math.floor(availableWidth * 0.98)));
      const popupHeight = Math.min(availableHeight, Math.max(700, Math.floor(availableHeight * 0.94)));
      const popup = window.open(
        popoutUrl,
        "bracketPopup",
        `width=${popupWidth},height=${popupHeight},left=0,top=0,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no`
      );
      if (!popup) {
        toast("Popup blocked. Please allow popups for this site.", "err");
        return;
      }
      try {
        popup.moveTo(0, 0);
        popup.resizeTo(window.screen?.availWidth || popupWidth, window.screen?.availHeight || popupHeight);
      } catch (_) {
        // Browsers may disallow moving or maximizing pop-ups; the responsive size still applies.
      }
      popOutBracket._popup = popup;
      toast("Bracket opened in new window.", "ok");
    };

    // ------------------------------------------------------------------------
    // Delegated toolbar click handler (single listener on the mount).
    // ------------------------------------------------------------------------
    const clickHandler = (e) => {
      const bid = mount.__glCeBracketId;
      const btn = e.target.closest(`[data-action][data-bracket-id="${bid}"]`);
      if (!btn) return;
      const action = btn.getAttribute("data-action");
      if (!action || !action.startsWith("bracket-")) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        switch (action) {
          case "bracket-zoom-to-current": centerCurrentMatch(); break;
          case "bracket-expand":          resetView(); break;
          case "bracket-zoom-in":         zoomIn(); break;
          case "bracket-zoom-out":        zoomOut(); break;
          case "bracket-popout":          popOutBracket(); break;
          case "bracket-current-battle-popout": popOutCurrentBattle(); break;
          default: console.warn("Unknown bracket action:", action);
        }
      } catch (err) {
        console.error(`Error handling bracket action "${action}":`, err);
        toast(`Error: ${err.message || "Unknown error"}`, "err");
      }
    };

    // ------------------------------------------------------------------------
    // Wire listeners onto the viewport. wheel uses {passive:false} so we can
    // preventDefault and stop page scroll while zooming inside the bracket.
    // ------------------------------------------------------------------------
    viewport.addEventListener("wheel", handleWheel, { passive: false });
    viewport.addEventListener("pointerdown", handlePointerDown);
    viewport.addEventListener("pointermove", handlePointerMove);
    viewport.addEventListener("pointerup", endDrag);
    viewport.addEventListener("pointercancel", endDrag);
    viewport.addEventListener("pointerleave", endDrag);
    viewport.addEventListener("pointerdown", handleTouchPointerDown);
    viewport.addEventListener("pointermove", handleTouchPointerMove);
    viewport.addEventListener("pointerup", handleTouchPointerUp);
    viewport.addEventListener("pointercancel", handleTouchPointerUp);
    // Suppress browser drag-image when the user drags a card-like element.
    viewport.addEventListener("dragstart", (e) => e.preventDefault());
    mount.addEventListener("click", clickHandler);

    // ------------------------------------------------------------------------
    // Initial fit — scheduled across 2 rAFs so layout has settled. Restore
    // prior pan/zoom state if this is a re-render (poll update) instead of
    // the very first mount, so the bracket doesn't snap back every ~2s.
    // ------------------------------------------------------------------------
    const scheduleFitToFrame = ({ reason = "initial", force = false } = {}) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const fitOk = fitBracketToView({ animate: reason === "reset" });
          if (!fitOk) return;
          // On window resize / orientation change, the viewport dimensions
          // changed, which means fitScale (and therefore minScale) just
          // changed too. Skip the priorState restore for this case: we want
          // to keep the bracket centered at the new fit scale, exactly like
          // the user's plan asks ("keep bracket centered if reset or initial
          // load"). Otherwise we'd risk leaving the user below the new fit
          // floor or off-center.
          const skipRestore = force || reason === "resize" || reason === "reset";
          if (!skipRestore && priorState && Number.isFinite(priorState.scale) && priorState.scale > 0) {
            // Restore the user's last zoom/pan but clamp to the new content
            // and viewport bounds (the bracket may have grown a round between
            // polls). Only restore if it's meaningfully different from fit.
            const restoredScale = clamp(priorState.scale, transformState.minScale, transformState.maxScale);
            if (Math.abs(restoredScale - transformState.fitScale) > 0.005) {
              transformState.scale = restoredScale;
              transformState.tx = Number.isFinite(priorState.tx) ? priorState.tx : transformState.fitTx;
              transformState.ty = Number.isFinite(priorState.ty) ? priorState.ty : transformState.fitTy;
              applyTransform({ animate: false });
            }
          }
          // Defensive: if anything left scale below the (possibly new)
          // minScale, snap back to the centered fit transform.
          if (transformState.scale < transformState.minScale - 0.001) {
            transformState.scale = transformState.fitScale;
            transformState.tx = transformState.fitTx;
            transformState.ty = transformState.fitTy;
            applyTransform({ animate: false });
          }
        });
      });
    };
    mount.__glCeFitToFrame = (info) => scheduleFitToFrame({ ...(info || {}), force: info?.reason === "reset" });
    mount.__glCeCenterCurrentMatch = () => centerCurrentMatch({ silent: true });
    scheduleFitToFrame({ reason: "mount" });

    // ------------------------------------------------------------------------
    // Cleanup contract — must mirror every listener we added above so the
    // next renderBracketVisual() pass starts with a clean slate.
    // ------------------------------------------------------------------------
    mount.__glCeBracketCleanup = () => {
      try { viewport.removeEventListener("wheel", handleWheel, { passive: false }); } catch (_) {}
      try { viewport.removeEventListener("pointerdown", handlePointerDown); } catch (_) {}
      try { viewport.removeEventListener("pointermove", handlePointerMove); } catch (_) {}
      try { viewport.removeEventListener("pointerup", endDrag); } catch (_) {}
      try { viewport.removeEventListener("pointercancel", endDrag); } catch (_) {}
      try { viewport.removeEventListener("pointerleave", endDrag); } catch (_) {}
      try { viewport.removeEventListener("pointerdown", handleTouchPointerDown); } catch (_) {}
      try { viewport.removeEventListener("pointermove", handleTouchPointerMove); } catch (_) {}
      try { viewport.removeEventListener("pointerup", handleTouchPointerUp); } catch (_) {}
      try { viewport.removeEventListener("pointercancel", handleTouchPointerUp); } catch (_) {}
      try { mount.removeEventListener("click", clickHandler); } catch (_) {}
    };
  }
  
  // Pop-out current battle selection
  function popOutCurrentBattle() {
    const payload = adminState.comp.payload;
    if (!payload || !payload.current_match) {
      toast("No current battle selected.", "info");
      return;
    }
    
    const cur = payload.current_match;
    const ev = adminState.selectedId
      ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
      : null;
    
    const popup = window.open("", "currentBattlePopup", "width=800,height=600,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no");
    if (!popup) {
      toast("Popup blocked. Please allow popups for this site.", "err");
      return;
    }
    
    const aLabel = esc(cur.driver_a_label || "Driver A");
    const bLabel = esc(cur.driver_b_label || "Driver B");
    const isIrl = isIrlEventType(payload?.event || ev);
    const popupMaxRoundFromMatches = Array.isArray(payload?.bracket?.matches)
      ? Math.max(0, ...payload.bracket.matches.filter((m) => !isConsolationMatch(m)).map(m => Number(m?.round || 0)).filter(Number.isFinite))
      : 0;
    const popupFinalRound = popupMaxRoundFromMatches > 0
      ? popupMaxRoundFromMatches
      : Number(payload?.bracket?.final_round || 0);
    const popupMatchTitle = buildJudgeMatchTitle(cur, popupFinalRound || null);
    const aMetaHtml = renderJudgeDriverMetaHtml("driver_a", cur, isIrl, "gl-ce-comp-driver-meta");
    const bMetaHtml = renderJudgeDriverMetaHtml("driver_b", cur, isIrl, "gl-ce-comp-driver-meta");
    
    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Current Battle - ${esc(ev?.event_name || "Event")} - ${esc(popupMatchTitle)}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: Arial, sans-serif;
            background: #f5f5f5;
            color: #333;
            padding: 20px;
          }
          .battle-card {
            background: white;
            border-radius: 8px;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            max-width: 600px;
            margin: 0 auto;
          }
          .battle-header {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 20px;
            text-align: center;
            color: #076251;
          }
          .battle-drivers {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .battle-driver {
            text-align: center;
            padding: 15px;
            background: #f8f8f8;
            border-radius: 6px;
            border: 2px solid #e0e0e0;
          }
          .battle-driver.winner {
            border-color: #076251;
            background: rgba(7,98,81,0.1);
          }
          .battle-driver h3 {
            font-size: 20px;
            margin-bottom: 10px;
          }
          .gl-ce-comp-driver-meta {
            font-size: 12px;
            line-height: 1.6;
            color: #555;
            margin-top: 6px;
          }
          .battle-vs {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            color: #666;
            margin: 10px 0;
          }
          .battle-info {
            text-align: center;
            color: #666;
            font-size: 14px;
            margin-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="battle-card">
          <div class="battle-header">Current Battle</div>
          <div class="battle-info">${esc(popupMatchTitle)}</div>
          <div class="battle-drivers">
            <div class="battle-driver ${cur.winner === cur.driver_a ? 'winner' : ''}">
              <h3>${aLabel}</h3>
              ${aMetaHtml}
              ${cur.winner === cur.driver_a ? '<div style="color: #076251; font-weight: bold;">WINNER</div>' : ''}
            </div>
            <div class="battle-driver ${cur.winner === cur.driver_b ? 'winner' : ''}">
              <h3>${bLabel}</h3>
              ${bMetaHtml}
              ${cur.winner === cur.driver_b ? '<div style="color: #076251; font-weight: bold;">WINNER</div>' : ''}
            </div>
          </div>
          <div class="battle-vs">VS</div>
          ${cur.winner ? `
            <div style="text-align: center; margin-top: 15px; padding: 10px; background: rgba(7,98,81,0.1); border-radius: 6px;">
              <strong>Winner: ${esc(cur.winner_label || cur.winner)}</strong>
            </div>
          ` : '<div style="text-align: center; margin-top: 15px; color: #666;">Pending</div>'}
        </div>
      </body>
      </html>
    `);
    popup.document.close();
  }

  // -----------------------------
  // DRIVER pane
  // -----------------------------
  const driverPane = root.querySelector('.gl-ce-pane[data-pane="driver"]');

  const driverState = {
    myEvents: [],
    code: "",
    event: null,
    bracket: null,
    qualifying: null,
    roster: null,
    rosterLoading: false,
    rosterError: "",
    bracketSig: "",
    qualifyingSig: "",
    rosterSig: "",
    bracketActiveRound: null,
    lastBracketAt: "",
    live: {
      enabled: true,
      bracketMs: 2500,
      rosterMs: 3000,
      tBracket: null,
      tRoster: null,
      quietErrors: 0,
    },
  };

  function renderEventSummaryCard(ev, { showCode } = {}) {
    const st = normalizeStatus(ev.computed_status || ev.status);
    const type = String(ev.type || "").toLowerCase();
    const prizes = parseMaybeJson(ev.prizes_json) || null;

    // Parse car download URL from car_links_json or use car_download_url
    let carDownload = ev.car_download_url || "";
    if (!carDownload && ev.car_links_json) {
      try {
        const carLinks = parseMaybeJson(ev.car_links_json);
        if (Array.isArray(carLinks) && carLinks.length > 0) {
          carDownload = carLinks[0];
        } else if (typeof carLinks === 'string') {
          carDownload = carLinks;
        }
      } catch {
        // If parsing fails, try as string
        carDownload = String(ev.car_links_json || "").trim();
      }
    }
    
    // Get track download URL from track_link or track_download_url
    const trackDownload = ev.track_download_url || ev.track_link || "";

    const prizesHtml = prizes
      ? `<pre class="gl-ce-pre">${esc(JSON.stringify(prizes, null, 2))}</pre>`
      : `<div class="gl-ce-muted">None</div>`;

    return `
      <div class="gl-ce-card">
        <h2 class="gl-ce-h">
          ${esc(eventDisplayName(ev, "Event"))}
          <span class="gl-ce-pill gl-ce-pill-${esc(st)}">${esc(st)}</span>
        </h2>

        <div class="gl-ce-grid2">
          <div>
            ${showCode ? `<div><b>Code:</b> <code>${esc(ev.comp_code || "")}</code></div>` : ""}
            <div><b>Type:</b> ${esc(type || "virtual")}</div>
            <div><b>Organizer:</b> ${esc(ev.organizer || "")}</div>
            <div><b>Venue:</b> ${esc(ev.venue || "")}</div>
            ${type === "irl" ? `<div><b>Address:</b> ${esc(ev.venue_address || "")}</div>` : ""}
            ${ev.start_at ? `<div><b>Start:</b> ${esc(fmtDateInTz(ev.start_at, ev.start_at_tz))}</div>` : ""}
            ${ev.reg_close_at ? `<div><b>Registration closes:</b> ${esc(fmtDateInTz(ev.reg_close_at, ev.reg_close_tz))}</div>` : ""}
          </div>
          <div>
            <div><b>Bracket max drivers:</b> ${esc(ev.max_drivers || 0)}</div>
            ${Number(ev.registration_cap_enabled || 0) === 1 && Number(ev.registration_cap || 0) > 0
              ? `<div><b>Public registration cap:</b> ${esc(ev.registration_cap)}</div>`
              : `<div><b>Public registration cap:</b> No cap</div>`}

            ${
              type === "virtual"
                ? `
                  <div><b>Game:</b> ${esc(ev.game || "")}</div>
                  ${
                    carDownload
                      ? `<div><b>Car download:</b> <a class="gl-ce-link" href="${esc(
                          carDownload
                        )}" target="_blank" rel="noopener">Open</a></div>`
                      : ""
                  }
                  ${
                    trackDownload
                      ? `<div><b>Track download:</b> <a class="gl-ce-link" href="${esc(
                          trackDownload
                        )}" target="_blank" rel="noopener">Open</a></div>`
                      : ""
                  }
                `
                : ``
            }
          </div>
        </div>

        ${ev.special_message ? `<div class="gl-ce-callout">${esc(ev.special_message)}</div>` : ""}

        <div class="gl-ce-grid2">
          <div>
            <h3 class="gl-ce-h3">Prizes</h3>
            ${prizesHtml}
          </div>
          <div>
            <h3 class="gl-ce-h3">Event Notes</h3>
            <div class="gl-ce-muted">Follow organizer rules, safety requirements, and application requirements.</div>
          </div>
        </div>
      </div>
    `;
  }

  function renderDriverRegistrationCard(ev) {
    const st = normalizeStatus(ev?.status);
    // Also consult the UTC close timestamp so the button flips to disabled
    // the instant the close time passes, without waiting for the next API
    // round-trip. The backend remains the source of truth — this is just a
    // tighter client-side gate.
    const timeOpen = isRegistrationOpen(ev);
    const eventType = String(ev?.type || "").trim().toLowerCase();
    const isVirtual = eventType === "virtual" || eventType === "sim";
    
    // Parse car download URL from car_links_json or use car_download_url
    let carDownload = ev.car_download_url || "";
    if (!carDownload && ev.car_links_json) {
      try {
        const carLinks = parseMaybeJson(ev.car_links_json);
        if (Array.isArray(carLinks) && carLinks.length > 0) {
          carDownload = carLinks[0];
        } else if (typeof carLinks === 'string') {
          carDownload = carLinks;
        }
      } catch {
        // If parsing fails, try as string
        carDownload = String(ev.car_links_json || "").trim();
      }
    }
    
    // Get track download URL from track_link or track_download_url
    const trackDownload = ev.track_download_url || ev.track_link || "";
    
    const rows = registrationRows(driverState.roster);
    const registrationCap = Number(ev?.registration_cap || 0);
    const registrationCapEnabled = Number(ev?.registration_cap_enabled || 0) === 1 && registrationCap > 0;
    const registrationFull = registrationCapEnabled && rows.length >= registrationCap;
    const regAllowed = isLoggedIn && st === "open" && timeOpen && !registrationFull;
    const isRegistered = isLoggedIn && !!rows.find((d) => String(d.email || "").toLowerCase() === customerEmail);
    const startTs = Date.parse(String(ev?.start_at || ""));
    const isCurrentOrFuture = Number.isFinite(startTs) ? startTs >= Date.now() : st !== "complete";
    const canUnregister = isLoggedIn && isRegistered && isCurrentOrFuture;

    return `
      <div class="gl-ce-card" id="glCeDriverRegistrationCard">
        <h3 class="gl-ce-h2">Registration</h3>
        ${
          !isLoggedIn
            ? `<div class="gl-ce-muted">Log in to register.</div>`
            : st !== "open"
              ? `<div class="gl-ce-muted">Registration is locked (status: ${esc(st)}).</div>`
              : registrationFull
                ? `<div class="gl-ce-muted">Registration is full (${esc(rows.length)} of ${esc(registrationCap)} public spots claimed).</div>`
              : `<div class="gl-ce-muted">Registration is open. Submit your details below.</div>`
        }

        <div class="gl-ce-row gl-ce-row-wrap">
          <input class="gl-ce-input" id="glCeRegFirst" placeholder="First name" ${regAllowed ? "" : "disabled"} />
          <input class="gl-ce-input" id="glCeRegLast" placeholder="Last name" ${regAllowed ? "" : "disabled"} />
          ${isVirtual ? `<input class="gl-ce-input" id="glCeRegEmail" placeholder="Email" ${regAllowed ? "" : "disabled"} />` : ""}
          ${!isVirtual ? `<input class="gl-ce-input" id="glCeRegDriverNumber" placeholder="Driver Number" type="number" min="0" ${regAllowed ? "" : "disabled"} />` : ""}
          ${!isVirtual ? `<input class="gl-ce-input" id="glCeRegCity" placeholder="City" ${regAllowed ? "" : "disabled"} />` : ""}
          ${!isVirtual ? `<input class="gl-ce-input" id="glCeRegCar" placeholder="Car" ${regAllowed ? "" : "disabled"} />` : ""}
          ${!isVirtual ? `<input class="gl-ce-input" id="glCeRegHp" placeholder="HP" type="number" min="0" ${regAllowed ? "" : "disabled"} />` : ""}
        </div>

        <div class="gl-ce-row">
          <button class="gl-ce-btn" data-action="driver-register" ${regAllowed ? "" : "disabled"} type="button">Register</button>
          ${canUnregister ? `<button class="gl-ce-btn gl-ce-btn-ghost" data-action="driver-unregister" type="button">Unregister</button>` : ""}
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="driver-refresh" ${ev ? "" : "disabled"} type="button">Refresh</button>
        </div>
        
        ${isVirtual && isRegistered && (carDownload || trackDownload) ? `
          <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
            <h4 class="gl-ce-h3" style="margin-bottom: 12px;">Download Links</h4>
            ${carDownload ? `
              <div style="margin-bottom: 10px;">
                <strong>Car Download:</strong>
                <a class="gl-ce-link" href="${esc(carDownload)}" target="_blank" rel="noopener" style="margin-left: 8px;">Download Car</a>
              </div>
            ` : ""}
            ${trackDownload ? `
              <div>
                <strong>Track Download:</strong>
                <a class="gl-ce-link" href="${esc(trackDownload)}" target="_blank" rel="noopener" style="margin-left: 8px;">Download Track</a>
              </div>
            ` : ""}
          </div>
        ` : ""}
      </div>
    `;
  }

  function renderDriverRegistrationPlaceholder() {
    return `
      <div class="gl-ce-card" id="glCeDriverRegistrationCard">
        <h3 class="gl-ce-h2">Registration</h3>
        <div class="gl-ce-muted">Look up an event code below to load the registration form.</div>
      </div>
    `;
  }

  function scrollDriverRegistrationIntoView({ focusFirstInput = false } = {}) {
    const card = driverPane?.querySelector?.("#glCeDriverRegistrationCard");
    if (!card) return;
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    card.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    if (!focusFirstInput) return;
    const firstEnabledInput = card.querySelector("input:not([disabled]), textarea:not([disabled]), select:not([disabled])");
    if (firstEnabledInput && typeof firstEnabledInput.focus === "function") {
      firstEnabledInput.focus({ preventScroll: true });
    }
  }

  function registrationRows(data) {
    const raw = Array.isArray(data?.registrations)
      ? data.registrations
      : Array.isArray(data?.drivers)
        ? data.drivers
        : [];
    return raw;
  }

  function renderRoster(mount, data, { adminRemove } = {}) {
    if (!mount) return;
    const drivers = registrationRows(data);
    if (!drivers.length) {
      mount.innerHTML = `<div class="gl-ce-muted">No registrations yet.</div>`;
      return;
    }
    mount.innerHTML = `
      <ol class="gl-ce-list gl-ce-list-ol gl-ce-driver-roster-list">
        ${drivers
          .map((d) => {
            const name = `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.email || d.customer_id || "Driver";
            const car = [d.car, d.hp ? `${d.hp}hp` : ""].filter(Boolean).join(" • ");
            const meta = [d.city, car].filter(Boolean).join(" • ");

            return `
              <li class="gl-ce-roster-row">
                <div>
                  <b>${esc(name)}</b>
                  ${meta ? `<div class="gl-ce-muted">${esc(meta)}</div>` : ""}
                </div>
                ${
                  adminRemove
                    ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-remove-driver" data-regid="${esc(
                        d.id
                      )}" type="button">X</button>`
                    : ``
                }
              </li>
            `;
          })
          .join("")}
      </ol>
    `;
  }

  function updateDriverBracketMount() {
    const mount = driverPane.querySelector("#glCeDriverBracket");
    if (!mount) {
      console.warn("updateDriverBracketMount: #glCeDriverBracket not found");
      return;
    }
    const currentMatchId = pickCurrentMatchId(driverState.bracket);
    const maxDrivers = driverState.event?.max_drivers || driverState.bracket?.max_drivers || 32;
    console.log("Rendering bracket:", {
      hasBracket: !!driverState.bracket,
      bracketData: driverState.bracket,
      maxDrivers,
      eventMaxDrivers: driverState.event?.max_drivers
    });
    const matches = Array.isArray(driverState.bracket?.matches) ? driverState.bracket.matches : [];
    const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 0)).filter(Number.isFinite)) : 0;
    const totalRounds = (Number(driverState.bracket?.final_round) > 0) ? Number(driverState.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : undefined);
    const activeRound = normalizeBracketRoundSelection(driverState.bracketActiveRound);
    renderBracketVisual(mount, driverState.bracket, {
      title: eventDisplayName(driverState.event, "Live"),
      live: normalizeStatus(driverState.event?.status) === "live",
      lastUpdated: driverState.lastBracketAt,
      currentMatchId,
      max_drivers: maxDrivers,
      totalRounds,
      finalRound: Number(driverState.bracket?.final_round || 0) || undefined,
      activeRound,
      showPopOut: false,
    });

    const tabsMount = driverPane.querySelector("#glCeDriverBracketTabs");
    if (tabsMount) {
      renderDriverBracketTabs(tabsMount, driverState.bracket);
    }
  }

  function driverQualifyingSignature(payload) {
    const currentRunId = String(
      payload?.qualifying?.current_run_id ||
      payload?.current_run_id ||
      ""
    );
    const qualifyingConfig = qualifyingDisplayConfig(payload?.qualifying);
    return fnv1a(JSON.stringify({
      currentRunId,
      rows: qualifyingLeaderboardRows(payload).map((row) => [
        row?.registration_id || "",
        row?.rank ?? "",
        row?.driver_name || "",
        row?.driver_number ?? "",
        row?.best_score ?? "",
        qualifyingRunScoreLabel(row, 1),
        qualifyingRunScoreLabel(row, 2),
        qualifyingPartialRunSignature(row),
      ].join("|")),
    }));
  }

  async function driverLoadQualifyingLeaderboard(eventId) {
    if (!eventId) return { leaderboard: [] };
    return apiGet(`/admin/events/${encodeURIComponent(eventId)}/qualifying/leaderboard`);
  }

  function updateDriverQualifyingMount() {
    const mount = driverPane.querySelector("#glCeDriverQualifyingLeaderboard");
    if (!mount) return;
    // Driver pane is read-only: no set-current / edit / save / dnf controls.
    // Mutations are admin-only and live on the Admin tab.
    renderAdminCompQualifyingTable(mount, driverState.qualifying, { readOnly: true });
  }

  function hasQualifyingResults(payload) {
    return qualifyingLeaderboardRows(payload).some((row) => Array.isArray(row?.runs) && row.runs.length > 0);
  }

  function updateDriverRosterMount() {
    const mount = driverPane.querySelector("#glCeDriverRoster");
    if (!mount) return;
    if (driverState.rosterLoading) {
      mount.innerHTML = `<div class="gl-ce-muted">Loading registrations...</div>`;
      return;
    }
    if (driverState.rosterError) {
      mount.innerHTML = `<div class="gl-ce-warn">Failed to load registrations: ${esc(driverState.rosterError)}</div>`;
      return;
    }
    renderRoster(mount, driverState.roster);
  }

  function renderDriverBracketTabs(mount, bracketData) {
    if (!mount || !bracketData || !bracketData.matches) {
      mount.innerHTML = "";
      return;
    }

    const matches = Array.isArray(bracketData.matches) ? bracketData.matches : [];
    if (!matches.length || normalizeStatus(driverState.event?.status) !== "live") {
      mount.innerHTML = "";
      driverState.bracketActiveRound = null;
      return;
    }

    try {
      // Match admin comp-view tab naming/order logic 1:1.
      const actualDrivers = new Set([...matches.map(m => m.driver_a), ...matches.map(m => m.driver_b)].filter(d => d && d !== "BYE" && d !== null)).size;
      const maxDrivers = driverState.event?.max_drivers || bracketData.max_drivers || 32;
      const N = actualDrivers > 0 ? actualDrivers : maxDrivers;

      const highestPowerOfTwoLessOrEqual = (n) => {
        n = Math.max(2, Math.min(64, Number(n) || 2));
        if (n <= 2) return 2;
        return Math.pow(2, Math.floor(Math.log2(n)));
      };
      const base = highestPowerOfTwoLessOrEqual(N);
      const hasPlayIn = (N !== base);

      const baseRounds = Math.log2(base) + 1;
      const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 1)).filter(Number.isFinite)) : 0;
      const totalRounds = maxRoundFromMatches > 0
        ? maxRoundFromMatches
        : ((Number(bracketData?.final_round) > 0)
          ? Number(bracketData.final_round)
          : (baseRounds + (hasPlayIn ? 1 : 0)));

      const rounds = [];
      const thirdPlaceMatches = matches.filter((m) => isConsolationMatch(m));
      const mainRoundNumbers = [...new Set(
        matches
          .filter((m) => !isConsolationMatch(m))
          .map((m) => Number(m.round || 1))
          .filter(Number.isFinite)
      )].sort((a, b) => a - b);
      for (const r of mainRoundNumbers) {
        const roundMatches = matches.filter(m => Number(m.round || 1) === r && !isConsolationMatch(m));
        if (roundMatches.length > 0) {
          const roundName = displayRoundName(r, null, totalRounds);
          rounds.push({ round: r, name: roundName, matches: roundMatches });
        }
      }
      if (thirdPlaceMatches.length > 0) {
        rounds.push({ round: "third-place", name: "Third Place Match", matches: thirdPlaceMatches });
      }

      if (!rounds.length) {
        mount.innerHTML = "";
        return;
      }

      if (driverState.bracketActiveRound == null) {
        const currentMatchId = pickCurrentMatchId(bracketData);
        const currentMatch = matches.find((m) => String(m.id) === String(currentMatchId));
        if (isConsolationMatch(currentMatch)) {
          driverState.bracketActiveRound = "third-place";
        } else {
          const currentRoundFromMatch = rounds.find(r =>
            Array.isArray(r.matches) && r.matches.some(m => String(m.id) === String(currentMatchId))
          )?.round;
          driverState.bracketActiveRound = Number.isFinite(currentRoundFromMatch)
            ? Number(currentRoundFromMatch)
            : (rounds.length ? Number(rounds[0].round) : 1);
        }
      }

      const activeRound = normalizeBracketRoundSelection(driverState.bracketActiveRound);
      const isFullBracket = activeRound === null;

      // Stable tab order: numeric ascending, then Third Place, then Final Battle (always last).
      const normalNumeric = rounds
        .filter(r => typeof r.round === "number" && Number.isFinite(r.round))
        .sort((a, b) => Number(a.round) - Number(b.round));
      const thirdTab = rounds.find(r => r.round === "third-place") || null;
      const finalRoundNum = normalNumeric.length
        ? Math.max(...normalNumeric.map(r => Number(r.round)))
        : null;
      const finalTab = (finalRoundNum != null)
        ? normalNumeric.find(r => Number(r.round) === finalRoundNum) || null
        : null;
      if (finalTab) finalTab.name = "Final Battle";
      const numericWithoutFinal = (finalRoundNum != null)
        ? normalNumeric.filter(r => Number(r.round) !== finalRoundNum)
        : normalNumeric;

      const orderedRounds = [
        ...numericWithoutFinal,
        ...(thirdTab ? [thirdTab] : []),
        ...(finalTab ? [finalTab] : []),
      ];

      mount.innerHTML = `
        <div class="gl-ce-bracket-tabs-container">
          <button class="gl-ce-bracket-tab ${isFullBracket ? "is-active" : ""}" data-action="driver-bracket-select-round" data-round="null" type="button">
            Full Bracket
          </button>
          ${orderedRounds.map(r => `
            <button class="gl-ce-bracket-tab ${activeRound === r.round ? "is-active" : ""}" data-action="driver-bracket-select-round" data-round="${r.round}" type="button">
              ${esc(r.name)}
              ${r.matches.length > 0 ? `<span class="gl-ce-bracket-tab-count">${r.matches.length}</span>` : ''}
            </button>
          `).join("")}
        </div>
      `;
    } catch (err) {
      console.error("Error rendering driver bracket tabs:", err);
      mount.innerHTML = "";
    }
  }

  function renderDriver() {
    const ev = driverState.event;
    const myEvents = driverState.myEvents || [];
    const st = ev ? eventStatusForUi(ev) : null;
    const registrationCard = ev ? renderDriverRegistrationCard(ev) : renderDriverRegistrationPlaceholder();
    const driverLookupCard = `
      <div class="gl-ce-card">
        <h2 class="gl-ce-h">Driver Portal</h2>
        <p class="gl-ce-sub">Enter your competition code to view the event, bracket, and roster.</p>

        <div class="gl-ce-row">
          <input class="gl-ce-input" id="glCeDriverCode" placeholder="Enter code (ex: GLC-ABC123)" value="${esc(
            driverState.code
          )}" />
          <button class="gl-ce-btn" data-action="driver-lookup" type="button">Lookup</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="driver-clear" type="button">Clear</button>
        </div>
      </div>
    `;
    const myEventsCard =
      myEvents.length > 0
        ? `<div class="gl-ce-card">
              <h3 class="gl-ce-h2">My Registered Events</h3>
              <div class="gl-ce-list">
                ${myEvents
                  .map(
                    (e) => {
                      const eventStatus = normalizeStatus(e.status);
                      const canDelete = eventStatus === "complete";
                      const actionLabel = canDelete ? "Delete" : "Unregister";
                      const actionTitle = canDelete ? "Delete completed event" : "Unregister from event";
                      const actionButton = `<button
                                class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger"
                                data-action="driver-remove-event"
                                data-event-id="${esc(e.id)}"
                                data-event-name="${esc(eventDisplayName(e, "Event"))}"
                                data-event-status="${esc(eventStatus)}"
                                title="${esc(actionTitle)}"
                                type="button"
                              >${esc(actionLabel)}</button>`;
                      return `
                  <div class="gl-ce-list-item" style="cursor:pointer;" data-action="driver-select-event" data-code="${esc(e.comp_code)}">
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                      <div>
                        <div style="font-weight:bold;margin-bottom:4px;">${esc(eventDisplayName(e, "Event"))}</div>
                        ${e.organizer ? `<div style="font-size:14px;color:#666;margin-bottom:4px;">${esc(e.organizer)}</div>` : ""}
                        ${e.start_at ? `<div style="font-size:13px;color:#666;margin-bottom:4px;">${esc(fmtDateInTz(e.start_at, e.start_at_tz))}</div>` : ""}
                        <div style="font-size:14px;color:#666;">
                          <code style="background:#f5f5f5;padding:2px 6px;border-radius:3px;font-size:12px;">${esc(e.comp_code)}</code>
                          ${e.status === "open" ? '<span style="color:#076251;margin-left:8px;">● Open</span>' : ""}
                          ${e.status === "live" ? '<span style="color:#d32f2f;margin-left:8px;">● Live</span>' : ""}
                          ${e.status === "complete" ? '<span style="color:#666;margin-left:8px;">● Complete</span>' : ""}
                        </div>
                      </div>
                      <div style="display:flex;gap:6px;align-items:center;">
                        <button class="gl-ce-btn gl-ce-btn-sm" data-action="driver-select-event" data-code="${esc(e.comp_code)}">View</button>
                        ${actionButton}
                      </div>
                    </div>
                  </div>
                `;
                    }
                  )
                  .join("")}
              </div>
            </div>`
        : "";

    driverPane.innerHTML = `
      ${driverLookupCard}
      ${registrationCard}
      ${myEventsCard}

      ${ev ? renderEventSummaryCard(ev, { showCode: true }) : ""}

      ${
        ev
          ? `${(st === "qualifying" || hasQualifyingResults(driverState.qualifying)) ? `
            <div class="gl-ce-card gl-ce-card-soft gl-ce-driver-qualifying-card" style="margin-top:12px;">
              <div class="gl-ce-row gl-ce-row-between" style="align-items:center; margin-bottom:10px;">
                <div>
                  <h3 class="gl-ce-h2" style="margin:0;">Qualifying Leaderboard</h3>
                  <div class="gl-ce-muted">${st === "qualifying" ? "Position updates automatically while qualifying is active." : "Final qualifying results."}</div>
                </div>
              </div>
              <div id="glCeDriverQualifyingLeaderboard"></div>
            </div>
            ` : ""}
            ${st === "complete" && driverState.bracket?.podium ? `
            <div class="gl-ce-card" style="margin-top: 12px;">
              <div id="glCeDriverPodium"></div>
              ${finalResultsMarkup(driverState.bracket?.final_results, driverState.bracket?.final_results_pending)}
            </div>
            ` : ""}
            <div class="gl-ce-card gl-ce-bracket-card gl-ce-driver-bracket-card">
              <h3 class="gl-ce-h2">Bracket</h3>
              <div id="glCeDriverBracketTabs" class="gl-ce-bracket-tabs"></div>
              <div id="glCeDriverBracket"></div>
            </div>
            <div class="gl-ce-card">
              <h3 class="gl-ce-h2 gl-ce-driver-roster-title">Registered Drivers</h3>
              <div id="glCeDriverRoster"></div>
            </div>`
          : ""
      }
    `;

    if (ev) {
      if (st === "qualifying" || hasQualifyingResults(driverState.qualifying)) {
        updateDriverQualifyingMount();
      }
      if (st !== "qualifying") {
        updateDriverBracketMount();
      }
      updateDriverRosterMount();
      // PHASE 4: Render podium if event is complete
      const pMount = driverPane.querySelector("#glCeDriverPodium");
      if (pMount && driverState.bracket?.podium) {
        renderPodium(pMount, driverState.bracket.podium);
      }
    }
  }

  function stopDriverLive() {
    clearInterval(driverState.live.tBracket);
    clearInterval(driverState.live.tRoster);
    driverState.live.tBracket = null;
    driverState.live.tRoster = null;
    driverState.live.quietErrors = 0;
  }

  function startDriverLive() {
    stopDriverLive();
    if (!driverState.live.enabled) return;
    if (!driverState.code) return;

    driverState.live.tBracket = setInterval(async () => {
      if (!driverState.code) return;
      if (document.hidden) return;

      try {
        const qualifyingActive = eventStatusForUi(driverState.event) === "qualifying";
        if (qualifyingActive || hasQualifyingResults(driverState.qualifying)) {
          const eventId = driverState.event?.id;
          const qualifying = await driverLoadQualifyingLeaderboard(eventId).catch((err) => {
            console.error("Live qualifying leaderboard poll error:", err);
            return null;
          });
          const sig = qualifying ? driverQualifyingSignature(qualifying) : "";
          if (qualifying && sig && sig !== driverState.qualifyingSig) {
            driverState.qualifying = qualifying;
            driverState.qualifyingSig = sig;
            driverState.lastBracketAt = new Date().toLocaleTimeString();
            updateDriverQualifyingMount();
          }
          driverState.live.quietErrors = 0;
          if (qualifyingActive) return;
        }

        const br = await apiGet(`/event/bracket?code=${encodeURIComponent(driverState.code)}`).catch((err) => {
          console.error("Live bracket poll error:", err);
          return { error: err.message || "Failed to load bracket", matches: [], max_drivers: driverState.event?.max_drivers || 32 };
        });
        
        // Ensure matches array exists
        if (br && !Array.isArray(br.matches)) {
          br.matches = [];
        }
        
        // Ensure max_drivers exists
        if (br && !br.max_drivers) {
          br.max_drivers = driverState.event?.max_drivers || 32;
        }
        
        const sig = bracketSignature(br);
        if (sig && sig !== driverState.bracketSig) {
          driverState.bracket = br;
          driverState.bracketSig = sig;
          driverState.lastBracketAt = new Date().toLocaleTimeString();
          updateDriverBracketMount();
        }
        driverState.live.quietErrors = 0;
      } catch (err) {
        console.error("Live bracket poll exception:", err);
        driverState.live.quietErrors++;
      }
    }, driverState.live.bracketMs);

    driverState.live.tRoster = setInterval(async () => {
      if (!driverState.code) return;
      if (document.hidden) return;
      try {
        const lookup = await apiPost("/events/lookup", { code: driverState.code }).catch(() => null);
        const nextEvent = lookup?.event || null;
        const prevStatus = eventStatusForUi(driverState.event);
        const nextStatus = eventStatusForUi(nextEvent);
        const registrationGateChanged = nextEvent && (
          prevStatus !== nextStatus ||
          Number(driverState.event?.registration_cap_enabled || 0) !== Number(nextEvent.registration_cap_enabled || 0) ||
          Number(driverState.event?.registration_cap || 0) !== Number(nextEvent.registration_cap || 0)
        );
        if (nextEvent) {
          driverState.event = nextEvent;
        }
        if (registrationGateChanged) {
          renderDriver();
        }

        const eventId = nextEvent?.id || driverState.event?.id;
        const regs = await driverLoadRegistrationsWithTimeout(eventId, driverState.code);
        const sig = fnv1a(JSON.stringify(registrationRows(regs).map((d) => d.id || d.email || d.customer_id || "")));
        driverState.rosterError = "";
        if (sig && sig !== driverState.rosterSig) {
          driverState.roster = regs;
          driverState.rosterSig = sig;
          updateDriverRosterMount();
          const registrationCard = driverPane.querySelector("#glCeDriverRegistrationCard");
          if (registrationCard && driverState.event) {
            registrationCard.outerHTML = renderDriverRegistrationCard(driverState.event);
          }
        } else if (driverPane.querySelector("#glCeDriverRoster")) {
          // Clear any stale error/loading message even when data signature is unchanged.
          updateDriverRosterMount();
        }
      } catch (err) {
        driverState.rosterError = err?.message || "Unable to refresh registrations.";
        driverState.rosterLoading = false;
        if (driverPane.querySelector("#glCeDriverRoster")) {
          updateDriverRosterMount();
        }
      }
    }, driverState.live.rosterMs);
  }

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && driverState.code && driverState.event) {
      driverRefreshAll({ silent: true }).catch(() => {});
    }
  });

  async function driverLoadMyEvents() {
    if (!isLoggedIn) return;
    try {
      const data = await apiGet("/driver/my-events");
      driverState.myEvents = data.events || [];
      renderDriver();
    } catch (err) {
      console.error("Failed to load my events:", err);
      driverState.myEvents = [];
    }
  }

  async function driverLookup(code) {
    const data = await apiPost("/events/lookup", { code });
    driverState.event = data.event || null;
    driverState.code = String(code || "").trim().toUpperCase();
    driverState.bracket = null;
    driverState.qualifying = null;
    driverState.roster = null;
    driverState.rosterLoading = false;
    driverState.rosterError = "";
    driverState.bracketSig = "";
    driverState.qualifyingSig = "";
    driverState.rosterSig = "";
    driverState.bracketActiveRound = null;
    driverState.lastBracketAt = "";
    renderDriver();
    if (driverState.event) {
      setTimeout(() => scrollDriverRegistrationIntoView({ focusFirstInput: true }), 0);
    }
    await driverRefreshAll({ silent: true });
    startDriverLive();
  }

  async function driverSelectEvent(code) {
    await driverLookup(code);
  }

  async function driverLoadRegistrationsByEvent(eventId, code) {
    if (eventId) {
      return apiGet(`/events/${encodeURIComponent(eventId)}/registrations`);
    }
    if (code) {
      return apiGet(`/event/registrations?code=${encodeURIComponent(code)}`);
    }
    return { drivers: [] };
  }

  async function driverLoadRegistrationsWithTimeout(eventId, code, ms = 12000) {
    let t = null;
    try {
      return await Promise.race([
        driverLoadRegistrationsByEvent(eventId, code),
        new Promise((_, reject) => {
          t = setTimeout(() => reject(new Error(`Registrations request timed out after ${ms}ms.`)), ms);
        }),
      ]);
    } finally {
      if (t) clearTimeout(t);
    }
  }

  async function driverRefreshAll({ silent } = {}) {
    const code = driverState.code;
    if (!code) return;
    try {
      driverState.rosterLoading = true;
      driverState.rosterError = "";
      const [br, ev] = await Promise.all([
        apiGet(`/event/bracket?code=${encodeURIComponent(code)}`).catch((err) => {
          console.error("Bracket API error:", err);
          return { error: err.message || "Failed to load bracket", matches: [], max_drivers: 32 };
        }),
        apiPost(`/events/lookup`, { code }).catch(() => ({ event: null })),
      ]);
      const eventId = ev?.event?.id || driverState.event?.id || "";
      const eventStatus = eventStatusForUi(ev?.event || driverState.event);
      const regs = await driverLoadRegistrationsWithTimeout(eventId, code).catch((err) => {
        driverState.rosterError = err?.message || "Unable to fetch registrations.";
        return { drivers: [] };
      });
      const qualifying = await driverLoadQualifyingLeaderboard(eventId).catch((err) => {
        console.error("Qualifying leaderboard API error:", err);
        return { leaderboard: [] };
      });
      
      console.log("Driver refresh - Bracket data:", br);
      console.log("Driver refresh - Event data:", ev.event);
      
      driverState.bracket = br;
      driverState.qualifying = qualifying;
      driverState.roster = regs;
      driverState.event = ev.event || driverState.event;
      
      // Ensure bracket has max_drivers from event if bracket doesn't have it
      if (driverState.bracket && driverState.event?.max_drivers && !driverState.bracket.max_drivers) {
        driverState.bracket.max_drivers = driverState.event.max_drivers;
      }
      
      // If bracket has error but no max_drivers, use event max_drivers
      if (driverState.bracket && driverState.bracket.error && !driverState.bracket.max_drivers) {
        driverState.bracket.max_drivers = driverState.event?.max_drivers || 32;
      }
      
      // Ensure matches array exists
      if (driverState.bracket && !Array.isArray(driverState.bracket.matches)) {
        driverState.bracket.matches = [];
      }
      
      driverState.bracketSig = bracketSignature(br);
      driverState.qualifyingSig = qualifying ? driverQualifyingSignature(qualifying) : "";
      driverState.rosterSig = fnv1a(JSON.stringify(registrationRows(regs).map((d) => d.id || d.email || d.customer_id || "")));
      driverState.lastBracketAt = new Date().toLocaleTimeString();

      if (driverPane.querySelector("#glCeDriverBracket")) {
        updateDriverBracketMount();
        updateDriverRosterMount();
        // PHASE 4: Render podium if event is complete
        const pMount = driverPane.querySelector("#glCeDriverPodium");
        if (pMount && driverState.bracket?.podium) {
          renderPodium(pMount, driverState.bracket.podium);
        }
        renderDriver();
      } else {
        renderDriver();
      }

      if (!silent) toast("Refreshed.", "ok");
    } catch (err) {
      console.error("Driver refresh error:", err);
      driverState.rosterError = err?.message || "Failed to refresh registrations.";
      if (!silent) toast(err?.message || "Failed to refresh.", "err");
    } finally {
      driverState.rosterLoading = false;
      updateDriverRosterMount();
    }
  }

  async function driverRegister() {
    const code = driverState.code;
    if (!code) throw new Error("Enter a code first.");

    const ev = driverState.event;
    const st = normalizeStatus(ev?.status);

    // Time-based guard FIRST: compare Date.now() against the UTC close
    // timestamp so a stale `ev.status === "open"` payload cannot let a
    // driver POST after the deadline. The server enforces this as well —
    // this is the strongest available client-side guard.
    if (!isRegistrationOpen(ev)) {
      throw new Error("Registration closed.");
    }

    if (st !== "open") {
      throw new Error(`Registration is locked (status: ${st}).`);
    }

    const regCount = (driverState.roster?.drivers || []).length;
    const registrationCap = Number(ev?.registration_cap || 0);
    if (Number(ev?.registration_cap_enabled || 0) === 1 && registrationCap > 0 && regCount >= registrationCap) {
      throw new Error("Registration closed (event is full).");
    }

    const eventType = String(ev?.type || "").trim().toLowerCase();
    const isVirtual = eventType === "virtual" || eventType === "sim";
    const emailInput = (driverPane.querySelector("#glCeRegEmail")?.value || "").trim();
    const body = {
      comp_code: code,
      first_name: (driverPane.querySelector("#glCeRegFirst")?.value || "").trim(),
      last_name: (driverPane.querySelector("#glCeRegLast")?.value || "").trim(),
      email: isVirtual ? emailInput : customerEmail,
      ...(driverPane.querySelector("#glCeRegDriverNumber")
        ? { driver_number: Number(driverPane.querySelector("#glCeRegDriverNumber")?.value || 0) || null }
        : {}),
      ...(driverPane.querySelector("#glCeRegCity") ? { city: (driverPane.querySelector("#glCeRegCity")?.value || "").trim() || null } : {}),
      ...(driverPane.querySelector("#glCeRegCar") ? { car: (driverPane.querySelector("#glCeRegCar")?.value || "").trim() || null } : {}),
      ...(driverPane.querySelector("#glCeRegHp") ? { hp: Number(driverPane.querySelector("#glCeRegHp")?.value || 0) || null } : {}),
    };

    try {
      await apiPost("/events/register", body);
      toast("Registration successful! Check your email for confirmation.", "ok");
      await driverRefreshAll({ silent: true });
      await driverLoadMyEvents(); // Reload registered events
      renderDriver();
    } catch (err) {
      const msg = err?.message || String(err);
      if (msg.includes("already registered") || msg.includes("duplicate")) {
        toast("Currently registered for this event.", "err");
      } else {
        throw err;
      }
    }
  }

  async function driverUnregister() {
    const code = driverState.code;
    if (!code) throw new Error("Enter a code first.");
    await apiPost("/events/unregister", { comp_code: code });
    toast("Unregistered.", "ok");
    await driverRefreshAll({ silent: true });
    await driverLoadMyEvents(); // Reload registered events
    renderDriver();
  }

  async function driverUnregisterByCode(code, eventName = "Event") {
    const compCode = String(code || "").trim().toUpperCase();
    if (!compCode) throw new Error("Missing event code.");
    const ok = window.confirm(`Unregister from ${eventName} (${compCode})?`);
    if (!ok) return;
    await apiPost("/events/unregister", { comp_code: compCode });
    toast("Unregistered.", "ok");
    if (driverState.code && String(driverState.code).trim().toUpperCase() === compCode) {
      await driverRefreshAll({ silent: true });
    }
    await driverLoadMyEvents();
    renderDriver();
  }

  async function driverDeleteByCode(code, eventName = "Event") {
    const compCode = String(code || "").trim().toUpperCase();
    if (!compCode) throw new Error("Missing event code.");
    const ok = window.confirm(
      `Delete ${eventName} (${compCode}) from your registered events list?\n\nThis only removes your registration from your list and does not delete the event itself.`
    );
    if (!ok) return;
    await apiPost("/events/delete", { comp_code: compCode });
    toast("Removed from your events list.", "ok");
    if (driverState.code && String(driverState.code).trim().toUpperCase() === compCode) {
      driverState.code = "";
      driverState.event = null;
      driverState.bracket = null;
      driverState.qualifying = null;
      driverState.roster = null;
      driverState.bracketSig = "";
      driverState.qualifyingSig = "";
      driverState.rosterSig = "";
      driverState.lastBracketAt = "";
    }
    await driverLoadMyEvents();
    renderDriver();
  }

  async function driverRemoveEventById(eventId, eventName = "Event", eventStatus = "") {
    const id = String(eventId || "").trim();
    if (!id) throw new Error("Missing event id.");
    const st = normalizeStatus(eventStatus);
    const isComplete = st === "complete";
    const ok = window.confirm(
      isComplete
        ? `Delete ${eventName} from your registered events list?\n\nThis only removes your registration from your list and does not delete the event itself.`
        : `Unregister from ${eventName}?`
    );
    if (!ok) return;
    await apiPost(`/driver/events/${encodeURIComponent(id)}/remove`, {});
    toast(isComplete ? "Removed from your events list." : "Unregistered.", "ok");
    await driverLoadMyEvents();
    renderDriver();
  }

  // -----------------------------
  // JUDGE pane
  // -----------------------------
  const judgePane = root.querySelector('.gl-ce-pane[data-pane="judge"]');

  const judgeState = {
    token: "",
    judge_code: "",
    judge_slot: null,
    event: null,
    matches: [],
    judge_name: "",
    current_match_id: null,
    notesCache: {},
    resultReveal: { showing: false, type: null, tally: [], matchTitle: "" },
    mode: null,
    qualifying: { run: null, total_drivers: 0 },
    qualifyingDraft: { runId: null, value: "", note: "" },
    qualifyingPollTimer: null,
    scorePanel: {
      matchId: null,
      decision: "",
      notes: "",
      submitting: false,
      error: "",
      expectedBattleNo: null,
      expectedOmtCount: null,
    },
  };

  const JUDGE_SESSION_STORAGE_KEY = "glCeJudgeSession";

  function clearStoredJudgeSession() {
    try { window.localStorage.removeItem(JUDGE_SESSION_STORAGE_KEY); } catch (_) {}
  }

  function persistJudgeSession() {
    if (!judgeState.token || !judgeState.judge_code) return;
    try {
      window.localStorage.setItem(JUDGE_SESSION_STORAGE_KEY, JSON.stringify({
        token: judgeState.token,
        judge_code: judgeState.judge_code,
      }));
    } catch (_) {}
  }

  function clearJudgeSessionState() {
    judgeStopQualifyingPoll();
    judgeState.token = "";
    judgeState.judge_code = "";
    judgeState.judge_slot = null;
    judgeState.event = null;
    judgeState.matches = [];
    judgeState.judge_name = "";
    judgeState.mode = null;
    judgeState.qualifying = { run: null, total_drivers: 0 };
    judgeState.qualifyingDraft = { runId: null, value: "", note: "" };
    judgeState.scorePanel = {
      matchId: null,
      decision: "",
      notes: "",
      submitting: false,
      error: "",
      expectedBattleNo: null,
      expectedOmtCount: null,
    };
    clearStoredJudgeSession();
  }

  function isInvalidJudgeSessionError(error) {
    return Number(error?.__glCeStatus) === 401 || Number(error?.__glCeStatus) === 404;
  }

  function renderJudgeMatches(mount) {
    if (!mount) return;

    // Show result overlay when a result was just declared (OMT or winner)
    if (judgeState.resultReveal.showing) {
      const { type, tally, matchTitle } = judgeState.resultReveal;
      const isOmt = type === "omt";
      const heading = isOmt ? "OMT" : "WINNER";
      const subheading = isOmt ? "ONE MORE TIME" : "";
      const tallyHtml = tally.length
        ? `<div style="margin: 18px 0; display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;">
            ${tally.map(t => `<div style="background:rgba(255,255,255,0.12);border-radius:8px;padding:10px 20px;font-size:15px;font-weight:600;text-align:center;">
              <div style="font-size:12px;opacity:0.7;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">${esc(t.label || t.winner)}</div>
              <div style="font-size:26px;">${t.count} vote${t.count !== 1 ? "s" : ""}</div>
            </div>`).join("")}
          </div>`
        : "";
      mount.innerHTML = `
        <style>
          @keyframes glCeResultPulse {
            0%,100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          .gl-ce-judge-result-overlay {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            border-radius: 10px;
            padding: 40px 30px;
            text-align: center;
            color: #fff;
            border: 2px solid ${isOmt ? "#ffc107" : "#076251"};
            box-shadow: 0 0 40px ${isOmt ? "rgba(255,193,7,0.3)" : "rgba(7,98,81,0.3)"};
            animation: glCeResultPulse 1.5s ease-in-out infinite;
          }
          .gl-ce-judge-result-heading {
            font-size: clamp(52px, 10vw, 96px);
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 4px;
            color: ${isOmt ? "#ffc107" : "#4caf50"};
            text-shadow: 0 0 30px ${isOmt ? "rgba(255,193,7,0.6)" : "rgba(76,175,80,0.6)"};
            line-height: 1;
            margin-bottom: 8px;
          }
          .gl-ce-judge-result-sub {
            font-size: clamp(18px, 3vw, 28px);
            font-weight: bold;
            letter-spacing: 3px;
            opacity: 0.9;
            margin-bottom: 6px;
          }
          .gl-ce-judge-result-battle {
            font-size: 13px;
            opacity: 0.6;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }
          .gl-ce-judge-result-next {
            font-size: 13px;
            opacity: 0.5;
            margin-top: 24px;
          }
        </style>
        <div class="gl-ce-judge-result-overlay">
          <div class="gl-ce-judge-result-heading">${heading}</div>
          ${subheading ? `<div class="gl-ce-judge-result-sub">${subheading}</div>` : ""}
          <div class="gl-ce-judge-result-battle">${esc(matchTitle)}</div>
          ${tallyHtml}
          <div class="gl-ce-judge-result-next">Next battle starting in 5 seconds...</div>
        </div>`;
      return;
    }

    const matches = judgeState.matches || [];
    const totalRounds = getJudgeTotalRounds(matches);

    if (!matches.length) {
      mount.innerHTML = `<div class="gl-ce-muted">No matches available yet.</div>`;
      return;
    }

    const orderedMatches = matches.slice().sort(compareMatchesForJudgingDisplay);

    mount.innerHTML = `
      <div class="gl-ce-tablewrap">
        <table class="gl-ce-table">
          <thead>
            <tr>
              <th>Match Title</th>
              <th>Drivers</th>
              <th>My Decision</th>
              <th>Notes</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            ${orderedMatches
              .map((m) => {
                const decided = !!m.winner;
                const locked = isJudgeMatchLocked(m);
                const a = esc(m.driver_a_label || "TBD");
                const b = esc(m.driver_b_label || "TBD");
                // Only surface a stored decision when the match is actually decided/locked.
                // For scoreable (open) matches, hide my_decision — this prevents a stale vote
                // from a prior battle_no leaking onto a fresh OMT round when D1 read-replica
                // lag returns an old battle_no where the judge's previous decision still matches.
                const showStoredDecision = decided || locked;
                const mine = (showStoredDecision && m.my_decision)
                  ? esc(m.my_decision_label || m.my_decision)
                  : "—";
                const hasTwo = matchHasTwoDrivers(m);
                // A match is "pending" (needs this judge's vote) whenever it's scoreable with
                // two drivers and not yet decided — regardless of any (possibly stale) stored
                // my_decision. After an OMT advance, the judge must always re-vote.
                const isPending = !locked && hasTwo && !decided;
                const isCurrent = judgeState.current_match_id && String(m.id) === String(judgeState.current_match_id);
                const matchTitle = buildJudgeMatchTitle(m, totalRounds);

                // Row styling: only the current battle row gets highlight (no row highlight for pending)
                let rowClass = "";
                let rowStyle = "";
                if (isCurrent) {
                  rowClass = "gl-ce-current-battle";
                  rowStyle = "background: rgba(7,98,81,0.25); border-left: 5px solid #076251; font-weight: 500;";
                }

                const inlineScorePanel = String(judgeState.scorePanel?.matchId || "") === String(m.id)
                  ? renderJudgeInlineScorePanel()
                  : "";
                return `
                  <tr ${rowClass ? `class="${rowClass}"` : ''} ${rowStyle ? `style="${rowStyle}"` : ''}>
                    <td><b>${esc(matchTitle)}</b></td>
                    <td>${a} vs ${b}${
                      !hasTwo && !decided ? `<div class="gl-ce-muted" style="font-size:11px;">${esc(tbdHint)}</div>` :
                      decided 
                        ? `<div class="gl-ce-muted">Winner: <b>${esc(m.winner_label || m.winner)}</b></div>` 
                        : isCurrent 
                          ? `<div style="color: #076251; font-weight: bold; font-size: 12px; margin-top: 4px;">🎯 CURRENT MATCH</div>` 
                          : isPending 
                            ? `<div style="color: #076251; font-weight: bold; font-size: 11px; margin-top: 4px;">PENDING</div>` 
                            : ""
                    }</td>
                    <td>${mine}</td>
                    <td>
                      <div class="gl-ce-muted">${m.my_notes_run1 ? `R1: ${esc(m.my_notes_run1)}` : ""}</div>
                      <div class="gl-ce-muted">${m.my_notes_run2 ? `R2: ${esc(m.my_notes_run2)}` : ""}</div>
                    </td>
                    <td>
                      ${
                        locked
                          ? `<span class="gl-ce-muted">Locked</span>`
                          : !hasTwo
                            ? `<span class="gl-ce-muted">Waiting</span>`
                            : `<button class="gl-ce-btn gl-ce-btn-sm ${isCurrent ? 'gl-ce-btn-primary' : ''}" data-action="judge-open-score" data-match="${esc(
                                m.id
                              )}" type="button">${isCurrent ? '🎯 Score Current Match' : 'Score'}</button>`
                      }
                    </td>
                  </tr>
                  ${inlineScorePanel ? `<tr class="gl-ce-judge-score-panel-row"><td colspan="5">${inlineScorePanel}</td></tr>` : ""}
                `;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  function renderJudgeInlineScorePanel() {
    const panel = judgeState.scorePanel || {};
    if (!panel.matchId) return "";
    const match = (judgeState.matches || []).find((item) => String(item.id) === String(panel.matchId));
    if (!match || isJudgeMatchLocked(match)) return "";
    const decision = String(panel.decision || "");
    const voteA = String(match.vote_a_id || "");
    const voteB = String(match.vote_b_id || "");
    const selected = (value) => decision === String(value) ? "is-selected" : "";
    const title = buildJudgeMatchTitle(match, getJudgeTotalRounds(judgeState.matches || []));
    return `
      <section class="gl-ce-judge-score-panel" data-match-id="${esc(match.id)}" aria-label="Score ${esc(title)}">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <div>
            <div class="gl-ce-muted" style="font-size:12px;">SCORE CURRENT MATCH</div>
            <h3 class="gl-ce-h2">${esc(title)}</h3>
          </div>
          <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="judge-close-score" type="button" ${panel.submitting ? "disabled" : ""}>Close</button>
        </div>
        <div class="gl-ce-judge-score-choices">
          <button class="gl-ce-judge-score-choice ${selected(voteA)}" data-action="judge-pick-score" data-decision="${esc(voteA)}" type="button">
            <span>Driver A</span><strong>${esc(match.driver_a_label || "Driver A")}</strong>
          </button>
          <button class="gl-ce-judge-score-choice gl-ce-judge-score-choice-omt ${selected("omt")}" data-action="judge-pick-score" data-decision="omt" type="button">
            <span>Decision</span><strong>ONE MORE TIME</strong>
          </button>
          <button class="gl-ce-judge-score-choice ${selected(voteB)}" data-action="judge-pick-score" data-decision="${esc(voteB)}" type="button">
            <span>Driver B</span><strong>${esc(match.driver_b_label || "Driver B")}</strong>
          </button>
        </div>
        <label class="gl-ce-label" for="glCeJudgeInlineNotes">Private notes</label>
        <textarea class="gl-ce-input" id="glCeJudgeInlineNotes" data-role="judge-inline-notes" rows="3" maxlength="4000" placeholder="Optional notes">${esc(panel.notes || "")}</textarea>
        ${panel.error ? `<div class="gl-ce-error" role="alert" style="margin-top:8px;">${esc(panel.error)}</div>` : ""}
        <button class="gl-ce-btn gl-ce-judge-submit-inline" data-action="judge-submit-inline" type="button"
                ${panel.submitting || !decision ? "disabled" : ""}>
          ${panel.submitting ? "Submitting…" : "Submit Decision"}
        </button>
      </section>
    `;
  }

  function isIrlEventType(ev) {
    const t = String(ev?.type || ev?.event_type || "").trim().toLowerCase();
    return t === "irl";
  }

  function getJudgeTotalRounds(matches) {
    const rounds = (matches || [])
      .filter((m) => !isConsolationMatch(m))
      .map((m) => Number(m?.round || 0))
      .filter(Number.isFinite);
    return rounds.length ? Math.max(...rounds) : null;
  }

  function buildJudgeMatchTitle(match, totalRounds) {
    const roundLabel = displayRoundName(match?.round || 1, match, totalRounds);
    const battleNo = Number(match?.battle_no || 1);
    return `${roundLabel} Match ${displayMatchNo(match?.match_no)} Battle ${battleNo}`;
  }

  function firstNonEmptyValue(...values) {
    for (const value of values) {
      if (value != null && String(value).trim() !== "") return value;
    }
    return null;
  }

  function renderJudgeDriverMetaHtml(prefix, match, isIrl, className = "driver-meta") {
    if (!isIrl || !match) return "";
    const driverNumber = match[`${prefix}_driver_number`] ?? null;
    const city = match[`${prefix}_city`] ?? null;
    const car = match[`${prefix}_car`] ?? null;
    const hp = match[`${prefix}_hp`] ?? null;
    const fields = [
      ["Driver Number", driverNumber],
      ["City", city],
      ["Car", car],
      ["HP", hp],
    ].filter(([, value]) => value != null && String(value).trim() !== "");
    if (!fields.length) return "";
    return `<div class="${esc(className)}">${fields
      .map(([label, value]) => `<div><strong>${label}:</strong> ${esc(String(value))}</div>`)
      .join("")}</div>`;
  }

  async function judgeGetMatchNotes(eventId, matchId) {
    const tok = judgeState.token;
    if (!tok || !eventId || !matchId) return "";
    const data = await apiGet(
      `/events/${encodeURIComponent(eventId)}/matches/${encodeURIComponent(matchId)}/judge-notes?token=${encodeURIComponent(tok)}`
    );
    return String(data?.notes || "");
  }

  async function judgeSaveMatchNotes(eventId, matchId, notes) {
    const tok = judgeState.token;
    if (!tok || !eventId || !matchId) return { ok: false };
    return apiPost(
      `/events/${encodeURIComponent(eventId)}/matches/${encodeURIComponent(matchId)}/judge-notes?token=${encodeURIComponent(tok)}`,
      { notes: String(notes ?? "") }
    );
  }

  async function openJudgeScorePopupLegacy(matchId) {
    // Always refresh before opening/reopening so pop-out reflects current match state.
    if (judgeState.token) {
      try {
        await judgeRefresh();
      } catch (err) {
        console.warn("[JUDGE/POPUP] Pre-open refresh failed:", err?.message || String(err));
      }
    }

    const m = (judgeState.matches || []).find((x) => String(x.id) === String(matchId));
    if (!m) return;
    if (isJudgeMatchLocked(m)) {
      toast("This match has already been decided and can no longer be scored.", "err");
      return;
    }

    let popup;
    const existing = openJudgeScoreModal._popup;
    if (existing && !existing.closed) {
      existing.focus();
      if (String(openJudgeScoreModal._matchId) === String(matchId)) return;
      openJudgeScoreModal._matchId = matchId;
      popup = existing;
    } else {
      popup = window.open(
        "",
        "judgeScore",
        "width=900,height=700,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no"
      );
      if (!popup) {
        toast("Popup blocked. Please allow popups for this site.", "err");
        return;
      }
    }

    if (openJudgeScoreModal._pollInterval) {
      clearInterval(openJudgeScoreModal._pollInterval);
      openJudgeScoreModal._pollInterval = null;
    }

    const aLabel = esc(m.driver_a_label || "Driver A");
    const bLabel = esc(m.driver_b_label || "Driver B");
    const aVoteId = String(m.vote_a_id || "");
    const bVoteId = String(m.vote_b_id || "");
    const popupJudgeToken = String(judgeState.token || "");
    const canVoteA = !!aVoteId && String(aVoteId).toUpperCase() !== "BYE" && m.driver_a_label !== "TBD";
    const canVoteB = !!bVoteId && String(bVoteId).toUpperCase() !== "BYE" && m.driver_b_label !== "TBD";
    const battleNo = Number(m.battle_no || 1);
    const totalRounds = getJudgeTotalRounds(judgeState.matches || []);
    const fullMatchTitle = buildJudgeMatchTitle(m, totalRounds);
    // Always start the scoring popup with NO pre-selected winner. This guarantees that
    // after an OMT advances the match to a new battle_no, the judge never sees their
    // previous vote carried over — even if the backend's /judge/matches response is
    // briefly stale (D1 read-replica lag can return an old battle_no where a prior
    // decision still matches). A judge must re-pick their vote for every new battle.
    const picked = "";
    const eventId = judgeState.event?.id || m.event_id || "";
    const isIrl = isIrlEventType(judgeState.event);
    let persistedMatchNotes = "";
    try {
      persistedMatchNotes = await judgeGetMatchNotes(eventId, m.id);
    } catch {
      persistedMatchNotes = "";
    }
    const initialMatchNotes = persistedMatchNotes || m.my_notes_run1 || m.my_notes_run2 || "";
    if (eventId && m.id) {
      judgeState.notesCache[`${eventId}::${m.id}`] = String(initialMatchNotes || "");
    }
    const aMetaHtml = renderJudgeDriverMetaHtml("driver_a", m, isIrl);
    const bMetaHtml = renderJudgeDriverMetaHtml("driver_b", m, isIrl);
    
    // PHASE 4: OMT display
    const omtCount = Number(m.omt_count || 0);
    const runSetNumber = omtCount + 1; // run_set_number = omt_count + 1
    const omtDisplay = omtCount > 0 ? ` • OMT #${omtCount} (Run Set ${runSetNumber})` : "";
    
    // Get logo URL (use global LOGO_URL or fallback)
    const logoUrl = LOGO_URL || (root?.dataset?.logoUrl || root?.getAttribute?.('data-logo-url') || '').trim();
    const judgeDebug = /\bjudgeDebug=1\b/.test(window.location.search || "");

    if (popup.document && typeof popup.document.open === "function" && popup === existing) {
      popup.document.open();
    }

    // Write HTML to popup - Formula Drift Style
    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Judge Score - ${esc(fullMatchTitle)}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            color: #fff;
            padding: 0;
            line-height: 1.4;
            overflow-x: hidden;
          }
          .header {
            background: linear-gradient(90deg, #076251 0%, #0a7a68 100%);
            padding: 20px 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }
          .header h1 {
            font-size: 28px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .header .battle-info {
            font-size: 14px;
            opacity: 0.9;
            text-transform: uppercase;
          }
          .close-btn {
            background: rgba(255,255,255,0.2);
            border: 2px solid rgba(255,255,255,0.3);
            color: white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            font-size: 24px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;
          }
          .close-btn:hover {
            background: rgba(255,255,255,0.3);
            transform: rotate(90deg);
          }
          .container {
            padding: 20px;
            max-width: 100%;
            margin: 0 auto;
            box-sizing: border-box;
            transition: opacity 0.3s ease;
          }
          .drivers-header {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .driver-card {
            background: rgba(255,255,255,0.05);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 8px;
            padding: 20px;
            text-align: center;
            transition: all 0.3s;
          }
          .driver-card.selected {
            border-color: #076251;
            background: rgba(7,98,81,0.2);
            box-shadow: 0 0 20px rgba(7,98,81,0.4);
          }
          .driver-card h2 {
            font-size: 32px;
            font-weight: bold;
            margin-bottom: 10px;
            letter-spacing: 1px;
          }
          .driver-meta {
            margin-top: 6px;
            font-size: 12px;
            opacity: 0.85;
            line-height: 1.45;
            text-align: center;
            display: block;
          }
          .driver-card .driver-label {
            font-size: 14px;
            opacity: 0.7;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .runs-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
          }
          .run-card {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 8px;
            padding: 20px;
          }
          .run-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid rgba(255,255,255,0.1);
          }
          .run-title {
            font-size: 18px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .run-role {
            font-size: 12px;
            opacity: 0.6;
            text-transform: uppercase;
          }
          .run-notes {
            width: 100%;
            min-height: 150px;
            background: rgba(0,0,0,0.3);
            border: 1px solid rgba(255,255,255,0.2);
            border-radius: 6px;
            padding: 15px;
            color: #fff;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
          }
          .run-notes:focus {
            outline: none;
            border-color: #076251;
            box-shadow: 0 0 10px rgba(7,98,81,0.3);
          }
          .run-notes::placeholder {
            color: rgba(255,255,255,0.4);
          }
          .score-section {
            background: rgba(255,255,255,0.05);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 8px;
            padding: 30px;
            margin-bottom: 20px;
            position: relative;
          }
          .score-title {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 20px;
            text-align: center;
          }
          .score-slider-container {
            position: relative;
            height: 80px;
            margin: 30px 0;
          }
          .score-track {
            position: absolute;
            top: 50%;
            left: 10%;
            right: 10%;
            height: 6px;
            background: rgba(255,255,255,0.2);
            border-radius: 3px;
            transform: translateY(-50%);
          }
          .score-slider {
            position: absolute;
            top: 50%;
            width: 200px;
            height: 60px;
            background: linear-gradient(135deg, #076251 0%, #0a7a68 100%);
            border: 3px solid rgba(255,255,255,0.3);
            border-radius: 8px;
            transform: translate(-50%, -50%);
            transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            text-transform: uppercase;
            letter-spacing: 1px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }
          .score-slider:hover {
            box-shadow: 0 6px 20px rgba(7,98,81,0.5);
            transform: translate(-50%, -50%) scale(1.05);
          }
          .score-slider.position-left {
            left: 15%;
          }
          .score-slider.position-center {
            left: 50%;
            background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
          }
          .score-slider.position-right {
            left: 85%;
          }
          .score-decision-buttons {
            display: flex;
            justify-content: space-around;
            margin-top: 20px;
            gap: 15px;
          }
          .score-decision-btn {
            padding: 12px 24px;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 6px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.3s;
            background: rgba(255,255,255,0.05);
            color: #fff;
            flex: 1;
          }
          .score-decision-btn:hover {
            background: rgba(255,255,255,0.1);
            border-color: rgba(255,255,255,0.5);
          }
          .score-decision-btn.active {
            background: #076251;
            border-color: #0a7a68;
            box-shadow: 0 0 15px rgba(7,98,81,0.6);
          }
          .score-decision-btn.omt-btn.active {
            background: #ffc107;
            color: #000;
            border-color: #ffc107;
          }
          .actions {
            display: flex;
            gap: 15px;
            justify-content: center;
          }
          .popup-net-error {
            display: none;
            margin-bottom: 14px;
            background: rgba(255, 107, 107, 0.14);
            border: 1px solid rgba(255, 107, 107, 0.45);
            border-radius: 8px;
            padding: 12px;
          }
          .popup-net-error.show {
            display: block;
          }
          .popup-result-overlay {
            display: none;
            position: fixed;
            inset: 0;
            background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
            z-index: 9999;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 40px 30px;
          }
          .popup-result-overlay.show {
            display: flex;
          }
          @keyframes popupResultPulse {
            0%,100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.85; transform: scale(1.02); }
          }
          .popup-result-heading {
            font-size: 96px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 6px;
            line-height: 1;
            margin-bottom: 12px;
            animation: popupResultPulse 1.5s ease-in-out infinite;
          }
          .popup-result-heading.omt { color: #ffc107; text-shadow: 0 0 40px rgba(255,193,7,0.7); }
          .popup-result-heading.winner { color: #4caf50; text-shadow: 0 0 40px rgba(76,175,80,0.7); }
          .popup-result-sub {
            font-size: 30px;
            font-weight: bold;
            letter-spacing: 4px;
            opacity: 0.9;
            margin-bottom: 8px;
          }
          .popup-result-battle {
            font-size: 13px;
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 28px;
          }
          .popup-result-tally {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
            margin-bottom: 30px;
          }
          .popup-result-tally-item {
            background: rgba(255,255,255,0.1);
            border-radius: 10px;
            padding: 12px 22px;
            font-size: 14px;
            font-weight: 600;
          }
          .popup-result-tally-item .tally-label {
            font-size: 11px;
            opacity: 0.65;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }
          .popup-result-tally-item .tally-count {
            font-size: 28px;
            font-weight: 900;
          }
          .popup-result-next {
            font-size: 13px;
            opacity: 0.45;
          }
          .popup-net-error-msg {
            font-size: 13px;
            line-height: 1.45;
            margin-bottom: 10px;
          }
          .popup-net-error-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
          .popup-mini-btn {
            border: 1px solid rgba(255,255,255,0.35);
            background: rgba(255,255,255,0.08);
            color: #fff;
            border-radius: 6px;
            font-size: 12px;
            padding: 7px 12px;
            cursor: pointer;
          }
          .action-btn {
            padding: 15px 40px;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.3s;
          }
          .btn-submit {
            background: #076251;
            color: white;
          }
          .btn-submit:hover {
            background: #0a7a68;
            box-shadow: 0 0 20px rgba(7,98,81,0.5);
            transform: translateY(-2px);
          }
          .btn-cancel {
            background: rgba(255,255,255,0.1);
            color: white;
            border: 2px solid rgba(255,255,255,0.3);
          }
          .btn-cancel:hover {
            background: rgba(255,255,255,0.2);
          }
          .info-box {
            background: rgba(255,255,255,0.05);
            border-left: 4px solid #076251;
            padding: 15px;
            margin-top: 20px;
            border-radius: 4px;
          }
          .info-box p {
            font-size: 13px;
            opacity: 0.8;
            line-height: 1.6;
          }
          @media (max-width: 950px) {
            .container { padding: 15px; }
            .header { padding: 15px 20px; }
            .header h1 { font-size: 22px; }
            .drivers-header { grid-template-columns: 1fr; gap: 12px; }
            .driver-card h2 { font-size: 24px; }
            .driver-card { padding: 15px; }
            .run-title { font-size: 16px; }
            .run-card { padding: 15px; }
            .score-section { padding: 20px; }
            .score-slider { width: 150px; height: 50px; font-size: 14px; }
            .score-decision-buttons { flex-direction: column; gap: 10px; }
            .score-decision-btn { width: 100%; }
            .actions { flex-direction: column; gap: 10px; }
            .action-btn { width: 100%; padding: 12px 30px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="display: flex; align-items: center; gap: 15px;">
            <img src="https://cdn.shopify.com/s/files/1/0720/3137/1432/files/20251119175125-1f0c5705-e2a9-6f32-9f1c-22a1214d2021.png?v=1763574999" alt="Ghostline Club" style="height: 50px; width: auto; object-fit: contain;" onerror="this.style.display='none';" />
            <div>
              <h1>${esc(eventDisplayName(judgeState.event, "JUDGE SCORING"))}</h1>
              <div class="battle-info">${esc(fullMatchTitle)}${omtDisplay}</div>
            </div>
          </div>
          <button class="close-btn" onclick="window.close()" title="Close">×</button>
        </div>

        <div class="container">
          <div class="drivers-header">
            <div class="driver-card ${picked === aVoteId ? 'selected' : ''}" id="card-driver-a">
              <h2>${aLabel}</h2>
              ${aMetaHtml}
            </div>
            <div class="driver-card ${picked === bVoteId ? 'selected' : ''}" id="card-driver-b">
              <h2>${bLabel}</h2>
              ${bMetaHtml}
            </div>
          </div>

          <div class="run-section">
            <div class="run-card" id="run-card">
              <div class="run-header">
                <div>
                  <div class="run-title" id="run-title">Match Notes / Scoring</div>
                  <div class="run-role" id="run-role">Current Match</div>
                </div>
                <div id="notes-save-status" style="font-size: 12px; opacity: 0.75;">Saved</div>
              </div>
              <textarea class="run-notes" id="notes-current" placeholder="Enter notes for this match">${esc(initialMatchNotes)}</textarea>
            </div>
          </div>

          <div class="score-section">
            <div class="score-title">SCORE DECISION</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
              <div style="text-align: center; flex: 1;">
                <div id="popup-driver-a-label" style="font-weight: bold; font-size: 18px;">${aLabel}</div>
              </div>
              <div style="text-align: center; flex: 1;">
                <div style="font-size: 14px; opacity: 0.7; margin-bottom: 5px;">OMT</div>
                <div style="font-weight: bold; font-size: 18px; color: #ffc107;">ONE MORE TIME</div>
              </div>
              <div style="text-align: center; flex: 1;">
                <div id="popup-driver-b-label" style="font-weight: bold; font-size: 18px;">${bLabel}</div>
              </div>
            </div>
            <div class="score-slider-container">
              <div class="score-track"></div>
              <div class="score-slider" id="score-slider" style="${picked === aVoteId ? 'left: 15%;' : picked === bVoteId ? 'left: 85%;' : picked === 'omt' ? 'left: 50%; background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);' : 'left: 50%;'}">
                ${picked === aVoteId ? aLabel : picked === bVoteId ? bLabel : picked === 'omt' ? 'OMT' : 'SELECT'}
              </div>
            </div>
            <div class="score-decision-buttons">
              <button class="score-decision-btn ${picked === aVoteId ? 'active' : ''}"
                      id="btn-driver-a"
                      ${!canVoteA ? 'disabled style="opacity: 0.5; pointer-events: none; cursor: not-allowed;"' : ''}>
                ${aLabel}
              </button>
              <button class="score-decision-btn omt-btn ${picked === 'omt' ? 'active' : ''}" 
                      onclick="selectWinner('omt')" 
                      id="btn-omt">OMT</button>
              <button class="score-decision-btn ${picked === bVoteId ? 'active' : ''}"
                      id="btn-driver-b"
                      ${!canVoteB ? 'disabled style="opacity: 0.5; pointer-events: none; cursor: not-allowed;"' : ''}>
                ${bLabel}
              </button>
            </div>
          </div>

          <div id="popup-net-error" class="popup-net-error">
            <div id="popup-net-error-msg" class="popup-net-error-msg">A temporary network error occurred.</div>
            <div class="popup-net-error-actions">
              <button type="button" class="popup-mini-btn" id="popup-btn-retry">Retry Submit</button>
              <button type="button" class="popup-mini-btn" id="popup-btn-resync">Resync Match</button>
            </div>
          </div>

          <div class="actions">
            <button class="action-btn btn-submit" id="btn-submit-score" onclick="submitScore()">SUBMIT SCORE</button>
            <button class="action-btn btn-cancel" onclick="window.close()">CANCEL</button>
          </div>

          <div class="info-box">
            <p><strong>Scoring Guidelines:</strong> Majority of 3 judges decides the winner. 2+ OMT votes forces an OMT (One More Time) battle. An undecided score will result in an OMT. Consider line, angle, style, and proximity when scoring.</p>
            ${omtCount > 0 ? `<p style="margin-top: 10px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.1);"><strong>OMT Status:</strong> This is OMT #${omtCount} (Run Set ${runSetNumber}). Previous run sets have been completed.</p>` : ''}
          </div>
        </div>

        <!-- Result overlay (position: fixed — covers full viewport regardless of DOM position) -->
        <div id="popup-result-overlay" class="popup-result-overlay">
          <div id="popup-result-heading" class="popup-result-heading omt">OMT</div>
          <div id="popup-result-sub" class="popup-result-sub">ONE MORE TIME</div>
          <div id="popup-result-battle" class="popup-result-battle"></div>
          <div id="popup-result-tally" class="popup-result-tally"></div>
          <div class="popup-result-next">This window will close in 5 seconds...</div>
        </div>

        <script>
          let selectedWinner = '${picked}';
          const matchId = '${esc(m.id)}';
          const eventId = '${esc(String(eventId || ""))}';
          var voteA = '${esc(aVoteId)}';
          var voteB = '${esc(bVoteId)}';
          let driverALabel = '${aLabel}';
          let driverBLabel = '${bLabel}';
          const omtCount = ${omtCount};
          const openBattleNo = ${battleNo};
          const runSetNumber = ${runSetNumber};
          const judgeToken = ${JSON.stringify(popupJudgeToken)};
          const DEBUG = ${judgeDebug ? "true" : "false"};
          let pendingSubmitRequestId = null;
          let lastSubmitPayload = null;
          let resyncInFlight = false;
          let resyncInterval = null;

          function debugLog() {
            if (!DEBUG) return;
            console.log('[judge-popup]', ...arguments);
          }

          function setSubmitBusy(isBusy) {
            const submitBtn = document.getElementById('btn-submit-score');
            if (!submitBtn) return;
            submitBtn.disabled = !!isBusy;
            submitBtn.textContent = isBusy ? 'SUBMITTING...' : 'SUBMIT SCORE';
          }

          function showNetworkError(message) {
            const box = document.getElementById('popup-net-error');
            const msg = document.getElementById('popup-net-error-msg');
            if (msg) msg.textContent = String(message || 'Temporary network error. Please retry or resync.');
            if (box) box.classList.add('show');
          }

          function clearNetworkError() {
            const box = document.getElementById('popup-net-error');
            if (box) box.classList.remove('show');
          }

          function showResultOverlay(type, tally, aLabel, bLabel, aId, bId) {
            const overlay = document.getElementById('popup-result-overlay');
            const heading = document.getElementById('popup-result-heading');
            const sub = document.getElementById('popup-result-sub');
            const tallyEl = document.getElementById('popup-result-tally');
            if (!overlay) return;
            const isOmt = type === 'omt';
            if (heading) {
              heading.textContent = isOmt ? 'OMT' : 'WINNER';
              heading.className = 'popup-result-heading ' + (isOmt ? 'omt' : 'winner');
            }
            if (sub) sub.textContent = isOmt ? 'ONE MORE TIME' : '';
            if (tallyEl) {
              tallyEl.innerHTML = (tally || []).map(function(t) {
                var label = t.winner === 'omt' ? 'OMT'
                  : (String(t.winner) === String(aId) ? (aLabel || 'Driver A')
                  : (String(t.winner) === String(bId) ? (bLabel || 'Driver B') : String(t.winner)));
                return '<div class="popup-result-tally-item"><div class="tally-label">' + label + '</div><div class="tally-count">' + t.count + ' vote' + (t.count !== 1 ? 's' : '') + '</div></div>';
              }).join('');
            }
            // Disable all scoring controls while overlay is showing
            var submitBtn = document.getElementById('btn-submit-score');
            if (submitBtn) submitBtn.disabled = true;
            overlay.classList.add('show');
          }

          function updateDriverLabelsUi() {
            const cardATitle = document.querySelector('#card-driver-a h2');
            const cardBTitle = document.querySelector('#card-driver-b h2');
            const btnA = document.getElementById('btn-driver-a');
            const btnB = document.getElementById('btn-driver-b');
            const scoreA = document.getElementById('popup-driver-a-label');
            const scoreB = document.getElementById('popup-driver-b-label');
            if (cardATitle) cardATitle.textContent = driverALabel;
            if (cardBTitle) cardBTitle.textContent = driverBLabel;
            if (btnA) btnA.textContent = driverALabel;
            if (btnB) btnB.textContent = driverBLabel;
            if (scoreA) scoreA.textContent = driverALabel;
            if (scoreB) scoreB.textContent = driverBLabel;
          }

          // debug helpers
          window.__judge = {
            matchId: matchId,
            voteA: voteA,
            voteB: voteB,
            debug: DEBUG,
            get selectedWinner(){ return selectedWinner; }
          };
          debugLog('init', { matchId, voteA, voteB, selectedWinner, runSetNumber, omtCount });

          document.getElementById('btn-driver-a')?.addEventListener('click', () => selectWinner(voteA));
          document.getElementById('btn-driver-b')?.addEventListener('click', () => selectWinner(voteB));

          function updateSubmitButtonState() {
            const submitBtn = document.getElementById('btn-submit-score');
            if (!submitBtn) return;
            if (pendingSubmitRequestId) {
              submitBtn.disabled = true;
              return;
            }
            submitBtn.disabled = !(selectedWinner === voteA || selectedWinner === voteB || selectedWinner === 'omt');
          }

          function selectWinner(winner) {
            selectedWinner = winner;
            debugLog('selectWinner', winner);
            clearNetworkError();

            const btnA = document.getElementById('btn-driver-a');
            const btnB = document.getElementById('btn-driver-b');
            const btnO = document.getElementById('btn-omt');
            const cardA = document.getElementById('card-driver-a');
            const cardB = document.getElementById('card-driver-b');
            const slider = document.getElementById('score-slider');

            // Clear button state
            if (btnA) btnA.className = 'score-decision-btn';
            if (btnB) btnB.className = 'score-decision-btn';
            if (btnO) btnO.className = 'score-decision-btn omt-btn';

            // Clear card state
            if (cardA) cardA.className = 'driver-card';
            if (cardB) cardB.className = 'driver-card';

            // Apply selected state (single-select)
            if (winner === voteA) {
              if (btnA) btnA.className += ' active';
              if (cardA) cardA.className += ' selected';

              slider.style.left = '15%';
              slider.style.background = 'linear-gradient(135deg, #076251 0%, #0a7a68 100%)';
              slider.textContent = driverALabel;
            } else if (winner === voteB) {
              if (btnB) btnB.className += ' active';
              if (cardB) cardB.className += ' selected';

              slider.style.left = '85%';
              slider.style.background = 'linear-gradient(135deg, #076251 0%, #0a7a68 100%)';
              slider.textContent = driverBLabel;
            } else if (winner === 'omt') {
              if (btnO) btnO.className += ' active';

              slider.style.left = '50%';
              slider.style.background = 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)';
              slider.textContent = 'OMT';
            } else {
              slider.style.left = '50%';
              slider.style.background = 'linear-gradient(135deg, #076251 0%, #0a7a68 100%)';
              slider.textContent = 'SELECT';
            }

            // Enable submit only when valid
            updateSubmitButtonState();
          }

          const notesField = document.getElementById('notes-current');
          const saveStatus = document.getElementById('notes-save-status');
          let notesSaveTimer = null;
          let lastSentNotes = String(notesField?.value || "");
          let lastSavedNotes = String(notesField?.value || "");

          function setSaveStatus(text, isError) {
            if (!saveStatus) return;
            saveStatus.textContent = text;
            saveStatus.style.color = isError ? '#ff9090' : 'rgba(255,255,255,0.75)';
          }

          function postNotesSave(force) {
            if (!window.opener || !notesField) return;
            const notes = String(notesField.value || "");
            if (!force && notes === lastSentNotes) return;
            lastSentNotes = notes;
            setSaveStatus('Saving...', false);
            window.opener.postMessage({
              type: 'judge-notes-draft',
              eventId: eventId,
              matchId: matchId,
              notes: notes,
              force: !!force
            }, '*');
          }

          if (notesField) {
            notesField.addEventListener('input', () => {
              if (notesSaveTimer) clearTimeout(notesSaveTimer);
              notesSaveTimer = setTimeout(() => postNotesSave(false), 800);
            });
            notesField.addEventListener('blur', () => postNotesSave(true));
          }

          function requestResync(reason) {
            if (!window.opener || resyncInFlight) return;
            resyncInFlight = true;
            window.opener.postMessage({
              type: 'judge-popup-resync-request',
              eventId: eventId,
              matchId: matchId,
              reason: String(reason || 'manual')
            }, '*');
          }

          function sendSubmitRequest(payload) {
            if (!window.opener) {
              showNetworkError('Parent window is unavailable. Reopen this pop-out and retry.');
              return;
            }
            const requestId = String(Date.now()) + ':' + String(Math.random()).slice(2);
            pendingSubmitRequestId = requestId;
            setSubmitBusy(true);
            clearNetworkError();
            window.opener.postMessage({
              type: 'judge-score-submit',
              requestId: requestId,
              matchId: matchId,
              decision: payload.decision,
              notes_run1: payload.notes_run1,
              notes_run2: payload.notes_run2,
              expectedBattleNo: openBattleNo,
              expectedOmtCount: omtCount,
              judgeToken: judgeToken
            }, '*');
          }

          function retryLastSubmit() {
            if (lastSubmitPayload) {
              sendSubmitRequest(lastSubmitPayload);
            } else {
              requestResync('retry_without_payload');
            }
          }

          window.addEventListener('message', function(evt) {
            if (evt.origin !== window.location.origin) return;
            const data = evt.data || {};
            if (data.type === 'judge-notes-save-status') {
              if (String(data.matchId || '') !== String(matchId)) return;
              if (data.status === 'saved') {
                lastSavedNotes = String(data.notes || '');
                setSaveStatus('Saved', false);
              } else if (data.status === 'error') {
                setSaveStatus('Save failed', true);
              }
              return;
            }
            if (data.type === 'judge-score-submit-result') {
              if (!pendingSubmitRequestId || String(data.requestId || '') !== String(pendingSubmitRequestId)) return;
              pendingSubmitRequestId = null;
              setSubmitBusy(false);
              updateSubmitButtonState();
              if (data.ok) {
                clearNetworkError();
                if (data.omt) {
                  // Show OMT result overlay for 5 seconds then close
                  showResultOverlay('omt', data.tally || [], data.driverALabel || driverALabel, data.driverBLabel || driverBLabel, data.voteAId || voteA, data.voteBId || voteB);
                  setTimeout(() => window.close(), 5000);
                } else {
                  window.close();
                }
                return;
              }
              showNetworkError(String(data.error || 'Score submission failed. Please retry or resync.'));
              return;
            }
            if (data.type === 'judge-popup-resync-result') {
              if (String(data.matchId || '') !== String(matchId)) return;
              resyncInFlight = false;
              if (!data.ok) {
                showNetworkError(String(data.error || 'Resync failed. Please try again.'));
                return;
              }
              const next = data.match || null;
              if (!next) {
                showNetworkError('This match is no longer active. Close and reopen judging from the latest battle.');
                return;
              }
              voteA = String(next.vote_a_id || '');
              voteB = String(next.vote_b_id || '');
              driverALabel = String(next.driver_a_label || driverALabel || 'Driver A');
              driverBLabel = String(next.driver_b_label || driverBLabel || 'Driver B');
              updateDriverLabelsUi();
              // If the battle has advanced (new OMT round or battle_no increased), reset selection.
              // battle_no check guards against D1 eventual-consistency returning stale old-battle data.
              if (Number(next.battle_no || 1) > openBattleNo || Number(next.omt_count || 0) > omtCount) {
                selectWinner('');
              } else if (selectedWinner !== 'omt' && selectedWinner !== voteA && selectedWinner !== voteB) {
                selectWinner('');
              } else {
                selectWinner(selectedWinner);
              }
              // Inline lock check (isJudgeMatchLocked is not available in popup scope)
              const nextWinnerVal = String(next?.winner || '').trim();
              const nextLocked = !!(nextWinnerVal && nextWinnerVal.toLowerCase() !== 'omt');
              if (nextLocked) {
                showNetworkError('This battle has already been decided. Close and open the next current battle.');
              } else {
                clearNetworkError();
              }
              return;
            }
          });

          function submitScore() {
            debugLog('submitScore:start', { selectedWinner, voteA, voteB });
            if (!selectedWinner) {
              alert('Please select a winner (Driver A, Driver B, or OMT)');
              return;
            }
            if (selectedWinner !== 'omt' && (!voteA || !voteB)) {
              alert('Cannot submit score: one or both vote IDs are missing for this battle.');
              return;
            }

            const finalNotes = String(notesField?.value || "").trim();
            const finalNotesRun1 = finalNotes || null;
            const finalNotesRun2 = null;

            const openerMatchId = window.opener && window.opener.openJudgeScoreModal ? window.opener.openJudgeScoreModal._matchId : null;
            if (openerMatchId != null && String(openerMatchId) !== String(matchId)) {
              alert("Current battle changed. Please reopen the score window for the active match.");
              return;
            }

            // Send message to parent window
            const payload = {
              decision: selectedWinner,
              notes_run1: finalNotesRun1,
              notes_run2: finalNotesRun2
            };
            lastSubmitPayload = payload;
            debugLog('postMessage:judge-score-submit', { matchId: matchId, ...payload });
            sendSubmitRequest(payload);
          }

          // Handle window close
          window.addEventListener('beforeunload', function() {
            if (resyncInterval) clearInterval(resyncInterval);
            if (window.opener) {
              debugLog('postMessage:judge-score-closed', { matchId: matchId });
              window.opener.postMessage({
                type: 'judge-score-closed',
                matchId: matchId
              }, '*');
            }
          });

          document.getElementById('popup-btn-retry')?.addEventListener('click', retryLastSubmit);
          document.getElementById('popup-btn-resync')?.addEventListener('click', () => requestResync('manual_button'));
          window.addEventListener('focus', () => requestResync('focus'));
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') requestResync('visibility');
          });
          resyncInterval = setInterval(() => requestResync('interval'), 8000);
          requestResync('initial_open');

          updateSubmitButtonState();
        </script>
      </body>
      </html>
    `);
    
    popup.document.close();
    openJudgeScoreModal._popup = popup;
    openJudgeScoreModal._matchId = matchId;
  }

  async function openJudgeScoreModal(matchId) {
    if (judgeState.token) await judgeRefresh().catch(() => {});
    const match = (judgeState.matches || []).find((item) => String(item.id) === String(matchId));
    if (!match || isJudgeMatchLocked(match) || !matchHasTwoDrivers(match)) {
      toast("This battle is not available to score.", "err");
      return;
    }
    const eventId = String(judgeState.event?.id || match.event_id || "");
    let notes = "";
    try {
      notes = await judgeGetMatchNotes(eventId, match.id);
    } catch (_) {
      notes = String(judgeState.notesCache[`${eventId}::${match.id}`] || "");
    }
    judgeState.scorePanel = {
      matchId: String(match.id),
      decision: "",
      notes,
      submitting: false,
      error: "",
      expectedBattleNo: Number(match.battle_no || 1),
      expectedOmtCount: Number(match.omt_count || 0),
    };
    renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
    judgePane.querySelector(".gl-ce-judge-score-panel")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  async function judgeSubmitInlineScore() {
    const panel = judgeState.scorePanel;
    if (!panel?.matchId || panel.submitting) return;
    const notes = String(judgePane.querySelector('[data-role="judge-inline-notes"]')?.value ?? panel.notes ?? "").trim();
    panel.notes = notes;
    panel.submitting = true;
    panel.error = "";
    renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
    try {
      const result = await judgeSubmitScore(panel.matchId, panel.decision, notes || null, null, {
        expectedBattleNo: panel.expectedBattleNo,
        expectedOmtCount: panel.expectedOmtCount,
      });
      const submittedMatchId = panel.matchId;
      judgeState.scorePanel = {
        matchId: null,
        decision: "",
        notes: "",
        submitting: false,
        error: "",
        expectedBattleNo: null,
        expectedOmtCount: null,
      };
      await judgeHandlePostScore(submittedMatchId, result);
    } catch (error) {
      panel.error = friendlyJudgeNetworkError(error, "Score submission failed.");
      throw error;
    } finally {
      if (judgeState.scorePanel?.matchId === panel.matchId) {
        judgeState.scorePanel.submitting = false;
        renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
      }
    }
  }

  function isJudgeScoreModalOpen() {
    return !!judgeState.scorePanel?.matchId;
  }

  function captureFocusedJudgeInput() {
    const input = document.activeElement;
    if (!input || !judgePane?.contains(input) || !/^(INPUT|TEXTAREA|SELECT)$/.test(input.tagName)) return null;
    return {
      id: String(input.id || ""),
      role: String(input.dataset?.role || ""),
      value: String(input.value ?? ""),
      selectionStart: typeof input.selectionStart === "number" ? input.selectionStart : null,
      selectionEnd: typeof input.selectionEnd === "number" ? input.selectionEnd : null,
      qualifyingRunId: input.dataset?.role === "judge-qual-score"
        ? String(input.dataset?.runId || "")
        : "",
    };
  }

  function restoreFocusedJudgeInput(snapshot) {
    if (!snapshot || !judgePane) return;
    if (
      snapshot.role === "judge-qual-score" &&
      snapshot.qualifyingRunId !== String(judgeState.qualifying?.run?.id || "")
    ) return;
    let input = snapshot.id ? judgePane.querySelector(`#${snapshot.id}`) : null;
    if (!input && snapshot.role) input = judgePane.querySelector(`[data-role="${snapshot.role}"]`);
    if (!input || !/^(INPUT|TEXTAREA|SELECT)$/.test(input.tagName)) return;
    input.value = snapshot.value;
    input.focus({ preventScroll: true });
    if (snapshot.selectionStart != null && typeof input.setSelectionRange === "function") {
      input.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd ?? snapshot.selectionStart);
    }
  }

  function renderJudge() {
    const focusedInput = captureFocusedJudgeInput();
    const tok = judgeState.token;
    const isQualifying = judgeState.mode === "qualifying";

    judgePane.innerHTML = `
      <div class="gl-ce-card">
        <h2 class="gl-ce-h">Judge Hub</h2>
        <p class="gl-ce-sub">Enter your Judge Code to start a judging session (works only when the event is <b>LIVE</b> or in <b>QUALIFYING</b>).</p>

        <div style="margin-top:10px;">
          <label class="gl-ce-label">Judge Code</label>
          <input class="gl-ce-input" id="glCeJudgeCode" maxlength="64" autocapitalize="characters" autocomplete="off" placeholder="6-character code" value="${esc(judgeState.judge_code)}" />
        </div>

        <div class="gl-ce-row" style="margin-top:10px;">
          <button class="gl-ce-btn" data-action="judge-start" type="button">Start Session</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="judge-clear" type="button">Clear</button>
        </div>

        <div class="gl-ce-muted" style="margin-top:10px;">Session token: ${tok ? `<code>${esc(tok)}</code>` : "—"}</div>
      </div>

      ${
        tok
          ? `<div class="gl-ce-card">
              ${judgeState.event ? `
                <div style="background: linear-gradient(90deg, #076251 0%, #0a7a68 100%); padding: 12px 16px; border-radius: 6px; margin-bottom: 16px; color: #fff;">
                  <div style="font-size: 14px; opacity: 0.9; margin-bottom: 4px;">CURRENT EVENT${isQualifying ? " · QUALIFYING" : ""}</div>
                  <div style="font-size: 18px; font-weight: bold;">${esc(eventDisplayName(judgeState.event, "Event"))}</div>
                  ${judgeState.event.comp_code ? `<div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Code: <code style="background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 3px;">${esc(judgeState.event.comp_code)}</code></div>` : ''}
                </div>
              ` : ''}
              <div class="gl-ce-row gl-ce-row-between">
                <h3 class="gl-ce-h2">${isQualifying ? "Qualifying Run" : "Matches"}</h3>
                <div class="gl-ce-row gl-ce-row-wrap">
                  ${!judgeState.judge_name ? `
                    <input class="gl-ce-input gl-ce-input-sm" id="glCeJudgeName" placeholder="Your display name (optional)" />
                    <button class="gl-ce-btn gl-ce-btn-sm" data-action="judge-save-name" type="button">Save Name</button>
                  ` : `
                    <div class="gl-ce-muted">Judge: <b>${esc(judgeState.judge_name)}</b>${judgeState.judge_slot ? ` <span class="gl-ce-muted">(Slot ${esc(judgeState.judge_slot)})</span>` : ""}</div>
                  `}
                  <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="judge-refresh" type="button">Refresh</button>
                </div>
              </div>
              ${!judgeState.judge_name ? `<div class="gl-ce-muted" style="margin-top:8px;">You can add a display name (optional).</div>` : ""}
              ${isQualifying
                ? `<div id="glCeJudgeQualifying"></div>`
                : `<div id="glCeJudgeMatches"></div>`}
            </div>`
          : ""
      }
    `;

    if (tok) {
      if (isQualifying) {
        renderJudgeQualifying(judgePane.querySelector("#glCeJudgeQualifying"));
      } else {
        renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
      }
    }
    restoreFocusedJudgeInput(focusedInput);
  }

  function renderJudgeQualifying(mount) {
    if (!mount) return;
    const run = judgeState.qualifying?.run || null;
    const total = Number(judgeState.qualifying?.total_drivers || 0);
    const mySlot = Number(judgeState.judge_slot || 0);
    const config = qualifyingDisplayConfig(judgeState.qualifying);

    if (!run) {
      mount.innerHTML = `
        <div class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;">
          <div class="gl-ce-muted">No qualifying run is currently pending. Waiting for the admin to start the next run.</div>
        </div>
      `;
      return;
    }

    const myScore = run.scores?.find((s) => Number(s.judge_slot) === mySlot) || null;
    const draftRunId = String(judgeState.qualifyingDraft?.runId || "");
    const inputValue = draftRunId === String(run.id || "")
      ? String(judgeState.qualifyingDraft?.value ?? "")
      : (myScore ? String(myScore.score) : "");

    const otherJudgeRows = [1, 2, 3]
      .filter((slot) => slot !== mySlot)
      .map((slot) => {
        const s = run.scores?.find((r) => Number(r.judge_slot) === slot) || null;
        return `<div class="gl-ce-qual-judge-row">
          <span>${esc(config.roleForSlot(slot))}</span>
          <strong data-role="judge-qual-other-score" data-slot="${esc(slot)}">${s ? esc(formatQualifyingScoreValue(s.score)) : "—"}</strong>
        </div>`;
      })
      .join("");

    const totalLabel = total > 0 ? `${total} driver${total === 1 ? "" : "s"}` : "—";
    const buttonLabel = myScore ? "Update Score" : "Submit Score";

    const driverNumberLabel = run.driver_number != null && String(run.driver_number).trim() !== ""
      ? `#${run.driver_number}`
      : "";

    mount.innerHTML = `
      <div class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;">
        <div class="gl-ce-row gl-ce-row-between" style="align-items:flex-start; gap:12px; flex-wrap:wrap;">
          <div>
            <div class="gl-ce-muted" style="font-size:12px;">DRIVER</div>
            <div style="font-size:20px; font-weight:700;">${esc(run.driver_name || "Driver")}${driverNumberLabel ? ` <span style="font-size:14px;color:#076251;">${esc(driverNumberLabel)}</span>` : ""}</div>
            <div class="gl-ce-muted" style="margin-top:4px;">Run ${esc(run.run_no || "—")} · ${esc(totalLabel)}</div>
          </div>
          <div style="text-align:right;">
            <div class="gl-ce-muted" style="font-size:12px;">YOUR SLOT</div>
            <div style="font-size:18px; font-weight:700;">${esc(mySlot ? config.roleForSlot(mySlot) : "—")}</div>
          </div>
        </div>

        <div style="margin-top:14px;">
          <label class="gl-ce-label" for="glCeJudgeQualScore">Your Score (0–${esc(config.maxForSlot(mySlot))})</label>
          <div class="gl-ce-row" style="gap:8px; align-items:center; flex-wrap:wrap;">
            <input class="gl-ce-input"
                   id="glCeJudgeQualScore"
                   data-role="judge-qual-score"
                   data-run-id="${esc(run.id)}"
                   type="text"
                   inputmode="decimal"
                   min="0"
                   max="${esc(config.maxForSlot(mySlot))}"
                   step="0.01"
                   placeholder="${esc(config.isLas ? `e.g. ${config.maxForSlot(mySlot) === 40 ? "35" : "25"}` : "e.g. 92.5")}"
                   style="max-width:160px;"
                   value="${esc(inputValue)}" />
            <button class="gl-ce-btn"
                    data-action="judge-qual-submit"
                    data-run-id="${esc(run.id)}"
                    type="button">${esc(buttonLabel)}</button>
            <span class="gl-ce-muted" data-role="judge-qual-last-submitted">${myScore ? `Your last submitted score: <b>${esc(formatQualifyingScoreValue(myScore.score))}</b>` : ""}</span>
          </div>
        </div>
        <div style="margin-top:12px;">
          <label class="gl-ce-label" for="glCeJudgeQualNote">Private judge note <span class="gl-ce-muted">(visible to you and admins only)</span></label>
          <textarea class="gl-ce-input" id="glCeJudgeQualNote" data-role="judge-qual-note" rows="3" maxlength="2000" placeholder="Optional qualifying-run note">${esc(draftRunId === String(run.id || "") ? String(judgeState.qualifyingDraft?.note ?? run.my_note ?? "") : String(run.my_note || ""))}</textarea>
        </div>

        <div style="margin-top:16px;">
          <div class="gl-ce-muted" style="font-size:12px; margin-bottom:6px;">OTHER JUDGES</div>
          <div class="gl-ce-qual-judge-grid">
            ${otherJudgeRows}
          </div>
        </div>

        <div class="gl-ce-muted" style="margin-top:14px;">
          ${esc(config.completionCopy)}
        </div>
      </div>
    `;

    const input = mount.querySelector('input[data-role="judge-qual-score"]');
    if (input) {
      input.addEventListener("input", (e) => {
        judgeState.qualifyingDraft = {
          runId: String(run.id || ""),
          value: e.target.value,
          note: String(judgeState.qualifyingDraft?.note ?? run.my_note ?? ""),
        };
      });
    }
    const note = mount.querySelector('[data-role="judge-qual-note"]');
    if (note) {
      note.addEventListener("input", (e) => {
        judgeState.qualifyingDraft = {
          runId: String(run.id || ""),
          value: String(judgeState.qualifyingDraft?.value ?? inputValue),
          note: e.target.value,
        };
      });
    }
  }

  async function judgeSubmitQualifyingScore(runId) {
    const tok = judgeState.token;
    if (!tok) throw new Error("Start a judge session first.");
    if (!runId) throw new Error("No active run.");
    const eventId = judgeState.event?.id;
    if (!eventId) throw new Error("Event id missing.");
    const slot = Number(judgeState.judge_slot || 0);
    if (!slot) throw new Error("Judge slot is missing.");

    const input = judgePane.querySelector('input[data-role="judge-qual-score"]');
    const raw = input ? String(input.value || "").trim() : String(judgeState.qualifyingDraft?.value ?? "").trim();
    if (raw === "") throw new Error("Enter a score before submitting.");
    const score = Number(raw);
    if (!Number.isFinite(score)) throw new Error("Enter a valid number.");
    const config = qualifyingDisplayConfig(judgeState.qualifying);
    const maximum = config.maxForSlot(slot);
    if (score < 0 || score > maximum) throw new Error(`Score must be between 0 and ${maximum}.`);
    const note = String(judgePane.querySelector('[data-role="judge-qual-note"]')?.value ?? judgeState.qualifyingDraft?.note ?? "").trim();

    await apiPost(
      `/admin/events/${encodeURIComponent(eventId)}/qualifying/runs/${encodeURIComponent(runId)}/scores?token=${encodeURIComponent(tok)}`,
      { judge_slot: slot, score, note }
    );
    judgeState.qualifyingDraft = { runId: String(runId), value: String(score), note };
    toast("Score submitted.", "ok");
    await judgeRefresh();
  }

  function patchJudgeLive() {
    if (!judgePane) return;

    // 1) Update match list only (safe)
    const mount = judgePane.querySelector("#glCeJudgeMatches");
    if (mount) renderJudgeMatches(mount);

    // 2) Do NOT touch modal DOM while open
    if (isJudgeScoreModalOpen()) {
      return;
    }

    // 3) If modal is closed, it's safe to update non-interactive header areas if you have them.
    // IMPORTANT: Do not replace judgePane.innerHTML here.
  }

  async function judgeStartSession(eventCode, judgeCode) {
    // Worker expects just "code" (judge code), event code is not needed
    const normalizedCode = String(judgeCode || "").trim().toUpperCase();
    const data = await apiPost("/judge/session", { code: normalizedCode });
    judgeState.token = data.token;
    judgeState.judge_code = normalizedCode;
    await judgeRefresh();
  }

  async function judgeRefresh() {
    const tok = judgeState.token;
    if (!tok) return;
    let data;
    try {
      data = await apiGet(`/judge/matches?token=${encodeURIComponent(tok)}`);
    } catch (error) {
      if (isInvalidJudgeSessionError(error)) {
        clearJudgeSessionState();
        renderJudge();
      }
      throw error;
    }
    judgeState.judge_slot = data.judge_slot;
    judgeState.event = data.event;
    judgeState.matches = data.matches || [];
    judgeState.judge_name = data.judge_name || judgeState.judge_name || "";
    judgeState.current_match_id = data.current_match_id || null;
    judgeState.mode = data.mode === "qualifying" ? "qualifying" : "battle";
    const openPanel = judgeState.scorePanel;
    if (openPanel?.matchId) {
      const authoritative = judgeState.matches.find((match) => String(match.id) === String(openPanel.matchId));
      const currentChanged = judgeState.current_match_id && String(judgeState.current_match_id) !== String(openPanel.matchId);
      const battleChanged = authoritative && (
        Number(authoritative.battle_no || 1) !== Number(openPanel.expectedBattleNo || 1) ||
        Number(authoritative.omt_count || 0) !== Number(openPanel.expectedOmtCount || 0)
      );
      if (!authoritative || isJudgeMatchLocked(authoritative) || currentChanged || battleChanged) {
        judgeState.scorePanel = {
          matchId: null, decision: "", notes: "", submitting: false, error: "",
          expectedBattleNo: null, expectedOmtCount: null,
        };
      }
    }
    let newRunId = "";
    if (judgeState.mode === "qualifying") {
      judgeState.qualifying = {
        run: data.qualifying?.run || null,
        total_drivers: Number(data.qualifying?.total_drivers || 0),
        mode: data.qualifying?.mode || "score",
        judge_roles: data.qualifying?.judge_roles || null,
      };
      newRunId = String(judgeState.qualifying.run?.id || "");
      if (judgeState.qualifyingDraft.runId !== newRunId) {
        judgeState.qualifyingDraft = { runId: newRunId, value: "", note: String(judgeState.qualifying.run?.my_note || "") };
      }
    } else {
      judgeState.qualifying = { run: null, total_drivers: 0 };
    }

    if (judgeState.mode === "qualifying") {
      const scoreInput = judgePane.querySelector('input[data-role="judge-qual-score"]');
      if (scoreInput === document.activeElement && String(scoreInput.dataset.runId || "") === newRunId) {
        patchJudgeQualifyingLive();
      } else {
        renderJudge();
      }
      judgeStartQualifyingPoll();
      persistJudgeSession();
      return;
    }
    judgeStopQualifyingPoll();
    judgeStartQualifyingPoll();

    // First time: do full render if judge UI hasn't been mounted yet
    if (!judgePane.querySelector("#glCeJudgeMatches")) {
      renderJudge();
      persistJudgeSession();
      return;
    }

    // Regular polling refresh: patch only live bits
    patchJudgeLive();
    persistJudgeSession();
  }

  function patchJudgeQualifyingLive() {
    const mount = judgePane.querySelector("#glCeJudgeQualifying");
    const run = judgeState.qualifying?.run;
    if (!mount || !run) return;
    const mySlot = Number(judgeState.judge_slot || 0);
    mount.querySelectorAll('[data-role="judge-qual-other-score"]').forEach((node) => {
      const slot = Number(node.dataset.slot || 0);
      const score = run.scores?.find((item) => Number(item.judge_slot) === slot);
      node.textContent = score ? formatQualifyingScoreValue(score.score) : "—";
    });
    const mine = run.scores?.find((item) => Number(item.judge_slot) === mySlot);
    const submit = mount.querySelector('[data-action="judge-qual-submit"]');
    if (submit) submit.textContent = mine ? "Update Score" : "Submit Score";
    const last = mount.querySelector('[data-role="judge-qual-last-submitted"]');
    if (last) last.innerHTML = mine ? `Your last submitted score: <b>${esc(formatQualifyingScoreValue(mine.score))}</b>` : "";
  }

  function judgeStartQualifyingPoll() {
    judgeStopQualifyingPoll();
    judgeState.qualifyingPollTimer = setInterval(() => {
      judgeRefresh().catch((err) => {
        console.error("Judge qualifying poll error:", err);
      });
    }, 4000);
  }

  function judgeStopQualifyingPoll() {
    if (judgeState.qualifyingPollTimer) {
      clearInterval(judgeState.qualifyingPollTimer);
      judgeState.qualifyingPollTimer = null;
    }
  }

  async function judgeSaveName() {
    const tok = judgeState.token;
    if (!tok) return;
    const value = (judgePane.querySelector("#glCeJudgeName")?.value ?? "").trim();
    if (!value) {
      toast("Enter a display name to save.", "info");
      return;
    }
    const data = await apiPost(`/judge/save-name?token=${encodeURIComponent(tok)}`, { name: value });
    judgeState.judge_name = String(data?.judge_name || value || "").trim();
    toast("Saved.", "ok");
    await judgeRefresh();
    // Full render so header shows updated judge name
    renderJudge();
  }

  async function judgeSubmitScore(matchId, decision, notes_run1, notes_run2, options = {}) {
    const interactiveRetry = options?.interactiveRetry !== false;
    const tok = String(options?.judgeToken || judgeState.token || "");
    if (!decision) throw new Error("Pick a winner or OMT first.");
    // Do not refresh immediately before submitting. A refresh can observe an
    // OMT transition caused by another judge and reject this already-clicked
    // final vote locally, before the server gets the chance to attach it to
    // the completed battle during its reveal window.
    const latestMatch = (judgeState.matches || []).find((x) => String(x.id) === String(matchId));
    if (isJudgeMatchLocked(latestMatch)) {
      throw new Error("This match has already been decided and can no longer be scored.");
    }
    const expectedBattleNo = Number.isFinite(Number(options?.expectedBattleNo))
      ? Number(options.expectedBattleNo)
      : Number(latestMatch?.battle_no || 1);
    const expectedOmtCount = Number.isFinite(Number(options?.expectedOmtCount))
      ? Number(options.expectedOmtCount)
      : Number(latestMatch?.omt_count || 0);
    const payload = {
      match_id: matchId,
      decision: decision,
      notes_run1: (notes_run1 != null && notes_run1 !== "") ? String(notes_run1).trim() : null,
      notes_run2: (notes_run2 != null && notes_run2 !== "") ? String(notes_run2).trim() : null,
      // Optimistic concurrency: tell the server which battle we think we're voting on.
      // If the server has already advanced (another judge triggered OMT first), it will
      // reject with 409 battle_advanced instead of silently stamping our vote onto the new battle.
      expected_battle_no: expectedBattleNo,
      expected_omt_count: expectedOmtCount,
    };

    let scoreResult = null;
    for (;;) {
      try {
        scoreResult = await apiPost(`/judge/score?token=${encodeURIComponent(tok)}`, payload);
        break;
      } catch (err) {
        // Server says our view of the battle is stale (another judge triggered OMT first).
        // Do NOT retry — refresh and surface a clear message; no vote was recorded.
        if (err && err.data && err.data.battle_advanced === true) {
          try { await judgeRefresh(); } catch (_) {}
          try { renderJudge(); } catch (_) {}
          throw new Error("This battle has already advanced. Your vote was not recorded.");
        }
        if (!isInvalidJsonResponseError(err)) throw err;
        console.error("[JUDGE/SCORE] Invalid/non-JSON response while submitting score:", err);
        if (!interactiveRetry) {
          throw new Error(friendlyJudgeNetworkError(err, "Temporary server error while submitting score."));
        }
        const retry = window.confirm(
          "The server returned an invalid response while submitting this score.\n\nRetry now?"
        );
        if (!retry) {
          throw new Error("Score was not submitted due to an invalid server response.");
        }
      }
    }

    toast("Score submitted.", "ok");
    return scoreResult;
  }

  // Called after a successful score submission. Shows the OMT/winner result overlay for 5 seconds
  // (matching the comp-view reveal delay) then refreshes the judge panel to the next battle.
  async function judgeHandlePostScore(matchId, scoreResult) {
    const REVEAL_MS = 5000;
    const isOmt = scoreResult?.omt === true;

    if (isOmt) {
      // Build labeled tally using match data still in judgeState (pre-refresh, so labels are fresh)
      const match = (judgeState.matches || []).find((x) => String(x.id) === String(matchId)) || {};
      const aId = match.vote_a_id ?? match.slot_a_driver_id ?? null;
      const bId = match.vote_b_id ?? match.slot_b_driver_id ?? null;
      const aLabel = match.driver_a_label || "Driver A";
      const bLabel = match.driver_b_label || "Driver B";
      const totalRounds = getJudgeTotalRounds(judgeState.matches || []);
      const matchTitle = match.id ? buildJudgeMatchTitle(match, totalRounds) : "";

      const rawTally = Array.isArray(scoreResult.tally) ? scoreResult.tally : [];
      const labeledTally = rawTally.map((t) => ({
        ...t,
        label: t.winner === "omt" ? "OMT"
          : (String(t.winner) === String(aId) ? aLabel
          : (String(t.winner) === String(bId) ? bLabel : String(t.winner))),
      }));

      // Show the OMT result overlay in the judge panel
      judgeState.resultReveal = { showing: true, type: "omt", tally: labeledTally, matchTitle };
      patchJudgeLive();

      await new Promise((resolve) => setTimeout(resolve, REVEAL_MS));

      // Clear overlay and load the fresh next-battle state
      judgeState.resultReveal = { showing: false, type: null, tally: [], matchTitle: "" };
    }

    await judgeRefresh();

    // Keep Comp View progress/bracket in sync immediately after a score submit.
    if (adminState.page === "comp" && adminState.selectedId) {
      adminCompPollTick({ reason: "manual", force: true }).catch(() => {});
    }

    // Check if round should auto-advance (if admin view is open)
    if (adminState.selectedId) {
      setTimeout(() => checkAndAutoAdvanceRound(adminState.selectedId), 2000);
    }
  }

  // -----------------------------
  // ADMIN pane
  // -----------------------------
  const adminPane = root.querySelector('.gl-ce-pane[data-pane="admin"]');

  adminState = {
    events: [],
    eventsLoadError: "",
    selectedId: "",
    page: "home", // home | create | series | event-access | manage | comp | super-admin | manual-bracket
    bracket: null,
    bracketSig: "",
    lastBracketAt: "",
    regs: null,
    regsLoading: false,
    regsError: "",
    regsPoll: {
      t: null,
      lastSig: "",
    },
    judges: null,
    judgesLoading: false,
    judgesError: "",
    judgeEditByCode: {},
    addDriverModalOpen: false,
    adminPinPanelOpen: false,
    editingRegistration: null,
    editBracket: false,
    bracketDraft: null,
    manualBracket: null,
    manualBracketRound: 1,
    manualRunnerTab: "bracket",
    manualPick: null,
    manualCurrentMatchId: "",
    finalResultsDraft: null,
    edit: { eventId: null },
    isSuperAdmin: false, // Will be set when checking admin status
    bracketActiveRound: null, // Active round for tabbed bracket view
    superAdmin: {
      users: null,
      organizers: null,
      admins: null,
      selectedOrganizer: null,
      organizerEvents: null,
    },
    comp: {
      t: null,
      payload: null,
      view: "live",
      lastAt: "",
      loading: false,
      error: "",
      qualifyingError: "",
      qualifyingLastValid: null,
      watchdogRetried: false,
      judgeNotes: {
        openMatchId: null,
        loadingMatchId: null,
        error: "",
        byMatchId: {},
      },
      winnerScreen: {
        showing: false,
        matchId: null,
        winner: null,
        resultType: null,
        timeoutId: null,
        lastShownMatchId: null,
        lastShownWinner: null,
      },
      qualifyingEditing: {
        registrationId: null,
        drafts: {},
      },
    },
    autoAdvance: {
      checking: false,
      lastCheckedRound: null,
      lastCheckedAt: null,
    },
    maintenance: {
      loading: false,
      lastResult: null,
      error: null,
      confirmText: "",
    },
    series: {
      selectedId: "",
      byId: {},
      pinByEvent: {},
      staffSession: null,
      recap: null,
      recapLoading: false,
      recapError: "",
    },
    fanVotes: {
      prompts: null,
      loading: false,
      error: "",
      detailsById: {},
      votersByPrompt: {},
    },
  };

  let adminCompPollTimer = null;
  let adminCompWatchdogTimer = null;
  let adminCompAbortController = null;
  let adminCompPollInFlight = false;

  function normalizeMatchStatusValue(match) {
    const status = String(match?.status || "").toLowerCase();
    if (status === "decided") return "complete";
    return status || "pending";
  }

  function isJudgeMatchLocked(match) {
    if (!match) return false;
    const status = normalizeMatchStatusValue(match);
    const winnerValue = String(match?.winner || match?.winner_id || match?.override_winner || match?.override_result || "").trim();
    const winnerNorm = winnerValue.toLowerCase();
    if (winnerNorm === "omt" || status === "omt") return false;
    if (winnerValue) return true;
    return status === "complete" || status === "completed" || status === "finalized";
  }

  function isByeMarkedMatch(match) {
    if (!match) return false;
    if (match.is_bye || match.isBye || String(match.role || "").toLowerCase() === "bye") return true;
    const a = String(match.driver_a || "").trim().toUpperCase();
    const b = String(match.driver_b || "").trim().toUpperCase();
    return a === "BYE" || b === "BYE";
  }

  function isCompletedMatchForProgress(match) {
    if (!match) return false;
    const status = normalizeMatchStatusValue(match);
    return !!match.winner || status === "complete" || status === "completed" || status === "finalized";
  }

  function getAdminMatchIndicatorState(match) {
    const status = normalizeMatchStatusValue(match);
    const statusNorm = String(status || "").toLowerCase();
    const winnerValue = String(match?.winner || match?.winner_id || match?.override_winner || match?.override_result || "").trim();
    const winnerNorm = winnerValue.toLowerCase();
    const omtCount = Number(match?.omt_count || 0);

    // Priority: awarded winner (green) first, then OMT (yellow), then default.
    if (winnerValue && winnerNorm !== "omt") return "awarded";
    if (winnerNorm === "omt" || statusNorm === "omt" || omtCount > 0) return "omt";

    // Fallback for feeds that only expose judged/completed status.
    if (statusNorm === "complete" || statusNorm === "completed" || statusNorm === "finalized" || statusNorm === "decided") {
      return "awarded";
    }
    return "";
  }

  function getProgressEligibleMatches(matches) {
    const list = Array.isArray(matches) ? matches : [];
    const hasMarkedByes = list.some((m) => isByeMarkedMatch(m));
    return hasMarkedByes ? list.filter((m) => !isByeMarkedMatch(m)) : list.slice();
  }

  function deriveBracketRounds(bracket) {
    // Keep overview round cards in the same stable order as bracket tabs:
    // numeric rounds ascending, then Third Place, then Final Battle (last).
    const orderRoundsLikeTabs = (rounds) => {
      const list = Array.isArray(rounds) ? rounds : [];
      const numeric = list
        .filter((r) => typeof r?.round === "number" && Number.isFinite(r.round))
        .sort((a, b) => Number(a.round) - Number(b.round));
      const finalRoundNum = numeric.length
        ? Math.max(...numeric.map((r) => Number(r.round)))
        : null;
      const finalRound = (finalRoundNum != null)
        ? (numeric.find((r) => Number(r.round) === finalRoundNum) || null)
        : null;
      const numericWithoutFinal = (finalRoundNum != null)
        ? numeric.filter((r) => Number(r.round) !== finalRoundNum)
        : numeric;
      const thirdRounds = list.filter((r) => r?.round === "third-place");
      const thirdRound = thirdRounds.length
        ? {
            ...thirdRounds[0],
            matchCount: thirdRounds.reduce((sum, r) => sum + Number(r?.matchCount || 0), 0),
          }
        : null;
      return [
        ...numericWithoutFinal,
        ...(thirdRound ? [thirdRound] : []),
        ...(finalRound ? [finalRound] : []),
      ];
    };

    if (!bracket) return [];
    const matches = Array.isArray(bracket.matches) ? bracket.matches : [];
    if (matches.length > 0) {
      const groupedMainRounds = new Map();
      const mainMatches = matches.filter((m) => !isConsolationMatch(m));
      const thirdPlaceMatches = matches.filter((m) => isConsolationMatch(m));
      const maxMainRound = Math.max(0, ...mainMatches.map((m) => Number(m?.round || 0)).filter(Number.isFinite));

      for (const m of mainMatches) {
        const round = Number(m?.round || 0);
        if (!Number.isFinite(round) || round <= 0) continue;
        groupedMainRounds.set(round, (groupedMainRounds.get(round) || 0) + 1);
      }

      const out = Array.from(groupedMainRounds.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([round, matchCount]) => ({
          round,
          roundName: displayRoundName(round, null, maxMainRound || null),
          matchCount,
        }));

      if (thirdPlaceMatches.length > 0) {
        out.push({
          round: "third-place",
          roundName: "Third Place Match",
          matchCount: thirdPlaceMatches.length,
        });
      }

      return orderRoundsLikeTabs(out);
    }

    if (Array.isArray(bracket.rounds) && bracket.rounds.length > 0) {
      const out = [];
      const maxStructuredRound = Math.max(
        0,
        ...bracket.rounds.map((round, idx) => Number(round?.round || idx + 1)).filter(Number.isFinite)
      );

      bracket.rounds
        .slice()
        .sort((a, b) => Number(a?.round || 0) - Number(b?.round || 0))
        .forEach((round, idx) => {
          const roundNumber = Number(round?.round || idx + 1) || (idx + 1);
          const roundMatches = Array.isArray(round?.matches)
            ? round.matches
            : (Array.isArray(round?.matchups) ? round.matchups : []);
          const mainCount = roundMatches.filter((m) => !isConsolationMatch(m)).length;
          const thirdCount = roundMatches.filter((m) => isConsolationMatch(m)).length;

          if (mainCount > 0) {
            const roundName = String(round?.roundName || "").trim() || displayRoundName(roundNumber, null, maxStructuredRound || null);
            out.push({ round: roundNumber, roundName, matchCount: mainCount });
          }
          if (thirdCount > 0) {
            out.push({ round: "third-place", roundName: "Third Place Match", matchCount: thirdCount });
          }
        });

      return orderRoundsLikeTabs(out);
    }
    return [];
  }

  function derivePodiumFromPayload(payload) {
    const podium = payload?.podium || null;
    if (podium && (podium.first || podium.second || podium.third)) {
      return {
        first: podium.first?.label || "Pending",
        second: podium.second?.label || "Pending",
        third: podium.third?.label || "Pending",
      };
    }

    const matches = Array.isArray(payload?.bracket?.matches) ? payload.bracket.matches : [];
    const mainMatches = matches.filter((m) => !isConsolationMatch(m));
    const finalMatch = mainMatches
      .slice()
      .sort((a, b) => (Number(b.round || 0) - Number(a.round || 0)) || (Number(b.match_no || 0) - Number(a.match_no || 0)))[0];
    const thirdPlaceMatch = matches.find((m) => isConsolationMatch(m));
    const first = finalMatch?.winner ? (finalMatch.winner_label || finalMatch.winner || "Pending") : "Pending";
    let second = "Pending";
    if (finalMatch?.winner && finalMatch?.driver_a && finalMatch?.driver_b) {
      const loserId = String(finalMatch.winner) === String(finalMatch.driver_a) ? finalMatch.driver_b : finalMatch.driver_a;
      if (String(loserId || "").toUpperCase() !== "BYE") {
        second = String(loserId) === String(finalMatch.driver_a)
          ? (finalMatch.driver_a_label || finalMatch.driver_a || "Pending")
          : (finalMatch.driver_b_label || finalMatch.driver_b || "Pending");
      }
    }
    const third = thirdPlaceMatch?.winner ? (thirdPlaceMatch.winner_label || thirdPlaceMatch.winner || "Pending") : "Pending";
    return { first, second, third };
  }

  function isMatchScoredForNotes(match) {
    return isCompletedMatchForProgress(match);
  }

  async function adminFetchJudgeNotes(eventId, matchId) {
    const id = String(matchId || "");
    if (!eventId || !id) return [];
    const data = await apiGet(`/events/${encodeURIComponent(eventId)}/matches/${encodeURIComponent(id)}/judge-notes`);
    return Array.isArray(data?.notes) ? data.notes : [];
  }

  async function adminToggleJudgeNotes(matchId) {
    const id = String(matchId || "");
    if (!id) return;
    const matches = Array.isArray(adminState.comp?.payload?.bracket?.matches) ? adminState.comp.payload.bracket.matches : [];
    const targetMatch = matches.find((m) => String(m.id) === id);
    if (!targetMatch) return;
    const state = adminState.comp.judgeNotes;
    if (state.openMatchId && String(state.openMatchId) === id) {
      state.openMatchId = null;
      state.loadingMatchId = null;
      state.error = "";
      renderAdmin();
      return;
    }

    state.openMatchId = id;
    state.error = "";
    const eventId = adminState.selectedId;
    if (state.byMatchId[id]) {
      renderAdmin();
      return;
    }

    state.loadingMatchId = id;
    renderAdmin();
    try {
      const notes = await adminFetchJudgeNotes(eventId, id);
      state.byMatchId[id] = notes;
      state.error = "";
    } catch (err) {
      state.error = err?.message || "Failed to load notes.";
      state.byMatchId[id] = [];
    } finally {
      if (state.loadingMatchId === id) state.loadingMatchId = null;
      renderAdmin();
    }
  }

  function renderJudgeNotesPanel(matchId) {
    const state = adminState.comp.judgeNotes;
    const id = String(matchId || "");
    const isLoading = String(state.loadingMatchId || "") === id;
    const notes = Array.isArray(state.byMatchId[id]) ? state.byMatchId[id] : [];
    if (isLoading) {
      return `<div class="gl-ce-muted" style="padding: 8px 10px;">Loading judge notes...</div>`;
    }
    if (state.error) {
      return `<div class="gl-ce-warn" style="padding: 8px 10px;">${esc(state.error)}</div>`;
    }
    if (!notes.length) {
      return `<div class="gl-ce-muted" style="padding: 8px 10px;">No notes submitted.</div>`;
    }
    return `
      <div style="padding: 8px 10px; display: grid; gap: 8px;">
        ${notes.map((n, idx) => `
          <div style="border: 1px solid rgba(7, 98, 81, 0.2); border-radius: 8px; padding: 8px;">
            <div style="font-weight: 700; margin-bottom: 4px;">${esc(n.judge_display_name || `Judge ${idx + 1}`)}</div>
            <div style="white-space: pre-wrap; line-height: 1.35;">${esc(n.notes || "")}</div>
          </div>
        `).join("")}
      </div>
    `;
  }

  function renderCompMatchControls(mount, payload) {
    if (!mount) return;
    const matches = Array.isArray(payload?.bracket?.matches) ? payload.bracket.matches : [];
    const curId = String(payload?.current_match_id || "");
    const openMatchId = String(adminState.comp.judgeNotes.openMatchId || "");
    const items = matches
      .slice()
      .sort(compareMatchesForJudgingDisplay)
      .map((m) => {
        const hasTwo = matchHasTwoDrivers(m);
        const isCur = curId && String(m.id) === curId;
        const isNotesOpen = openMatchId && String(m.id) === openMatchId;
        const indicatorState = getAdminMatchIndicatorState(m);
        const indicatorClass = indicatorState ? ` is-${indicatorState}` : "";
        const indicatorBadge = indicatorState === "awarded"
          ? `<span class="gl-ce-pill gl-ce-pill-match-indicator gl-ce-pill-match-indicator-awarded">Judged</span>`
          : indicatorState === "omt"
            ? `<span class="gl-ce-pill gl-ce-pill-match-indicator gl-ce-pill-match-indicator-omt">OMT</span>`
            : "";
        const title = isConsolationMatch(m) ? `Third Place Match` : `R${Number(m.round || 0)} M${displayMatchNo(m.match_no)}`;
        return `
          <div class="gl-ce-row gl-ce-row-between gl-ce-minirow gl-ce-comp-match-control-row${indicatorClass}" style="align-items: flex-start;">
            <div style="flex: 1; min-width: 0;">
              <div class="gl-ce-row gl-ce-row-wrap" style="gap: 8px; align-items: center;">
                <b>${esc(title)}</b> — <span class="gl-ce-muted">${esc(m.driver_a_label || "TBD")} vs ${esc(m.driver_b_label || "TBD")}</span>
                ${indicatorBadge}
              </div>
              ${isNotesOpen ? `<div style="margin-top: 8px; border: 1px solid rgba(7, 98, 81, 0.2); border-radius: 8px;">${renderJudgeNotesPanel(m.id)}</div>` : ""}
            </div>
            <div class="gl-ce-row gl-ce-row-wrap" style="margin-left: 10px; align-items: center;">
              <button class="gl-ce-btn gl-ce-btn-sm ${isCur ? "" : "gl-ce-btn-ghost"} ${isCur ? "gl-ce-btn-green" : ""}"
                data-action="admin-set-current" data-matchid="${esc(m.id)}" ${hasTwo ? "" : "disabled"} title="${hasTwo ? "Make this the match shown to judges and spectators" : "A current battle needs two assigned drivers"}" type="button">
                ${isCur ? "Current Battle" : "Set Current"}
              </button>
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost"
                data-action="admin-override-open" data-matchid="${esc(m.id)}" title="Manually award, change, or clear this match result" type="button">Override</button>
          <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost"
            data-action="admin-judge-notes-toggle"
            data-matchid="${esc(m.id)}"
            title="Show notes from all judges"
            type="button">${isNotesOpen ? "Hide Notes" : "Judge Notes ▼"}</button>
            </div>
          </div>
        `;
      })
      .join("");
    mount.innerHTML = items || `<div class="gl-ce-muted">No matches.</div>`;
  }

  function setAdminPage(page) {
    adminState.page = page;
    renderAdmin();
  }

  function fieldSetError(el, on) {
    if (!el) return;
    el.classList.toggle("gl-ce-input-error", !!on);
    el.setAttribute("aria-invalid", on ? "true" : "false");
    
    // Add/remove inline "Required" message
    const parent = el.closest("div");
    if (!parent) return;
    
    let errorMsg = parent.querySelector(".gl-ce-field-error");
    if (on && !errorMsg) {
      errorMsg = document.createElement("div");
      errorMsg.className = "gl-ce-field-error";
      errorMsg.textContent = "Required";
      parent.appendChild(errorMsg);
    } else if (!on && errorMsg) {
      errorMsg.remove();
    }
  }

  // -------- Live Comp View helpers ----------
  const REQUIRED_LIVE_JUDGE_VOTES = 3;
  const WINNER_REVEAL_DELAY_MS = 5000; // Keep winner visible ~5s before next battle.

  /**
   * Shared HTML for live judge rows (embedded comp view + judging popout).
   * Caller gates inclusion with showJudgeRows (assignments exist); pending slots show Waiting.
   */
  function renderLiveJudgeRowsHtml(judges, cur, aLabel, bLabel) {
    const driverA = cur?.driver_a;
    const driverB = cur?.driver_b;
    return (judges || []).map((j) => {
      const rawDec = j?.decision;
      const hasDecision = rawDec != null && String(rawDec).trim() !== "";
      const selectedDriverA = hasDecision && rawDec === driverA;
      const selectedDriverB = hasDecision && rawDec === driverB;
      const selectedOMT = hasDecision && rawDec === "omt";
      const unknownDecision = hasDecision && !selectedDriverA && !selectedDriverB && !selectedOMT;
      const rowClass =
        selectedDriverA || selectedDriverB ? "winner-row" : selectedOMT ? "omt-row" : "waiting-row";
      const sideBadgeClass = selectedDriverA || selectedDriverB ? "winner" : selectedOMT ? "omt" : "pending";
      const judgeSuffix = j?.name ? ` (${esc(j.name)})` : "";
      let centerExtra = "";
      if (!hasDecision) {
        centerExtra = `<div class="gl-ce-comp-judge-waiting">Waiting</div>`;
      } else if (unknownDecision) {
        centerExtra = `<div class="gl-ce-comp-judge-decision pending">${esc(j.decision_label || String(rawDec))}</div>`;
      }
      return `
                <div class="gl-ce-comp-judge-row ${rowClass}">
                  <div class="gl-ce-comp-judge-decision-col left">
                    ${hasDecision && (selectedDriverA || selectedOMT) ? `
                      <div class="gl-ce-comp-judge-decision ${sideBadgeClass}">${selectedOMT ? "OMT" : esc(aLabel)}</div>
                    ` : '<div style="height: 32px;"></div>'}
                  </div>
                  <div class="gl-ce-comp-judge-name-col">
                    <div class="gl-ce-comp-judge-name">JUDGE ${j.slot}${judgeSuffix}</div>
                    ${centerExtra}
                  </div>
                  <div class="gl-ce-comp-judge-decision-col right">
                    ${hasDecision && (selectedDriverB || selectedOMT) ? `
                      <div class="gl-ce-comp-judge-decision ${sideBadgeClass}">${selectedOMT ? "OMT" : esc(bLabel)}</div>
                    ` : '<div style="height: 32px;"></div>'}
                  </div>
                </div>
              `;
    }).join("");
  }

  function fitLiveJudgingCardToViewport(mount) {
    if (!mount || !mount.isConnected) return;
    const container = mount.closest(".gl-ce-comp-judging-container") || mount;
    const scoreCard = mount.querySelector(".gl-ce-comp-score");
    if (!container || !scoreCard) return;

    // Reset before measuring natural dimensions.
    scoreCard.style.transform = "none";
    scoreCard.style.width = "100%";
    scoreCard.style.maxWidth = "100%";
    container.style.height = "auto";

    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const rect = container.getBoundingClientRect();
    const availableHeight = Math.max(220, viewportH - rect.top - 16);
    const naturalWidth = scoreCard.scrollWidth || scoreCard.getBoundingClientRect().width || 1;
    const naturalHeight = scoreCard.scrollHeight || scoreCard.getBoundingClientRect().height || 1;
    const widthScale = (container.clientWidth || rect.width || 1) / naturalWidth;
    const heightScale = availableHeight / naturalHeight;
    const scale = Math.max(0.7, Math.min(1, widthScale, heightScale));

    container.style.overflowX = "hidden";
    container.style.overflowY = "hidden";

    if (scale < 0.999) {
      scoreCard.style.width = `${(100 / scale).toFixed(4)}%`;
      scoreCard.style.maxWidth = "none";
      scoreCard.style.transformOrigin = "top left";
      scoreCard.style.transform = `scale(${scale})`;
      container.style.height = `${Math.ceil(naturalHeight * scale)}px`;
    }
  }

  function formatQualifyingScoreValue(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : "-";
  }

  function qualifyingScoreBySlot(run, slot) {
    const scores = Array.isArray(run?.scores) ? run.scores : [];
    return scores.find((score) => Number(score?.slot ?? score?.judge_slot) === slot)?.score;
  }

  function qualifyingDisplayConfig(payload) {
    const qualifying = payload?.qualifying || payload || {};
    const isLas = String(qualifying?.mode || "").toLowerCase() === "las";
    const roles = qualifying?.judge_roles && typeof qualifying.judge_roles === "object"
      ? qualifying.judge_roles
      : { 1: "line", 2: "angle", 3: "style" };
    const roleForSlot = (slot) => isLas
      ? String(roles[slot] || roles[String(slot)] || `judge ${slot}`).replace(/\b\w/g, (c) => c.toUpperCase())
      : `Judge ${slot}`;
    const maxForSlot = (slot) => {
      if (!isLas) return 100;
      const role = String(roles[slot] || roles[String(slot)] || "").toLowerCase();
      return role === "line" ? 40 : 30;
    };
    return {
      isLas,
      roleForSlot,
      maxForSlot,
      resultLabel: isLas ? "Total" : "Average",
      completionCopy: isLas
        ? "Once Line, Angle, and Style submit a score, the run auto-locks, the total is computed, and rankings refresh automatically."
        : "Once all 3 judges submit a score, the run auto-locks, the average is computed, and rankings refresh automatically.",
    };
  }

  function qualifyingPositionLabel(data) {
    const run = data?.run || null;
    const scores = Array.isArray(run?.scores) ? run.scores : [];
    const locked = normalizeStatus(run?.status) === "locked";
    const avgSet = Number.isFinite(Number(run?.avg_score));
    const rank = Number(run?.rank);
    const total = Number(data?.total_drivers);
    if (!locked || scores.length < REQUIRED_LIVE_JUDGE_VOTES || !avgSet || !Number.isFinite(rank) || rank <= 0) return "-";
    return Number.isFinite(total) && total > 0 ? `P${rank} / ${total}` : `P${rank}`;
  }

  function renderQualifyingPopout(mount, data) {
    if (!mount) return;
    const run = data?.run || null;
    if (!run) {
      mount.innerHTML = `
        <div class="gl-ce-qual-popout">
          <div class="gl-ce-muted">No qualifying run is currently available.</div>
        </div>
      `;
      return;
    }

    const driverNumberLabel = run.driver_number != null && String(run.driver_number).trim() !== ""
      ? String(run.driver_number)
      : "—";
    const config = qualifyingDisplayConfig(data);

    mount.innerHTML = `
      <div class="gl-ce-qual-popout">
        <div class="gl-ce-qual-row"><span>Driver:</span><strong>${esc(run.driver_name || "Driver")}</strong></div>
        <div class="gl-ce-qual-row"><span>#:</span><strong>${esc(driverNumberLabel)}</strong></div>
        <div class="gl-ce-qual-row"><span>Run:</span><strong>${esc(run.run_no || "-")}</strong></div>
        <div class="gl-ce-qual-scores">
          <div class="gl-ce-qual-row"><span>${esc(config.roleForSlot(1))}:</span><strong>${esc(formatQualifyingScoreValue(qualifyingScoreBySlot(run, 1)))}</strong></div>
          <div class="gl-ce-qual-row"><span>${esc(config.roleForSlot(2))}:</span><strong>${esc(formatQualifyingScoreValue(qualifyingScoreBySlot(run, 2)))}</strong></div>
          <div class="gl-ce-qual-row"><span>${esc(config.roleForSlot(3))}:</span><strong>${esc(formatQualifyingScoreValue(qualifyingScoreBySlot(run, 3)))}</strong></div>
        </div>
        <div class="gl-ce-qual-row gl-ce-qual-total"><span>${esc(config.resultLabel)}:</span><strong>${esc(formatQualifyingScoreValue(run.avg_score))}</strong></div>
        <div class="gl-ce-qual-row gl-ce-qual-position"><span>Position:</span><strong>${esc(qualifyingPositionLabel(data))}</strong></div>
      </div>
    `;
  }

  function qualifyingLeaderboardRows(payload) {
    if (Array.isArray(payload?.qualifying?.leaderboard)) return payload.qualifying.leaderboard;
    if (Array.isArray(payload?.qualifying_leaderboard)) return payload.qualifying_leaderboard;
    if (Array.isArray(payload?.leaderboard)) return payload.leaderboard;
    return [];
  }

  const qualifyingRankSnapshots = new WeakMap();

  function qualifyingLeaderboardEventKey(payload) {
    return String(
      payload?.qualifying?.event_id ||
      payload?.qualifying?.event?.id ||
      payload?.event_id ||
      payload?.event?.id ||
      ""
    );
  }

  function qualifyingLeaderboardRowKey(row) {
    return String(
      row?.registration_id ||
      row?.driver_id ||
      row?.user_id ||
      row?.email ||
      row?.driver_name ||
      ""
    );
  }

  function qualifyingRankDeltaIndicator(row, previousRanks) {
    const rank = Number(row?.rank);
    const rowKey = qualifyingLeaderboardRowKey(row);
    if (!rowKey || !Number.isFinite(rank) || rank <= 0) return "";

    const previousRank = previousRanks.get(rowKey);
    if (!Number.isFinite(previousRank) || previousRank === rank) return "";

    const movedUp = rank < previousRank;
    const label = movedUp ? "Position gained" : "Position lost";
    const symbol = movedUp ? "↑" : "↓";
    const direction = movedUp ? "up" : "down";
    return `<span class="gl-ce-qual-rank-delta gl-ce-qual-rank-delta-${direction}" aria-label="${label}">${symbol}</span>`;
  }

  function captureQualifyingRankSnapshot(rows) {
    const snapshot = new Map();
    rows.forEach((row) => {
      const rowKey = qualifyingLeaderboardRowKey(row);
      const rank = Number(row?.rank);
      if (rowKey && Number.isFinite(rank) && rank > 0) {
        snapshot.set(rowKey, rank);
      }
    });
    return snapshot;
  }

  function qualifyingRunScoreLabel(row, runNo) {
    const run = (Array.isArray(row?.runs) ? row.runs : [])
      .find((item) => Number(item?.run_no) === Number(runNo));
    if (!run) return "-";
    if (normalizeStatus(run.status) === "dnf") return "DNF";
    return formatQualifyingScoreValue(run.avg_score);
  }

  function qualifyingPartialRunSignature(row) {
    return (Array.isArray(row?.runs) ? row.runs : [])
      .slice()
      .sort((a, b) => Number(a?.run_no || 0) - Number(b?.run_no || 0))
      .map((run) => {
        const scores = (Array.isArray(run?.scores) ? run.scores : [])
          .slice()
          .sort((a, b) => Number(a?.judge_slot || 0) - Number(b?.judge_slot || 0))
          .map((score) => `${Number(score?.judge_slot || 0)}:${score?.score ?? ""}:${score?.updated_at || ""}`)
          .join(",");
        const notes = (Array.isArray(run?.judge_notes) ? run.judge_notes : [])
          .slice()
          .sort((a, b) => Number(a?.judge_slot || 0) - Number(b?.judge_slot || 0))
          .map((note) => `${Number(note?.judge_slot || 0)}:${note?.note || ""}:${note?.updated_at || ""}`)
          .join(",");
        return `${run?.id || ""}:${run?.status || ""}:${run?.avg_score ?? ""}:${scores}:${notes}`;
      })
      .join(";");
  }

  function qualifyingRankLabel(row) {
    const rank = Number(row?.rank);
    return Number.isFinite(rank) && rank > 0 ? `P${rank}` : "-";
  }

  async function downloadQualifyingResults(eventId) {
    const id = String(eventId || "").trim();
    if (!id) throw new Error("Select an event first.");
    const path = `/admin/events/${encodeURIComponent(id)}/qualifying/export.xlsx`;
    const candidates = buildApiGetCandidates(path);
    let lastError = null;
    for (const url of candidates) {
      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.error || data?.message || `Export failed (${response.status}).`);
        }
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = "";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Unable to export qualifying results.");
  }

  function renderAdminCompQualifyingTable(mount, payload, options = {}) {
    if (!mount) return;
    // Hard guard: callers from the public/driver pane MUST pass readOnly:true so
    // admin-only override buttons (set-current, toggle-edit, save-scores, dnf)
    // are never rendered for non-admin users. If a caller forgets, we still fall
    // back to read-only when the user is not an admin so a driver can never see
    // qualifying admin controls. See: "Restrict qualifying admin controls from
    // drivers" — comp-events viewer access rules.
    const readOnly = options.readOnly === true || !hasAdminPortalAccess();
    const rows = qualifyingLeaderboardRows(payload);
    const qualifyingConfig = qualifyingDisplayConfig(payload?.qualifying || payload);
    const eventKey = qualifyingLeaderboardEventKey(payload);
    const previousSnapshot = qualifyingRankSnapshots.get(mount);
    const previousRanks = previousSnapshot && (!eventKey || previousSnapshot.eventKey === eventKey)
      ? previousSnapshot.ranks
      : new Map();
    if (!rows.length) {
      mount.innerHTML = `<div class="gl-ce-muted">No qualifying leaderboard rows yet.</div>`;
      qualifyingRankSnapshots.set(mount, { eventKey, ranks: new Map() });
      return;
    }

    const editingRegId = String(adminState.comp.qualifyingEditing?.registrationId || "");
    // Leaderboard responses expose ties at the top level; retain the nested
    // fallback for older cached payloads.
    const tieIds = new Set((payload?.tie_groups || payload?.qualifying?.tie_groups || [])
      .flatMap((group) => group.ids || group.registration_ids || []).map(String));
    const currentRunId = String(
      payload?.qualifying?.current_run_id ||
      payload?.current_run_id ||
      ""
    );

    mount.innerHTML = `
      <div class="gl-ce-tablewrap gl-ce-qualifying-leaderboard-wrap">
        <table class="gl-ce-table gl-ce-qualifying-leaderboard-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Driver</th>
              <th>#</th>
              <th>Run 1</th>
              <th>Run 2</th>
              <th>Best</th>
              ${readOnly ? "" : `<th style="text-align:right;">Override</th>`}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => {
              const regId = String(row.registration_id || "");
              const isEditing = !readOnly && editingRegId && editingRegId === regId;
              const driverLabel = row.driver_name || [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email || regId || "Driver";
              const driverNumberLabel = row.driver_number != null && String(row.driver_number).trim() !== "" ? String(row.driver_number) : "—";
              const runs = Array.isArray(row.runs) ? row.runs : [];
              const nextPendingRun = runs
                .filter((r) => String(r?.status || "pending").toLowerCase() === "pending")
                .sort((a, b) => Number(a?.run_no || 0) - Number(b?.run_no || 0))[0] || null;
              const isCurrentRow = !!(currentRunId && runs.some((r) => String(r?.id || "") === currentRunId));
              const isDnf = Boolean(row.dnf) || runs.some((run) => normalizeStatus(run?.status) === "dnf");
              const isDnq = row.qualifying_eligible === false && runs.some((run) => ["locked", "dnf"].includes(normalizeStatus(run?.status)));
              const lockedDoubleZero = runs.filter((run) => normalizeStatus(run?.status) === "locked").length >= 2
                && runs.filter((run) => normalizeStatus(run?.status) === "locked").every((run) => Number(run?.avg_score) === 0);
              const isTied = tieIds.has(regId);
              const visibleJudgeNotes = readOnly ? [] : runs.flatMap((run) =>
                (Array.isArray(run?.judge_notes) ? run.judge_notes : [])
                  .filter((entry) => String(entry?.note || "").trim())
                  .map((entry) => ({
                    runNo: Number(run?.run_no || 0),
                    role: qualifyingConfig.roleForSlot(entry.judge_slot),
                    note: String(entry.note || "").trim(),
                  }))
              );
              const currentRunNo = isCurrentRow
                ? Number((runs.find((r) => String(r?.id || "") === currentRunId) || {}).run_no || 0)
                : 0;
              const setCurrentDisabled = !nextPendingRun;
              const setCurrentTitle = setCurrentDisabled
                ? "All runs locked or no pending run for this driver."
                : `Set Run ${nextPendingRun.run_no} for ${driverLabel} as current. Judges will score this run.`;
              const setCurrentLabel = isCurrentRow && nextPendingRun
                ? `Currently Run ${currentRunNo || nextPendingRun.run_no}`
                : nextPendingRun
                  ? `Set Run ${nextPendingRun.run_no}`
                  : "Set Current";
              const overrideCell = readOnly ? "" : `
                <td style="text-align:right; white-space:nowrap;">
                  <button class="gl-ce-btn gl-ce-btn-sm ${isCurrentRow ? "gl-ce-btn-current-active" : ""}"
                          data-action="admin-qualifying-set-current"
                          data-reg-id="${esc(regId)}"
                          data-run-id="${esc(nextPendingRun ? nextPendingRun.id : "")}"
                          ${setCurrentDisabled ? "disabled" : ""}
                          title="${esc(setCurrentTitle)}"
                          type="button"
                          style="margin-right:6px;${isCurrentRow ? "background:#ffc107;color:#222;" : ""}">${esc(setCurrentLabel)}</button>
                  <button class="gl-ce-btn gl-ce-btn-sm ${isEditing ? "gl-ce-btn-ghost" : ""}"
                          data-action="admin-qualifying-toggle-edit"
                          data-reg-id="${esc(regId)}"
                          type="button">
                    ${isEditing ? "Close" : "Edit"}
                  </button>
                  <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost"
                          data-action="admin-qualifying-move-seed"
                          data-reg-id="${esc(regId)}"
                          data-direction="up"
                          type="button" title="Move this driver up one qualifying seed">Seed ↑</button>
                  <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost"
                          data-action="admin-qualifying-move-seed"
                          data-reg-id="${esc(regId)}"
                          data-direction="down"
                          type="button" title="Move this driver down one qualifying seed">Seed ↓</button>
                </td>`;
              const colspan = readOnly ? 6 : 7;
              return `
              <tr data-qual-row="${esc(regId)}" class="${[isCurrentRow ? "gl-ce-qual-row-current" : "", (isDnf || isDnq) ? "gl-ce-qual-row-alert" : "", lockedDoubleZero ? "gl-ce-qual-row-double-zero" : "", isTied ? "gl-ce-qual-row-tie" : ""].filter(Boolean).join(" ")}">
                <td><strong class="gl-ce-qual-rank">${esc(qualifyingRankLabel(row))}${qualifyingRankDeltaIndicator(row, previousRanks)}</strong></td>
                <td>${esc(driverLabel)}${isCurrentRow ? ` <span class="gl-ce-pill gl-ce-pill-qualifying" style="margin-left:6px;">CURRENT · Run ${esc(currentRunNo || nextPendingRun?.run_no || "")}</span>` : ""}</td>
                <td>${esc(driverNumberLabel)}</td>
                <td>${esc(qualifyingRunScoreLabel(row, 1))}</td>
                <td>${esc(qualifyingRunScoreLabel(row, 2))}</td>
                <td><strong>${esc(formatQualifyingScoreValue(row.best_score))}</strong></td>
                ${overrideCell}
              </tr>
              ${visibleJudgeNotes.length ? `
                <tr class="gl-ce-qual-notes-row" data-qual-notes-for="${esc(regId)}">
                  <td colspan="${colspan}">
                    <div class="gl-ce-qual-notes" style="font-size:12px;">
                      <strong>Judge notes</strong>
                      ${visibleJudgeNotes.map((entry) =>
                        `<div><span class="gl-ce-muted">Run ${esc(entry.runNo)} · ${esc(entry.role)}:</span> ${esc(entry.note)}</div>`
                      ).join("")}
                    </div>
                  </td>
                </tr>
              ` : ""}
              ${isEditing ? `
                <tr class="gl-ce-qual-edit-row" data-qual-edit-for="${esc(regId)}">
                  <td colspan="${colspan}">
                    ${renderAdminQualifyingRunEditor(row)}
                  </td>
                </tr>
              ` : ""}
            `;
            }).join("")}
          </tbody>
        </table>
      </div>
    `;
    qualifyingRankSnapshots.set(mount, { eventKey, ranks: captureQualifyingRankSnapshot(rows) });

    if (readOnly) return;

    const scoreInputs = mount.querySelectorAll('input[data-role="qual-score-input"]');
    scoreInputs.forEach((input) => {
      input.addEventListener("input", (e) => {
        const runId = String(e.target.dataset.runId || "");
        const slot = Number(e.target.dataset.slot || 0);
        if (!runId || !slot) return;
        if (!adminState.comp.qualifyingEditing) {
          adminState.comp.qualifyingEditing = { registrationId: null, drafts: {} };
        }
        if (!adminState.comp.qualifyingEditing.drafts) {
          adminState.comp.qualifyingEditing.drafts = {};
        }
        adminState.comp.qualifyingEditing.drafts[adminQualifyingDraftKey(runId, slot)] = e.target.value;
      });
    });
  }

  function adminQualifyingDraftKey(runId, slot) {
    return `${runId}::${slot}`;
  }

  function getAdminQualifyingDraftScore(runId, slot, fallback) {
    const drafts = adminState.comp.qualifyingEditing?.drafts || {};
    const key = adminQualifyingDraftKey(runId, slot);
    if (Object.prototype.hasOwnProperty.call(drafts, key)) return drafts[key];
    return fallback;
  }

  function renderAdminQualifyingRunEditor(row) {
    const regId = String(row.registration_id || "");
    const driverLabel = esc(row.driver_name || [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email || regId || "Driver");
    const config = qualifyingDisplayConfig(adminState.comp?.payload?.qualifying);
    const runs = Array.isArray(row.runs) ? row.runs : [];
    const runByNo = new Map();
    for (const r of runs) runByNo.set(Number(r.run_no || 0), r);
    const blocks = [1, 2].map((runNo) => {
      const run = runByNo.get(runNo);
      if (!run) {
        return `<div class="gl-ce-qual-edit-block">
          <div class="gl-ce-qual-edit-head">
            <strong>Run ${runNo}</strong>
            <span class="gl-ce-muted">Not initialized</span>
          </div>
        </div>`;
      }
      const status = String(run.status || "pending").toLowerCase();
      const isLocked = status === "locked";
      const isDnf = status === "dnf";
      const scoreInputs = [1, 2, 3].map((slot) => {
        const existing = (run.scores || []).find((s) => Number(s.judge_slot) === slot);
        const draft = getAdminQualifyingDraftScore(run.id, slot, existing ? String(existing.score) : "");
        return `
          <label class="gl-ce-qual-edit-slot">
            <span>${esc(config.roleForSlot(slot))}</span>
            <input class="gl-ce-input gl-ce-input-sm"
                   type="number"
                   inputmode="decimal"
                   min="0" max="${esc(config.maxForSlot(slot))}" step="0.01"
                   data-role="qual-score-input"
                   data-run-id="${esc(run.id)}"
                   data-slot="${esc(slot)}"
                   value="${esc(draft)}"
                   placeholder="—" />
          </label>
        `;
      }).join("");
      const privateNotes = (Array.isArray(run.judge_notes) ? run.judge_notes : [])
        .filter((entry) => String(entry?.note || "").trim())
        .map((entry) => `<div><b>${esc(config.roleForSlot(entry.judge_slot))}:</b> ${esc(entry.note)}</div>`)
        .join("");
      const statusBadge = isLocked
        ? `<span class="gl-ce-pill gl-ce-pill-live">LOCKED</span>`
        : isDnf
        ? `<span class="gl-ce-pill gl-ce-pill-closed">DNF</span>`
        : `<span class="gl-ce-pill gl-ce-pill-open">PENDING</span>`;
      const resultLabel = run.avg_score != null ? formatQualifyingScoreValue(run.avg_score) : "—";

      return `
        <div class="gl-ce-qual-edit-block">
          <div class="gl-ce-qual-edit-head">
            <strong>Run ${runNo}</strong>
            ${statusBadge}
            <span class="gl-ce-muted">${esc(config.resultLabel)}: ${esc(resultLabel)}</span>
          </div>
          <div class="gl-ce-qual-edit-slots">${scoreInputs}</div>
          ${privateNotes ? `<div class="gl-ce-muted" style="margin-top:8px; font-size:12px;"><b>Private judge notes</b>${privateNotes}</div>` : ""}
          <div class="gl-ce-row gl-ce-row-wrap" style="gap:6px; margin-top:6px;">
            <button class="gl-ce-btn gl-ce-btn-sm"
                    data-action="admin-qualifying-save-scores"
                    data-run-id="${esc(run.id)}"
                    type="button">Save Scores</button>
            ${!isDnf ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger"
                    data-action="admin-qualifying-dnf"
                    data-run-id="${esc(run.id)}"
                    type="button">Mark DNF</button>` : ""}
          </div>
          ${!isLocked && !isDnf ? `<div class="gl-ce-muted" style="margin-top:6px; font-size:12px;">Auto-locks once all 3 judges submit a score. Use Save Scores to override.</div>` : ""}
        </div>
      `;
    }).join("");

    return `
      <div class="gl-ce-qual-edit-card">
        <div class="gl-ce-qual-edit-title">Score override · ${driverLabel}</div>
        <div class="gl-ce-qual-edit-grid">${blocks}</div>
      </div>
    `;
  }

  async function adminQualifyingSaveScores(runId) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to edit qualifying scores.", "err"); return; }
    const eventId = String(adminState.selectedId || "");
    if (!eventId) throw new Error("No event selected.");
    if (!runId) throw new Error("No run id.");
    const inputs = Array.from(adminPane.querySelectorAll(`input[data-role="qual-score-input"][data-run-id="${runId}"]`));
    const updates = [];
    for (const input of inputs) {
      const slot = Number(input.dataset.slot);
      const raw = String(input.value || "").trim();
      if (raw === "") continue;
      const score = Number(raw);
      const config = qualifyingDisplayConfig(adminState.comp?.payload?.qualifying);
      const maximum = config.maxForSlot(slot);
      if (!Number.isFinite(score) || score < 0 || score > maximum) {
        throw new Error(`${config.roleForSlot(slot)}: score must be a number between 0 and ${maximum}.`);
      }
      updates.push({ judge_slot: slot, score });
    }
    if (!updates.length) {
      toast("No score changes to save.", "info");
      return;
    }
    for (const update of updates) {
      await apiPost(
        `/admin/events/${encodeURIComponent(eventId)}/qualifying/runs/${encodeURIComponent(runId)}/scores/edit`,
        update
      );
    }
    adminState.comp.qualifyingEditing.drafts = {};
    toast(`Saved ${updates.length} score${updates.length === 1 ? "" : "s"}.`, "ok");
    adminState.comp._lastSig = undefined;
    await adminCompPollTick({ reason: "qualifying-save" });
  }

  async function adminQualifyingLockRun(runId) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to lock qualifying runs.", "err"); return; }
    const eventId = String(adminState.selectedId || "");
    if (!eventId) throw new Error("No event selected.");
    if (!runId) throw new Error("No run id.");
    await apiPost(
      `/admin/events/${encodeURIComponent(eventId)}/qualifying/runs/${encodeURIComponent(runId)}/lock`,
      {}
    );
    toast("Run locked.", "ok");
    adminState.comp._lastSig = undefined;
    await adminCompPollTick({ reason: "qualifying-lock" });
  }

  async function adminQualifyingDnfRun(runId) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to mark qualifying DNF.", "err"); return; }
    const eventId = String(adminState.selectedId || "");
    if (!eventId) throw new Error("No event selected.");
    if (!runId) throw new Error("No run id.");
    const ok = window.confirm("Mark this run as DNF? Score will be cleared and ranks recomputed.");
    if (!ok) return;
    await apiPost(
      `/admin/events/${encodeURIComponent(eventId)}/qualifying/runs/${encodeURIComponent(runId)}/dnf`,
      {}
    );
    toast("Run marked DNF.", "ok");
    adminState.comp._lastSig = undefined;
    await adminCompPollTick({ reason: "qualifying-dnf" });
  }

  async function adminQualifyingSetCurrentRun(runId) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to set the current qualifying run.", "err"); return; }
    const eventId = String(adminState.selectedId || "");
    if (!eventId) throw new Error("No event selected.");
    if (!runId) throw new Error("All runs are locked or no pending run is available for this driver.");
    await apiPost(
      `/admin/events/${encodeURIComponent(eventId)}/qualifying/runs/${encodeURIComponent(runId)}/set-current`,
      {}
    );
    toast("Driver set as current run. Judges will score this run.", "ok");
    adminState.comp._lastSig = undefined;
    await adminCompPollTick({ reason: "qualifying-set-current" });
  }

  function renderLiveJudgingOnly(mount, payload, options = {}) {
    if (!mount) {
      console.warn("renderLiveJudgingOnly: mount element not found");
      return;
    }
    
    // Ensure smooth transitions
    if (!mount.style.transition) {
      mount.style.transition = 'opacity 0.3s ease';
    }
    
    if (!payload) {
      mount.innerHTML = `<div class="gl-ce-muted" style="padding:20px; text-align:center;">No data available. Please ensure the event is LIVE and a current battle is set.</div>`;
      return;
    }
    
    const ev = payload.event || null;
    const cur = payload.current_match || null;
    const judges = payload.current_judges || [];
    const st = normalizeStatus(ev?.status);
    const isEventComplete = st === "complete" || st === "completed";
    const showFinalStandingsOnComplete = options?.showFinalStandingsOnComplete === true;
    const selectedEvent = adminState.selectedId
      ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
      : null;
    const resolvedEventName = eventDisplayName(ev, "") || eventDisplayName(selectedEvent, "") || "Event";

    if (showFinalStandingsOnComplete && isEventComplete) {
      const finalPodium = payload?.podium || null;
      mount.innerHTML = `
        <div class="gl-ce-card gl-ce-card-soft" style="padding: 16px;">
          <div class="gl-ce-row gl-ce-row-between" style="margin-bottom: 10px; align-items: center;">
            <h3 class="gl-ce-h2" style="margin: 0;">${esc(resolvedEventName)}</h3>
            <span class="gl-ce-muted" style="font-size: 12px;">Event Complete</span>
          </div>
          <div id="glCePopoutFinalStandings"></div>
        </div>
      `;
      const podiumMount = mount.querySelector("#glCePopoutFinalStandings");
      if (podiumMount) {
        renderPodium(podiumMount, finalPodium);
      }
      return;
    }

    if (!cur) {
      mount.innerHTML = `
        <div style="padding:20px; text-align:center; background:rgba(255,255,255,0.05); border-radius:8px;">
          <div class="gl-ce-muted" style="margin-bottom:10px;">No current battle selected.</div>
          <div style="font-size:12px; opacity:0.7;">Go to Comp View and click "Set Current" on a match to view live judging.</div>
        </div>
      `;
      return;
    }

    const aLabel = esc(cur.driver_a_label || "Driver A");
    const bLabel = esc(cur.driver_b_label || "Driver B");
    const isIrl = isIrlEventType(ev);
    const liveMaxRoundFromMatches = Array.isArray(payload?.bracket?.matches)
      ? Math.max(0, ...payload.bracket.matches.filter((m) => !isConsolationMatch(m)).map(m => Number(m?.round || 0)).filter(Number.isFinite))
      : 0;
    const liveFinalRound = liveMaxRoundFromMatches > 0
      ? liveMaxRoundFromMatches
      : Number(payload?.bracket?.final_round || 0);
    const liveMatchTitle = buildJudgeMatchTitle(cur, liveFinalRound || null);
    const aMetaHtml = renderJudgeDriverMetaHtml("driver_a", cur, isIrl, "gl-ce-comp-driver-meta");
    const bMetaHtml = renderJudgeDriverMetaHtml("driver_b", cur, isIrl, "gl-ce-comp-driver-meta");
    const votedCount = judges.filter((j) => j?.decision != null && String(j.decision).trim() !== "").length;
    const allJudged = judges.length >= REQUIRED_LIVE_JUDGE_VOTES && votedCount >= REQUIRED_LIVE_JUDGE_VOTES && judges.every((j) => j?.decision != null && String(j.decision).trim() !== "");
    const showFinalJudgeBreakdown = allJudged;
    const showJudgeRows = judges.length > 0;
    const judgeTotalForStatus = judges.length > 0 ? judges.length : REQUIRED_LIVE_JUDGE_VOTES;
    // When the backend is in the OMT reveal window it sends winner:"omt" which is not a real
    // driver. Treat it as null so the OMT detection logic below fires correctly for all vote
    // splits (1-1-1 or 2-OMT) instead of being swallowed as a "winner".
    const isOmtReveal = !!(cur?.is_omt_reveal);
    const winner = (cur.winner && !isOmtReveal) ? esc(cur.winner_label || cur.winner) : null;

    // Get tally counts
    const tally = cur.tally || [];
    const driverAVotes = tally.find(t => t.winner === cur.driver_a)?.count || 0;
    const driverBVotes = tally.find(t => t.winner === cur.driver_b)?.count || 0;
    const omtVotes = tally.find(t => t.winner === "omt")?.count || 0;
    const allJudgedNoDecision = allJudged && !winner && omtVotes < 2;
    const omtJustDeclared = isOmtReveal || omtVotes >= 2 || allJudgedNoDecision;
    const liveStatusClass = winner ? "decided" : omtJustDeclared ? "omt" : "pending";
    const liveStatusText = winner
      ? `WINNER: ${winner}`
      : (isOmtReveal || omtVotes >= 2)
        ? "OMT - ONE MORE TIME"
        : allJudgedNoDecision
          ? "TIE - OMT TIEBREAKER REQUIRED"
          : `${votedCount} of ${judgeTotalForStatus} judges voted`;

    // Check if winner was just declared (3 judges voted and winner determined, not OMT)
    const winnerJustDeclared = winner && allJudged && !isOmtReveal && omtVotes < 2;
    const resultJustDeclared = winnerJustDeclared || omtJustDeclared;
    const curMatchId = String(cur.id || "");
    // Use a per-OMT-round key so the reveal fires for every successive OMT on the same match.
    const curWinner = isOmtReveal ? `omt_${Number(cur.omt_count || 1)}` : String(winner || "");
    const alreadyShown =
      adminState.comp.winnerScreen.lastShownMatchId === curMatchId &&
      adminState.comp.winnerScreen.lastShownWinner === curWinner;
    const isCurrentWinnerScreen = adminState.comp.winnerScreen.showing && adminState.comp.winnerScreen.matchId === cur.id;
    const shouldShowWinnerScreen =
      resultJustDeclared &&
      !alreadyShown &&
      !adminState.comp.winnerScreen.showing;

    // If winner just declared and we haven't shown the screen yet, show it
    if (shouldShowWinnerScreen) {
      adminState.comp.winnerScreen.lastShownMatchId = curMatchId;
      adminState.comp.winnerScreen.lastShownWinner = curWinner;
      adminState.comp.winnerScreen.showing = true;
      adminState.comp.winnerScreen.matchId = cur.id;
      adminState.comp.winnerScreen.winner = winner;
      adminState.comp.winnerScreen.resultType = winnerJustDeclared ? "winner" : "omt";

      // Clear any existing timeout
      if (adminState.comp.winnerScreen.timeoutId) {
        clearTimeout(adminState.comp.winnerScreen.timeoutId);
      }

      // Hide winner screen after delay (matches backend winner-reveal delay; do not clear lastShown* — they prevent re-trigger)
      adminState.comp.winnerScreen.timeoutId = setTimeout(() => {
        const _wasOmt = adminState.comp.winnerScreen.resultType === "omt";
        adminState.comp.winnerScreen.showing = false;
        adminState.comp.winnerScreen.matchId = null;
        adminState.comp.winnerScreen.winner = null;
        adminState.comp.winnerScreen.resultType = null;
        adminState.comp.winnerScreen.timeoutId = null;
        // After an OMT reveal, wipe stale judge decisions from the cached payload so the
        // live judging panel shows a blank slate for the new battle. The backend returns the
        // PREVIOUS battle's votes during the 5-second omtLockActive window; those linger in
        // adminState.comp.payload.current_judges until the next poll and would appear as if
        // the last judge had already voted for the new round.
        if (_wasOmt && adminState.comp.payload) {
          adminState.comp.payload.current_judges = (adminState.comp.payload.current_judges || [])
            .map(j => ({ ...j, decision: null, decision_label: null, notes_run1: null, notes_run2: null }));
          adminState.comp._lastSig = ""; // Invalidate so next poll forces a re-render with fresh data
        }
        renderAdmin(); // Re-render to show next battle
      }, WINNER_REVEAL_DELAY_MS);
    }

    // Get logo URL (use global LOGO_URL or fallback)
    const logoUrl = LOGO_URL || (root?.dataset?.logoUrl || root?.getAttribute?.('data-logo-url') || '').trim();

    const winnerScreenResultType = adminState.comp.winnerScreen.resultType === "omt" ? "omt" : "winner";
    const winnerScreenHeading = winnerScreenResultType === "omt" ? "OMT" : "WINNER";
    const winnerScreenBody =
      winnerScreenResultType === "omt" ? "ONE MORE TIME" : adminState.comp.winnerScreen.winner;

    // Build tally + judge-breakdown HTML for the reveal overlay so OMT/winner reveals show
    // the same information (what each judge voted + totals).
    const tallyLabelFor = (decId) => {
      if (decId === "omt") return "OMT";
      if (String(decId) === String(cur.driver_a)) return aLabel;
      if (String(decId) === String(cur.driver_b)) return bLabel;
      return String(decId || "");
    };
    const revealTallyItems = (Array.isArray(tally) ? tally : [])
      .filter((t) => t && (t.count || 0) > 0)
      .map((t) => {
        const isOmtVote = t.winner === "omt";
        const bg = isOmtVote ? "rgba(255,193,7,0.18)" : "rgba(255,255,255,0.08)";
        const color = isOmtVote ? "#ffc107" : "#fff";
        return `<div style="background:${bg};border:1px solid rgba(255,255,255,0.12);border-radius:10px;padding:10px 18px;min-width:130px;text-align:center;color:${color};">
          <div style="font-size:11px;opacity:0.7;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:4px;">${esc(tallyLabelFor(t.winner))}</div>
          <div style="font-size:28px;font-weight:900;">${Number(t.count || 0)}</div>
          <div style="font-size:10px;opacity:0.6;">vote${Number(t.count || 0) === 1 ? "" : "s"}</div>
        </div>`;
      })
      .join("");
    const revealTallyHtml = revealTallyItems
      ? `<div style="display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:22px;">${revealTallyItems}</div>`
      : "";
    const revealJudgeRows = (judges || [])
      .filter((j) => j && j.decision)
      .map((j) => {
        const name = esc(j.name || `Judge ${j.slot}`);
        const isOmtVote = j.decision === "omt";
        const pillColor = isOmtVote ? "#ffc107" : "#0a7a68";
        const pillBg = isOmtVote ? "rgba(255,193,7,0.18)" : "rgba(10,122,104,0.22)";
        const voteLabel = esc(tallyLabelFor(j.decision));
        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:8px 14px;">
          <div style="font-size:13px;font-weight:600;color:#e6e6e6;">${name}</div>
          <div style="font-size:12px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:${pillColor};background:${pillBg};padding:4px 10px;border-radius:999px;">${voteLabel}</div>
        </div>`;
      })
      .join("");
    const revealJudgesHtml = revealJudgeRows
      ? `<div style="display:flex;flex-direction:column;gap:6px;margin-top:18px;width:min(420px,92vw);">${revealJudgeRows}</div>`
      : "";

    mount.innerHTML = `
      <style>
        .gl-ce-comp-score {
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          border-radius: 8px;
          padding: 14px;
          color: #fff;
          width: 100%;
          max-width: 980px;
          margin: 0 auto;
          transform-origin: top left;
        }
        .gl-ce-comp-score-header {
          background: linear-gradient(90deg, #076251 0%, #0a7a68 100%);
          padding: 10px 12px;
          border-radius: 6px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .gl-ce-comp-score-header .logo {
          height: clamp(44px, 6vw, 72px);
          width: auto;
          object-fit: contain;
          max-width: 170px;
        }
        .gl-ce-comp-score-header h3 {
          margin: 0;
          font-size: clamp(15px, 2vw, 18px);
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .gl-ce-comp-score-header .battle-info {
          font-size: 12px;
          opacity: 0.9;
          margin-top: 4px;
        }
        .gl-ce-comp-drivers {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }
        .gl-ce-comp-driver-card {
          background: rgba(255,255,255,0.05);
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          padding: 10px;
          text-align: center;
          transition: all 0.3s;
        }
        .gl-ce-comp-driver-card.winner {
          border-color: #076251;
          background: rgba(7,98,81,0.2);
          box-shadow: 0 0 15px rgba(7,98,81,0.4);
        }
        .gl-ce-comp-driver-card h4 {
          margin: 0 0 5px 0;
          font-size: clamp(16px, 2.2vw, 20px);
          font-weight: bold;
        }
        .gl-ce-comp-driver-meta {
          font-size: 12px;
          opacity: 0.8;
          margin-top: 8px;
          line-height: 1.6;
          text-align: center;
        }
        .gl-ce-comp-judges-section {
          margin-bottom: 12px;
        }
        .gl-ce-comp-judges {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .gl-ce-comp-judge-row {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          padding: 8px;
          background: rgba(255,255,255,0.03);
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          border-left: 3px solid rgba(255,255,255,0.2);
          gap: 10px;
        }
        .gl-ce-comp-judge-row.winner-row {
          border-left-color: #076251;
          background: rgba(7,98,81,0.1);
        }
        .gl-ce-comp-judge-row.omt-row {
          border-left-color: #ffc107;
          background: rgba(255,193,7,0.1);
        }
        .gl-ce-comp-judge-decision-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .gl-ce-comp-judge-decision-col.left {
          align-items: flex-start;
        }
        .gl-ce-comp-judge-decision-col.right {
          align-items: flex-end;
        }
        .gl-ce-comp-judge-name {
          font-weight: bold;
          font-size: 15px;
          text-align: center;
        }
        .gl-ce-comp-judge-name-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          min-width: 0;
        }
        .gl-ce-comp-judge-waiting {
          font-size: 12px;
          font-weight: 600;
          color: #aaa;
          letter-spacing: 0.04em;
        }
        .gl-ce-comp-judge-row.waiting-row {
          border-left-color: rgba(200, 200, 200, 0.35);
        }
        .gl-ce-comp-judge-decision {
          font-size: 13px;
          font-weight: bold;
          padding: 6px 12px;
          border-radius: 4px;
        }
        .gl-ce-comp-judge-decision.winner {
          background: rgba(7,98,81,0.3);
          color: #0a7a68;
        }
        .gl-ce-comp-judge-decision.omt {
          background: rgba(255,193,7,0.3);
          color: #ffc107;
        }
        .gl-ce-comp-judge-decision.pending {
          opacity: 0.5;
          color: #999;
        }
        .gl-ce-comp-judge-notes {
          font-size: 12px;
          opacity: 0.8;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .gl-ce-comp-judge-notes div {
          margin-top: 5px;
        }
        .gl-ce-comp-judge-notes div:first-child {
          margin-top: 0;
        }
        .gl-ce-comp-status {
          text-align: center;
          padding: 10px;
          margin-top: 10px;
          border-radius: 6px;
          font-weight: bold;
        }
        .gl-ce-comp-status.pending {
          background: rgba(255,193,7,0.2);
          color: #ffc107;
        }
        .gl-ce-comp-status.decided {
          background: rgba(7,98,81,0.2);
          color: #0a7a68;
        }
        .gl-ce-comp-status.omt {
          background: rgba(255,193,7,0.3);
          color: #ffc107;
        }
        .gl-ce-winner-screen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.95);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .gl-ce-winner-screen-content {
          text-align: center;
          color: #fff;
        }
        .gl-ce-winner-screen h1 {
          font-size: 72px;
          font-weight: bold;
          margin: 0 0 20px 0;
          text-transform: uppercase;
          letter-spacing: 4px;
          color: #076251;
          text-shadow: 0 0 20px rgba(7,98,81,0.5);
        }
        .gl-ce-winner-screen.omt h1 {
          color: #ffc107;
          text-shadow: 0 0 24px rgba(255,193,7,0.55);
        }
        .gl-ce-winner-screen h2 {
          font-size: 48px;
          font-weight: bold;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        .gl-ce-winner-screen.omt h2 { color: #ffc107; }
        .gl-ce-winner-screen .countdown {
          font-size: 20px;
          margin-top: 28px;
          opacity: 0.7;
        }
      </style>
      ${isCurrentWinnerScreen ? `
        <div class="gl-ce-winner-screen ${winnerScreenResultType === 'omt' ? 'omt' : ''}">
          <div class="gl-ce-winner-screen-content">
            <h1>${winnerScreenHeading}</h1>
            <h2>${winnerScreenBody}</h2>
            ${revealTallyHtml}
            ${revealJudgesHtml}
            <div class="countdown">Next battle in 5 seconds...</div>
          </div>
        </div>
      ` : ''}
      <div class="gl-ce-comp-score" style="${isCurrentWinnerScreen ? 'opacity: 0.3; pointer-events: none;' : ''}">
        <div class="gl-ce-comp-score-header">
          <img src="https://cdn.shopify.com/s/files/1/0720/3137/1432/files/20251119175125-1f0c5705-e2a9-6f32-9f1c-22a1214d2021.png?v=1763574999" alt="Ghostline Club" class="logo" onerror="this.style.display='none';" />
          <div>
            <h3>${esc(resolvedEventName)}</h3>
            <div class="battle-info">${esc(liveMatchTitle)}</div>
          </div>
        </div>

        <div class="gl-ce-comp-drivers">
          <div class="gl-ce-comp-driver-card ${winner === aLabel ? 'winner' : ''}">
            <h4>${aLabel}</h4>
            ${aMetaHtml}
            ${showFinalJudgeBreakdown ? (() => {
              const driverAJudges = judges.filter(j => j.decision === cur.driver_a).map(j => j.name || `Judge ${j.slot}`).join(', ');
              return driverAJudges ? `<div style="font-size: 11px; color: #0a7a68; margin-top: 8px; font-weight: 500;">Judges: ${esc(driverAJudges)}</div>` : '';
            })() : ""}
          </div>
          <div class="gl-ce-comp-driver-card ${winner === bLabel ? 'winner' : ''}">
            <h4>${bLabel}</h4>
            ${bMetaHtml}
            ${showFinalJudgeBreakdown ? (() => {
              const driverBJudges = judges.filter(j => j.decision === cur.driver_b).map(j => j.name || `Judge ${j.slot}`).join(', ');
              return driverBJudges ? `<div style="font-size: 11px; color: #0a7a68; margin-top: 8px; font-weight: 500;">Judges: ${esc(driverBJudges)}</div>` : '';
            })() : ""}
          </div>
        </div>

        <div class="gl-ce-comp-judges-section">
          <div class="gl-ce-comp-judges">
            ${showJudgeRows ? renderLiveJudgeRowsHtml(judges, cur, aLabel, bLabel) : ""}
          </div>
        </div>

        <div class="gl-ce-comp-status ${liveStatusClass}" style="margin-top: 20px;">
          ${liveStatusText}
        </div>
      </div>
    `;

    renderLiveJudgingOnly._lastMount = mount;
    fitLiveJudgingCardToViewport(mount);
    if (!renderLiveJudgingOnly._boundResize) {
      renderLiveJudgingOnly._boundResize = true;
      window.addEventListener("resize", () => {
        const lastMount = renderLiveJudgingOnly._lastMount;
        if (!lastMount || !lastMount.isConnected) return;
        fitLiveJudgingCardToViewport(lastMount);
      });
    }
  }

  // B1: Payload signature for comp view — only re-update UI when data that affects display changes
  function compPayloadSig(p) {
    try {
      const ev = p?.event || {};
      const matches = Array.isArray(p?.bracket?.matches) ? p.bracket.matches : [];
      const currentJudges = Array.isArray(p?.current_judges) ? p.current_judges : [];
      const currentMatch = p?.current_match || {};

      const core = matches.map(m => {
        const hasSlots = m && ("slot_a_driver_id" in m || "slot_b_driver_id" in m);
        const a = hasSlots ? (m.slot_a_driver_id || "") : (m.driver_a || "");
        const b = hasSlots ? (m.slot_b_driver_id || "") : (m.driver_b || "");
        return [
          m.id,
          m.round,
          m.match_no,
          m.battle_no,
          m.is_third_place ? "T" : "F",
          a,
          b,
          m.winner || "",
          m.status || "",
          m.omt_count || 0
        ].join("|");
      }).join(";");

      const judgesCore = currentJudges
        .slice()
        .sort((a, b) => Number(a?.slot || 0) - Number(b?.slot || 0))
        .map((j) => [
          j?.slot || "",
          j?.decision || "",
          j?.name || "",
          j?.judge_display_name || "",
        ].join("|"))
        .join(";");
      const tallyCore = (Array.isArray(currentMatch?.tally) ? currentMatch.tally : [])
        .slice()
        .sort((a, b) => String(a?.winner || "").localeCompare(String(b?.winner || "")))
        .map((t) => `${t?.winner || ""}:${Number(t?.count || 0)}`)
        .join(";");
      const currentCore = [
        currentMatch?.id || "",
        currentMatch?.winner || "",
        currentMatch?.winner_label || "",
        currentMatch?.status || "",
        currentMatch?.is_omt_reveal ? "1" : "0",
        tallyCore,
      ].join("|");
      const qualifyingCurrentRunId = String(
        p?.qualifying?.current_run_id ||
        p?.current_run_id ||
        ""
      );
      const qualifyingCore = qualifyingLeaderboardRows(p)
        .map((row) => [
          row?.registration_id || "",
          row?.rank ?? "",
          row?.driver_name || "",
          row?.driver_number ?? "",
          row?.best_score ?? "",
          qualifyingRunScoreLabel(row, 1),
          qualifyingRunScoreLabel(row, 2),
          qualifyingPartialRunSignature(row),
        ].join("|"))
        .join(";")
        + "@" + qualifyingCurrentRunId;

      return [
        ev.id || "",
        eventStatusForUi(ev),
        ev.bracket_mode || "",
        p?.current_match_id || "",
        p?.active_regen_id || ev.current_regen_id || "",
        core,
        judgesCore,
        currentCore,
        qualifyingCore
      ].join("::");
    } catch (e) {
      return String(Date.now()); // fail-open (force update)
    }
  }

  function captureCompViewInteractionState() {
    const active = document.activeElement;
    const state = {
      pageX: window.scrollX,
      pageY: window.scrollY,
      focused: null,
      scrollables: [],
    };
    if (active && adminPane?.contains(active)) {
      const role = active.getAttribute("data-role");
      const runId = active.getAttribute("data-run-id");
      const slot = active.getAttribute("data-slot");
      if (active.id) {
        state.focused = { selector: `#${CSS.escape(active.id)}` };
      } else if (role) {
        state.focused = {
          selector: `[data-role="${CSS.escape(role)}"]${runId ? `[data-run-id="${CSS.escape(runId)}"]` : ""}${slot ? `[data-slot="${CSS.escape(slot)}"]` : ""}`,
          start: typeof active.selectionStart === "number" ? active.selectionStart : null,
          end: typeof active.selectionEnd === "number" ? active.selectionEnd : null,
        };
      }
    }
    adminPane?.querySelectorAll(".gl-ce-tablewrap, [data-preserve-scroll]").forEach((el, index) => {
      if (el.scrollTop || el.scrollLeft) {
        state.scrollables.push({ index, top: el.scrollTop, left: el.scrollLeft });
      }
    });
    return state;
  }

  function restoreCompViewInteractionState(state) {
    if (!state) return;
    requestAnimationFrame(() => {
      window.scrollTo(state.pageX || 0, state.pageY || 0);
      adminPane?.querySelectorAll(".gl-ce-tablewrap, [data-preserve-scroll]").forEach((el, index) => {
        const saved = state.scrollables.find((item) => item.index === index);
        if (saved) {
          el.scrollTop = saved.top;
          el.scrollLeft = saved.left;
        }
      });
      if (!state.focused?.selector) return;
      const target = adminPane?.querySelector(state.focused.selector);
      if (!target || target.disabled) return;
      target.focus({ preventScroll: true });
      if (state.focused.start != null && typeof target.setSelectionRange === "function") {
        try { target.setSelectionRange(state.focused.start, state.focused.end); } catch (_) {}
      }
    });
  }

  // B1: Patch only live sections on poll — no full re-render (preserves scroll, tab, modals)
  function applyCompViewLiveUpdate(payload) {
    if (!payload || !adminPane) return;
    const interactionState = captureCompViewInteractionState();
    adminState.comp.lastAt = new Date().toLocaleTimeString();
    const prevPayload = adminState.comp.prevPayload || null;

    const lastAtEl = adminPane.querySelector("#glCeCompLastAt");
    if (lastAtEl) lastAtEl.textContent = adminState.comp.lastAt || "—";

    if (adminState.comp.view === "qualifying" || eventStatusForUi(payload?.event) === "qualifying") {
      const qMount = adminPane.querySelector("#glCeCompQualifyingLeaderboard");
      if (qMount) {
        renderAdminCompQualifyingTable(qMount, payload);
        if (adminState.comp.qualifyingError) {
          qMount.insertAdjacentHTML("afterbegin", `<div class="gl-ce-warn" role="status" style="margin-bottom:10px;">${esc(adminState.comp.qualifyingError)} Showing the last live qualifying results.</div>`);
        }
      }
      restoreCompViewInteractionState(interactionState);
      return;
    }

    // If backend already advanced current_match, preserve a brief result reveal from previous payload.
    const prevCur = prevPayload?.current_match || null;
    const prevCurId = prevCur?.id != null ? String(prevCur.id) : "";
    const nextCurId = payload?.current_match_id != null
      ? String(payload.current_match_id)
      : (payload?.current_match?.id != null ? String(payload.current_match.id) : "");
    const changedMatch = !!prevCurId && !!nextCurId && prevCurId !== nextCurId;
    if (changedMatch) {
      const nextMatch = (payload?.bracket?.matches || []).find((match) => String(match?.id) === nextCurId);
      if (nextMatch && !isManualCompView(payload, adminState.events.find((event) => String(event.id) === String(adminState.selectedId)))) {
        adminState.bracketExplicitFull = false;
        adminState.bracketActiveRound = isConsolationMatch(nextMatch)
          ? "third-place"
          : Number(nextMatch.round || 1);
        const tabsMount = adminPane.querySelector("#glCeBracketTabs");
        const selectedEvent = adminState.selectedId
          ? adminState.events.find((event) => String(event.id) === String(adminState.selectedId))
          : null;
        if (tabsMount) renderBracketTabs(tabsMount, payload.bracket, selectedEvent);
      }
    }
    const prevJudges = Array.isArray(prevPayload?.current_judges) ? prevPayload.current_judges : [];
    const prevAllJudged = prevJudges.length >= 3 && prevJudges.every(j => j?.decision);
    const prevTally = Array.isArray(prevCur?.tally) ? prevCur.tally : [];
    const prevOmtVotes = prevTally.find(t => t.winner === "omt")?.count || 0;
    const prevWinnerRaw = prevCur ? (prevCur.winner_label || prevCur.winner || "") : "";
    const prevWinner = prevWinnerRaw ? esc(prevWinnerRaw) : "";
    const prevAllJudgedNoDecision = prevAllJudged && !prevWinner && prevOmtVotes < 2;
    const prevWinnerJustDeclared = !!prevWinner && prevAllJudged && prevOmtVotes < 2;
    const prevOmtJustDeclared = prevOmtVotes >= 2 || prevAllJudgedNoDecision;
    const prevResultJustDeclared = prevWinnerJustDeclared || prevOmtJustDeclared;
    const prevAlreadyShown =
      adminState.comp.winnerScreen.lastShownMatchId === prevCurId &&
      adminState.comp.winnerScreen.lastShownWinner === prevWinner;
    const shouldHoldPreviousResult =
      changedMatch &&
      prevResultJustDeclared &&
      !adminState.comp.winnerScreen.showing &&
      !prevAlreadyShown;

    if (shouldHoldPreviousResult) {
      adminState.comp.winnerScreen.lastShownMatchId = prevCurId;
      adminState.comp.winnerScreen.lastShownWinner = prevWinner;
      adminState.comp.winnerScreen.showing = true;
      adminState.comp.winnerScreen.matchId = prevCur.id;
      adminState.comp.winnerScreen.winner = prevWinner || null;
      adminState.comp.winnerScreen.resultType = prevWinnerJustDeclared ? "winner" : "omt";

      if (adminState.comp.winnerScreen.timeoutId) {
        clearTimeout(adminState.comp.winnerScreen.timeoutId);
      }
      adminState.comp.winnerScreen.timeoutId = setTimeout(() => {
        const _wasOmt = adminState.comp.winnerScreen.resultType === "omt";
        adminState.comp.winnerScreen.showing = false;
        adminState.comp.winnerScreen.matchId = null;
        adminState.comp.winnerScreen.winner = null;
        adminState.comp.winnerScreen.resultType = null;
        adminState.comp.winnerScreen.timeoutId = null;
        if (_wasOmt && adminState.comp.payload) {
          adminState.comp.payload.current_judges = (adminState.comp.payload.current_judges || [])
            .map(j => ({ ...j, decision: null, decision_label: null, notes_run1: null, notes_run2: null }));
          adminState.comp._lastSig = "";
        }
        renderAdmin();
      }, WINNER_REVEAL_DELAY_MS);
    }

    // Detect OMT advance on the same match via omt_count increase.
    // This is a reliable primary trigger for the OMT overlay that does not depend on
    // the backend lock window or is_omt_reveal timing — it fires as soon as the poll
    // detects the omt_count went up, even if the 5-second lock already expired.
    if (!shouldHoldPreviousResult && !changedMatch) {
      const prevOmtCount = Number(prevCur?.omt_count || 0);
      const nextOmtCount = Number(payload?.current_match?.omt_count || 0);
      if (nextOmtCount > prevOmtCount && !payload?.current_match?.winner && !adminState.comp.winnerScreen.showing && nextCurId) {
        const omtRevealKey = `omt_${nextOmtCount}`;
        const alreadyShownOmt =
          adminState.comp.winnerScreen.lastShownMatchId === nextCurId &&
          adminState.comp.winnerScreen.lastShownWinner === omtRevealKey;
        if (!alreadyShownOmt) {
          adminState.comp.winnerScreen.lastShownMatchId = nextCurId;
          adminState.comp.winnerScreen.lastShownWinner = omtRevealKey;
          adminState.comp.winnerScreen.showing = true;
          adminState.comp.winnerScreen.matchId = payload.current_match?.id || nextCurId;
          adminState.comp.winnerScreen.winner = null;
          adminState.comp.winnerScreen.resultType = "omt";
          if (adminState.comp.winnerScreen.timeoutId) clearTimeout(adminState.comp.winnerScreen.timeoutId);
          adminState.comp.winnerScreen.timeoutId = setTimeout(() => {
            adminState.comp.winnerScreen.showing = false;
            adminState.comp.winnerScreen.matchId = null;
            adminState.comp.winnerScreen.winner = null;
            adminState.comp.winnerScreen.resultType = null;
            adminState.comp.winnerScreen.timeoutId = null;
            // This path always fires for an OMT advance — clear stale judges from the
            // cached payload so the new OMT battle starts with no pre-filled votes.
            if (adminState.comp.payload) {
              adminState.comp.payload.current_judges = (adminState.comp.payload.current_judges || [])
                .map(j => ({ ...j, decision: null, decision_label: null, notes_run1: null, notes_run2: null }));
              adminState.comp._lastSig = "";
            }
            renderAdmin();
          }, WINNER_REVEAL_DELAY_MS);
        }
      }
    }

    // Judging mount includes winner screen overlay; patch updates it from adminState.comp.winnerScreen
    // During winner reveal delay, do not let polling replace with a different match (preserves winner state)
    const jMount = adminPane.querySelector("#glCeCompJudging");
    if (jMount) {
      if (shouldHoldPreviousResult && prevPayload) {
        // Force one render from previous payload so the previous result remains visible before advancing.
        renderLiveJudgingOnly(jMount, prevPayload);
      } else {
      const winnerScreenShowing = adminState.comp.winnerScreen.showing && adminState.comp.winnerScreen.matchId != null;
      const payloadMatchId = payload.current_match_id != null ? String(payload.current_match_id) : "";
      const winnerScreenMatchId = adminState.comp.winnerScreen.matchId != null ? String(adminState.comp.winnerScreen.matchId) : "";
      if (winnerScreenShowing && payloadMatchId !== winnerScreenMatchId) {
        // Keep showing winner; do not patch to a different match during delay
      } else {
        renderLiveJudgingOnly(jMount, payload);
      }
      }
    }

    const cMount = adminPane.querySelector("#glCeCompMatchControls");
    if (cMount && payload.bracket && Array.isArray(payload.bracket.matches)) {
      renderCompMatchControls(cMount, payload);
    }

    // Patch bracket with backend data and backend current_match_id only (no client-side advance)
    const bMount = adminPane.querySelector("#glCeCompBracket");
    if (bMount && payload.bracket) {
      const ev = adminState.selectedId
        ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
        : null;
      const currentMatchId = payload.current_match_id != null ? String(payload.current_match_id) : "";
      const maxDrivers = ev?.max_drivers || payload.bracket?.max_drivers;
      const matches = payload.bracket?.matches || [];
      const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 0)).filter(Number.isFinite)) : 0;
      const totalRounds = (Number(payload.bracket?.final_round) > 0) ? Number(payload.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : undefined);
      const manualMode = isManualCompView(payload, ev);
      const slotsUnchanged = manualMode
        && manualBracketSlotsSig(prevPayload?.bracket) === manualBracketSlotsSig(payload.bracket);
      if (slotsUnchanged) {
        patchManualBracketCurrentMatch(currentMatchId);
        wireManualRunnerClicks(bMount, String(ev?.id || adminState.selectedId || ""));
      } else {
        renderBracketVisual(bMount, payload.bracket, {
          title: ev?.event_name || "Live",
          live: true,
          lastUpdated: adminState.comp.lastAt,
          currentMatchId,
          max_drivers: maxDrivers,
          totalRounds,
          finalRound: Number(payload.bracket?.final_round || 0) || undefined,
          activeRound: manualMode ? null : normalizeBracketRoundSelection(adminState.bracketActiveRound),
          manualInteract: manualMode,
          manualDrivers: manualMode ? manualEligibleDriversList() : [],
          podium: manualMode ? (payload.bracket?.podium || payload.podium || null) : null,
          showCurrentMatchButton: !manualMode,
        });
        if (manualMode) {
          wireManualRunnerClicks(bMount, String(ev?.id || adminState.selectedId || ""));
        }
        if (changedMatch && !manualMode) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => bMount.__glCeCenterCurrentMatch?.());
          });
        }
      }
    }

    // C4: Patch bracket overview (consolation row) so polling does not reset UI
    const overviewMount = adminPane.querySelector("#glCeCompOverviewContent");
    if (overviewMount && payload.bracket) {
      const ev = adminState.selectedId
        ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
        : null;
      renderAdminCompOverview(overviewMount, payload, ev);
    }
    restoreCompViewInteractionState(interactionState);
  }

  function manualBracketSlotsSig(bracket) {
    const matches = Array.isArray(bracket?.matches) ? bracket.matches : [];
    return matches.map((m) => [
      m.id,
      m.slot_a_driver_id || m.driver_a || "",
      m.slot_b_driver_id || m.driver_b || "",
      m.winner || "",
      m.status || "",
      m.driver_a_label || m.driver_a || "",
      m.driver_b_label || m.driver_b || "",
    ].join("|")).join(";") + `::${[bracket?.podium?.first?.id || "", bracket?.podium?.second?.id || "", bracket?.podium?.third?.id || ""].join("|")}`;
  }

  function patchManualBracketCurrentMatch(matchId) {
    const id = String(matchId || "");
    const roots = [];
    const bMount = adminPane?.querySelector("#glCeCompBracket");
    const rMount = adminPane?.querySelector("#glCeManualRunnerMount");
    if (bMount) roots.push(bMount);
    if (rMount) roots.push(rMount);
    for (const root of roots) {
      root.querySelectorAll(".gl-ce-bracket-match-traditional").forEach((el) => {
        const elId = String(el.getAttribute("data-id") || el.getAttribute("data-match-id") || "");
        el.classList.toggle("is-current", !!id && elId === id);
      });
    }
  }

  async function apiGetWithSignal(path, signal) {
    const candidates = buildApiGetCandidates(path);
    let lastErr = null;
    const primaryUrl = candidates[0] || "";

    for (let i = 0; i < candidates.length; i++) {
      const requestUrl = candidates[i];
      try {
        if (i > 0) {
          console.warn("[API/GET] Retrying with fallback base", { path, requestUrl, attempt: i + 1 });
        }
        const res = await fetch(requestUrl, {
          method: "GET",
          headers: { accept: "application/json" },
          credentials: "same-origin",
          signal,
        });

        const contentLength = res.headers.get("content-length");
        if (contentLength === "0" || res.status === 204) {
          throw new Error(`Empty response from ${path} (status: ${res.status})`);
        }

        const contentType = res.headers.get("content-type") || "";
        const isJsonResponse =
          contentType.includes("application/json") || contentType.includes("text/json");
        let data = null;
        if (isJsonResponse) {
          data = await res.json().catch((e) => {
            throw new Error(`Failed to parse JSON from ${path}: ${e.message}`);
          });
        } else if (!res.ok) {
          const raw = await res.text().catch(() => "");
          if (raw) {
            try {
              data = JSON.parse(raw);
            } catch (_) {
              data = { ok: false, error: raw };
            }
          }
        } else {
          throw new Error(`Expected JSON but got ${contentType || "unknown content-type"} from ${path}`);
        }

        if (!res.ok || data?.ok === false) {
          const apiErr = new Error(data?.message || data?.error || `Request failed (${res.status})`);
          apiErr.__glCeStopFallback = true;
          apiErr.__glCeApiPayload = data || { ok: false, error: `Request failed (${res.status})` };
          apiErr.__glCeStatus = res.status;
          throw apiErr;
        }

        if (i > 0) {
          console.info("[API/GET] Fallback base succeeded", { path, requestUrl, primaryUrl });
        }
        return data;
      } catch (err) {
        if (signal?.aborted || err?.name === "AbortError") throw err;
        lastErr = err;
        if (err && err.__glCeStopFallback) throw err;
        if (i === 0 && candidates.length > 1) {
          console.warn("[API/GET] Primary base failed; trying fallbacks", {
            path,
            requestUrl,
            error: err?.message || String(err),
          });
        }
        if (i < candidates.length - 1) continue;
      }
    }

    throw lastErr || new Error(`Request failed for ${path}`);
  }

  function scheduleAdminCompWatchdog(id, baselineSig) {
    clearTimeout(adminCompWatchdogTimer);
    adminCompWatchdogTimer = setTimeout(() => {
      if (adminState.comp.watchdogRetried) return;
      if (adminState.page !== "comp") return;
      if (String(adminState.selectedId || "") !== String(id || "")) return;
      const currentSig = adminState.comp._lastSig;
      const missingPayload = !adminState.comp.payload;
      const unchanged = baselineSig != null && baselineSig === currentSig;
      const stillLoading = adminState.comp.loading;
      if (missingPayload || unchanged || stillLoading) {
        adminState.comp.watchdogRetried = true;
        if (adminCompAbortController) {
          try {
            adminCompAbortController.abort();
          } catch (_) {}
        }
        adminCompPollTick({ reason: "watchdog", force: true }).catch(() => {});
      }
    }, 8000);
  }

  // Polling loop: runs every ~2s; fetches comp-view and re-renders.
  async function adminCompPollTick({ reason = "interval", force = false } = {}) {
    if (!adminState.selectedId) return;
    if (adminState.page !== "comp" && reason !== "manual") return;

    const modal = adminPane.querySelector("#glCeOverrideModal");
    if (modal && modal.style.display !== "none" && modal.style.display !== "") {
      return;
    }

    const id = adminState.selectedId;
    const baselineSig = adminState.comp._lastSig;
    if (adminCompPollInFlight && !force) return;
    if (adminCompPollInFlight && force && adminCompAbortController) {
      try {
        adminCompAbortController.abort();
      } catch (_) {}
    }
    adminCompAbortController = new AbortController();
    adminCompPollInFlight = true;
    adminState.comp.loading = true;
    if (reason === "enter" || reason === "watchdog" || reason === "manual") {
      adminState.comp.error = "";
    }
    const refreshIndicator = document.getElementById('glCeRefreshIndicator');
    if (refreshIndicator) {
      refreshIndicator.classList.add('refreshing');
      const prog = refreshIndicator.querySelector('.refresh-progress');
      if (prog) prog.style.width = '0%';
      setTimeout(() => { if (refreshIndicator && refreshIndicator.querySelector('.refresh-progress')) refreshIndicator.querySelector('.refresh-progress').style.width = '100%'; }, 10);
    }

    try {
      const prevPayload = adminState.comp.payload || null;
      const data = await apiGetWithSignal(
        `/admin/events/${encodeURIComponent(id)}/comp-view`,
        adminCompAbortController.signal
      );
      if (String(data?.event?.bracket_mode || "") === "manual" && !(Array.isArray(adminState.manualBracket?.eligible_drivers) && adminState.manualBracket.eligible_drivers.length)) {
        try {
          const draftRes = await apiGetWithSignal(
            `/admin/events/${encodeURIComponent(id)}/manual-bracket`,
            adminCompAbortController.signal
          );
          if (draftRes?.draft) adminState.manualBracket = draftRes.draft;
        } catch (draftErr) {
          if (adminCompAbortController?.signal?.aborted || draftErr?.name === "AbortError") throw draftErr;
        }
      }
      const selectedEvent = adminState.events.find((e) => String(e.id) === String(id));
      const dataStatus = eventStatusForUi({
        ...(selectedEvent || {}),
        ...(data?.event || {}),
      });
      if (adminState.comp.view === "qualifying" || dataStatus === "qualifying") {
        const priorQualifying = adminState.comp.qualifyingLastValid || prevPayload?.qualifying || null;
        try {
          const qualifyingData = await apiGetWithSignal(
            `/admin/events/${encodeURIComponent(id)}/qualifying/leaderboard`,
            adminCompAbortController.signal
          );
          const nextRows = qualifyingLeaderboardRows(qualifyingData);
          const priorRows = qualifyingLeaderboardRows(priorQualifying);
          const hasValidShape = !!qualifyingData && Array.isArray(qualifyingData.leaderboard);
          const wouldBlankLiveBoard = priorRows.length > 0 && nextRows.length === 0;
          if (!hasValidShape || wouldBlankLiveBoard) {
            throw new Error("Qualifying leaderboard returned an incomplete refresh.");
          }
          adminState.comp.qualifyingLastValid = qualifyingData;
          adminState.comp.qualifyingError = "";
          data.qualifying = qualifyingData;
        } catch (qualifyingError) {
          if (adminCompAbortController?.signal?.aborted || qualifyingError?.name === "AbortError") throw qualifyingError;
          adminState.comp.qualifyingError = qualifyingError?.message || "Live qualifying refresh failed.";
          if (priorQualifying) data.qualifying = priorQualifying;
        }
      }
      adminState.comp.prevPayload = prevPayload;
      adminState.comp.payload = data;
      adminState.comp.lastAt = new Date().toLocaleTimeString();
      adminState.comp.loading = false;
      adminState.comp.error = "";
      const prevStatus = eventStatusForUi(prevPayload?.event);
      const nextStatus = eventStatusForUi(data?.event);
      if (adminState.page === "comp" && nextStatus === "complete" && data?.final_results_pending && !adminState.finalResultsDraft) {
        await adminOpenFinalResultsDraft(id);
        return;
      }
      if (adminState.page === "comp") {
        const newSig = compPayloadSig(data);
        if (adminState.comp._lastSig === newSig) {
          if (refreshIndicator) setTimeout(() => refreshIndicator.classList.remove('refreshing'), 300);
          return;
        }
        const wasFirstPayload = adminState.comp._lastSig === undefined;
        adminState.comp._lastSig = newSig;
        if (wasFirstPayload || (prevStatus && nextStatus && prevStatus !== nextStatus)) {
          renderAdmin();
        } else {
          applyCompViewLiveUpdate(data);
        }
        if (adminState.selectedId && (!adminState.autoAdvance.lastCheckedAt || (Date.now() - adminState.autoAdvance.lastCheckedAt) > 10000)) {
          setTimeout(() => checkAndAutoAdvanceRound(adminState.selectedId), 1000);
        }
      } else {
        renderAdmin();
      }

      if (refreshIndicator) setTimeout(() => refreshIndicator.classList.remove('refreshing'), 300);
    } catch (err) {
      if (adminCompAbortController?.signal?.aborted || err?.name === "AbortError") {
        return;
      }
      console.error("Comp View poll error:", err);
      adminState.comp.error = err?.message || "Failed to load comp view.";
      adminState.comp.loading = false;
      if (!adminState.comp.payload) {
        adminState.comp.payload = null;
      }
      adminState.comp.lastAt = new Date().toLocaleTimeString();
      renderAdmin();
      if (refreshIndicator) refreshIndicator.classList.remove('refreshing');
    } finally {
      adminCompPollInFlight = false;
      adminCompAbortController = null;
      if (adminState.page === "comp" && String(adminState.selectedId || "") === String(id || "")) {
        scheduleAdminCompWatchdog(id, baselineSig);
      }
    }
  }

  function adminStopCompPoll() {
    clearInterval(adminCompPollTimer);
    adminCompPollTimer = null;
    clearTimeout(adminCompWatchdogTimer);
    adminCompWatchdogTimer = null;
    clearInterval(adminState.comp.t);
    adminState.comp.t = null;
    if (adminCompAbortController) {
      try {
        adminCompAbortController.abort();
      } catch (_) {}
    }
    adminCompAbortController = null;
    adminCompPollInFlight = false;
    adminState.comp.loading = false;
  }

  function adminStartCompPoll() {
    adminStopCompPoll();
    const id = String(adminState.selectedId || "").trim();
    if (!id || adminState.page !== "comp") return;
    adminState.comp.loading = true;
    adminState.comp.error = "";
    adminState.comp.watchdogRetried = false;
    adminCompPollTick({ reason: "enter" }).catch(() => {});
    adminCompPollTimer = setInterval(() => adminCompPollTick({ reason: "interval" }).catch(() => {}), 2000);
    adminState.comp.t = adminCompPollTimer;
    scheduleAdminCompWatchdog(id, adminState.comp._lastSig);
  }

  function registrationsPayloadSig(data) {
    const rows = registrationRows(data);
    const compact = rows.map((r) => [
      String(r.id || ""),
      String(r.status || r.registration_status || ""),
      String(r.updated_at || r.created_at || ""),
    ]);
    return fnv1a(JSON.stringify(compact));
  }

  function adminStopRegsPoll() {
    clearInterval(adminState.regsPoll.t);
    adminState.regsPoll.t = null;
  }

  async function fetchAdminRegistrations(eventId) {
    const withTimeout = async (promise, ms, label) => {
      let t = null;
      try {
        return await Promise.race([
          promise,
          new Promise((_, reject) => {
            t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms.`)), ms);
          }),
        ]);
      } finally {
        if (t) clearTimeout(t);
      }
    };

    try {
      return await withTimeout(
        apiGet(`/admin/events/${encodeURIComponent(eventId)}/registrations`),
        12000,
        "Admin registrations request"
      );
    } catch (_) {
      const fallback = await withTimeout(
        apiGet(`/events/${encodeURIComponent(eventId)}/registrations`),
        12000,
        "Registrations fallback request"
      );
      return {
        ...fallback,
        registrations: Array.isArray(fallback?.registrations)
          ? fallback.registrations
          : Array.isArray(fallback?.drivers)
          ? fallback.drivers
          : [],
      };
    }
  }

  async function adminPollRegistrations() {
    const id = String(adminState.selectedId || "").trim();
    if (adminState.page !== "manage" || !id) return;
    try {
      const data = await fetchAdminRegistrations(id);
      const nextSig = registrationsPayloadSig(data);
      if (nextSig && nextSig === adminState.regsPoll.lastSig) return;
      adminState.regs = data;
      adminState.regsError = "";
      adminState.regsLoading = false;
      adminState.regsPoll.lastSig = nextSig;
      renderAdmin();
    } catch (err) {
      if (!adminState.regs) {
        adminState.regs = { registrations: [] };
        adminState.regsError = err?.message || "Unable to load registrations.";
        adminState.regsLoading = false;
        renderAdmin();
      }
    }
  }

  function adminStartRegsPoll() {
    adminStopRegsPoll();
    if (adminState.page !== "manage" || !adminState.selectedId) return;
    adminPollRegistrations().catch(() => {});
    adminState.regsPoll.t = setInterval(() => adminPollRegistrations().catch(() => {}), 3000);
  }

  // -------- Core admin data loaders ----------
  async function adminRefreshEvents() {
    try {
      const session = activeStaffSession();
      if (session?.event_id) {
        const data = await apiGet(`/admin/events/${encodeURIComponent(session.event_id)}`);
        adminState.events = data.event ? [data.event] : [];
      } else {
        const data = await apiGet("/admin/events");
        adminState.events = data.events || [];
      }
      adminState.eventsLoadError = "";
    } catch (err) {
      adminState.eventsLoadError = err?.message || "Unable to load events.";
      throw err;
    }
  }

  function normalizeSeriesForUi(series) {
    return {
      ...series,
      drivers: Array.isArray(series?.drivers) ? series.drivers : [],
      staff: (Array.isArray(series?.staff) ? series.staff : []).map((member) => {
        let roles = Array.isArray(member.roles) ? member.roles : [];
        if (!roles.length && member.roles_json) {
          try { roles = JSON.parse(member.roles_json); } catch (_) { roles = []; }
        }
        return { ...member, roles: Array.isArray(roles) ? roles : [] };
      }),
      admins: Array.isArray(series?.admins) ? series.admins : [],
      events: Array.isArray(series?.events) ? series.events : [],
    };
  }

  async function adminRefreshSeries({ includeSelectedDetail = true } = {}) {
    const data = await apiGet("/admin/series");
    const rows = (data.series || []).map(normalizeSeriesForUi);
    adminState.series.byId = Object.fromEntries(rows.map((series) => [series.id, series]));
    if (!adminState.series.byId[adminState.series.selectedId]) {
      adminState.series.selectedId = rows[0]?.id || "";
    }
    if (includeSelectedDetail && adminState.series.selectedId) {
      const detail = await apiGet(`/admin/series/${encodeURIComponent(adminState.series.selectedId)}`);
      if (detail.series) {
        adminState.series.byId[detail.series.id] = normalizeSeriesForUi(detail.series);
      }
    }
  }

  async function adminLoadBracketByCode(code, { silent } = {}) {
    const c = String(code || "").trim().toUpperCase();
    if (!c) return;

    const br = await apiGet(`/event/bracket?code=${encodeURIComponent(c)}`);
    const sig = bracketSignature(br);

    if (sig && sig !== adminState.bracketSig) {
      adminState.bracket = br;
      adminState.bracketSig = sig;
      adminState.lastBracketAt = new Date().toLocaleTimeString();
      if (!silent) toast("Bracket updated.", "ok");
      renderAdmin();
    }
  }

  async function adminLoadRegistrations(eventId, { silent } = {}) {
    const id = String(eventId || "").trim();
    if (!id) {
      adminState.regs = { registrations: [] };
      adminState.regsError = "";
      adminState.regsLoading = false;
      adminState.regsPoll.lastSig = registrationsPayloadSig(adminState.regs);
      if (!silent) renderAdmin();
      return;
    }
    adminState.regsLoading = true;
    adminState.regsError = "";
    if (!silent) renderAdmin();
    try {
      const data = await fetchAdminRegistrations(id);
      adminState.regs = data;
      adminState.regsError = "";
      adminState.regsPoll.lastSig = registrationsPayloadSig(data);
    } catch (err) {
      adminState.regs = { registrations: [] };
      adminState.regsError = err?.message || "Unable to load registrations.";
      adminState.regsPoll.lastSig = registrationsPayloadSig(adminState.regs);
    } finally {
      adminState.regsLoading = false;
      renderAdmin();
    }
  }

  async function adminLoadJudges(eventId, { silent } = {}) {
    const id = String(eventId || "").trim();
    if (!id) return;
    adminState.judgesLoading = true;
    adminState.judgesError = "";
    if (!silent) renderAdmin();
    try {
      const data = await apiGet(`/admin/events/${encodeURIComponent(id)}/judges`);
      adminState.judges = data.judges || [];
    } catch (err) {
      adminState.judges = [];
      adminState.judgesError = err?.message || "Unable to load judges.";
    } finally {
      adminState.judgesLoading = false;
      renderAdmin();
    }
  }

  async function adminLoadFanVotePrompts(eventId, { silent = false } = {}) {
    const id = String(eventId || "").trim();
    if (!id) return;
    adminState.fanVotes.loading = true;
    adminState.fanVotes.error = "";
    if (!silent) renderAdmin();
    try {
      const data = await apiGet(`/admin/events/${encodeURIComponent(id)}/fan-vote/prompts`);
      adminState.fanVotes.prompts = Array.isArray(data?.prompts) ? data.prompts : [];
    } catch (error) {
      adminState.fanVotes.prompts = [];
      adminState.fanVotes.error = error?.message || "Unable to load fan vote prompts.";
    } finally {
      adminState.fanVotes.loading = false;
      if (!silent) renderAdmin();
    }
  }

  async function adminLoadFanVoteDetails(eventId, promptId, { votersPage = null } = {}) {
    const base = `/admin/events/${encodeURIComponent(eventId)}/fan-vote/prompts/${encodeURIComponent(promptId)}`;
    const [results, voters] = await Promise.all([
      apiGet(`${base}/results`),
      votersPage == null ? Promise.resolve(null) : apiGet(`${base}/voters?page=${encodeURIComponent(votersPage)}&limit=25`),
    ]);
    adminState.fanVotes.detailsById[promptId] = results;
    if (voters) adminState.fanVotes.votersByPrompt[promptId] = voters;
    return { results, voters };
  }

  function openFanVotePromptModal(eventId, prompt = null) {
    const editing = !!prompt;
    const modal = document.createElement("div");
    modal.className = "gl-ce-modal gl-ce-fan-vote-modal";
    modal.style.display = "flex";
    modal.style.alignItems = "center";
    modal.style.justifyContent = "center";
    modal.innerHTML = `
      <div class="gl-ce-modal__inner gl-ce-card gl-ce-fan-vote-modal__inner" role="dialog" aria-modal="true" aria-label="${editing ? "Edit Fan Vote" : "Create Fan Vote"}">
        <div class="gl-ce-row gl-ce-row-between">
          <h3 class="gl-ce-h2">${editing ? "Edit Fan Vote" : "Create Fan Vote"}</h3>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="fan-vote-modal-cancel" type="button">Close</button>
        </div>
        <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:10px;">
          <input class="gl-ce-input" name="title" maxlength="280" placeholder="Question *" value="${esc(prompt?.title || "")}" required />
          <textarea class="gl-ce-input gl-ce-textarea" name="public_note" rows="3" placeholder="Public note (optional)">${esc(prompt?.public_note || "")}</textarea>
          <textarea class="gl-ce-input gl-ce-textarea" name="private_note" rows="3" placeholder="Organizer note (optional)">${esc(prompt?.private_note || "")}</textarea>
          <label class="gl-ce-row" style="gap:8px;margin:0;width:100%;"><input name="public_results_enabled" type="checkbox" ${Number(prompt?.public_results_enabled || 0) ? "checked" : ""}> <span>Show public tallies</span></label>
          <div class="gl-ce-muted">All registered drivers are eligible. The current Worker does not support saving a prompt-specific driver subset.</div>
          ${editing ? "" : `<div class="gl-ce-muted">This prompt will open immediately. You can close or reopen it from the Fan Votes panel.</div>`}
        </div>
        <div class="gl-ce-row" style="margin-top:12px;justify-content:flex-end;">
          <button class="gl-ce-btn" data-action="admin-fan-vote-save" data-event-id="${esc(eventId)}" data-prompt-id="${esc(prompt?.id || "")}" type="button">${editing ? "Save Changes" : "Create Prompt"}</button>
        </div>
      </div>`;
    const close = () => modal.remove();
    modal.addEventListener("click", (event) => {
      if (event.target === modal || event.target.closest('[data-action="fan-vote-modal-cancel"]')) close();
    });
    // Keep the prompt modal inside the app root: the admin save action is
    // handled by the root's delegated click listener below. Appending this
    // modal to document.body made "Create Prompt" clicks invisible to that
    // listener, so the button appeared to do nothing.
    root.appendChild(modal);
  }

  async function adminRefreshSelectedEventStatus({ silent } = {}) {
    if (!adminState.selectedId) return;
    await adminRefreshEvents();
    if (!silent) renderAdmin();
  }

  function adminOpenAddDriverModal() {
    adminState.editingRegistration = null;
    adminState.addDriverModalOpen = true;
    renderAdmin();
  }

  function adminCloseAddDriverModal() {
    adminState.addDriverModalOpen = false;
    adminState.editingRegistration = null;
    renderAdmin();
  }

  function eventAccessFormHtml(staffSession = activeStaffSession()) {
    return `
      <div class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;">
        <h3 class="gl-ce-h2">Event Access</h3>
        <div class="gl-ce-muted">Enter an event ID or code and its Admin PIN. Any signed-in account with the current PIN receives admin access to that event only.</div>
        <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:8px;">
          <input class="gl-ce-input" id="glCeStaffEventLookup" placeholder="Event ID or code" value="${esc(staffSession?.event_id || "")}" />
          <input class="gl-ce-input" id="glCeStaffPin" placeholder="Admin PIN" inputmode="numeric" autocomplete="one-time-code" />
          <button class="gl-ce-btn gl-ce-btn-sm" data-action="staff-session-start" type="button">Enter Event Admin</button>
          ${staffSession ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="staff-session-revoke" type="button">Sign Out</button>` : ""}
        </div>
        <div class="gl-ce-muted" style="margin-top:7px;">${staffSession ? `Event access is active until ${esc(fmtDateLocalish(staffSession.expires_at))}.` : ""}</div>
      </div>
    `;
  }

  // -------- Admin request access (non-admin) ----------
  function renderAdminRequestAccess() {
    const loggedTxt = isLoggedIn ? "You are logged in, but not an admin." : "You are not logged in.";
    const staffSession = activeStaffSession();
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <h2 class="gl-ce-h">Admin Portal</h2>
        <div class="gl-ce-muted">${esc(loggedTxt)}</div>
        ${isLoggedIn ? eventAccessFormHtml(staffSession) : ""}
        <div style="margin-top:10px;">
          <button class="gl-ce-btn" data-action="admin-request-open" type="button">Request admin access to create events</button>
        </div>
        <div id="glCeAdminRequestWrap" style="margin-top:12px; display:none;"></div>
      </div>
    `;
  }

  function openAdminRequestForm() {
    const wrap = adminPane.querySelector("#glCeAdminRequestWrap");
    if (!wrap) return;
    wrap.style.display = "block";
    wrap.innerHTML = `
      <div class="gl-ce-card gl-ce-card-soft">
        <h3 class="gl-ce-h2">Request Admin Access</h3>
        <div class="gl-ce-muted">This will email <b>support@ghostlineclub.com</b>.</div>

        <div class="gl-ce-grid2" style="margin-top:10px;">
          <div>
            <label class="gl-ce-label">First name *</label>
            <input class="gl-ce-input" id="glCeReqFirst" />
          </div>
          <div>
            <label class="gl-ce-label">Last name *</label>
            <input class="gl-ce-input" id="glCeReqLast" />
          </div>
          <div>
            <label class="gl-ce-label">Email (must match login email) *</label>
            <input class="gl-ce-input" id="glCeReqEmail" type="email" value="${esc(customerEmail)}" />
            ${customerEmail ? `<div class="gl-ce-muted" style="margin-top:4px; font-size:12px;">Your login email: ${esc(customerEmail)}</div>` : ""}
          </div>
          <div>
            <label class="gl-ce-label">Organization *</label>
            <input class="gl-ce-input" id="glCeReqOrg" />
          </div>
          <div class="gl-ce-span2">
            <label class="gl-ce-label">Reason *</label>
            <textarea class="gl-ce-textarea" id="glCeReqReason"
              placeholder="In order to limit false, or spam events, we ask that admin requests are only requested by event organizers."
              onfocus="if(this.placeholder) this.placeholder=''"
              onblur="if(!this.value) this.placeholder='In order to limit false, or spam events, we ask that admin requests are only requested by event organizers.'"></textarea>
          </div>
        </div>

        <div class="gl-ce-row" style="margin-top:10px;">
          <button class="gl-ce-btn" data-action="admin-request-submit" type="button">Send Request</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-request-cancel" type="button">Cancel</button>
        </div>
      </div>
    `;
  }

  async function submitAdminRequest() {
    const f = adminPane.querySelector("#glCeReqFirst");
    const l = adminPane.querySelector("#glCeReqLast");
    const e = adminPane.querySelector("#glCeReqEmail");
    const o = adminPane.querySelector("#glCeReqOrg");
    const r = adminPane.querySelector("#glCeReqReason");

    const first = (f?.value || "").trim();
    const last = (l?.value || "").trim();
    const email = (e?.value || "").trim().toLowerCase();
    const org = (o?.value || "").trim();
    const reason = (r?.value || "").trim();

    fieldSetError(f, !first);
    fieldSetError(l, !last);
    const emailMismatch = customerEmail && email !== customerEmail;
    fieldSetError(e, !email || emailMismatch);
    fieldSetError(o, !org);
    fieldSetError(r, !reason);

    if (!first || !last || !email || !org || !reason) throw new Error("Please fill all required fields.");
    if (emailMismatch) throw new Error("Email must match your login email.");

    await apiPost("/admin/request-access", { first, last, email, organization: org, reason });
    toast("Request sent.", "ok");

    const wrap = adminPane.querySelector("#glCeAdminRequestWrap");
    if (wrap) {
      wrap.style.display = "none";
      wrap.innerHTML = "";
    }
  }

  // -------- Admin create event view ----------
  function renderAdminCreateView() {
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <h2 class="gl-ce-h">Create Event</h2>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button">Back</button>
        </div>

        <div class="gl-ce-muted">Fields marked with <b>*</b> are required.</div>

        <div class="gl-ce-grid2" style="margin-top:12px;">
          <div>
            <label class="gl-ce-label">Type *</label>
            <select class="gl-ce-input" id="glCeNewType">
              <option value="irl">IRL</option>
              <option value="virtual" selected>Virtual</option>
            </select>
          </div>
          <div>
            <label class="gl-ce-label">Event Name *</label>
            <input class="gl-ce-input" id="glCeNewEventName" placeholder="Example: Ghostline Winter Bash" />
          </div>

          <div>
            <label class="gl-ce-label">Organizer *</label>
            <input class="gl-ce-input" id="glCeNewOrganizer" placeholder="Ghostline Club" />
          </div>
          <div>
            <label class="gl-ce-label">Series</label>
            <select class="gl-ce-input" id="glCeNewSeries">${renderSeriesOptions(adminState.series.selectedId)}</select>
          </div>
          <div>
            <label class="gl-ce-label">Venue *</label>
            <input class="gl-ce-input" id="glCeNewVenue" placeholder="Track / Server / Location" />
          </div>

          <div id="glCeNewAddrWrap">
            <label class="gl-ce-label">Venue Address *</label>
            <input class="gl-ce-input" id="glCeNewVenueAddr" placeholder="Street, City, State" />
          </div>

          <div>
            <label class="gl-ce-label">Logo URL</label>
            <input class="gl-ce-input" id="glCeNewLogo" placeholder="https://..." />
          </div>

          <div>
            <label class="gl-ce-label">Registration closes (datetime + timezone) *</label>
            <input class="gl-ce-input" id="glCeNewRegClose" type="datetime-local" />
            <select class="gl-ce-input" id="glCeNewRegCloseTz" style="margin-top:6px;">
              ${renderTimezoneOptions()}
            </select>
          </div>
          <div>
            <label class="gl-ce-label">Event start (datetime + timezone) *</label>
            <input class="gl-ce-input" id="glCeNewStartAt" type="datetime-local" />
            <select class="gl-ce-input" id="glCeNewStartAtTz" style="margin-top:6px;">
              ${renderTimezoneOptions()}
            </select>
          </div>

          <div class="gl-ce-span2">
            <label class="gl-ce-label">Total number of drivers * <span class="gl-ce-muted">(2–64)</span></label>
            <div class="gl-ce-row" style="gap: 12px; align-items: center;">
              <input class="gl-ce-range" id="glCeNewMax" type="range" min="2" max="64" step="1" value="32" style="flex: 1;" />
              <div class="gl-ce-rangeval"><b id="glCeNewMaxVal">32</b></div>
            </div>
          </div>
          <div class="gl-ce-span2">
            <label class="gl-ce-row" style="gap:8px;"><input id="glCeNewRegCapEnabled" type="checkbox" /> Limit public registrations independently ${adminHelpIcon("registration-cap-create", "Sets a public sign-up limit without changing bracket capacity. Admins can still add drivers after the cap.")}</label>
            <input class="gl-ce-input" id="glCeNewRegCap" type="number" min="1" step="1" placeholder="Public registration cap" disabled style="margin-top:6px;" />
            <div class="gl-ce-muted">Admins can add drivers after this cap or the registration close.</div>
          </div>

          <div>
            <label class="gl-ce-label">Comp Event Code <span class="gl-ce-muted">(leave blank to auto-generate)</span></label>
            <input class="gl-ce-input" id="glCeNewCode" placeholder="GLC-ABC123" />
          </div>

          <div id="glCeNewGameWrap">
            <label class="gl-ce-label">Game *</label>
            <input class="gl-ce-input" id="glCeNewGame" placeholder="Assetto Corsa" />
          </div>

          <div id="glCeNewCarDLWrap">
            <label class="gl-ce-label">Car download link URL *</label>
            <input class="gl-ce-input" id="glCeNewCarDL" placeholder="https://..." />
          </div>

          <div id="glCeNewTrackDLWrap">
            <label class="gl-ce-label">Track download link URL *</label>
            <input class="gl-ce-input" id="glCeNewTrackDL" placeholder="https://..." />
          </div>

          <div>
            <label class="gl-ce-label">Prize 1st place</label>
            <input class="gl-ce-input" id="glCeNewP1" placeholder="Example: $250 + Trophy" />
          </div>
          <div>
            <label class="gl-ce-label">Prize 2nd place</label>
            <input class="gl-ce-input" id="glCeNewP2" placeholder="Example: $100" />
          </div>
          <div>
            <label class="gl-ce-label">Third place prize</label>
            <input class="gl-ce-input" id="glCeNewP3" placeholder="Example: $50" />
          </div>

          <div class="gl-ce-span2">
            <h4 class="gl-ce-h3" style="margin: 6px 0 0;">Judges (emails optional)</h4>
            <div class="gl-ce-muted">Judge codes are generated automatically after creation.</div>
            <div class="gl-ce-row gl-ce-row-wrap">
              <input class="gl-ce-input" id="glCeNewJudge1" placeholder="Judge 1 email (optional)" />
              <input class="gl-ce-input" id="glCeNewJudge2" placeholder="Judge 2 email (optional)" />
              <input class="gl-ce-input" id="glCeNewJudge3" placeholder="Judge 3 email (optional)" />
            </div>
          </div>

          <div class="gl-ce-span2">
            <label class="gl-ce-label">Special messages</label>
            <input class="gl-ce-input" id="glCeNewMsg" placeholder="Notes shown on the event card" />
          </div>
        </div>

        <div class="gl-ce-row" style="margin-top:12px;">
          <button class="gl-ce-btn" data-action="admin-create-submit" type="button">Create Event</button>
        </div>
      </div>
    `;

    const typeEl = adminPane.querySelector("#glCeNewType");
    const maxEl = adminPane.querySelector("#glCeNewMax");
    const maxVal = adminPane.querySelector("#glCeNewMaxVal");

    const syncConditional = () => {
      const type = String(typeEl?.value || "virtual").toLowerCase();
      const isVirtual = type === "virtual";
      const isIrl = type === "irl";

      const show = (id, on) => {
        const el = adminPane.querySelector(id);
        if (!el) return;
        el.style.display = on ? "" : "none";
      };

      show("#glCeNewAddrWrap", isIrl);
      show("#glCeNewGameWrap", isVirtual);
      show("#glCeNewCarDLWrap", isVirtual);
      show("#glCeNewTrackDLWrap", isVirtual);
    };

    typeEl?.addEventListener("change", syncConditional);
    maxEl?.addEventListener("input", () => {
      if (maxVal) maxVal.textContent = String(maxEl.value || "32");
    });
    const capEnabled = adminPane.querySelector("#glCeNewRegCapEnabled");
    const cap = adminPane.querySelector("#glCeNewRegCap");
    capEnabled?.addEventListener("change", () => { if (cap) cap.disabled = !capEnabled.checked; });
    syncConditional();
  }

  // Shared admin create/edit helper — keeps the timezone dropdown markup
  // identical between both views.
  function renderTimezoneOptions(selected) {
    const sel = String(selected || "").trim();
    return EVENT_TIMEZONES.map(
      ({ value, label }) =>
        `<option value="${esc(value)}"${value === sel ? " selected" : ""}>${esc(label)}</option>`
    ).join("");
  }

  async function adminCreateEventSubmit() {
    const typeEl = adminPane.querySelector("#glCeNewType");
    const eventNameEl = adminPane.querySelector("#glCeNewEventName");
    const organizerEl = adminPane.querySelector("#glCeNewOrganizer");
    const seriesEl = adminPane.querySelector("#glCeNewSeries");
    const venueEl = adminPane.querySelector("#glCeNewVenue");
    const addrEl = adminPane.querySelector("#glCeNewVenueAddr");
    const logoEl = adminPane.querySelector("#glCeNewLogo");
    const regCloseEl = adminPane.querySelector("#glCeNewRegClose");
    const startAtEl = adminPane.querySelector("#glCeNewStartAt");
    const maxEl = adminPane.querySelector("#glCeNewMax");
    const codeEl = adminPane.querySelector("#glCeNewCode");
    const gameEl = adminPane.querySelector("#glCeNewGame");
    const carDlEl = adminPane.querySelector("#glCeNewCarDL");
    const trackDlEl = adminPane.querySelector("#glCeNewTrackDL");
    const p1El = adminPane.querySelector("#glCeNewP1");
    const p2El = adminPane.querySelector("#glCeNewP2");
    const p3El = adminPane.querySelector("#glCeNewP3");
    const j1El = adminPane.querySelector("#glCeNewJudge1");
    const j2El = adminPane.querySelector("#glCeNewJudge2");
    const j3El = adminPane.querySelector("#glCeNewJudge3");
    const msgEl = adminPane.querySelector("#glCeNewMsg");

    const type = String(typeEl?.value || "virtual").toLowerCase();
    const isVirtual = type === "virtual";
    const isIrl = type === "irl";

    const event_name = (eventNameEl?.value || "").trim();
    const organizer = (organizerEl?.value || "").trim();
    const venue = (venueEl?.value || "").trim();
    const venue_address = (addrEl?.value || "").trim();
    const logo_url = (logoEl?.value || "").trim();
    const regCloseTzEl = adminPane.querySelector("#glCeNewRegCloseTz");
    const startAtTzEl = adminPane.querySelector("#glCeNewStartAtTz");
    const reg_close_at = (regCloseEl?.value || "").trim();
    const reg_close_tz = (regCloseTzEl?.value || "").trim() || "UTC";
    const start_at = (startAtEl?.value || "").trim();
    const start_at_tz = (startAtTzEl?.value || "").trim() || "UTC";
    const max_drivers = Number(maxEl?.value || 32);
    const registration_cap_enabled = !!adminPane.querySelector("#glCeNewRegCapEnabled")?.checked;
    const registration_cap = Number(adminPane.querySelector("#glCeNewRegCap")?.value || 0);
    const comp_code = (codeEl?.value || "").trim().toUpperCase();

    const game = (gameEl?.value || "").trim();
    const car_download_url = (carDlEl?.value || "").trim();
    const track_download_url = (trackDlEl?.value || "").trim();

    const p1 = (p1El?.value || "").trim();
    const p2 = (p2El?.value || "").trim();
    const p3 = (p3El?.value || "").trim();

    const special_message = (msgEl?.value || "").trim();

    // required validation + red highlight
    const missing = [];
    fieldSetError(eventNameEl, !event_name); if (!event_name) missing.push("Event Name");
    fieldSetError(organizerEl, !organizer); if (!organizer) missing.push("Organizer");
    fieldSetError(venueEl, !venue); if (!venue) missing.push("Venue");
    fieldSetError(regCloseEl, !reg_close_at); if (!reg_close_at) missing.push("Registration Ends");
    fieldSetError(startAtEl, !start_at); if (!start_at) missing.push("Event Start");
    fieldSetError(maxEl, !Number.isFinite(max_drivers) || max_drivers < 2 || max_drivers > 64); if (!Number.isFinite(max_drivers) || max_drivers < 2 || max_drivers > 64) missing.push("Total Drivers");
    if (registration_cap_enabled && (!Number.isInteger(registration_cap) || registration_cap < 1)) missing.push("Public registration cap");

    // Client-side guardrail only. The dropdown is constrained to entries from
    // EVENT_TIMEZONES (our own static list of IANA zones) and the backend
    // re-validates with its own Luxon import, so we just confirm the values
    // came from the allowed set rather than relying on a Luxon call here.
    // This keeps the form usable even if `assets/luxon.min.js` hasn't loaded
    // yet on the storefront.
    const tzAllowed = new Set(EVENT_TIMEZONES.map((t) => t.value));
    const tzBad = (tz) => tz && !tzAllowed.has(tz);
    if (tzBad(reg_close_tz)) {
      fieldSetError(regCloseTzEl, true);
      missing.push(`Registration close timezone (${reg_close_tz} is not a recognized option)`);
    } else {
      fieldSetError(regCloseTzEl, false);
    }
    if (tzBad(start_at_tz)) {
      fieldSetError(startAtTzEl, true);
      missing.push(`Event start timezone (${start_at_tz} is not a recognized option)`);
    } else {
      fieldSetError(startAtTzEl, false);
    }
    // `datetime-local` inputs always emit the canonical "YYYY-MM-DDTHH:mm"
    // form when non-empty. A simple shape check is enough here — the server
    // does the actual UTC conversion before storage.
    const datetimeShapeOk = (raw) => LOCAL_DATETIME_RE.test(String(raw || "").trim());
    if (reg_close_at && !datetimeShapeOk(reg_close_at)) {
      fieldSetError(regCloseEl, true);
      missing.push("Registration close date/time could not be parsed");
    }
    if (start_at && !datetimeShapeOk(start_at)) {
      fieldSetError(startAtEl, true);
      missing.push("Event start date/time could not be parsed");
    }

    if (isIrl) {
      fieldSetError(addrEl, !venue_address);
      if (!venue_address) missing.push("Venue Address");
    } else {
      fieldSetError(addrEl, false);
    }

    if (isVirtual) {
      fieldSetError(gameEl, !game); if (!game) missing.push("Game");
      fieldSetError(carDlEl, !car_download_url); if (!car_download_url) missing.push("Car download URL");
      fieldSetError(trackDlEl, !track_download_url); if (!track_download_url) missing.push("Track download URL");
    } else {
      fieldSetError(gameEl, false);
      fieldSetError(carDlEl, false);
      fieldSetError(trackDlEl, false);
    }

    if (missing.length) throw new Error(`Missing required: ${missing.join(", ")}`);

    const prizes = {};
    if (p1) prizes["1st"] = p1;
    if (p2) prizes["2nd"] = p2;
    if (p3) prizes["3rd"] = p3;

    const judge_emails = [j1El?.value, j2El?.value, j3El?.value].map((x) => String(x || "").trim() || null);

    const payload = {
      type,
      event_name,
      organizer,
      venue,
      venue_address: isIrl ? venue_address : null,
      logo_url: logo_url || null,
      reg_close_at,
      reg_close_tz,
      start_at,
      start_at_tz,
      max_drivers,
      registration_cap_enabled,
      registration_cap: registration_cap_enabled ? registration_cap : null,
      comp_code: comp_code || null,
      game: isVirtual ? game : null,
      car_download_url: isVirtual ? car_download_url : null,
      track_download_url: isVirtual ? track_download_url : null,
      prizes: Object.keys(prizes).length ? prizes : null,
      special_message: special_message || null,
      judge_emails: judge_emails.some(Boolean) ? judge_emails : null,
      series_id: String(seriesEl?.value || "").trim() || null,
    };

    const data = await apiPost("/admin/events", payload);
    toast(`Event created. Code: ${data.comp_code}`, "ok");

    await adminRefreshEvents();
    adminState.selectedId = String(data.event_id);
    adminState.page = "manage";
    adminState.editBracket = false;

    const ev = adminState.events.find((x) => String(x.id) === adminState.selectedId);
    if (ev?.comp_code) {
      await adminLoadBracketByCode(ev.comp_code, { silent: true });
      await adminLoadRegistrations(ev.id, { silent: true });
    }
    await adminLoadJudges(adminState.selectedId);
    renderAdmin();
  }

  // -------- Admin manage view ----------
  function renderAdminManageView() {
    const ev = adminState.selectedId
      ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
      : null;

    if (!ev) {
      adminPane.innerHTML = `
        <div class="gl-ce-card">
          <div class="gl-ce-row gl-ce-row-between">
            <h2 class="gl-ce-h">Admin Portal</h2>
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button">Back</button>
          </div>
          <div class="gl-ce-muted">Open an event to manage it.</div>
        </div>
      `;
      return;
    }

    const eventSeries = ev.series_id ? adminState.series.byId[String(ev.series_id)] : null;
    const st = normalizeStatus(ev.status);
    const storedStatus = normalizeStatus(ev.status_stored || ev.status);
    const locked = st === "live" || st === "complete";
    const isLiveOrComplete = st === "live" || st === "complete";
    // Driver removal is allowed in draft, open, and closed (matches backend gate).
    const canRemoveDriver = ["draft", "open", "closed"].includes(storedStatus)
      || ["draft", "open", "closed"].includes(st);
    const canGenerateBracketByStatus = storedStatus === "draft" || storedStatus === "open" || storedStatus === "closed";
    const canShuffleByStatus = ["draft", "open", "closed", "bracket_generated"].includes(storedStatus)
      && !isLiveOrComplete;
    const canConvertGeneratedToSimple = String(ev.bracket_mode || "") !== "manual"
      && !["complete", "completed", "qualifying"].includes(st)
      && !["complete", "completed", "qualifying"].includes(storedStatus)
      && Array.isArray(adminState.bracket?.matches)
      && adminState.bracket.matches.length > 0;
    const canDeleteEvent = st === "complete";
    const supportsBatchAddDrivers = false;
    const addDriverLabel = supportsBatchAddDrivers ? "Add Drivers" : "Add Driver";

    // Backend lock flag: blocks normal regenerate unless shuffle=true
    const bracketLocked = Number(ev.bracket_locked || 0) === 1;

    // Tooltip/help text for bracket lock state (pre-LIVE)
    const whyLockedTooltip = bracketLocked
      ? (canShuffleByStatus
        ? "Bracket is locked — Generate Random is disabled. Use Shuffle & Re-generate to re-roll."
        : "Bracket is locked — use Recover to clear the lock, then regenerate.")
      : "";

    const code = String(ev.comp_code || "");
    const evName = ev?.event_name != null ? String(ev.event_name).trim() : "";
    const evNameHtml = evName ? esc(evName) : `<span class="gl-ce-muted">(not set)</span>`;
    const judgeListQualifyingConfig = qualifyingDisplayConfig({
      mode: ev?.qualifying_mode,
      judge_roles: ev?.qualifying_judge_roles_json
        ? (() => { try { return JSON.parse(ev.qualifying_judge_roles_json); } catch (_) { return null; } })()
        : null,
    });

    const judgesHtml = adminState.judgesLoading
      ? `<div class="gl-ce-muted">Loading judges...</div>`
      : adminState.judgesError
        ? `<div class="gl-ce-warn">Failed to load judges: ${esc(adminState.judgesError)}</div>`
        : Array.isArray(adminState.judges) && adminState.judges.length
          ? `
            <ul class="gl-ce-list">
              ${adminState.judges
                .map((j) => {
                  const codeVal = String(j.judge_code || "");
                  const isEditing = !!adminState.judgeEditByCode[codeVal];
                  const slotLabel = judgeListQualifyingConfig.roleForSlot(j.slot);
                  const displayName = String(j.display_name || j.name || slotLabel).trim();
                  return `<li data-judge-row>
                    <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap" style="gap:8px;">
                      <div>
                        <b>${esc(slotLabel)}</b>: <code>${esc(codeVal)}</code>
                        ${j.email ? ` <span class="gl-ce-muted">• ${esc(j.email)}</span>` : ""}
                        <div class="gl-ce-muted">Display Name: ${esc(displayName || slotLabel)}</div>
                      </div>
                      <div class="gl-ce-row" style="gap:6px;" data-judge-code="${esc(codeVal)}" data-event-id="${esc(ev.id)}">
                        ${
                          isEditing
                            ? `
                              <input class="gl-ce-input" data-role="judge-display-name" placeholder="Display Name" value="${esc(String(j.display_name || j.name || ""))}" />
                              <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-judge-name-save" data-judge-code="${esc(codeVal)}" data-id="${esc(ev.id)}" type="button">Save</button>
                              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-judge-name-cancel" data-judge-code="${esc(codeVal)}" type="button">Cancel</button>
                            `
                            : `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-judge-name-edit" data-judge-code="${esc(codeVal)}" type="button" title="Edit Display Name">✏</button>`
                        }
                        <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-remove-judge" data-judge-code="${esc(codeVal)}" data-id="${esc(ev.id)}" type="button">Remove</button>
                      </div>
                    </div>
                  </li>`;
                })
                .join("")}
            </ul>
          `
          : `<div class="gl-ce-muted">No judges loaded.</div>`;

    const regsCount = registrationRows(adminState.regs).length;
    const editingDriver = adminState.editingRegistration;
    const adminPinResult = String(adminState.series.pinByEvent[ev.id] || "");

    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <div>
            <!-- Title: Event Name -->
            <div style="font-weight:800; font-size:20px; margin-top:2px;">
              ${esc(ev.event_name || eventDisplayName(ev, "Event"))}
            </div>

            <!-- Event Code -->
            <div class="gl-ce-muted" style="margin-top:6px;">Event Code</div>
            <div class="gl-ce-codebig"><code>${esc(code)}</code></div>

            <!-- Organizer -->
            <div style="font-size:16px; margin-top:6px; color:#666;">
              ${esc(ev.organizer || "")}
            </div>
          </div>
          <div class="gl-ce-row gl-ce-row-wrap">
            <button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="admin-back-home"
                    type="button">Back</button>

            <button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="admin-comp-view"
                    data-id="${esc(ev.id)}"
                    type="button">Open Comp View</button>
            <button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="admin-qualifying-view"
                    data-id="${esc(ev.id)}"
                    type="button">View &amp; Edit Qualifying</button>

            <button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="admin-download-flyer"
                    data-id="${esc(ev.id)}"
                    type="button">Download PDF</button>

            ${!activeStaffSession() ? `<button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="admin-pin-open"
                    type="button">Admin Pin</button>${adminHelpIcon("admin-pin-access", "Generate a PIN that grants any signed-in person admin access to this event only. Rotating or revoking the PIN immediately ends active PIN sessions.")}` : ""}
            ${activeStaffSession() ? `<button class="gl-ce-btn gl-ce-btn-ghost"
                    data-action="staff-session-revoke"
                    type="button">End Event Access</button>` : ""}

            <button class="gl-ce-btn gl-ce-btn-sm"
                    data-action="admin-edit-details"
                    data-id="${esc(ev.id)}"
                    type="button"
                    ${isLiveOrComplete ? "disabled" : ""}>
              Edit Details
            </button>
          </div>
        </div>

        <div class="gl-ce-muted">Status: <span class="gl-ce-pill gl-ce-pill-${esc(st)}">${esc(st)}</span>${
          hasManualReopenOverride(ev)
            ? ` <span class="gl-ce-pill" style="background:#ffe9b3;color:#7a4c00;border:1px solid #d8a25b;font-size:11px;" title="Admin reopened registration after the scheduled close time. Re-save the event with a new close time, or set status to Closed/Live/etc. to clear.">Reopened past close</span>`
            : ""
        }</div>

        <div class="gl-ce-grid2" style="margin-top:12px;">
          <div>
            <h4 class="gl-ce-h3">Status Controls ${adminHelpIcon("status-controls", "Controls the event lifecycle. Open accepts registrations, Closed stops them, Qualifying enables scoring, Live starts battles, and Complete locks the event.")}</h4>
            <div class="gl-ce-row gl-ce-row-wrap">
              ${[
                { status: "draft", label: "Draft" },
                { status: "open", label: "Open" },
                { status: "closed", label: "Close" },
              ].map(
                (s) =>
                  `<button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-set-status" data-id="${esc(
                    ev.id
                  )}" data-status="${esc(s.status)}" title="Set this event's status to ${esc(s.status)}" type="button">${esc(s.label)}</button>`
              ).join("")}
              <button class="gl-ce-btn gl-ce-btn-sm"
                data-action="admin-start-qualifying"
                data-id="${esc(ev.id)}"
                ${(storedStatus !== "open" && storedStatus !== "closed") ? "disabled" : ""}
                title="${(storedStatus !== "open" && storedStatus !== "closed") ? "Qualifying can only start when status is OPEN or CLOSED" : "Start qualifying mode"}"
                type="button">Qualifying</button>
              ${[
                { status: "live", label: "Live" },
                { status: "complete", label: "Complete" },
              ].map(
                (s) =>
                  `<button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-set-status" data-id="${esc(
                    ev.id
                  )}" data-status="${esc(s.status)}" title="Set this event's status to ${esc(s.status)}" type="button">${esc(s.label)}</button>`
              ).join("")}
              ${canDeleteEvent ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-delete-event" data-id="${esc(
                ev.id
              )}" data-event-id="${esc(ev.id)}" type="button">Delete</button>` : ""}
            </div>
            <div class="gl-ce-muted">Closed is automatic when registration time hits. Qualifying is the scoring step before Live. Setting status to Open after the close time keeps registration open until you change status or update the close time. Live from Qualifying opens the seeded bracket preview. Edits lock once LIVE.</div>
          </div>

          <div>
            <h4 class="gl-ce-h3">Bracket Controls ${adminHelpIcon("bracket-controls", "Generate a seeded bracket from qualifying results, or generate a random bracket that ignores qualifying. Recover clears a stuck pre-Live regeneration lock; Reset / Refresh clears every stored bracket batch so a new one can be made.")}</h4>
            <div class="gl-ce-row gl-ce-row-wrap" style="gap:8px;align-items:center;">
              <button class="gl-ce-btn gl-ce-btn-sm"
                data-action="admin-generate-qualifying-bracket"
                data-id="${esc(ev.id)}"
                ${(storedStatus !== "qualifying" && st !== "qualifying") ? "disabled" : ""}
                title="${(storedStatus !== "qualifying" && st !== "qualifying") ? "Set status to Qualifying first, then generate from qualifying results." : "Create a seeded bracket draft from saved qualifying results for review"}"
                type="button">Generate Bracket from Qualifying Results</button>
              <button class="gl-ce-btn gl-ce-btn-sm"
                data-action="admin-regenerate-bracket"
                data-id="${esc(ev.id)}"
                data-shuffle="false"
                ${!canGenerateBracketByStatus || bracketLocked ? "disabled" : ""}
                title="${!canGenerateBracketByStatus ? "Random bracket generation is only allowed when stored status is DRAFT, OPEN, or CLOSED." : (!isLiveOrComplete && bracketLocked ? esc(whyLockedTooltip) : "Create a randomly generated bracket that does not use qualifying results")}"
                type="button">Generate Random Bracket</button>
              <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-regenerate-bracket" data-id="${esc(
                ev.id
              )}" data-shuffle="true" ${!canShuffleByStatus ? "disabled" : ""} title="${!canShuffleByStatus ? "Shuffle is only available before the event is live." : "Randomize eligible drivers, then rebuild the bracket"}" type="button" style="background:#076251;color:#fff;">🔄 Shuffle & Re-generate</button>
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-refresh-bracket" data-code="${esc(
                code
              )}" title="Reload the latest saved bracket" type="button">Refresh</button>
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-bracket-recovery-refresh" data-id="${esc(
                ev.id
              )}" data-code="${esc(code)}" title="Clear every stored bracket batch and generation lock so a new bracket can be created" type="button">Reset / Refresh Bracket</button>
              ${canConvertGeneratedToSimple ? `<button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-convert-simple-bracket" data-id="${esc(ev.id)}" type="button">Convert to Simple Bracket</button>` : ""}
              ${!isLiveOrComplete && (ev.current_regen_id && String(ev.current_regen_id).trim()) ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-force-clear-regen-lock" data-id="${esc(ev.id)}" type="button">Recover</button>` : ""}
            </div>
            ${!isLiveOrComplete && bracketLocked ? `<div class="gl-ce-muted" style="margin-top:6px;" title="${esc(whyLockedTooltip)}">${canShuffleByStatus ? "Bracket is locked — use Shuffle & Re-generate to re-roll." : "Bracket is locked — use Recover to clear the lock, then regenerate."}</div>` : ""}
            <div class="gl-ce-muted">
              ${
                !canGenerateBracketByStatus && !canShuffleByStatus
                  ? `Bracket generation requires stored status DRAFT, OPEN, or CLOSED (current stored: ${esc(storedStatus)}).`
                  : locked
                    ? "Bracket locked (LIVE/COMPLETE)."
                    : bracketLocked
                      ? (canShuffleByStatus
                        ? "Generate Random is locked after the first generate. Use Shuffle & Re-generate to re-roll."
                        : "Bracket is locked — use Recover to clear the lock, then regenerate.")
                      : "Use Generate Bracket from Qualifying Results after Qualifying. Generate Random Bracket ignores qualifying scores. Shuffle randomizes driver order. If the bracket needs a manual fix, convert to Simple Bracket from Bracket Controls or Comp View."
              }
              ${
                !locked && bracketLocked
                  ? ` <span title="${esc(whyLockedTooltip)}" style="text-decoration:underline;cursor:help;">Why locked?</span>`
                  : ""
              }
            </div>
          </div>
        </div>

        ${st === "live" ? `
          <div class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;">
            <h4 class="gl-ce-h3">Before-battle recovery</h4>
            <div class="gl-ce-muted">If no battle has started, correct qualifying or rebuild the bracket from the saved qualifying results. This clears the active bracket and is audited.</div>
            <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:8px;">
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-active-bracket-reset" data-id="${esc(ev.id)}" title="Return to qualifying correction and rebuild a draft bracket; available only before battles begin" type="button">Correct Qualifying / Rebuild Draft</button>
            </div>
          </div>` : ""}

        ${!isLiveOrComplete ? `
        <div style="display:none !important;">
        <div style="margin-top:12px;">
          <h4 class="gl-ce-h3">Swap Drivers (pre-LIVE)</h4>
          <div class="gl-ce-card gl-ce-card-soft" id="glCeSwapDriversPanel">
            ${(() => {
              // Check if bracket is loaded
              if (!adminState.bracket || !Array.isArray(adminState.bracket.matches)) {
                if (ev.comp_code) {
                  return `
                    <div class="gl-ce-muted" style="margin-bottom:8px;">Load bracket to enable swap.</div>
                    <button class="gl-ce-btn gl-ce-btn-sm" id="glCeSwapLoadBracket" data-action="admin-load-bracket" data-code="${esc(ev.comp_code)}" type="button">Load bracket</button>
                  `;
                } else {
                  return `<div class="gl-ce-muted">Bracket code missing.</div>`;
                }
              }
              
              const matches = adminState.bracket.matches;
              const eligibleMatches = matches.filter(m => {
                const a = String(m.driver_a || "");
                const b = String(m.driver_b || "");
                return a && a !== "BYE" && b && b !== "BYE";
              });
              
              if (eligibleMatches.length === 0) {
                return `<div class="gl-ce-muted">No eligible matches (matches must have two non-BYE drivers).</div>`;
              }
              
              const matchOptions = eligibleMatches.map(m => {
                const aLabel = getDriverLabelFromMatch(m, "a");
                const bLabel = getDriverLabelFromMatch(m, "b");
                const matchShortId = String(m.id || "").substring(0, 8);
                return `<option value="${esc(m.id)}">Match ${esc(matchShortId)} — ${esc(aLabel)} vs ${esc(bLabel)}</option>`;
              }).join("");
              
              return `
                <div style="display:grid;gap:10px;">
                  <div>
                    <label style="display:block;margin-bottom:4px;font-weight:500;">Match:</label>
                    <select class="gl-ce-input" id="glCeSwapMatchSelect" style="width:100%;">
                      <option value="">Select a match...</option>
                      ${matchOptions}
                    </select>
                  </div>
                  <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                    <div>
                      <label style="display:block;margin-bottom:4px;font-weight:500;">Driver A:</label>
                      <select class="gl-ce-input" id="glCeSwapDriverA" disabled style="width:100%;">
                        <option value="">Select match first</option>
                      </select>
                    </div>
                    <div>
                      <label style="display:block;margin-bottom:4px;font-weight:500;">Driver B:</label>
                      <select class="gl-ce-input" id="glCeSwapDriverB" disabled style="width:100%;">
                        <option value="">Select match first</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style="display:block;margin-bottom:4px;font-weight:500;">Reason (5-250 chars):</label>
                    <input type="text" class="gl-ce-input" id="glCeSwapReason" placeholder="Enter reason for swap..." maxlength="250" style="width:100%;" />
                  </div>
                  <div>
                    <button class="gl-ce-btn gl-ce-btn-sm" id="glCeSwapSubmit" data-action="admin-swap-submit" data-eventid="${esc(ev.id)}" disabled type="button">Swap</button>
                    <span id="glCeSwapStatus" style="margin-left:8px;"></span>
                  </div>
                </div>
              `;
            })()}
          </div>
        </div>
        </div>
        ` : ""}

        <div class="gl-ce-grid2" style="margin-top:12px;">
          ${String(ev.bracket_mode || "") === "manual" && st === "live" ? "" : `<div>
            <h4 class="gl-ce-h3">Judges</h4>
            <div class="gl-ce-row">
              <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-load-judges" data-id="${esc(
                ev.id
              )}" type="button">Reload Judges</button>
            </div>
            <div style="margin-top:8px;">${judgesHtml}</div>
          </div>`}

          <div>
            <details class="gl-ce-details">
              <summary class="gl-ce-h3" style="cursor: pointer; user-select: none; display: flex; align-items: center; gap: 8px; color: #076251; border: 1px solid rgba(7, 98, 81, 0.35); border-radius: 12px; padding: 10px 12px; transition: background 0.15s ease;">
                <span>Registrations</span>
                <span class="gl-ce-muted" style="font-size: 12px; font-weight: normal;">
                  (${regsCount})
                </span>
                ${
                  adminState.regsError
                    ? `<span id="glCeAdmRegsErrorBadge" class="gl-ce-inline-error-badge" title="${esc(adminState.regsError)}">Fetch issue: ${esc(adminState.regsError)}</span>`
                    : `<span id="glCeAdmRegsErrorBadge" class="gl-ce-inline-error-badge" style="display:none;"></span>`
                }
              </summary>
              <div style="margin-top:8px;">
                <div class="gl-ce-row">
                  <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-load-regs" data-event-id="${esc(
                    ev.id
                  )}" type="button">Refresh</button>
                  <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-open-add-driver" data-event-id="${esc(
                    ev.id
                  )}" type="button">${esc(addDriverLabel)}</button>
                </div>
                <div id="glCeAdmRegsList" style="margin-top:8px;"></div>
              </div>
            </details>
          </div>
        </div>

        <div class="gl-ce-card gl-ce-card-soft" style="margin-top:12px;">
          <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
            <div>
              <h4 class="gl-ce-h3" style="margin:0;">Fan Votes ${adminHelpIcon("fan-votes", "Create event-wide driver polls that open immediately. Close or reopen them manually, and use Tallies & Audit to review results and voter records.")}</h4>
              <div class="gl-ce-muted">Create and run event-wide fan vote prompts. All registered drivers are eligible under the current API.</div>
            </div>
            <div class="gl-ce-row">
              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-refresh" data-event-id="${esc(ev.id)}" type="button">Refresh</button>
              <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-fan-vote-create" data-event-id="${esc(ev.id)}" type="button">Create Prompt</button>
            </div>
          </div>
          <div style="margin-top:10px;">
            ${adminState.fanVotes.loading
              ? `<div class="gl-ce-muted">Loading fan vote prompts…</div>`
              : adminState.fanVotes.error
                ? `<div class="gl-ce-warn">${esc(adminState.fanVotes.error)}</div>`
                : !Array.isArray(adminState.fanVotes.prompts)
                  ? `<div class="gl-ce-muted">Load prompts to manage fan voting.</div>`
                  : !adminState.fanVotes.prompts.length
                    ? `<div class="gl-ce-muted">No fan vote prompts have been created.</div>`
                    : adminState.fanVotes.prompts.map((prompt) => {
                      const detail = adminState.fanVotes.detailsById[prompt.id];
                      const voters = adminState.fanVotes.votersByPrompt[prompt.id];
                      const status = String(prompt.status || "closed");
                      return `<div style="border-top:1px solid #d9e4df;padding:12px 0;">
                        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
                          <div>
                            <b>${esc(prompt.title)}</b> <span class="gl-ce-pill">${esc(status)}</span>
                            ${Number(prompt.public_results_enabled || 0) ? `<span class="gl-ce-muted"> · Public tallies enabled</span>` : ""}
                          </div>
                          <div class="gl-ce-row gl-ce-row-wrap" style="gap:6px;">
                            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-edit" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" type="button">Edit</button>
                            ${status !== "open" && status !== "cancelled" ? `<button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-fan-vote-status" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" data-status="open" type="button">Open</button>` : ""}
                            ${status === "open" ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-status" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" data-status="close" type="button">Close</button>` : ""}
                            ${status !== "cancelled" ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-fan-vote-status" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" data-status="cancel" type="button">Cancel</button>` : ""}
                            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-details" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" type="button">Tallies &amp; Audit</button>
                          </div>
                        </div>
                        ${detail ? `<div class="gl-ce-grid2" style="margin-top:10px;">
                          <div><b>Live tallies</b>${detail.results?.length ? `<div class="gl-ce-tablewrap"><table class="gl-ce-table"><thead><tr><th>Driver</th><th>#</th><th>Votes</th></tr></thead><tbody>${detail.results.map((row) => `<tr><td>${esc(`${row.first_name || ""} ${row.last_name || ""}`.trim() || "Driver")}</td><td>${esc(row.driver_number || "—")}</td><td>${esc(row.votes || 0)}</td></tr>`).join("")}</tbody></table></div>` : `<div class="gl-ce-muted">No eligible drivers.</div>`}</div>
                          <div><b>Voter audit</b>${voters ? (voters.voters?.length ? `<div class="gl-ce-tablewrap"><table class="gl-ce-table"><thead><tr><th>Voter ID</th><th>Selection</th><th>When</th></tr></thead><tbody>${voters.voters.map((row) => `<tr><td><code>${esc(row.customer_id)}</code></td><td>${esc(`${row.first_name || ""} ${row.last_name || ""}`.trim() || "Driver")}</td><td>${esc(fmtDateLocalish(row.created_at))}</td></tr>`).join("")}</tbody></table></div>` : `<div class="gl-ce-muted">No votes recorded.</div>`) : `<div class="gl-ce-muted">Audit not loaded.</div>`}
                          ${voters ? `<div class="gl-ce-row" style="margin-top:8px;"><span class="gl-ce-muted">${esc(voters.total || 0)} total</span>${voters.page > 1 ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-voters-page" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" data-page="${esc(voters.page - 1)}" type="button">Previous</button>` : ""}${voters.page * voters.limit < voters.total ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-fan-vote-voters-page" data-event-id="${esc(ev.id)}" data-prompt-id="${esc(prompt.id)}" data-page="${esc(voters.page + 1)}" type="button">Next</button>` : ""}</div>` : ""}</div>
                        </div>` : ""}
                      </div>`;
                    }).join("")}
          </div>
        </div>

        ${
          adminState.addDriverModalOpen
            ? `<div class="gl-ce-modal" style="display:flex;align-items:center;justify-content:center;">
                <div class="gl-ce-modal__inner gl-ce-card">
                  <div class="gl-ce-row gl-ce-row-between">
                    <h3 class="gl-ce-h2">${editingDriver ? "Edit Driver" : esc(addDriverLabel)}</h3>
                    <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-close-add-driver" type="button">Close</button>
                  </div>
                  <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:10px;">
                    ${!editingDriver && eventSeries?.drivers?.length ? `<select class="gl-ce-input" id="glCeAdmSeriesDriver"><option value="">Quick-add series driver…</option>${eventSeries.drivers.map((driver) => `<option value="${esc(driver.id)}">${esc(`${driver.first_name} ${driver.last_name}`)}</option>`).join("")}</select>` : ""}
                    <input class="gl-ce-input" id="glCeAdmRegFirst" placeholder="First name *" value="${esc(editingDriver?.first_name || "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegLast" placeholder="Last name *" value="${esc(editingDriver?.last_name || "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegEmail" placeholder="Email *" type="email" value="${esc(editingDriver?.email || "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegCarNumber" placeholder="Car Number *" value="${esc(editingDriver?.car_number ?? editingDriver?.driver_number ?? "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegCity" placeholder="City (optional)" value="${esc(editingDriver?.city || "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegCar" placeholder="Car (optional)" value="${esc(editingDriver?.car || "")}" />
                    <input class="gl-ce-input" id="glCeAdmRegHp" placeholder="HP (optional)" type="number" min="0" value="${esc(editingDriver?.hp ?? "")}" />
                  </div>
                  <div class="gl-ce-row" style="margin-top:12px;justify-content:flex-end;">
                    <button class="gl-ce-btn" data-action="admin-add-registration" data-event-id="${esc(ev.id)}" type="button">${editingDriver ? "Save Driver" : esc(addDriverLabel)}</button>
                  </div>
                </div>
              </div>`
            : ""
        }

        ${
          !activeStaffSession() && adminState.adminPinPanelOpen
            ? `<div class="gl-ce-modal gl-ce-admin-pin-modal">
                <div class="gl-ce-modal__inner gl-ce-card" role="dialog" aria-modal="true" aria-labelledby="glCeAdminPinTitle">
                  <div class="gl-ce-row gl-ce-row-between">
                    <div>
                      <h3 class="gl-ce-h2" id="glCeAdminPinTitle">Admin Pin ${adminHelpIcon("admin-pin-modal", "This PIN grants any signed-in person admin access to this event only. It does not grant access to other events, series, or global admin tools.")}</h3>
                      <div class="gl-ce-muted">Share this PIN only with people who should manage this event. Rotating or revoking it immediately ends active PIN sessions.</div>
                    </div>
                    <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-pin-close" type="button">Close</button>
                  </div>
                  <div style="margin-top:14px;">
                    <div class="gl-ce-label">Current Pin</div>
                    <div class="gl-ce-codebig"><code>${esc(adminPinResult || "Not generated")}</code></div>
                    <div class="gl-ce-muted" style="margin-top:6px;">The active PIN remains available to event credential managers until it is rotated or revoked.</div>
                  </div>
                  <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:14px;">
                    <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-pin-generate" data-event-id="${esc(ev.id)}" type="button">Generate Pin</button>
                    <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-pin-rotate" data-event-id="${esc(ev.id)}" type="button">Rotate Pin</button>
                    <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-pin-revoke" data-event-id="${esc(ev.id)}" type="button">Revoke Pin</button>
                  </div>
                </div>
              </div>`
            : ""
        }

        <div class="gl-ce-bracket-card" style="margin-top:14px;">
          <div class="gl-ce-row gl-ce-row-between">
            <h4 class="gl-ce-h3" style="margin:0;">Bracket</h4>
            <div class="gl-ce-muted">Always visible (slots fill as drivers register).</div>
          </div>
          <div id="glCeAdmBracket" style="margin-top:10px;"></div>

        </div>
      </div>
    `;

    const adminPinModal = adminPane.querySelector(".gl-ce-admin-pin-modal");
    adminPinModal?.addEventListener("click", (event) => {
      if (event.target !== adminPinModal) return;
      adminState.adminPinPanelOpen = false;
      renderAdmin();
    });

    // bracket render
    const seriesDriverSelect = adminPane.querySelector("#glCeAdmSeriesDriver");
    seriesDriverSelect?.addEventListener("change", () => {
      const driver = eventSeries?.drivers?.find((item) => String(item.id) === String(seriesDriverSelect.value));
      if (!driver) return;
      const fill = (id, value) => { const input = adminPane.querySelector(id); if (input) input.value = value || ""; };
      fill("#glCeAdmRegFirst", driver.first_name);
      fill("#glCeAdmRegLast", driver.last_name);
      fill("#glCeAdmRegEmail", driver.email);
      fill("#glCeAdmRegCarNumber", driver.driver_number);
      fill("#glCeAdmRegCity", driver.city);
      fill("#glCeAdmRegCar", driver.car);
      fill("#glCeAdmRegHp", driver.hp);
    });

    const mount = adminPane.querySelector("#glCeAdmBracket");
    if (mount) {
      const currentMatchId = String(ev.current_match_id || "").trim() || pickCurrentMatchId(adminState.bracket);
      const maxDrivers = ev?.max_drivers || adminState.bracket?.max_drivers || 32;
      const matches = Array.isArray(adminState.bracket?.matches) ? adminState.bracket.matches : [];
      const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 0)).filter(Number.isFinite)) : 0;
      const totalRounds = (Number(adminState.bracket?.final_round) > 0) ? Number(adminState.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : undefined);
      renderBracketVisual(mount, adminState.bracket, {
        title: "Admin View",
        live: st === "live",
        lastUpdated: adminState.lastBracketAt,
        currentMatchId,
        totalRounds,
        max_drivers: maxDrivers,
        finalRound: Number(adminState.bracket?.final_round || 0) || undefined,
      });
    }

    // registrations render
    const regsList = adminPane.querySelector("#glCeAdmRegsList");
    const regsErrorBadge = adminPane.querySelector("#glCeAdmRegsErrorBadge");
    if (regsList) {
      if (regsErrorBadge) {
        if (adminState.regsError) {
          const reason = String(adminState.regsError || "Unknown registration fetch error.");
          regsErrorBadge.textContent = `Fetch issue: ${reason}`;
          regsErrorBadge.title = reason;
          regsErrorBadge.style.display = "inline-flex";
        } else {
          regsErrorBadge.textContent = "";
          regsErrorBadge.title = "";
          regsErrorBadge.style.display = "none";
        }
      }
      if (adminState.regsLoading) {
        regsList.innerHTML = `<div class="gl-ce-muted">Loading registrations...</div>`;
      } else if (adminState.regsError) {
        regsList.innerHTML = `<div class="gl-ce-warn">Failed to load registrations: ${esc(adminState.regsError)}</div>`;
      } else {
        const rows = registrationRows(adminState.regs);
        if (!rows.length) {
          regsList.innerHTML = `<div class="gl-ce-muted">No registrations yet.</div>`;
        } else {
          regsList.innerHTML = `
            <div class="gl-ce-tablewrap">
              <table class="gl-ce-table">
                <thead>
                  <tr data-event-id="${esc(ev.id)}">
                    <th>Name</th>
                    <th>Email</th>
                    <th>Car Number</th>
                    <th>City</th>
                    <th>Car</th>
                    <th>HP</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${rows
                    .map((d) => {
                      const name = `${d.first_name || ""} ${d.last_name || ""}`.trim() || "Driver";
                      const carNumber = d.car_number ?? d.driver_number ?? "—";
                      const created = d.created_at ? fmtDateLocalish(d.created_at) : "—";
                      const statusLabel = d.status || d.registration_status || "registered";
                      const hpValue = d.hp != null && String(d.hp).trim() !== "" ? String(d.hp) : "—";
                      return `
                        <tr>
                          <td>${esc(name)}</td>
                          <td>${esc(d.email || "—")}</td>
                          <td>${esc(String(carNumber))}</td>
                          <td>${esc(d.city || "—")}</td>
                          <td>${esc(d.car || "—")}</td>
                          <td>${esc(hpValue)}</td>
                          <td>${esc(String(statusLabel))}</td>
                          <td>${esc(created)}</td>
                          <td>
                            <span class="gl-ce-row" style="gap:6px;flex-wrap:wrap;">
                              <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-edit-registration" data-event-id="${esc(ev.id)}" data-regid="${esc(d.id)}" type="button">Edit</button>
                              ${canRemoveDriver ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-remove-registration" data-event-id="${esc(ev.id)}" data-regid="${esc(d.id)}" type="button">Remove</button>` : ""}
                            </span>
                          </td>
                        </tr>
                      `;
                    })
                    .join("")}
                </tbody>
              </table>
            </div>
          `;
        }
      }
    }

    // Wire up swap drivers UI
    if (!isLiveOrComplete) {
      const matchSelect = adminPane.querySelector("#glCeSwapMatchSelect");
      const driverASelect = adminPane.querySelector("#glCeSwapDriverA");
      const driverBSelect = adminPane.querySelector("#glCeSwapDriverB");
      const reasonInput = adminPane.querySelector("#glCeSwapReason");
      const submitBtn = adminPane.querySelector("#glCeSwapSubmit");

      const updateSwapSubmitButton = () => {
        const matchId = String(matchSelect?.value || "").trim();
        const driverAId = String(driverASelect?.value || "").trim();
        const driverBId = String(driverBSelect?.value || "").trim();
        const reason = String(reasonInput?.value || "").trim();
        
        const isValid = matchId && driverAId && driverBId && driverAId !== driverBId && reason.length >= 5 && reason.length <= 250;
        if (submitBtn) submitBtn.disabled = !isValid;
      };

      if (matchSelect) {
        matchSelect.addEventListener("change", () => {
          const matchId = String(matchSelect.value || "").trim();
          const matches = Array.isArray(adminState.bracket?.matches) ? adminState.bracket.matches : [];
          const match = matches.find(m => String(m.id) === matchId);

          if (match) {
            const a = String(match.driver_a || "");
            const b = String(match.driver_b || "");
            const aLabel = getDriverLabelFromMatch(match, "a");
            const bLabel = getDriverLabelFromMatch(match, "b");

            driverASelect.innerHTML = `
              <option value="${esc(a)}">${esc(aLabel)}</option>
              <option value="${esc(b)}">${esc(bLabel)}</option>
            `;
            driverBSelect.innerHTML = `
              <option value="${esc(a)}">${esc(aLabel)}</option>
              <option value="${esc(b)}">${esc(bLabel)}</option>
            `;
            driverASelect.disabled = false;
            driverBSelect.disabled = false;
            
            // Set initial values (driver A in slot A, driver B in slot B)
            driverASelect.value = a;
            driverBSelect.value = b;
            
            updateSwapSubmitButton();
          } else {
            driverASelect.innerHTML = '<option value="">Select match first</option>';
            driverBSelect.innerHTML = '<option value="">Select match first</option>';
            driverASelect.disabled = true;
            driverBSelect.disabled = true;
            submitBtn.disabled = true;
          }
        });
      }

      if (driverASelect && driverBSelect && reasonInput) {
        driverASelect.addEventListener("change", updateSwapSubmitButton);
        driverBSelect.addEventListener("change", updateSwapSubmitButton);
        reasonInput.addEventListener("input", updateSwapSubmitButton);
      }
    }

  }

  function renderAdminEditView() {
    const ev = adminState.events.find((e) => String(e.id) === String(adminState.edit?.eventId));
    if (!ev) {
      adminState.edit.eventId = null;
      adminState.page = "manage";
      renderAdmin();
      return;
    }
    // Show the time exactly as the admin originally entered it, in the
    // timezone they originally selected (so 5 PM ET reads back as 5 PM ET, not
    // some UTC-shifted value).
    const regCloseTz = String(ev.reg_close_tz || "").trim();
    const startAtTz = String(ev.start_at_tz || "").trim();
    const regCloseLocal = ev.reg_close_at ? toLocalInputValueInTz(ev.reg_close_at, regCloseTz) : "";
    const startAtLocal = ev.start_at ? toLocalInputValueInTz(ev.start_at, startAtTz) : "";
    const tzOptions = (selected) => renderTimezoneOptions(selected);
    let prizes1 = "", prizes2 = "", prizes3 = "";
    if (ev.prizes_json) {
      try {
        const p = typeof ev.prizes_json === "string" ? JSON.parse(ev.prizes_json) : ev.prizes_json;
        if (p["1st"]) prizes1 = p["1st"];
        if (p["2nd"]) prizes2 = p["2nd"];
        if (p["3rd"]) prizes3 = p["3rd"];
      } catch (_) {}
    }
    const trackUrl = ev.track_link || ev.track_download_url || "";
    let carUrl = "";
    if (ev.car_download_url) carUrl = ev.car_download_url;
    else if (ev.car_links_json) {
      try {
        const arr = typeof ev.car_links_json === "string" ? JSON.parse(ev.car_links_json) : ev.car_links_json;
        carUrl = Array.isArray(arr) && arr[0] ? arr[0] : "";
      } catch (_) {}
    }
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <h2 class="gl-ce-h">Edit Event Details</h2>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-edit-cancel" type="button">Cancel</button>
        </div>
        <div class="gl-ce-muted">Update event details. Edits are blocked when event is Live or Complete.</div>
        <div class="gl-ce-grid2" style="margin-top:12px;">
          <div>
            <label class="gl-ce-label">Type *</label>
            <select class="gl-ce-input" id="glCeNewType">
              <option value="irl" ${ev.type === "irl" ? "selected" : ""}>IRL</option>
              <option value="virtual" ${(ev.type || "virtual") === "virtual" ? "selected" : ""}>Virtual</option>
            </select>
          </div>
          <div>
            <label class="gl-ce-label">Event Name *</label>
            <input class="gl-ce-input" id="glCeNewEventName" placeholder="Example: Ghostline Winter Bash" value="${esc(ev.event_name || "")}" />
          </div>
          <div>
            <label class="gl-ce-label">Organizer *</label>
            <input class="gl-ce-input" id="glCeNewOrganizer" placeholder="Ghostline Club" value="${esc(ev.organizer || "")}" />
          </div>
          <div>
            <label class="gl-ce-label">Series</label>
            <select class="gl-ce-input" id="glCeNewSeries">${renderSeriesOptions(ev.series_id || "")}</select>
          </div>
          <div>
            <label class="gl-ce-label">Venue *</label>
            <input class="gl-ce-input" id="glCeNewVenue" placeholder="Track / Server / Location" value="${esc(ev.venue || "")}" />
          </div>
          <div id="glCeNewAddrWrap">
            <label class="gl-ce-label">Venue Address *</label>
            <input class="gl-ce-input" id="glCeNewVenueAddr" placeholder="Street, City, State" value="${esc(ev.venue_address || "")}" />
          </div>
          <div>
            <label class="gl-ce-label">Logo URL</label>
            <input class="gl-ce-input" id="glCeNewLogo" placeholder="https://..." value="${esc(ev.logo_url || "")}" />
          </div>
          <div>
            <label class="gl-ce-label">Registration closes (datetime + timezone) *</label>
            <input class="gl-ce-input" id="glCeNewRegClose" type="datetime-local" value="${esc(regCloseLocal)}" />
            <select class="gl-ce-input" id="glCeNewRegCloseTz" style="margin-top:6px;">
              ${tzOptions(regCloseTz)}
            </select>
          </div>
          <div>
            <label class="gl-ce-label">Event start (datetime + timezone) *</label>
            <input class="gl-ce-input" id="glCeNewStartAt" type="datetime-local" value="${esc(startAtLocal)}" />
            <select class="gl-ce-input" id="glCeNewStartAtTz" style="margin-top:6px;">
              ${tzOptions(startAtTz)}
            </select>
          </div>
          <div class="gl-ce-span2">
            <label class="gl-ce-label">Total number of drivers * <span class="gl-ce-muted">(2–64)</span></label>
            <div class="gl-ce-row" style="gap: 12px; align-items: center;">
              <input class="gl-ce-range" id="glCeNewMax" type="range" min="2" max="64" step="1" value="${Number(ev.max_drivers) || 32}" style="flex: 1;" />
              <div class="gl-ce-rangeval"><b id="glCeNewMaxVal">${Number(ev.max_drivers) || 32}</b></div>
            </div>
          </div>
          <div class="gl-ce-span2">
            <label class="gl-ce-row" style="gap:8px;"><input id="glCeNewRegCapEnabled" type="checkbox" ${Number(ev.registration_cap_enabled || 0) === 1 ? "checked" : ""} /> Limit public registrations independently ${adminHelpIcon("registration-cap-edit", "Sets a public sign-up limit without changing bracket capacity. Admins can still add drivers after the cap.")}</label>
            <input class="gl-ce-input" id="glCeNewRegCap" type="number" min="1" step="1" placeholder="Public registration cap" value="${Number(ev.registration_cap || 0) || ""}" ${Number(ev.registration_cap_enabled || 0) === 1 ? "" : "disabled"} style="margin-top:6px;" />
            <div class="gl-ce-muted">This controls public registrations only; it does not change the bracket max above.</div>
          </div>
          <div>
            <label class="gl-ce-label">Comp Event Code</label>
            <input class="gl-ce-input" id="glCeNewCode" placeholder="GLC-ABC123" value="${esc(ev.comp_code || "")}" />
          </div>
          <div id="glCeNewGameWrap">
            <label class="gl-ce-label">Game *</label>
            <input class="gl-ce-input" id="glCeNewGame" placeholder="Assetto Corsa" value="${esc(ev.game || "")}" />
          </div>
          <div id="glCeNewCarDLWrap">
            <label class="gl-ce-label">Car download link URL *</label>
            <input class="gl-ce-input" id="glCeNewCarDL" placeholder="https://..." value="${esc(carUrl)}" />
          </div>
          <div id="glCeNewTrackDLWrap">
            <label class="gl-ce-label">Track download link URL *</label>
            <input class="gl-ce-input" id="glCeNewTrackDL" placeholder="https://..." value="${esc(trackUrl)}" />
          </div>
          <div>
            <label class="gl-ce-label">Prize 1st place</label>
            <input class="gl-ce-input" id="glCeNewP1" placeholder="Example: $250 + Trophy" value="${esc(prizes1)}" />
          </div>
          <div>
            <label class="gl-ce-label">Prize 2nd place</label>
            <input class="gl-ce-input" id="glCeNewP2" placeholder="Example: $100" value="${esc(prizes2)}" />
          </div>
          <div>
            <label class="gl-ce-label">Third place prize</label>
            <input class="gl-ce-input" id="glCeNewP3" placeholder="Example: $50" value="${esc(prizes3)}" />
          </div>
          <div class="gl-ce-span2">
            <h4 class="gl-ce-h3" style="margin: 6px 0 0;">Judges (emails optional)</h4>
            <div class="gl-ce-row gl-ce-row-wrap">
              <input class="gl-ce-input" id="glCeNewJudge1" placeholder="Judge 1 email (optional)" />
              <input class="gl-ce-input" id="glCeNewJudge2" placeholder="Judge 2 email (optional)" />
              <input class="gl-ce-input" id="glCeNewJudge3" placeholder="Judge 3 email (optional)" />
            </div>
          </div>
          <div class="gl-ce-span2">
            <label class="gl-ce-label">Special messages</label>
            <input class="gl-ce-input" id="glCeNewMsg" placeholder="Notes shown on the event card" value="${esc(ev.special_message || "")}" />
          </div>
        </div>
        <div class="gl-ce-row" style="margin-top:12px;">
          <button class="gl-ce-btn" data-action="admin-edit-save" type="button">Save Changes</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-edit-cancel" type="button">Cancel</button>
        </div>
      </div>
    `;
    const typeEl = adminPane.querySelector("#glCeNewType");
    const maxEl = adminPane.querySelector("#glCeNewMax");
    const maxValEl = adminPane.querySelector("#glCeNewMaxVal");
    const show = (sel, on) => {
      const el = adminPane.querySelector(sel);
      if (el) el.style.display = on ? "" : "none";
    };
    const syncConditional = () => {
      const type = String(typeEl?.value || "virtual").toLowerCase();
      show("#glCeNewAddrWrap", type === "irl");
      show("#glCeNewGameWrap", type === "virtual");
      show("#glCeNewCarDLWrap", type === "virtual");
      show("#glCeNewTrackDLWrap", type === "virtual");
    };
    if (maxEl) maxEl.addEventListener("input", () => { if (maxValEl) maxValEl.textContent = maxEl.value; });
    const capEnabled = adminPane.querySelector("#glCeNewRegCapEnabled");
    const cap = adminPane.querySelector("#glCeNewRegCap");
    capEnabled?.addEventListener("change", () => { if (cap) cap.disabled = !capEnabled.checked; });
    if (typeEl) typeEl.addEventListener("change", syncConditional);
    syncConditional();
  }

  async function handleAdminEditSave() {
    const id = adminState.edit?.eventId;
    if (!id) return;
    const val = (sel) => String(root.querySelector(sel)?.value ?? "").trim();
    const p1 = val("#glCeNewP1"), p2 = val("#glCeNewP2"), p3 = val("#glCeNewP3");
    const prizes = {};
    if (p1) prizes["1st"] = p1;
    if (p2) prizes["2nd"] = p2;
    if (p3) prizes["3rd"] = p3;
    const payload = {
      type: val("#glCeNewType"),
      event_name: val("#glCeNewEventName"),
      organizer: val("#glCeNewOrganizer"),
      venue: val("#glCeNewVenue"),
      venue_address: val("#glCeNewVenueAddr"),
      logo_url: val("#glCeNewLogo"),
      reg_close_at: val("#glCeNewRegClose"),
      reg_close_tz: val("#glCeNewRegCloseTz") || "UTC",
      start_at: val("#glCeNewStartAt"),
      start_at_tz: val("#glCeNewStartAtTz") || "UTC",
      max_drivers: Number(val("#glCeNewMax") || 0),
      registration_cap_enabled: !!root.querySelector("#glCeNewRegCapEnabled")?.checked,
      registration_cap: root.querySelector("#glCeNewRegCapEnabled")?.checked ? Number(val("#glCeNewRegCap") || 0) || null : null,
      comp_code: val("#glCeNewCode"),
      game: val("#glCeNewGame"),
      car_download_url: val("#glCeNewCarDL"),
      track_download_url: val("#glCeNewTrackDL"),
      prizes: Object.keys(prizes).length ? prizes : null,
      special_message: val("#glCeNewMsg"),
      series_id: val("#glCeNewSeries") || null,
    };
    try {
      const priorSeriesId = String(adminState.events.find((event) => String(event.id) === String(id))?.series_id || "");
      if (priorSeriesId !== String(payload.series_id || "")) {
        const confirmed = await showConfirmModal({
          title: payload.series_id ? "Change event series" : "Remove event from series",
          message: payload.series_id
            ? "Changing the series affects which roster staff can be assigned to this event."
            : "Removing the series disconnects this event from its reusable roster.",
          confirmText: payload.series_id ? "Change Series" : "Remove Series",
          danger: !payload.series_id,
        });
        if (!confirmed) return;
      }
      await apiPost("/admin/events/" + encodeURIComponent(id) + "/update", payload);
      // Re-fetch from the server so that converted timestamps (raw wall-clock
      // -> UTC ISO) and the now-canonical tz fields land in adminState.events.
      try { await adminRefreshEvents(); } catch (_) {
        // Fall back to a local patch if the refresh failed for any reason.
        const ev = adminState.events.find((e) => String(e.id) === String(id));
        if (ev) Object.assign(ev, { event_name: payload.event_name, organizer: payload.organizer, venue: payload.venue, venue_address: payload.venue_address, logo_url: payload.logo_url, reg_close_tz: payload.reg_close_tz, start_at_tz: payload.start_at_tz, max_drivers: payload.max_drivers, registration_cap_enabled: payload.registration_cap_enabled ? 1 : 0, registration_cap: payload.registration_cap, comp_code: payload.comp_code, game: payload.game, track_link: payload.track_download_url, special_message: payload.special_message });
      }
      adminState.edit.eventId = null;
      adminState.page = "manage";
      renderAdmin();
      toast("Event updated.", "ok");
    } catch (err) {
      alert(err.message || "Failed to update event.");
    }
  }

  function renderBracketEditUI(mount, ev) {
    const matches = Array.isArray(adminState.bracket?.matches) ? adminState.bracket.matches : [];
    const regs = Array.isArray(adminState.regs?.drivers) ? adminState.regs.drivers : [];

    // edit only round 1 slots (simple + safe)
    const round1 = matches.filter((m) => Number(m.round || 0) === 1).sort((a, b) => Number(a.match_no) - Number(b.match_no));

    const options = [`<option value="">(empty)</option>`]
      .concat(
        regs.map((d) => {
          const name = `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.email || d.id;
          return `<option value="${esc(d.id)}">${esc(name)}</option>`;
        })
      )
      .join("");

    const curAssigned = new Set();
    for (const m of round1) {
      if (m.driver_a && m.driver_a !== "BYE") curAssigned.add(String(m.driver_a));
      if (m.driver_b && m.driver_b !== "BYE") curAssigned.add(String(m.driver_b));
    }

    mount.innerHTML = `
      <div class="gl-ce-card gl-ce-card-soft">
        <h4 class="gl-ce-h3">Edit Bracket (Round 1)</h4>
        <div class="gl-ce-muted">Assign drivers to slots. Changes reset downstream rounds.</div>

        <div class="gl-ce-tablewrap" style="margin-top:10px;">
          <table class="gl-ce-table">
            <thead>
              <tr>
                <th>Match</th>
                <th>Slot A</th>
                <th>Slot B</th>
                <th>Save</th>
              </tr>
            </thead>
            <tbody>
              ${round1
                .map((m) => {
                  const a = String(m.driver_a || "");
                  const b = String(m.driver_b || "");
                  return `
                    <tr>
                      <td><b>#${esc(displayMatchNo(m.match_no))}</b></td>
                      <td>
                        <select class="gl-ce-input gl-ce-input-sm" data-brset-round="1" data-brset-match="${esc(
                          m.match_no
                        )}" data-brset-slot="a">
                          ${options}
                        </select>
                      </td>
                      <td>
                        <select class="gl-ce-input gl-ce-input-sm" data-brset-round="1" data-brset-match="${esc(
                          m.match_no
                        )}" data-brset-slot="b">
                          ${options}
                        </select>
                      </td>
                      <td>
                        <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-bracket-save-row" data-matchid="${esc(
                          m.id
                        )}" data-matchno="${esc(m.match_no)}" data-eventid="${esc(ev.id)}" type="button">Save</button>
                      </td>
                    </tr>
                  `;
                })
                .join("")}
            </tbody>
          </table>
        </div>

        <div class="gl-ce-muted" style="margin-top:10px;">
          Tip: if you set the same driver twice, the system will keep them only in the last saved slot.
        </div>
      </div>
    `;

    // preselect values after render
    const selects = [...mount.querySelectorAll("select[data-brset-round]")];
    for (const sel of selects) {
      const matchNo = Number(sel.dataset.brsetMatch || 0);
      const slot = String(sel.dataset.brsetSlot || "");
      const m = round1.find((x) => Number(x.match_no) === matchNo);
      if (!m) continue;
      const v = slot === "a" ? String(m.driver_a || "") : String(m.driver_b || "");
      sel.value = v && v !== "BYE" ? v : "";
    }
  }

  // -------- Series roster (the current worker exposes create-only roster APIs;
  // records created in this browser remain available until the page reloads). ----------
  function selectedSeries() {
    return adminState.series.byId[adminState.series.selectedId] || null;
  }

  function renderSeriesOptions(selectedId) {
    const rows = Object.values(adminState.series.byId);
    return `<option value="">No series</option>${rows.map((series) =>
      `<option value="${esc(series.id)}"${String(series.id) === String(selectedId || "") ? " selected" : ""}>${esc(series.name)}</option>`
    ).join("")}`;
  }

  function recapPositionLabel(position) {
    const n = Number(position);
    return n === 1 ? "1st place" : n === 2 ? "2nd place" : n === 3 ? "3rd place" : "No final podium result";
  }

  function renderSeriesDriverRecap() {
    const recap = adminState.series.recap;
    if (adminState.series.recapLoading) {
      return `<div class="gl-ce-card gl-ce-card-soft" style="margin-top:16px;"><div class="gl-ce-muted">Loading driver recap…</div></div>`;
    }
    if (adminState.series.recapError) {
      return `<div class="gl-ce-card gl-ce-card-soft" style="margin-top:16px;"><div class="gl-ce-warn">Could not load driver recap: ${esc(adminState.series.recapError)}</div></div>`;
    }
    if (!recap?.driver) return "";
    const driver = recap.driver;
    const events = Array.isArray(recap.events) ? recap.events : [];
    const imageSrc = driverImageSrc(driver.image_url);
    const image = imageSrc
      ? `<img src="${esc(imageSrc)}" alt="${esc(driver.name || "Driver")}" style="width:76px;height:76px;border-radius:50%;object-fit:cover;border:1px solid #ddd;" />`
      : `<div style="width:76px;height:76px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:#eee;color:#666;font-weight:800;">${esc((driver.first_name || "?").slice(0, 1))}</div>`;
    return `
      <div class="gl-ce-card gl-ce-card-soft" style="margin-top:16px;">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <div class="gl-ce-row" style="gap:12px;">${image}<div><h3 class="gl-ce-h2" style="margin:0;">${esc(driver.name || `${driver.first_name || ""} ${driver.last_name || ""}`)}</h3><div class="gl-ce-muted">${driver.driver_number ? `#${esc(driver.driver_number)} · ` : ""}${esc(driver.car || "Driver recap")} ${driver.city ? `· ${esc(driver.city)}` : ""}</div></div></div>
          <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-series-recap-close" type="button">Close Recap</button>
        </div>
        ${events.length ? events.map((entry) => {
          const qualifying = entry.qualifying || {};
          const runs = Array.isArray(qualifying.runs) ? qualifying.runs : [];
          const matches = Array.isArray(entry.matches) ? entry.matches : [];
          return `<div style="margin-top:14px;padding-top:14px;border-top:1px solid #ddd;">
            <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
              <div><b>${esc(entry.event?.name || "Event")}</b> ${entry.event?.comp_code ? `<code>${esc(entry.event.comp_code)}</code>` : ""}<div class="gl-ce-muted">${entry.event?.start_at ? esc(fmtDateLocalish(entry.event.start_at)) : ""} ${entry.event?.status ? `· ${esc(entry.event.status)}` : ""}</div></div>
              <span class="gl-ce-pill">${esc(recapPositionLabel(entry.result?.final_position))}</span>
            </div>
            <div class="gl-ce-grid2" style="margin-top:10px;">
              <div><h4 class="gl-ce-h3">Qualifying</h4>
                <div class="gl-ce-muted">Rank: ${qualifying.rank ?? "—"} · Best: ${qualifying.best_score ?? "—"} · Second: ${qualifying.second_best_score ?? "—"}${qualifying.dnf ? " · DNF" : ""}</div>
                ${runs.length ? `<ul class="gl-ce-list" style="margin-top:6px;">${runs.map((run) => `<li>Run ${esc(run.run_no)}: ${run.avg_score ?? "—"} <span class="gl-ce-muted">(${esc(run.status || "pending")}${run.locked_at ? " · locked" : ""})</span></li>`).join("")}</ul>` : `<div class="gl-ce-muted" style="margin-top:6px;">No qualifying runs recorded.</div>`}
              </div>
              <div><h4 class="gl-ce-h3">Battles</h4>
                ${matches.length ? `<ul class="gl-ce-list">${matches.map((match) => `<li>R${esc(match.round)} · Battle ${esc(match.battle_no)} vs ${esc(match.opponent?.name || "BYE / TBD")} — <b>${esc(match.outcome || match.status || "pending")}</b>${Number(match.omt_count || 0) ? ` · OMT ×${esc(match.omt_count)}` : ""}${match.win_method ? ` · ${esc(match.win_method)}` : ""}</li>`).join("")}</ul>` : `<div class="gl-ce-muted">No battles recorded.</div>`}
              </div>
            </div>
          </div>`;
        }).join("") : `<div class="gl-ce-muted" style="margin-top:14px;">No event history is available for this driver. Recaps match the series roster email to event registrations.</div>`}
      </div>
    `;
  }

  function showSeriesDriverEditModal(driver) {
    return new Promise((resolve) => {
      const modal = document.createElement("div");
      modal.className = "gl-ce-modal";
      modal.style.display = "flex";
      modal.style.alignItems = "center";
      modal.style.justifyContent = "center";
      modal.style.position = "fixed";
      modal.style.inset = "0";
      modal.style.zIndex = "9999";
      modal.innerHTML = `
        <div class="gl-ce-modal__inner gl-ce-card" style="max-width:720px;width:calc(100% - 32px);">
          <h3 class="gl-ce-h2" style="margin:0;">Edit Series Driver</h3>
          <form data-series-driver-edit-form>
            <div class="gl-ce-grid2" style="margin-top:12px;">
              <div><label class="gl-ce-label">First name *</label><input class="gl-ce-input" name="first_name" required value="${esc(driver.first_name || "")}" /></div>
              <div><label class="gl-ce-label">Last name *</label><input class="gl-ce-input" name="last_name" required value="${esc(driver.last_name || "")}" /></div>
              <div><label class="gl-ce-label">Email</label><input class="gl-ce-input" name="email" type="email" value="${esc(driver.email || "")}" /></div>
              <div><label class="gl-ce-label">Car number</label><input class="gl-ce-input" name="driver_number" value="${esc(driver.driver_number || "")}" /></div>
              <div><label class="gl-ce-label">City</label><input class="gl-ce-input" name="city" value="${esc(driver.city || "")}" /></div>
              <div><label class="gl-ce-label">Car</label><input class="gl-ce-input" name="car" value="${esc(driver.car || "")}" /></div>
              <div><label class="gl-ce-label">HP</label><input class="gl-ce-input" name="hp" type="number" min="0" value="${esc(driver.hp ?? "")}" /></div>
              <div><label class="gl-ce-label">Upload image</label><input class="gl-ce-input" name="image_file" type="file" accept="image/jpeg,image/png,image/webp,image/gif" /></div>
              <div><label class="gl-ce-label">External image URL (optional fallback)</label><input class="gl-ce-input" name="image_url" value="${esc(isOwnedDriverImageReference(driver.image_url) ? "" : (driver.image_url || ""))}" /></div>
            </div>
            <div class="gl-ce-row" style="margin-top:16px;justify-content:flex-end;">
              <button class="gl-ce-btn gl-ce-btn-ghost" data-action="series-driver-edit-cancel" type="button">Cancel</button>
              <button class="gl-ce-btn" type="submit">Save Driver</button>
            </div>
          </form>
        </div>
      `;
      const cleanup = (result) => {
        modal.remove();
        resolve(result);
      };
      modal.querySelector("[data-action='series-driver-edit-cancel']")?.addEventListener("click", () => cleanup(null));
      modal.querySelector("form")?.addEventListener("submit", (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const value = (name) => String(form.get(name) || "").trim();
        const payload = {
          first_name: value("first_name"),
          last_name: value("last_name"),
          email: value("email") || null,
          driver_number: value("driver_number") || null,
          city: value("city") || null,
          car: value("car") || null,
          hp: value("hp") || null,
          image_url: value("image_url") || (isOwnedDriverImageReference(driver.image_url) ? driver.image_url : null),
          imageFile: form.get("image_file"),
        };
        if (!payload.first_name || !payload.last_name) return;
        cleanup(payload);
      });
      (adminPane?.isConnected ? adminPane : document.body).appendChild(modal);
    });
  }

  function renderAdminSeriesView() {
    const series = selectedSeries();
    const drivers = series?.drivers || [];
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <div><h2 class="gl-ce-h" style="margin:0;">Series</h2><div class="gl-ce-muted">Create a reusable driver roster for event registrations.</div></div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button">Back</button>
        </div>
        <div class="gl-ce-grid2" style="margin-top:12px;">
          <div><label class="gl-ce-label">Series name *</label><input class="gl-ce-input" id="glCeSeriesName" placeholder="2026 Drift Series" /></div>
          <div><label class="gl-ce-label">Description</label><input class="gl-ce-input" id="glCeSeriesDescription" placeholder="Optional" /></div>
        </div>
        <div class="gl-ce-row" style="margin-top:10px;"><button class="gl-ce-btn" data-action="admin-series-create" type="button">Create Series</button></div>
        <div style="margin-top:16px;">
          <label class="gl-ce-label">Selected series</label>
          <select class="gl-ce-input" id="glCeSeriesSelect">${renderSeriesOptions(adminState.series.selectedId)}</select>
        </div>
        ${series ? `
          <div class="gl-ce-card gl-ce-card-soft" style="margin-top:16px;">
            <h3 class="gl-ce-h2">Series Details</h3>
            <div class="gl-ce-grid2">
              <div><label class="gl-ce-label">Series name *</label><input class="gl-ce-input" id="glCeSeriesEditName" value="${esc(series.name || "")}" /></div>
              <div><label class="gl-ce-label">Description</label><input class="gl-ce-input" id="glCeSeriesEditDescription" value="${esc(series.description || "")}" /></div>
            </div>
            <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:8px;"><button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-series-save-details" type="button">Save Series Details</button><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-series-delete" type="button">Delete Series</button></div>
          </div>
          <div class="gl-ce-card gl-ce-card-soft" style="margin-top:16px;">
            <h3 class="gl-ce-h2">Series Admins</h3>
            <div class="gl-ce-muted">Series admins can manage this series and its assigned events. Add them by Shopify customer ID.</div>
            <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:8px;">
              <input class="gl-ce-input" id="glCeSeriesAdminCustomerId" placeholder="Shopify customer ID" />
              <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-series-admin-add" type="button">Add Series Admin</button>
            </div>
            <div class="gl-ce-list" style="margin-top:8px;">
              ${(series.admins || []).length ? series.admins.map((admin) => `<div class="gl-ce-row gl-ce-row-between" style="margin-top:5px;"><span><code>${esc(admin.customer_id)}</code><span class="gl-ce-muted"> · added ${admin.created_at ? esc(fmtDateLocalish(admin.created_at)) : "—"}</span></span><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-series-admin-remove" data-customer-id="${esc(admin.customer_id)}" type="button">Remove</button></div>`).join("") : `<div class="gl-ce-muted">No series admins loaded.</div>`}
            </div>
          </div>
          <div style="margin-top:16px;">
            <div class="gl-ce-card gl-ce-card-soft">
              <h3 class="gl-ce-h2">Drivers</h3>
              <div class="gl-ce-row gl-ce-row-wrap">
                <input class="gl-ce-input" id="glCeSeriesDriverFirst" placeholder="First name *" />
                <input class="gl-ce-input" id="glCeSeriesDriverLast" placeholder="Last name *" />
                <input class="gl-ce-input" id="glCeSeriesDriverEmail" placeholder="Email" type="email" />
                <input class="gl-ce-input" id="glCeSeriesDriverNumber" placeholder="Car number" />
                <input class="gl-ce-input" id="glCeSeriesDriverCity" placeholder="City" />
                <input class="gl-ce-input" id="glCeSeriesDriverCar" placeholder="Car" />
                <input class="gl-ce-input" id="glCeSeriesDriverHp" placeholder="HP" type="number" min="0" />
                <input class="gl-ce-input" id="glCeSeriesDriverImageFile" type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label="Upload driver image" />
                <input class="gl-ce-input" id="glCeSeriesDriverImage" placeholder="External image URL (optional fallback)" />
              </div>
              <button class="gl-ce-btn gl-ce-btn-sm" style="margin-top:8px;" data-action="admin-series-driver-create" type="button">Add Driver</button>
              <div class="gl-ce-muted" style="margin-top:10px;">${drivers.length ? drivers.map((driver) => `<div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap" style="margin-top:5px;"><span>${esc(`${driver.first_name} ${driver.last_name}`)}${driver.driver_number ? ` · #${esc(driver.driver_number)}` : ""}</span><div class="gl-ce-row" style="gap:6px;"><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-series-driver-recap" data-driver-id="${esc(driver.id)}" type="button">Recap & History</button><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-series-driver-edit" data-driver-id="${esc(driver.id)}" type="button">Edit</button><button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-series-driver-remove" data-driver-id="${esc(driver.id)}" type="button">Remove</button></div></div>`).join("") : "No drivers yet."}</div>
            </div>
          </div>
          ${renderSeriesDriverRecap()}
        ` : `<div class="gl-ce-muted" style="margin-top:16px;">Create a series to build its driver roster.</div>`}
      </div>
    `;
    adminPane.querySelector("#glCeSeriesSelect")?.addEventListener("change", async (event) => {
      adminState.series.selectedId = String(event.target.value || "");
      adminState.series.recap = null;
      adminState.series.recapError = "";
      try {
        await adminRefreshSeries({ includeSelectedDetail: true });
      } catch (err) {
        toast(err?.message || "Unable to load series.", "err");
      }
      renderAdmin();
    });
  }

  function renderAdminEventAccessView() {
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between gl-ce-row-wrap">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Event Access</h2>
            <div class="gl-ce-muted">Use an event's Admin PIN to open event-scoped admin controls.</div>
          </div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button">Back</button>
        </div>
        ${eventAccessFormHtml()}
      </div>
    `;
  }

  // -------- Admin home list view ----------
  function renderAdminHomeView() {
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Create events, manage status, generate brackets, and edit details</h2>
            <div class="gl-ce-muted">Admin tools for competition events.</div>
          </div>
          <button class="gl-ce-btn" data-action="admin-open-create" type="button">Create Event</button>
        </div>

        <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:10px;gap:8px;">
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-open-series" type="button">Series</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-open-event-access" type="button">Event Access</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-refresh-events" type="button">Refresh My Events</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-check-super-admin" type="button" style="border-color:#076251;color:#076251;">🔑 Super Admin</button>
        </div>

        <div id="glCeAdminEvents" style="margin-top:10px;"></div>

        ${adminState.isSuperAdmin ? `
        <div class="gl-ce-card" style="margin-top:16px; border-color:#076251;">
          <h3 class="gl-ce-h2" style="margin:0 0 8px 0;">🔧 Super Admin Tools</h3>
          <div class="gl-ce-muted" style="margin-bottom:10px;">Repair next_match_id (r-m keys → UUIDs) across all events with broken rows.</div>
          <div class="gl-ce-row gl-ce-row-wrap" style="gap:8px;align-items:center;">
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="maintenance-preview" type="button" ${adminState.maintenance.loading ? "disabled" : ""}>Preview Repairs</button>
            <input class="gl-ce-input" id="glCeMaintenanceConfirm" type="text" placeholder="Type REPAIR_NEXT_MATCH_ID to enable Apply" value="${esc(adminState.maintenance.confirmText || "")}" style="width:280px;" data-action="maintenance-confirm-input" />
            <button class="gl-ce-btn" data-action="maintenance-apply" type="button" ${adminState.maintenance.loading || adminState.maintenance.confirmText !== "REPAIR_NEXT_MATCH_ID" ? "disabled" : ""}>Apply Repairs</button>
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="maintenance-copy-report" type="button" ${!adminState.maintenance.lastResult ? "disabled" : ""}>Copy Report</button>
          </div>
          ${adminState.maintenance.error ? `<div class="gl-ce-muted" style="color:#c00;margin-top:8px;">${esc(adminState.maintenance.error)}</div>` : ""}
          ${adminState.maintenance.lastResult ? `
          <div class="gl-ce-tablewrap" style="margin-top:12px;">
            <table class="gl-ce-table">
              <thead>
                <tr>
                  <th>Event ID</th>
                  <th>Broken</th>
                  <th>Fixable</th>
                  <th>Unresolved</th>
                  <th>Unresolved sample</th>
                </tr>
              </thead>
              <tbody>
                ${(adminState.maintenance.lastResult.per_event || []).map((pe) => `
                  <tr>
                    <td><code>${esc(String(pe.event_id).slice(0, 8))}…</code></td>
                    <td>${pe.bad_count}</td>
                    <td>${pe.updated_count}</td>
                    <td>${pe.skipped_count}</td>
                    <td>${(pe.unresolved_sample || []).map((u) => esc(u.reason)).join("; ") || "—"}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
            <div class="gl-ce-muted" style="margin-top:6px;">Applied: ${adminState.maintenance.lastResult.applied ? "Yes" : "No"} · Events: ${adminState.maintenance.lastResult.events_processed} · Total updated: ${adminState.maintenance.lastResult.total_updated_count} · Total skipped: ${adminState.maintenance.lastResult.total_skipped_count}</div>
          </div>
          ` : ""}
        </div>
        ` : ""}
      </div>
    `;

    const mount = adminPane.querySelector("#glCeAdminEvents");
    if (!mount) return;

    const loadErrorBanner = adminState.eventsLoadError
      ? `<div class="gl-ce-warn">${esc(adminState.eventsLoadError)}</div>`
      : "";
    if (!adminState.events.length) {
      mount.innerHTML = adminState.eventsLoadError
        ? `${loadErrorBanner}<div class="gl-ce-muted" style="margin-top:8px;">Events could not be loaded. Click Refresh My Events to retry.</div>`
        : `<div class="gl-ce-muted">No events yet.</div>`;
      return;
    }

    mount.innerHTML = `
      ${loadErrorBanner}
      <div class="gl-ce-tablewrap">
        <table class="gl-ce-table">
          <thead>
            <tr>
              <th>Start</th>
              <th>Code</th>
              <th>Event</th>
              <th>Type</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${adminState.events
              .map((e) => {
                const st = normalizeStatus(e.computed_status || e.status);
                const canDeleteFromHome = st === "complete";
                const winners = parseMaybeJson(e.winners_json) || null;
                const hasWinners = winners && (winners.first || winners.second || winners.third);

                return `
                  <tr>
                    <td>${esc(fmtDateInTz(e.start_at, e.start_at_tz))}</td>
                    <td><code>${esc(e.comp_code || "")}</code></td>
                    <td>
                      <b>${esc(eventDisplayName(e, "Event"))}</b>
                      ${e.venue ? `<div class="gl-ce-muted">${esc(e.venue)}</div>` : ""}
                      ${
                        st === "complete" && hasWinners
                          ? `<div class="gl-ce-muted" style="margin-top:6px;">
                              <div><b>1st:</b> ${esc(winners.first || "—")}</div>
                              <div><b>2nd:</b> ${esc(winners.second || "—")}</div>
                              <div><b>3rd:</b> ${esc(winners.third || "—")}</div>
                             </div>`
                          : ""
                      }
                    </td>
                    <td>${esc(String(e.type || "").toLowerCase())}</td>
                    <td><span class="gl-ce-pill gl-ce-pill-${esc(st)}">${esc(st)}</span></td>
                    <td class="gl-ce-actions">
                      <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-open" data-id="${esc(e.id)}" type="button">Open</button>
                      ${
                        st === "live"
                          ? `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="admin-comp-view" data-id="${esc(
                              e.id
                            )}" type="button">Comp View</button>`
                          : ``
                      }
                      <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost"
                              data-action="admin-qualifying-view"
                              data-id="${esc(e.id)}"
                              type="button">View &amp; Edit Qualifying</button>
                      ${
                        canDeleteFromHome
                          ? `<button
                              class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger"
                              data-action="admin-delete-event"
                              data-id="${esc(e.id)}"
                              data-event-id="${esc(e.id)}"
                              title="Delete this event"
                              type="button"
                            >Delete</button>`
                          : ""
                      }
                    </td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    `;

    const confirmInput = adminPane.querySelector("#glCeMaintenanceConfirm");
    const applyBtn = adminPane.querySelector("[data-action=maintenance-apply]");
    if (confirmInput && applyBtn) {
      confirmInput.addEventListener("input", () => {
        adminState.maintenance.confirmText = (confirmInput.value || "").trim();
        applyBtn.disabled = adminState.maintenance.loading || adminState.maintenance.confirmText !== "REPAIR_NEXT_MATCH_ID";
      });
    }
  }

  // -------- Super Admin Views ----------
  async function renderSuperAdminView() {
    const isSuperAdmin = await checkSuperAdminStatus();
    
    if (!isSuperAdmin) {
      adminPane.innerHTML = `
        <div class="gl-ce-card">
          <h2 class="gl-ce-h">Super Admin Access</h2>
          <div class="gl-ce-muted" style="margin-top:10px;">You do not have super admin privileges.</div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button" style="margin-top:10px;">Back</button>
        </div>
      `;
      return;
    }

    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Super Admin Portal</h2>
            <div class="gl-ce-muted">Manage all events, users, organizers, and admins</div>
          </div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-home" type="button">Back</button>
        </div>

        <div class="gl-ce-row" style="margin-top:20px;gap:8px;flex-wrap:wrap;">
          <button class="gl-ce-btn" data-action="super-admin-load-users" type="button">👥 All Users</button>
          <button class="gl-ce-btn" data-action="super-admin-load-organizers" type="button">🏢 All Organizers</button>
          <button class="gl-ce-btn" data-action="super-admin-load-admins" type="button">🔐 All Admins</button>
        </div>

        <div id="glCeSuperAdminContent" style="margin-top:20px;"></div>
      </div>
    `;

    const mount = adminPane.querySelector("#glCeSuperAdminContent");
    if (mount) {
      mount.innerHTML = `<div class="gl-ce-muted">Select an option above to view data.</div>`;
    }
  }

  async function superAdminLoadUsers() {
    try {
      const data = await apiGet("/super-admin/users");
      adminState.superAdmin.users = data.users || [];
      renderSuperAdminUsers();
    } catch (err) {
      toast("Failed to load users: " + (err.message || "Unknown error"), "err");
    }
  }

  async function superAdminLoadOrganizers() {
    try {
      const data = await apiGet("/super-admin/organizers");
      adminState.superAdmin.organizers = data.organizers || [];
      renderSuperAdminOrganizers();
    } catch (err) {
      toast("Failed to load organizers: " + (err.message || "Unknown error"), "err");
    }
  }

  async function superAdminLoadAdmins() {
    // Only super admins should load this endpoint
    if (!adminState.isSuperAdmin) {
      const mount = adminPane.querySelector("#glCeSuperAdminContent");
      if (mount) {
        mount.innerHTML = `
          <div class="gl-ce-card">
            <h3 class="gl-ce-h3">All Admins</h3>
            <div class="gl-ce-muted" style="margin-top:10px;">Super Admin only.</div>
          </div>
        `;
      }
      return;
    }
    try {
      const data = await apiGet("/super-admin/admins");
      adminState.superAdmin.admins = data;
      renderSuperAdminAdmins();
    } catch (err) {
      toast("Failed to load admins: " + (err.message || "Unknown error"), "err");
    }
  }

  function renderSuperAdminUsers() {
    const mount = adminPane.querySelector("#glCeSuperAdminContent");
    if (!mount) return;

    const users = adminState.superAdmin.users || [];
    
    mount.innerHTML = `
      <div>
        <h3 class="gl-ce-h3">All Users (${users.length})</h3>
        <div style="max-height:600px;overflow-y:auto;margin-top:10px;">
          <table class="gl-ce-table" style="width:100%;">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Event</th>
                <th>Organizer</th>
                <th>Driver Number/City/Car/HP</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              ${users.map(u => `
                <tr>
                  <td><b>${esc(u.first_name || "")} ${esc(u.last_name || "")}</b></td>
                  <td>${esc(u.email || "")}</td>
                  <td>${esc(u.event_name || u.comp_code || "N/A")}</td>
                  <td>${esc(u.organizer || "N/A")}</td>
                  <td>
                    ${u.driver_number != null && String(u.driver_number).trim() !== "" ? `Driver Number: ${esc(String(u.driver_number))}` : ""}
                    ${u.city ? ` • ${esc(u.city)}` : ""}
                    ${u.car ? ` • ${esc(u.car)}` : ""}
                    ${u.hp ? ` • ${u.hp}HP` : ""}
                  </td>
                  <td>${fmtDateLocalish(u.created_at)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  function renderSuperAdminOrganizers() {
    const mount = adminPane.querySelector("#glCeSuperAdminContent");
    if (!mount) return;

    const organizers = adminState.superAdmin.organizers || [];
    
    mount.innerHTML = `
      <div>
        <h3 class="gl-ce-h3">All Organizers (${organizers.length})</h3>
        <div style="margin-top:10px;">
          ${organizers.map(org => `
            <div class="gl-ce-card" style="margin-bottom:10px;">
              <div class="gl-ce-row gl-ce-row-between">
                <div>
                  <h4 style="margin:0;">${esc(org.organizer)}</h4>
                  <div class="gl-ce-muted" style="margin-top:4px;">
                    ${org.event_count} event(s) • 
                    First: ${fmtDateLocalish(org.first_event_at)} • 
                    Last: ${fmtDateLocalish(org.last_event_at)}
                  </div>
                  ${org.sample_event_codes && org.sample_event_codes.length > 0 ? `
                    <div style="margin-top:8px;">
                      <span class="gl-ce-muted">Event Codes: </span>
                      ${org.sample_event_codes.map(c => `<code style="background:#f5f5f5;padding:2px 6px;border-radius:3px;margin-right:4px;">${esc(c)}</code>`).join("")}
                    </div>
                  ` : ""}
                </div>
                <button class="gl-ce-btn gl-ce-btn-sm" data-action="super-admin-view-organizer-events" data-organizer="${esc(org.organizer)}" type="button">View Events</button>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  async function superAdminViewOrganizerEvents(organizer) {
    try {
      const encoded = encodeURIComponent(organizer);
      const data = await apiGet(`/super-admin/organizers/${encoded}/events`);
      adminState.superAdmin.selectedOrganizer = organizer;
      adminState.superAdmin.organizerEvents = data.events || [];
      renderSuperAdminOrganizerEvents();
    } catch (err) {
      toast("Failed to load events: " + (err.message || "Unknown error"), "err");
    }
  }

  function renderSuperAdminOrganizerEvents() {
    const mount = adminPane.querySelector("#glCeSuperAdminContent");
    if (!mount) return;

    const organizer = adminState.superAdmin.selectedOrganizer;
    const events = adminState.superAdmin.organizerEvents || [];
    
    mount.innerHTML = `
      <div>
        <div class="gl-ce-row gl-ce-row-between" style="margin-bottom:10px;">
          <h3 class="gl-ce-h3">Events by ${esc(organizer)} (${events.length})</h3>
          <button class="gl-ce-btn gl-ce-btn-ghost gl-ce-btn-sm" data-action="super-admin-load-organizers" type="button">← Back to Organizers</button>
        </div>
        <div style="max-height:600px;overflow-y:auto;">
          ${events.map(ev => `
            <div class="gl-ce-card" style="margin-bottom:10px;">
              <div class="gl-ce-row gl-ce-row-between">
                <div>
                  <div><b>${esc(eventDisplayName(ev, "Event"))}</b></div>
                  <div class="gl-ce-muted" style="margin-top:4px;">
                    Code: <code>${esc(ev.comp_code || "")}</code> • 
                    Status: <span class="gl-ce-pill gl-ce-pill-${esc(ev.status || "draft")}">${esc(ev.status || "draft")}</span> • 
                    ${fmtDateInTz(ev.start_at, ev.start_at_tz)}
                  </div>
                </div>
                <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-open" data-id="${esc(ev.id)}" type="button">Manage</button>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  function renderSuperAdminAdmins() {
    const mount = adminPane.querySelector("#glCeSuperAdminContent");
    if (!mount) return;

    const data = adminState.superAdmin.admins || {};
    const compAdmins = data.comp_admins || [];
    const adminUsers = data.admin_users || [];
    const superAdmins = data.super_admins || [];
    
    mount.innerHTML = `
      <div>
        <h3 class="gl-ce-h3">All Admins</h3>
        
        <div style="margin-top:20px;">
          <h4 class="gl-ce-h3">Super Admins (${superAdmins.length})</h4>
          <div class="gl-ce-card" style="margin-top:10px;">
            ${superAdmins.length > 0 ? `
              <ul class="gl-ce-list">
                ${superAdmins.map(a => `
                  <li>
                    <b>Customer ID:</b> ${esc(a.customer_id || "N/A")} • 
                    <b>Email:</b> ${esc(a.email || "N/A")} • 
                    <span class="gl-ce-muted">Created: ${fmtDateLocalish(a.created_at)}</span>
                  </li>
                `).join("")}
              </ul>
            ` : `<div class="gl-ce-muted">No super admins found.</div>`}
          </div>
        </div>

        <div style="margin-top:20px;">
          <h4 class="gl-ce-h3">Regular Admins (${compAdmins.length})</h4>
          <div class="gl-ce-card" style="margin-top:10px;">
            ${compAdmins.length > 0 ? `
              <ul class="gl-ce-list">
                ${compAdmins.map(a => `
                  <li>
                    <b>${esc(a.name || a.email || "Unknown")}</b>${a.email ? ` &lt;${esc(a.email)}&gt;` : ""} • 
                    <span class="gl-ce-muted">ID: ${esc(a.customer_id || "N/A")} • Created: ${fmtDateLocalish(a.created_at)}</span>
                    <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="super-admin-revoke-access" data-customer-id="${esc(a.customer_id)}" type="button" style="margin-left:10px;">Revoke</button>
                  </li>
                `).join("")}
              </ul>
            ` : `<div class="gl-ce-muted">No regular admins found.</div>`}
          </div>
        </div>

        <div style="margin-top:20px;">
          <h4 class="gl-ce-h3">Legacy Admin Users (${adminUsers.length})</h4>
          <div class="gl-ce-card" style="margin-top:10px;">
            ${adminUsers.length > 0 ? `
              <ul class="gl-ce-list">
                ${adminUsers.map(a => `
                  <li>
                    <b>${esc(a.name || a.email || "Unknown")}</b>${a.email ? ` &lt;${esc(a.email)}&gt;` : ""} • 
                    <span class="gl-ce-muted">ID: ${esc(a.customer_id || "N/A")} • Created: ${fmtDateLocalish(a.created_at)}</span>
                  </li>
                `).join("")}
              </ul>
            ` : `<div class="gl-ce-muted">No legacy admin users found.</div>`}
          </div>
        </div>

        <div style="margin-top:20px;padding:15px;background:#f5f5f5;border-radius:6px;">
          <h4 class="gl-ce-h3">Grant Admin Access</h4>
          <div style="margin-top:10px;">
            <input type="text" id="glCeGrantAdminEmail" class="gl-ce-input" placeholder="Email or Customer ID" style="width:300px;margin-right:8px;" />
            <button class="gl-ce-btn" data-action="super-admin-grant-access" type="button">Grant Access</button>
          </div>
          <div class="gl-ce-muted" style="margin-top:8px;">Enter the customer's email (they must have opened the Comp Events page while logged in at least once, or registered for an event), or paste their numeric Shopify customer ID. Granting is immediate.</div>
        </div>
      </div>
    `;
  }

  async function superAdminGrantAccess() {
    const input = adminPane.querySelector("#glCeGrantAdminEmail");
    if (!input) return;
    
    const value = (input.value || "").trim();
    if (!value) {
      toast("Please enter an email or customer ID", "err");
      return;
    }

    try {
      const isEmail = value.includes("@");
      const body = isEmail ? { email: value } : { customer_id: value };
      await apiPost("/super-admin/grant-access", body);
      toast("Admin access granted!", "ok");
      input.value = "";
      await superAdminLoadAdmins(); // Refresh admin list
    } catch (err) {
      toast("Failed to grant access: " + (err.message || "Unknown error"), "err");
    }
  }

  async function superAdminRevokeAccess(customerId) {
    if (!window.confirm(`Revoke admin access for customer ID: ${customerId}?`)) return;

    try {
      await apiPost("/super-admin/revoke-access", { customer_id: customerId });
      toast("Admin access revoked!", "ok");
      await superAdminLoadAdmins(); // Refresh admin list
    } catch (err) {
      toast("Failed to revoke access: " + (err.message || "Unknown error"), "err");
    }
  }

  // Check if current user is super admin
  async function checkSuperAdminStatus() {
    try {
      // Try to access a super admin endpoint - if it works, user is super admin
      await apiGet("/super-admin/admins");
      adminState.isSuperAdmin = true;
      return true;
    } catch (err) {
      adminState.isSuperAdmin = false;
      return false;
    }
  }

  // PHASE 4.4: Admin Comp Overview (Match State, Progress, Quick Actions)
  function renderAdminCompOverview(mount, payload, ev) {
    if (!mount || !payload || !payload.bracket) return;

    const matches = payload.bracket.matches || [];
    const mainMatches = matches.filter((m) => !isConsolationMatch(m));
    const rounds = deriveBracketRounds(payload.bracket);
    const progressMatches = getProgressEligibleMatches(matches);

    // Calculate match status counts from all real matches (exclude BYE placeholders)
    const statusCounts = {
      pending: 0,
      ready: 0,
      live: 0,
      complete: 0,
      total: progressMatches.length
    };

    progressMatches.forEach((m) => {
      let status = normalizeMatchStatusValue(m);
      if (status === "pending" && m.winner) status = "complete";

      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts.pending++;
      }
    });

    const totalMatches = progressMatches.length;
    const completedMatches = progressMatches.filter((m) => isCompletedMatchForProgress(m)).length;
    const completionPercent = totalMatches > 0
      ? Math.max(0, Math.min(100, Math.round((completedMatches / totalMatches) * 100)))
      : 0;

    const canAdvanceRound = statusCounts.ready > 0 || statusCounts.live > 0;
    const canSetLive = ev && normalizeStatus(ev.status) !== "live";
    const canComplete = statusCounts.complete === statusCounts.total && statusCounts.total > 0;
    const podium = derivePodiumFromPayload(payload);
    const roundsSummaryHtml = rounds.length
      ? rounds.map((r) => `
          <div class="gl-ce-status-item">
            <div class="gl-ce-status-info">
              <div class="gl-ce-status-label">${esc(r.roundName)}</div>
              <div class="gl-ce-status-count">${Number(r.matchCount || 0)} match${Number(r.matchCount || 0) === 1 ? "" : "es"}</div>
            </div>
          </div>
        `).join("")
      : `<div class="gl-ce-muted">No rounds available.</div>`;

    mount.innerHTML = `
      <div class="gl-ce-row gl-ce-row-between" style="align-items: flex-start; margin-bottom: 16px;">
        <div>
          <h3 class="gl-ce-h2" style="margin: 0 0 8px 0;">Bracket Overview</h3>
          <div class="gl-ce-muted" style="font-size: 13px;">Event: ${esc(eventDisplayName(ev, "Event"))}</div>
        </div>
        <div class="gl-ce-bracket-quick-actions">
          ${canAdvanceRound ? `
          ` : ''}
          ${canSetLive ? `
            <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-green" data-action="admin-set-status-quick" data-event-id="${esc(ev?.id || "")}" data-status="live" type="button" title="Set event to LIVE">
              ▶️ Go Live
            </button>
          ` : ''}
          ${canComplete ? `
            <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-set-status-quick" data-event-id="${esc(ev?.id || "")}" data-status="complete" type="button" title="Mark event as complete">
              ✓ Complete
            </button>
          ` : ''}
        </div>
      </div>

      <!-- Progress Indicator -->
      <div style="margin-bottom: 16px;">
        <div class="gl-ce-row gl-ce-row-between" style="margin-bottom: 6px;">
          <span class="gl-ce-muted" style="font-size: 13px;">Event Completion</span>
          <span class="gl-ce-muted" style="font-size: 13px; font-weight: bold;">${completionPercent}%</span>
        </div>
        <div class="gl-ce-progress-bar">
          <div class="gl-ce-progress-fill" style="width: ${completionPercent}%;"></div>
        </div>
        <div class="gl-ce-muted" style="font-size: 12px; margin-top: 6px;">
          ${completedMatches} of ${totalMatches} match${totalMatches === 1 ? "" : "es"} finished
        </div>
      </div>

      <div style="margin-bottom: 12px;">
        <div class="gl-ce-muted" style="font-size: 13px; margin-bottom: 8px;">Rounds & Matches</div>
        <div class="gl-ce-status-summary">
          ${roundsSummaryHtml}
        </div>
      </div>

      <!-- Match Status Summary -->
      <div class="gl-ce-status-summary">
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-badge gl-ce-status-pending"></div>
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label">Pending</div>
            <div class="gl-ce-status-count">${statusCounts.pending}</div>
          </div>
        </div>
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-badge gl-ce-status-ready"></div>
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label">Ready</div>
            <div class="gl-ce-status-count">${statusCounts.ready}</div>
          </div>
        </div>
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-badge gl-ce-status-live"></div>
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label">Live</div>
            <div class="gl-ce-status-count">${statusCounts.live}</div>
          </div>
        </div>
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-badge gl-ce-status-complete"></div>
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label">Complete</div>
            <div class="gl-ce-status-count">${statusCounts.complete}</div>
          </div>
        </div>
        <div class="gl-ce-status-item gl-ce-status-total">
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label" style="font-weight: bold;">Total Battles</div>
            <div class="gl-ce-status-count" style="font-weight: bold;">${totalMatches}</div>
          </div>
        </div>
      </div>

      <div class="gl-ce-overview-podium-strip" style="display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; margin-top:12px;">
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label" style="font-weight:700;">1st place</div>
            <div class="gl-ce-status-count">${esc(podium.first || "Pending")}</div>
          </div>
        </div>
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label" style="font-weight:700;">2nd place</div>
            <div class="gl-ce-status-count">${esc(podium.second || "Pending")}</div>
          </div>
        </div>
        <div class="gl-ce-status-item">
          <div class="gl-ce-status-info">
            <div class="gl-ce-status-label" style="font-weight:700;">3rd place</div>
            <div class="gl-ce-status-count">${esc(podium.third || "Pending")}</div>
          </div>
        </div>
      </div>
    `;
  }

  // Render bracket tabs for rounds
  function renderBracketTabs(mount, bracketData, ev) {
    if (!mount || !bracketData || !bracketData.matches) {
      mount.innerHTML = '';
      return;
    }
    
    try {
      const matches = Array.isArray(bracketData.matches) ? bracketData.matches : [];
      // Calculate N (actual driver count) and base (highest power of 2 <= N) for play-in strategy
      const actualDrivers = new Set([...matches.map(m => m.driver_a), ...matches.map(m => m.driver_b)].filter(d => d && d !== "BYE" && d !== null)).size;
      const maxDrivers = ev?.max_drivers || bracketData.max_drivers || 32;
      const N = actualDrivers > 0 ? actualDrivers : maxDrivers;
      
      // Calculate base (highest power of 2 <= N) for play-in strategy
      const highestPowerOfTwoLessOrEqual = (n) => {
        n = Math.max(2, Math.min(64, Number(n) || 2));
        if (n <= 2) return 2;
        return Math.pow(2, Math.floor(Math.log2(n)));
      };
      const base = highestPowerOfTwoLessOrEqual(N);
      const hasPlayIn = (N !== base);
      
      // Calculate total rounds: prefer final_round from payload; else max round from matches; fallback to baseRounds + play-in
      const baseRounds = Math.log2(base) + 1;
      const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 1)).filter(Number.isFinite)) : 0;
      // Prefer actual rounds in this bracket payload to avoid stale final_round labels on older events.
      const totalRounds = maxRoundFromMatches > 0
        ? maxRoundFromMatches
        : ((Number(bracketData?.final_round) > 0)
          ? Number(bracketData.final_round)
          : (baseRounds + (hasPlayIn ? 1 : 0)));

      // Group matches by actual payload rounds only (avoids missing finals from sparse round ranges).
      const rounds = [];
      const thirdPlaceMatches = matches.filter((m) => isConsolationMatch(m));
      const mainRoundNumbers = [...new Set(
        matches
          .filter((m) => !isConsolationMatch(m))
          .map((m) => Number(m.round || 1))
          .filter(Number.isFinite)
      )].sort((a, b) => a - b);
      for (const r of mainRoundNumbers) {
        const roundMatches = matches.filter(m => Number(m.round || 1) === r && !isConsolationMatch(m));
        if (roundMatches.length > 0) {
          const roundName = displayRoundName(r, null, totalRounds);
          rounds.push({ round: r, name: roundName, matches: roundMatches });
        }
      }
      if (thirdPlaceMatches.length > 0) {
        rounds.push({ round: "third-place", name: "Third Place Match", matches: thirdPlaceMatches });
      }
      
      // Default to current battle round (fallback: first available round)
      if (adminState.bracketActiveRound == null && !adminState.bracketExplicitFull) {
        const currentMatchId = adminState.comp?.payload?.current_match_id != null
          ? String(adminState.comp.payload.current_match_id)
          : "";
        const currentMatch = matches.find((m) => String(m.id) === currentMatchId);
        if (isConsolationMatch(currentMatch)) {
          adminState.bracketActiveRound = "third-place";
        } else {
          const currentRoundFromMatch = rounds.find(r =>
            Array.isArray(r.matches) && r.matches.some(m => String(m.id) === currentMatchId)
          )?.round;
          adminState.bracketActiveRound = Number.isFinite(currentRoundFromMatch)
            ? Number(currentRoundFromMatch)
            : (rounds.length ? Number(rounds[0].round) : 1);
        }
      }
      const activeRound = adminState.bracketExplicitFull
        ? null
        : normalizeBracketRoundSelection(adminState.bracketActiveRound);
      const isFullBracket = activeRound === null;

      // Stable tab order: numeric ascending, then Third Place, then Final Battle (always last).
      const normalNumeric = rounds
        .filter(r => typeof r.round === "number" && Number.isFinite(r.round))
        .sort((a, b) => Number(a.round) - Number(b.round));
      const thirdTab = rounds.find(r => r.round === "third-place") || null;
      const finalRoundNum = normalNumeric.length
        ? Math.max(...normalNumeric.map(r => Number(r.round)))
        : null;
      const finalTab = (finalRoundNum != null)
        ? normalNumeric.find(r => Number(r.round) === finalRoundNum) || null
        : null;
    if (finalTab) finalTab.name = "Final Battle";
      const numericWithoutFinal = (finalRoundNum != null)
        ? normalNumeric.filter(r => Number(r.round) !== finalRoundNum)
        : normalNumeric;
      const orderedRounds = [
        ...numericWithoutFinal,
        ...(thirdTab ? [thirdTab] : []),
        ...(finalTab ? [finalTab] : []),
      ];
    const seen = new Set();
    const orderedRoundsDeduped = orderedRounds.filter(r => {
      const k = String(r.round);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
      
      mount.innerHTML = `
        <div class="gl-ce-bracket-tabs-container">
          <button class="gl-ce-bracket-tab ${isFullBracket ? 'is-active' : ''}" 
                  data-action="bracket-select-round" 
                  data-round="null"
                  type="button">
            Full Bracket
          </button>
      ${orderedRoundsDeduped.map(r => `
            <button class="gl-ce-bracket-tab ${activeRound === r.round ? 'is-active' : ''}" 
                    data-action="bracket-select-round" 
                    data-round="${r.round}"
                    type="button">
              ${esc(r.name)}
              ${r.matches.length > 0 ? `<span class="gl-ce-bracket-tab-count">${r.matches.length}</span>` : ''}
            </button>
          `).join('')}
        </div>
      `;
    } catch (err) {
      console.error("Error rendering bracket tabs:", err);
      mount.innerHTML = '';
    }
  }

  // -------- Admin Comp View (LIVE) ----------
  function renderAdminCompView() {
    const ev = adminState.selectedId
      ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
      : null;

    const payload = adminState.comp.payload || null;
    const compError = String(adminState.comp.error || "").trim();
    const isCompLoading = !!adminState.comp.loading;
    const st = eventStatusForUi({
      ...(ev || {}),
      ...(payload?.event || {}),
    });
    const showQualifying = adminState.comp.view === "qualifying" || st === "qualifying";
    const canShow = ev && payload && payload.ok !== false;
    const payloadErrorText = String(payload?.error || "").trim();
    const errorText = payloadErrorText || compError || "Unable to load live view data.";
    const hasPayloadError = !!(payload && payload.ok === false);
    const hideManualJudging = isManualCompView(payload, ev);
    const canConvertToSimple = !showQualifying
      && st === "live"
      && !hideManualJudging
      && Array.isArray(payload?.bracket?.matches)
      && payload.bracket.matches.length > 0
      && String(payload?.event?.bracket_mode || ev?.bracket_mode || "") !== "manual";

    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">${showQualifying ? "Qualifying Results" : "Comp View"}</h2>
            <div class="gl-ce-muted">${showQualifying ? "View and correct qualifying results." : (hideManualJudging ? "Live Simple Bracket. Admin awards winners by hand." : "Live bracket + judges.")} Last update: <b id="glCeCompLastAt">${esc(adminState.comp.lastAt || "—")}</b></div>
          </div>
          <div class="gl-ce-row gl-ce-row-wrap">
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-back-manage" type="button">Back</button>
            ${hideManualJudging ? "" : `<button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-comp-popout" type="button">${showQualifying ? "Pop-out Qualifying" : "Pop-out Judging"}</button>`}
            ${canConvertToSimple ? `<button class="gl-ce-btn" data-action="admin-convert-simple-bracket" data-id="${esc(ev?.id || adminState.selectedId || "")}" type="button">Convert to Simple Bracket</button>` : ""}
            ${showQualifying ? `<button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-qualifying-export" type="button">Export Excel</button>` : ""}
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-comp-refresh" type="button">Refresh</button>
          </div>
        </div>
        ${hideManualJudging && !showQualifying ? `<div class="gl-ce-muted" style="margin-top:10px;">Click a match to set it as the live battle. Use the dropdown beside a name to assign a driver. Long-press (phone) or double-click (computer) a slot to clear it.</div>` : ""}

        ${
          canShow
            ? showQualifying
              ? `<div class="gl-ce-card gl-ce-card-soft gl-ce-comp-qualifying-card" style="margin-top:12px;">
                  <div class="gl-ce-row gl-ce-row-between" style="align-items:center; margin-bottom:10px;">
                    <div>
                      <h3 class="gl-ce-h2" style="margin:0;">Qualifying Leaderboard ${adminHelpIcon("qualifying-seed-overrides", "Edit scores or manually move a driver’s seed. Saved seed order is used when the qualifying bracket draft is built.")}</h3>
                      <div class="gl-ce-muted">Positions and judge scores update live throughout qualifying.</div>
                    </div>
                    <div id="glCeRefreshIndicator" class="gl-ce-refresh-indicator">
                      <div class="refresh-icon">⟳</div>
                      <div class="refresh-progress-bar">
                        <div class="refresh-progress"></div>
                      </div>
                    </div>
                  </div>
                  <div id="glCeCompQualifyingLeaderboard"></div>
                </div>`
              : `<div class="gl-ce-grid gl-ce-comp-view-grid" style="margin-top:8px;">
                ${st === "complete" && payload.podium ? `
                <div class="gl-ce-card gl-ce-card-soft gl-ce-comp-podium-card" style="grid-column: 1 / -1;">
                  <div id="glCeCompPodium"></div>
                  ${finalResultsMarkup(payload.final_results, payload.final_results_pending)}
                  <div style="margin-top:12px;"><button class="gl-ce-btn" data-action="admin-open-final-results" data-id="${esc(ev?.id || adminState.selectedId || "")}" type="button">${payload.final_results_pending ? "Review Final Results" : "View & Edit Final Results"}</button></div>
                </div>
                ` : ""}
                <!-- PHASE 4.4: Match State Overview & Progress -->
                <div class="gl-ce-card gl-ce-card-soft gl-ce-comp-overview-card" style="grid-column: 1 / -1;" id="glCeCompOverview">
                  <div id="glCeCompOverviewContent"></div>
                </div>
                
                ${hideManualJudging ? "" : `<!-- Live Judging and Current Battle side by side above Bracket -->
                <div class="gl-ce-card gl-ce-card-soft gl-ce-comp-judging-card">
                  <div class="gl-ce-row gl-ce-row-between" style="align-items: center; margin-bottom: 10px;">
                    <h3 class="gl-ce-h2" style="margin: 0;">Live Judging</h3>
                    <div id="glCeRefreshIndicator" class="gl-ce-refresh-indicator">
                      <div class="refresh-icon">⟳</div>
                      <div class="refresh-progress-bar">
                        <div class="refresh-progress"></div>
                      </div>
                    </div>
                  </div>
                  <div id="glCeCompJudging" class="gl-ce-comp-judging-container" style="transition: opacity 0.3s ease;"></div>
                </div>
                
                <!-- Current Battle / Override card -->
                <div class="gl-ce-card gl-ce-card-soft gl-ce-comp-battle-controls-card">
                  <h3 class="gl-ce-h2" style="margin: 0 0 10px 0;">Current Battle / Override</h3>
                  <div id="glCeCompMatchControls"></div>
                </div>`}
                
                <!-- Bracket with tabs for rounds - Expanded horizontally -->
                <div class="gl-ce-card gl-ce-card-soft gl-ce-comp-bracket-card">
                  <div class="gl-ce-row gl-ce-row-between" style="align-items: center; margin-bottom: 10px;">
                    <h3 class="gl-ce-h2" style="margin: 0;">Bracket</h3>
                    ${hideManualJudging ? "" : `<button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-ghost" data-action="bracket-current-battle-popout" title="Pop-out Current Battle">
                      <span>⛶</span> Current Battle
                    </button>`}
                  </div>
                  ${hideManualJudging ? "" : `<div id="glCeBracketTabs" class="gl-ce-bracket-tabs"></div>`}
                  <div id="glCeCompBracket" class="gl-ce-comp-bracket-container"></div>
                </div>
              </div>`
            : !ev
              ? `<div style="margin-top:12px;">
                  <div class="gl-ce-muted">Event not found.</div>
                </div>`
              : hasPayloadError
                ? `<div style="margin-top:12px;" class="gl-ce-card gl-ce-card-soft">
                    <div class="gl-ce-warn">Error loading live view: ${esc(errorText)}</div>
                    <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:10px;">
                      <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-comp-retry" type="button">Retry</button>
                    </div>
                  </div>`
                : `<div style="margin-top:12px;" class="gl-ce-card gl-ce-card-soft">
                    <div class="gl-ce-row" style="align-items:center; gap:8px;">
                      <span aria-hidden="true" style="display:inline-block; animation: refreshSpin 0.8s linear infinite;">⟳</span>
                      <span class="gl-ce-muted">Loading live view...</span>
                    </div>
                    <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:10px;">
                      <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-comp-retry" type="button">Retry</button>
                    </div>
                  </div>`
        }
      </div>
    `;

    if (!canShow) return;

    if (showQualifying) {
      const qMount = adminPane.querySelector("#glCeCompQualifyingLeaderboard");
      if (qMount) renderAdminCompQualifyingTable(qMount, payload);
      if (payload) adminState.comp._lastSig = compPayloadSig(payload);
      return;
    }

    // PHASE 4.4: Match State Overview & Progress
    const overviewMount = adminPane.querySelector("#glCeCompOverviewContent");
    if (overviewMount) {
      renderAdminCompOverview(overviewMount, payload, ev);
    }

    // PHASE 4: podium (if event is complete)
    if (st === "complete" && payload.podium) {
      const pMount = adminPane.querySelector("#glCeCompPodium");
      if (pMount) {
        renderPodium(pMount, payload.podium);
      }
    }

    // Render bracket tabs
    const tabsMount = adminPane.querySelector("#glCeBracketTabs");
    if (tabsMount && payload.bracket && payload.bracket.matches && !hideManualJudging) {
      // Initialize to current battle round (fallback: first available round)
      if (adminState.bracketActiveRound == null && !adminState.bracketExplicitFull) {
        const currentMatchId = payload.current_match_id != null ? String(payload.current_match_id) : "";
        const rounds = payload.bracket.matches
          .filter(m => !isConsolationMatch(m))
          .map(m => Number(m.round || 1))
          .filter(Number.isFinite)
          .sort((a, b) => a - b)
          .filter((round, idx, arr) => idx === 0 || round !== arr[idx - 1]);
        const currentMatch = payload.bracket.matches.find(m => String(m.id) === currentMatchId);
        if (isConsolationMatch(currentMatch)) {
          adminState.bracketActiveRound = "third-place";
        } else {
          const currentRound = Number(currentMatch?.round || NaN);
          adminState.bracketActiveRound = Number.isFinite(currentRound)
            ? currentRound
            : (rounds.length ? rounds[0] : 1);
        }
      }
      
      renderBracketTabs(tabsMount, payload.bracket, ev);
    }
    
    // bracket
    const bMount = adminPane.querySelector("#glCeCompBracket");
    if (bMount) {
      const currentMatchId = payload.current_match_id != null ? String(payload.current_match_id) : "";
      const maxDrivers = ev?.max_drivers || payload.bracket?.max_drivers;
      const manualMode = isManualCompView(payload, ev);
      const activeRound = manualMode
        ? null
        : (adminState.bracketExplicitFull
          ? null
          : normalizeBracketRoundSelection(adminState.bracketActiveRound));
      const matches = payload.bracket?.matches || [];
      const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 0)).filter(Number.isFinite)) : 0;
      const totalRounds = (Number(payload.bracket?.final_round) > 0) ? Number(payload.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : undefined);
      renderBracketVisual(bMount, payload.bracket, {
        title: ev.event_name || "Live",
        live: true,
        lastUpdated: adminState.comp.lastAt,
        currentMatchId,
        max_drivers: maxDrivers,
        totalRounds,
        finalRound: Number(payload.bracket?.final_round || 0) || undefined,
        activeRound,
        manualInteract: manualMode,
        manualDrivers: manualMode ? manualEligibleDriversList() : [],
        podium: manualMode ? (payload.bracket?.podium || payload.podium || null) : null,
        showCurrentMatchButton: !manualMode,
      });
      if (manualMode) wireManualRunnerClicks(bMount, String(ev?.id || adminState.selectedId || ""));
    }

    // per-match controls list (Current Battle + Override/Revert)
    const cMount = adminPane.querySelector("#glCeCompMatchControls");
    if (cMount) {
      renderCompMatchControls(cMount, payload);
    }

    // judging panel
    const jMount = adminPane.querySelector("#glCeCompJudging");
    if (jMount && !hideManualJudging) {
      if (!jMount.style.transition) {
        jMount.style.transition = 'opacity 0.3s ease';
      }
      renderLiveJudgingOnly(jMount, payload);
    }

    if (payload) adminState.comp._lastSig = compPayloadSig(payload);
  }

  function openOverrideModal(matchId) {
    const payload = adminState.comp.payload;
    if (!payload) return;
    const matches = payload.bracket?.matches || [];
    const m = matches.find((x) => String(x.id) === String(matchId));
    if (!m) return;
    const modalFinalRound = Number(payload?.bracket?.final_round || 0)
      || Math.max(0, ...matches.filter((x) => !isConsolationMatch(x)).map((x) => Number(x?.round || 0)).filter(Number.isFinite));
    const modalRoundLabel = displayRoundName(m.round || 1, m, modalFinalRound || null);
    const roster = registrationRows(adminState.regs)
      .filter((registration) => String(registration.role || "driver").toLowerCase() === "driver")
      .slice()
      .sort((a, b) =>
        String(a.first_name || "").localeCompare(String(b.first_name || ""), undefined, { sensitivity: "base" }) ||
        String(a.last_name || "").localeCompare(String(b.last_name || ""), undefined, { sensitivity: "base" })
      );
    const driverOptions = (selectedId) => roster.map((registration) => {
      const id = String(registration.id || "");
      const label = [registration.first_name, registration.last_name].filter(Boolean).join(" ") || registration.email || id;
      return `<option value="${esc(id)}" ${String(selectedId || "") === id ? "selected" : ""}>${esc(label)}</option>`;
    }).join("");

    // Stop auto-refresh when override modal opens
    adminStopCompPoll();

    const modalId = "glCeOverrideModal";
    let modal = adminPane.querySelector(`#${modalId}`);
    if (!modal) {
      modal = document.createElement("div");
      modal.id = modalId;
      modal.className = "gl-ce-modal";
      adminPane.appendChild(modal);
    }

    modal.style.display = "flex";
    modal.style.alignItems = "center";
    modal.style.justifyContent = "center";
    modal.innerHTML = `
      <div class="gl-ce-modal__inner gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <h3 class="gl-ce-h2">Override Result</h3>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-override-close" type="button">Close</button>
        </div>

        <div class="gl-ce-muted">${esc(modalRoundLabel)} • Match ${esc(displayMatchNo(m.match_no))} • Battle ${esc(
      m.battle_no || 1
    )}</div>
        <div class="gl-ce-override-drivers" style="margin-top:12px;">
          <label class="gl-ce-label">Driver A
            <select class="gl-ce-input" data-role="override-driver-a">${driverOptions(m.slot_a_driver_id || m.driver_a)}</select>
          </label>
          <div class="gl-ce-override-versus">VS</div>
          <label class="gl-ce-label">Driver B
            <select class="gl-ce-input" data-role="override-driver-b">${driverOptions(m.slot_b_driver_id || m.driver_b)}</select>
          </label>
        </div>
        <div class="gl-ce-muted" style="margin-top:6px;">Current matchup: <strong>${esc(m.driver_a_label || "TBD")} vs ${esc(m.driver_b_label || "TBD")}</strong>. Change either driver to repair this bracket slot.</div>

        <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:12px;">
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-override-apply-drivers"
                  data-matchid="${esc(m.id)}"
                  data-original-a="${esc(m.slot_a_driver_id || m.driver_a || "")}"
                  data-original-b="${esc(m.slot_b_driver_id || m.driver_b || "")}"
                  type="button" disabled>Apply Driver Changes</button>
          <button class="gl-ce-btn" data-action="admin-override-set" data-matchid="${esc(m.id)}" data-winner-slot="a" type="button">Award Driver A</button>
          <button class="gl-ce-btn" data-action="admin-override-set" data-matchid="${esc(m.id)}" data-winner-slot="b" type="button">Award Driver B</button>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-override-set" data-matchid="${esc(
            m.id
          )}" data-winner="omt" type="button">Force OMT</button>
          <button class="gl-ce-btn gl-ce-btn-danger" data-action="admin-override-set" data-matchid="${esc(
            m.id
          )}" data-winner="" type="button">Revert (clear)</button>
        </div>

        <div class="gl-ce-muted" style="margin-top:10px;">
          Use “Revert” if the wrong driver advanced. This will clear downstream placements from this match.
        </div>
      </div>
    `;
    const driverASelect = modal.querySelector('[data-role="override-driver-a"]');
    const driverBSelect = modal.querySelector('[data-role="override-driver-b"]');
    const applyDriversButton = modal.querySelector('[data-action="admin-override-apply-drivers"]');
    const updateApplyDriversState = () => {
      if (!applyDriversButton) return;
      const changed =
        String(driverASelect?.value || "") !== String(applyDriversButton.dataset.originalA || "") ||
        String(driverBSelect?.value || "") !== String(applyDriversButton.dataset.originalB || "");
      applyDriversButton.disabled = !changed;
    };
    driverASelect?.addEventListener("change", updateApplyDriversState);
    driverBSelect?.addEventListener("change", updateApplyDriversState);
  }

  // -------- Admin root render ----------
  function renderAdmin() {
    // If not admin or not logged in, show request flow
    if (!hasAdminPortalAccess() || !isLoggedIn) {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminRequestAccess();
      return;
    }

    if (adminState.page === "create") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminCreateView();
      return;
    }

    if (adminState.page === "series") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminSeriesView();
      return;
    }

    if (adminState.page === "event-access") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminEventAccessView();
      return;
    }

    if (adminState.page === "manage") {
      adminStopCompPoll();
      // Registrations in manage view are loaded manually via Refresh.
      adminStopRegsPoll();
      renderAdminManageView();
      return;
    }

    if (adminState.page === "edit") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminEditView();
      return;
    }

    if (adminState.page === "comp") {
      adminStopRegsPoll();
      renderAdminCompView();
      return;
    }

    if (adminState.page === "bracket-draft") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminBracketDraftView();
      return;
    }

    if (adminState.page === "manual-bracket") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminManualBracketView();
      return;
    }

    if (adminState.page === "final-results-draft") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderAdminFinalResultsDraftView();
      return;
    }

    if (adminState.page === "super-admin") {
      adminStopCompPoll();
      adminStopRegsPoll();
      renderSuperAdminView();
      return;
    }

    adminStopCompPoll();
    adminStopRegsPoll();
    renderAdminHomeView();
  }

  // -------- Admin actions ----------
  async function adminOpenEvent(id) {
    adminState.selectedId = String(id || "");
    adminState.page = "manage";
    adminState.editBracket = false;
    adminState.comp.payload = null;
    adminState.comp.judgeNotes = {
      openMatchId: null,
      loadingMatchId: null,
      error: "",
      byMatchId: {},
    };
    adminStopCompPoll();
    adminState.addDriverModalOpen = false;
    adminState.adminPinPanelOpen = false;
    adminState.fanVotes.prompts = null;
    adminState.fanVotes.error = "";
    adminState.fanVotes.detailsById = {};
    adminState.fanVotes.votersByPrompt = {};
    await adminRefreshSelectedEventStatus({ silent: true });

    const ev = adminState.events.find((x) => String(x.id) === adminState.selectedId);
    if (ev?.comp_code) {
      await adminLoadBracketByCode(ev.comp_code, { silent: true });
      await adminLoadRegistrations(ev.id, { silent: true });
    }
    await Promise.all([
      adminLoadJudges(adminState.selectedId, { silent: true }),
      adminLoadFanVotePrompts(adminState.selectedId, { silent: true }),
    ]);
    renderAdmin();
  }

  function adminApplyStatusToLocalState(eventId, nextStatus) {
    const id = String(eventId || "");
    const st = normalizeStatus(nextStatus);
    if (!id || !st) return;

    const event = adminState.events.find((e) => String(e.id) === id);
    if (event) {
      event.status = st;
      event.status_stored = st;
      event.computed_status = st;
    }

    if (id === String(adminState.selectedId || "")) {
      if (adminState.comp?.payload?.event) {
        adminState.comp.payload.event.status = st;
        adminState.comp.payload.event.status_stored = st;
        adminState.comp.payload.event.computed_status = st;
      }
      // Invalidate comp-view payload cache so next poll reflects fresh server state.
      adminState.comp._lastSig = undefined;
      adminState.regsPoll.lastSig = "";
    }
  }

  async function adminSetStatus(eventId, status, { refreshComp } = {}) {
    const desiredStatus = normalizeStatus(status);
    const ev = adminState.events.find((e) => String(e.id) === String(eventId));
    const currentStatus = normalizeStatus(ev?.status_stored || ev?.status || ev?.computed_status);
    if (desiredStatus === "live" && currentStatus === "qualifying") {
      await adminFinalizeQualifyingAndGoLive(eventId, { refreshComp });
      return;
    }
    // If admin is forcing "open" after the close time has passed, warn that
    // the server will turn on the manual reopen override.
    const reopeningPastClose =
      desiredStatus === "open" && ev && !isRegistrationOpen({ ...ev, reg_close_override: 0 });
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/status`, { status: desiredStatus });
    const savedStatus = normalizeStatus(data?.status || desiredStatus);
    adminApplyStatusToLocalState(eventId, savedStatus);
    if (reopeningPastClose && savedStatus === "open") {
      toast("Registration reopened after the scheduled close time. Update the close date or change status to clear.", "ok");
    } else {
      toast(`Status set to ${savedStatus}.`, "ok");
    }
    await adminRefreshEvents();
    if (refreshComp) {
      await adminCompRefresh();
      return;
    }
    renderAdmin();
  }

  function renderAdminBracketDraftView() {
    const draft = adminState.bracketDraft;
    if (String(draft?.mode || "") === "simple") {
      const slots = Array.isArray(draft?.manual_slots) ? draft.manual_slots : [];
      const drivers = Array.isArray(draft?.eligible_drivers) ? draft.eligible_drivers : [];
      const driverOptions = `<option value="">BYE / empty</option>${drivers.map((driver) => `<option value="${esc(driver.id)}">${esc(`${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.email || driver.id)}</option>`).join("")}`;
      adminPane.innerHTML = `
        <div class="gl-ce-card">
          <div class="gl-ce-row gl-ce-row-between">
            <div><h2 class="gl-ce-h" style="margin:0;">Bracket Override</h2><div class="gl-ce-muted">Round 1 starts in Formula Drift qualifying seed positions. You can edit every driver dropdown.</div></div>
            <div class="gl-ce-row" style="gap:8px;">
              <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-bracket-draft-back" type="button">Back</button>
              <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-simple-bracket-restore-qualifying" type="button">Restore Qualifying Draft</button>
            </div>
          </div>
          <div class="gl-ce-grid2" style="margin-top:16px;">
            ${Array.from({ length: slots.length / 2 }, (_, index) => `
              <section class="gl-ce-card gl-ce-card-soft">
                <h3 class="gl-ce-h3">Battle ${index + 1}</h3>
                <select class="gl-ce-input" data-simple-slot="${index * 2}">${driverOptions}</select>
                <select class="gl-ce-input" data-simple-slot="${index * 2 + 1}" style="margin-top:8px;">${driverOptions}</select>
              </section>`).join("")}
          </div>
          <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:16px;">
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-simple-bracket-save" type="button">Save Matchups</button>
            <button class="gl-ce-btn" data-action="admin-simple-bracket-activate" type="button">Activate Bracket Override</button>
          </div>
        </div>`;
      for (const select of adminPane.querySelectorAll("[data-simple-slot]")) {
        select.value = slots[Number(select.dataset.simpleSlot)] || "";
      }
      return;
    }
    const slots = Array.isArray(draft?.preview?.slots) ? draft.preview.slots : [];
    const drivers = slots
      .filter((slot) => slot.registration_id)
      .sort((a, b) => Number(a.qualifying_seed) - Number(b.qualifying_seed));
    const includedIds = new Set(drivers.map((driver) => String(driver.registration_id)));
    const available = (draft?.eligible_drivers || []).filter((driver) => !includedIds.has(String(driver.id)));
    const unresolvedTies = Array.isArray(draft?.unresolved_tie_groups) ? draft.unresolved_tie_groups : [];
    const tieIds = new Set((draft?.tie_groups || []).flatMap((group) => group.ids || group.registration_ids || []).map(String));
    const lockedDoubleZeroDrivers = Array.isArray(draft?.locked_double_zero_drivers) ? draft.locked_double_zero_drivers : [];
    const matchups = [];
    for (let i = 0; i < slots.length; i += 2) matchups.push([slots[i], slots[i + 1]]);
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Review Bracket Draft</h2>
            <div class="gl-ce-muted">Nothing is live until you approve and activate this bracket.</div>
          </div>
          <div class="gl-ce-row gl-ce-row-wrap">
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-bracket-draft-qualifying" type="button">View Qualifying Results</button>
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-bracket-draft-back" type="button">Back</button>
          </div>
        </div>
        ${!draft ? `<div class="gl-ce-muted" style="margin-top:16px;">Loading bracket draft…</div>` : `
          ${unresolvedTies.length ? `<div class="gl-ce-warn" style="margin-top:16px;">Qualifying total tie${unresolvedTies.length === 1 ? "" : "s"} must be resolved manually before approval. Confirm the intended seed order after reviewing the tied drivers.</div>` : ""}
          ${lockedDoubleZeroDrivers.length ? `<div class="gl-ce-warn gl-ce-qual-highlight-double-zero" style="margin-top:12px;"><b>Did not qualify (two locked 0.00 runs):</b> ${lockedDoubleZeroDrivers.map((driver) => esc(`${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.email || driver.id)).join(", ")}</div>` : ""}
          <div class="gl-ce-grid2" style="margin-top:16px;align-items:start;">
            <section class="gl-ce-card gl-ce-card-soft">
              <h3 class="gl-ce-h2">Qualified drivers</h3>
              <div class="gl-ce-muted">Move drivers to change their seed, or remove them before approval.</div>
              <ol style="padding-left:28px;margin:12px 0;">
                ${drivers.map((driver, index) => `<li class="gl-ce-row gl-ce-row-between ${driver.locked_double_zero ? "gl-ce-qual-highlight-double-zero" : ""} ${tieIds.has(String(driver.registration_id)) ? "gl-ce-qual-highlight-tie" : ""}" style="margin:7px 0;gap:8px;">
                  <span><b>#${esc(String(index + 1))}</b> ${esc(driver.driver_name || "TBD")}</span>
                  <span class="gl-ce-row" style="gap:4px;">
                    <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-bracket-draft-move" data-index="${index}" data-direction="-1" ${index === 0 ? "disabled" : ""} type="button">↑</button>
                    <button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-bracket-draft-move" data-index="${index}" data-direction="1" ${index === drivers.length - 1 ? "disabled" : ""} type="button">↓</button>
                    <button class="gl-ce-btn gl-ce-btn-sm gl-ce-btn-danger" data-action="admin-bracket-draft-remove" data-index="${index}" ${drivers.length <= 2 ? "disabled" : ""} type="button">Remove</button>
                  </span>
                </li>`).join("")}
              </ol>
              ${available.length ? `<div class="gl-ce-muted" style="margin-top:12px;">Eligible drivers removed from this draft</div>
                <div style="display:grid;gap:6px;margin-top:6px;">
                  ${available.map((driver) => `<div class="gl-ce-row gl-ce-row-between"><span>${esc(`${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.email || driver.id)}</span><button class="gl-ce-btn gl-ce-btn-sm" data-action="admin-bracket-draft-add" data-regid="${esc(driver.id)}" type="button">Add</button></div>`).join("")}
                </div>` : ""}
            </section>
            <section class="gl-ce-card gl-ce-card-soft">
              <h3 class="gl-ce-h2">Round 1 preview</h3>
              <div class="gl-ce-muted">Field: ${esc(String(draft.bracket_size || draft.preview?.bracket_size || 0))}. Empty slots are byes.</div>
              <div style="display:grid;gap:8px;margin-top:12px;">
                ${matchups.map(([top, bottom], index) => `<div style="border:1px solid #ddd;border-radius:8px;padding:8px;">
                  <div class="gl-ce-muted">Battle ${index + 1}</div>
                  <div class="${top?.locked_double_zero ? "gl-ce-qual-highlight-double-zero" : ""} ${tieIds.has(String(top?.registration_id || "")) ? "gl-ce-qual-highlight-tie" : ""}">${top?.registration_id ? `#${esc(String(top.qualifying_seed))} ${esc(top.driver_name)}` : "BYE / TBD"}</div>
                  <div class="${bottom?.locked_double_zero ? "gl-ce-qual-highlight-double-zero" : ""} ${tieIds.has(String(bottom?.registration_id || "")) ? "gl-ce-qual-highlight-tie" : ""}">${bottom?.registration_id ? `#${esc(String(bottom.qualifying_seed))} ${esc(bottom.driver_name)}` : "BYE / TBD"}</div>
                </div>`).join("")}
              </div>
            </section>
          </div>
          <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:16px;">
            ${unresolvedTies.length ? `<button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-bracket-draft-resolve-ties" type="button">Confirm Tie Resolution</button>` : ""}
            <button class="gl-ce-btn" data-action="admin-bracket-draft-approve" ${unresolvedTies.length ? "disabled title=\"Resolve qualifying ties first\"" : ""} type="button">Approve &amp; Activate Bracket</button>
            <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-open-simple-bracket" type="button">Use Bracket Override</button>
          </div>
        `}
      </div>`;
  }

  const MANUAL_BRACKET_NOTE = "Simple Bracket is fully manual. You build every round and award every winner. There are no judge votes and no automated scoring. The admin must advance this event by hand.";

  function manualDriverLabel(driver) {
    if (!driver) return "TBD";
    return `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.email || driver.id;
  }

  function renderAdminManualBracketView() {
    const draft = adminState.manualBracket;
    const eventId = String(adminState.selectedId || "");
    if (!draft) {
      adminPane.innerHTML = `<div class="gl-ce-card"><div class="gl-ce-muted">Loading Simple Bracket…</div></div>`;
      return;
    }
    const isLive = String(draft.status || "") === "live"
      || ["live", "complete"].includes(String(draft.event_status || "").toLowerCase());
    if (isLive) {
      renderAdminManualBracketRunner(draft, eventId);
      return;
    }
    const drivers = Array.isArray(draft.eligible_drivers) ? draft.eligible_drivers : [];
    const byId = Object.fromEntries(drivers.map((driver) => [String(driver.id), driver]));
    const rounds = Array.isArray(draft.rounds) ? draft.rounds : [];
    const totalRounds = Number(draft.total_rounds || rounds.length || 0);
    const selectedRound = Number(adminState.manualBracketRound || rounds[0]?.round || 1);
    const round = rounds.find((item) => Number(item.round) === selectedRound) || rounds[0];
    const driverOptions = `<option value="">BYE / empty</option>${drivers.map((driver) => `<option value="${esc(driver.id)}">${esc(manualDriverLabel(driver))}</option>`).join("")}`;
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Simple Bracket</h2>
            <div class="gl-ce-muted">${esc(MANUAL_BRACKET_NOTE)}</div>
          </div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-manual-bracket-back" type="button">Back</button>
        </div>
        <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:12px;gap:6px;">
          ${rounds.map((item) => `<button class="gl-ce-btn gl-ce-btn-sm${Number(item.round) === selectedRound ? "" : " gl-ce-btn-ghost"}" data-action="admin-manual-bracket-tab" data-round="${esc(String(item.round))}" type="button">${esc(displayRoundName(item.round, null, totalRounds))}</button>`).join("")}
        </div>
        <div class="gl-ce-grid2" style="margin-top:16px;align-items:start;">
          <section class="gl-ce-card gl-ce-card-soft">
            <h3 class="gl-ce-h2">${esc(displayRoundName(round?.round, null, totalRounds))}</h3>
            <div class="gl-ce-muted">Pick both drivers for each battle. Empty is TBD.</div>
            ${(round?.matches || []).map((match) => `
              <section class="gl-ce-card" style="margin-top:10px;">
                <h4 class="gl-ce-h3">Battle ${esc(String(match.match_no))}</h4>
                <select class="gl-ce-input" data-manual-match="${esc(String(match.match_no))}" data-manual-slot="a">${driverOptions}</select>
                <select class="gl-ce-input" data-manual-match="${esc(String(match.match_no))}" data-manual-slot="b" style="margin-top:8px;">${driverOptions}</select>
              </section>`).join("") || `<div class="gl-ce-muted" style="margin-top:8px;">No battles in this round.</div>`}
            <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:12px;">
              <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-manual-bracket-save" type="button">Save Round</button>
              <button class="gl-ce-btn" data-action="admin-manual-bracket-activate" type="button">Activate Simple Bracket</button>
            </div>
          </section>
          <section class="gl-ce-card gl-ce-card-soft">
            <h3 class="gl-ce-h2">Bracket preview</h3>
            <div style="display:grid;gap:12px;margin-top:8px;">
              ${rounds.map((item) => `
                <div>
                  <div class="gl-ce-muted">${esc(displayRoundName(item.round, null, totalRounds))}</div>
                  ${(item.matches || []).map((match) => {
                    const a = match.slot_a ? byId[String(match.slot_a)] : null;
                    const b = match.slot_b ? byId[String(match.slot_b)] : null;
                    return `<div style="border:1px solid #ddd;border-radius:8px;padding:8px;margin-top:6px;">
                      <div class="gl-ce-muted">Battle ${esc(String(match.match_no))}</div>
                      <div>${esc(a ? manualDriverLabel(a) : "TBD")} vs ${esc(b ? manualDriverLabel(b) : "TBD")}</div>
                    </div>`;
                  }).join("")}
                </div>`).join("")}
            </div>
          </section>
        </div>
      </div>`;
    for (const select of adminPane.querySelectorAll("[data-manual-match]")) {
      const matchNo = Number(select.dataset.manualMatch);
      const slot = select.dataset.manualSlot;
      const match = (round?.matches || []).find((item) => Number(item.match_no) === matchNo);
      select.value = (slot === "a" ? match?.slot_a : match?.slot_b) || "";
    }
  }

  function renderAdminManualBracketRunner(draft, eventId) {
    const hasQualifying = !!draft.has_qualifying;
    const tab = hasQualifying && adminState.manualRunnerTab === "qualifying" ? "qualifying" : "bracket";
    adminPane.innerHTML = `
      <div class="gl-ce-card">
        <div class="gl-ce-row gl-ce-row-between">
          <div>
            <h2 class="gl-ce-h" style="margin:0;">Simple Bracket</h2>
            <div class="gl-ce-muted">Click a match to set it as the live battle. Use the dropdown beside a name to assign a driver. Long-press (phone) or double-click (computer) a slot to clear it.</div>
          </div>
          <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-manual-bracket-back" type="button">Back</button>
        </div>
        ${hasQualifying ? `
          <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:12px;gap:6px;">
            <button class="gl-ce-btn gl-ce-btn-sm${tab === "bracket" ? "" : " gl-ce-btn-ghost"}" data-action="admin-manual-runner-tab" data-tab="bracket" type="button">Bracket</button>
            <button class="gl-ce-btn gl-ce-btn-sm${tab === "qualifying" ? "" : " gl-ce-btn-ghost"}" data-action="admin-manual-runner-tab" data-tab="qualifying" type="button">Qualifying</button>
          </div>` : ""}
        <div id="glCeManualRunnerMount" style="margin-top:16px;"></div>
      </div>`;
    const mount = adminPane.querySelector("#glCeManualRunnerMount");
    if (!mount) return;
    if (tab === "qualifying") {
      mount.innerHTML = `<div class="gl-ce-muted">Loading qualifying results…</div>`;
      apiGet(`/admin/events/${encodeURIComponent(eventId)}/qualifying/leaderboard`).then((data) => {
        if (adminState.page !== "manual-bracket" || adminState.manualRunnerTab !== "qualifying") return;
        renderAdminCompQualifyingTable(mount, data, { readOnly: true });
      }).catch((err) => {
        if (adminState.page !== "manual-bracket") return;
        mount.innerHTML = `<div class="gl-ce-warn">${esc(err?.message || "Unable to load qualifying results.")}</div>`;
      });
      return;
    }
    const currentMatchId = String(adminState.manualCurrentMatchId || pickCurrentMatchId(adminState.bracket) || "");
    const matches = Array.isArray(adminState.bracket?.matches) ? adminState.bracket.matches : [];
    const maxRoundFromMatches = matches.length ? Math.max(...matches.filter((m) => !isConsolationMatch(m)).map((m) => Number(m.round || 0)).filter(Number.isFinite)) : 0;
    const totalRounds = (Number(adminState.bracket?.final_round) > 0) ? Number(adminState.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : Number(draft.total_rounds || 0) || undefined);
    renderBracketVisual(mount, adminState.bracket, {
      title: "Simple Bracket",
      live: true,
      lastUpdated: adminState.lastBracketAt,
      currentMatchId,
      totalRounds,
      max_drivers: adminState.bracket?.max_drivers || draft.bracket_size,
      finalRound: Number(adminState.bracket?.final_round || 0) || undefined,
      activeRound: null,
      manualInteract: true,
      manualDrivers: manualEligibleDriversList(),
      podium: adminState.bracket?.podium || null,
    });
    wireManualRunnerClicks(mount, eventId);
  }

  function wireManualRunnerClicks(mount, eventId) {
    if (!mount || mount.__glCeManualRunnerBound) return;
    mount.__glCeManualRunnerBound = true;
    const TAP_SLOP_PX = 10;
    const LONG_PRESS_MS = 500;
    const DOUBLE_TAP_MS = 400;
    let ptr = null;
    let longTimer = null;
    let longFired = false;
    let lastTap = null;

    const clearLongTimer = () => {
      if (longTimer) {
        clearTimeout(longTimer);
        longTimer = null;
      }
    };

    mount.addEventListener("pointerdown", (event) => {
      if (event.target?.closest?.("[data-manual-select]")) return;
      const slotEl = event.target?.closest?.("[data-manual-slot]");
      const matchEl = event.target?.closest?.("[data-manual-interact][data-match-id]");
      if (!slotEl && !matchEl) return;
      ptr = { id: event.pointerId, x: event.clientX, y: event.clientY, slotEl, matchEl };
      longFired = false;
      clearLongTimer();
      if (slotEl) {
        longTimer = setTimeout(() => {
          longFired = true;
          ptr = null;
          void adminManualClearSlot({
            eventId,
            dest_match_id: slotEl.dataset.matchId,
            dest_slot: slotEl.dataset.manualSlot,
          });
        }, LONG_PRESS_MS);
      }
    });
    mount.addEventListener("pointermove", (event) => {
      if (!ptr || event.pointerId !== ptr.id) return;
      if (Math.hypot(event.clientX - ptr.x, event.clientY - ptr.y) > TAP_SLOP_PX) {
        clearLongTimer();
        ptr = null;
      }
    });
    const endPointer = (event) => {
      if (!ptr || (event.pointerId != null && event.pointerId !== ptr.id)) return;
      const { slotEl, matchEl } = ptr;
      ptr = null;
      clearLongTimer();
      if (longFired || event.type === "pointercancel") return;
      const slotKey = slotEl ? `${slotEl.dataset.matchId}:${slotEl.dataset.manualSlot}` : "";
      const now = Date.now();
      if (slotKey && lastTap && lastTap.key === slotKey && now - lastTap.at < DOUBLE_TAP_MS) {
        lastTap = null;
        void adminManualClearSlot({
          eventId,
          dest_match_id: slotEl.dataset.matchId,
          dest_slot: slotEl.dataset.manualSlot,
        });
        return;
      }
      lastTap = slotKey ? { key: slotKey, at: now } : null;
      void handleManualRunnerTap({ eventId, slotEl, matchEl });
    };
    mount.addEventListener("pointerup", endPointer);
    mount.addEventListener("pointercancel", endPointer);
    mount.addEventListener("contextmenu", (event) => {
      if (event.target?.closest?.("[data-manual-slot]")) event.preventDefault();
    });
    mount.addEventListener("dblclick", (event) => {
      if (event.target?.closest?.("[data-manual-select]")) return;
      const slotEl = event.target?.closest?.("[data-manual-slot]");
      if (!slotEl) return;
      event.preventDefault();
      event.stopPropagation();
      void adminManualClearSlot({
        eventId,
        dest_match_id: slotEl.dataset.matchId,
        dest_slot: slotEl.dataset.manualSlot,
      });
    });
    mount.addEventListener("change", (event) => {
      const podiumSelect = event.target?.closest?.("[data-manual-podium]");
      if (podiumSelect) {
        event.stopPropagation();
        const root = podiumSelect.closest(".gl-ce-manual-podium") || mount;
        const valueFor = (place) => String(root.querySelector(`[data-manual-podium="${place}"]`)?.value || "").trim();
        void adminManualSetPodium({
          eventId,
          first: valueFor("first"),
          second: valueFor("second"),
          third: valueFor("third"),
        });
        return;
      }
      const select = event.target?.closest?.("[data-manual-select]");
      if (!select) return;
      event.stopPropagation();
      const driverId = String(select.value || "").trim();
      if (!driverId) {
        void adminManualClearSlot({
          eventId,
          dest_match_id: select.dataset.matchId,
          dest_slot: select.dataset.manualSlot,
        });
        return;
      }
      void adminManualPlaceDriver({
        eventId,
        driver_id: driverId,
        dest_match_id: select.dataset.matchId,
        dest_slot: select.dataset.manualSlot,
      });
    });
    mount.addEventListener("pointerdown", (event) => {
      if (!event.target?.closest?.("[data-manual-select], [data-manual-podium]")) return;
      event.stopPropagation();
    }, true);
  }

  async function handleManualRunnerTap({ eventId, slotEl, matchEl }) {
    if (adminState.manualPlacing) return;
    const matchId = String(slotEl?.dataset.matchId || matchEl?.dataset.matchId || "");
    if (matchId) await adminManualSetCurrentMatch(eventId, matchId);
  }

  async function adminManualReloadBracket(eventId) {
    const ev = adminState.events.find((item) => String(item.id) === String(eventId));
    if (!ev?.comp_code) return;
    const br = await apiGet(`/event/bracket?code=${encodeURIComponent(String(ev.comp_code).trim().toUpperCase())}`);
    adminState.bracket = br;
    adminState.bracketSig = bracketSignature(br);
    adminState.lastBracketAt = new Date().toLocaleTimeString();
  }

  async function refreshManualBracketUi(eventId) {
    if (adminState.page === "comp") {
      adminState.comp._lastSig = undefined;
      await adminCompPollTick({ reason: "manual", force: true });
      return;
    }
    await adminManualReloadBracket(eventId);
    renderAdmin();
  }

  async function adminManualSetCurrentMatch(eventId, matchId) {
    adminState.manualCurrentMatchId = String(matchId || "");
    patchManualBracketCurrentMatch(matchId);
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/set-current-match`, { match_id: matchId });
  }

  async function adminManualSetPodium({ eventId, first, second, third }) {
    try {
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/places`, {
        first: first || "",
        second: second || "",
        third: third || "",
      });
      if (data?.podium) {
        if (adminState.comp.payload?.bracket) adminState.comp.payload.bracket.podium = data.podium;
        if (adminState.comp.payload) adminState.comp.payload.podium = data.podium;
        if (adminState.bracket) adminState.bracket.podium = data.podium;
      }
      toast("Podium updated.", "ok");
      await refreshManualBracketUi(eventId);
    } catch (err) {
      toast(err?.message || "Unable to save podium places.", "err");
      await refreshManualBracketUi(eventId);
    }
  }

  async function adminManualPlaceDriver({ eventId, driver_id, dest_match_id, dest_slot }) {
    adminState.manualPlacing = true;
    try {
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/place`, {
        driver_id, dest_match_id, dest_slot,
      });
      if (data?.draft) adminState.manualBracket = data.draft;
      toast("Driver placed.", "ok");
      await refreshManualBracketUi(eventId);
    } finally {
      adminState.manualPlacing = false;
    }
  }

  async function adminManualClearSlot({ eventId, dest_match_id, dest_slot }) {
    if (adminState.manualPlacing) return;
    adminState.manualPlacing = true;
    try {
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/place`, {
        dest_match_id, dest_slot, clear: true, driver_id: null,
      });
      if (data?.draft) adminState.manualBracket = data.draft;
      adminState.manualPick = null;
      toast("Slot cleared.", "ok");
      await refreshManualBracketUi(eventId);
    } finally {
      adminState.manualPlacing = false;
    }
  }

  async function adminConvertToSimpleBracket(eventId) {
    const id = String(eventId || adminState.selectedId || "");
    if (!id) throw new Error("Select an event first.");
    const confirmed = await showConfirmModal({
      title: "Convert to Simple Bracket",
      message: "This is one-way. Live Judging will hide, and you will place or swap drivers by hand on this same bracket. Continue?",
      confirmText: "Convert",
      danger: true,
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const ev = adminState.events.find((item) => String(item.id) === id);
    const stored = String(ev?.status_stored || ev?.status || "").toLowerCase();
    const uiStatus = eventStatusForUi(ev || {});
    const liveJudged = stored === "live" || uiStatus === "live";
    const data = liveJudged
      ? await apiPost(`/admin/events/${encodeURIComponent(id)}/manual-bracket/convert`, { confirm: true })
      : await apiPost(`/admin/events/${encodeURIComponent(id)}/manual-bracket`, { confirm: true });
    if (data?.draft) adminState.manualBracket = data.draft;
    adminState.manualPick = null;
    adminState.bracketExplicitFull = true;
    adminState.bracketActiveRound = null;
    if (adminState.comp.payload) {
      adminState.comp.payload.event = {
        ...(adminState.comp.payload.event || {}),
        bracket_mode: "manual",
      };
    }
    await adminRefreshEvents();
    const latest = adminState.events.find((item) => String(item.id) === id);
    if (latest?.comp_code) {
      try {
        await adminLoadBracketByCode(latest.comp_code, { silent: true });
      } catch (err) {
        console.error("[SIMPLE] bracket reload after convert failed:", err);
      }
    }
    toast("Converted to Simple Bracket.", "ok");
    if (adminState.page === "comp") {
      adminState.comp._lastSig = undefined;
      renderAdmin();
      await adminCompPollTick({ reason: "manual", force: true });
    } else {
      renderAdmin();
    }
  }

  async function adminOpenManualBracket(eventId) {
    const id = String(eventId || adminState.selectedId || "");
    if (!id) throw new Error("Select an event first.");
    const ev = adminState.events.find((item) => String(item.id) === id);
    const alreadyManual = String(ev?.bracket_mode || "") === "manual";
    const status = String(ev?.status || "").toLowerCase();
    if (!alreadyManual) {
      const confirmed = await showConfirmModal({
        title: "Simple Bracket",
        message: `${MANUAL_BRACKET_NOTE} Continue?`,
        confirmText: "Open Simple Bracket",
        mountEl: adminPane,
      });
      if (!confirmed) return;
    }
    const data = alreadyManual && (status === "live" || status === "complete")
      ? await apiGet(`/admin/events/${encodeURIComponent(id)}/manual-bracket`)
      : await apiPost(`/admin/events/${encodeURIComponent(id)}/manual-bracket`, { confirm: true });
    const draft = data?.draft;
    if (!draft) throw new Error("Unable to open Simple Bracket.");
    adminState.selectedId = id;
    adminState.manualBracket = draft;
    adminState.manualBracketRound = Number(draft.rounds?.[0]?.round || 1);
    adminState.manualRunnerTab = "bracket";
    adminState.manualPick = null;
    adminState.manualCurrentMatchId = "";
    adminState.page = "manual-bracket";
    if (data?.adopted || String(draft.status || "") === "live") {
      await adminRefreshEvents();
      const latest = adminState.events.find((item) => String(item.id) === id);
      if (latest?.comp_code) {
        const br = await apiGet(`/event/bracket?code=${encodeURIComponent(String(latest.comp_code).trim().toUpperCase())}`);
        adminState.bracket = br;
        adminState.bracketSig = bracketSignature(br);
        adminState.lastBracketAt = new Date().toLocaleTimeString();
      }
    }
    renderAdmin();
  }

  function manualBracketMatchesFromForm() {
    const byMatch = new Map();
    for (const select of adminPane.querySelectorAll("[data-manual-match]")) {
      const matchNo = Number(select.dataset.manualMatch);
      if (!byMatch.has(matchNo)) byMatch.set(matchNo, { match_no: matchNo, slot_a: null, slot_b: null });
      const row = byMatch.get(matchNo);
      const value = String(select.value || "").trim() || null;
      if (select.dataset.manualSlot === "a") row.slot_a = value;
      else row.slot_b = value;
    }
    return [...byMatch.values()];
  }

  async function adminSaveManualBracketRound() {
    const eventId = String(adminState.selectedId || "");
    const matches = manualBracketMatchesFromForm();
    const round = Number(adminState.manualBracketRound || 1);
    let data;
    try {
      data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/slots`, {
        confirm: true, round, matches,
      });
    } catch (err) {
      if (!/already has a winner/i.test(String(err?.message || ""))) throw err;
      const confirmed = await showConfirmModal({
        title: "Change scored battle?",
        message: "This battle already has a winner. Changing drivers will clear that result.",
        confirmText: "Change Drivers",
        danger: true,
        mountEl: adminPane,
      });
      if (!confirmed) return;
      data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/slots`, {
        confirm: true, confirm_scored: true, round, matches,
      });
    }
    adminState.manualBracket = data.draft;
    toast("Round saved.", "ok");
    renderAdmin();
  }

  async function adminActivateManualBracket() {
    const eventId = String(adminState.selectedId || "");
    await adminSaveManualBracketRound();
    const confirmed = await showConfirmModal({
      title: "Activate Simple Bracket",
      message: `${MANUAL_BRACKET_NOTE} Activating starts the event live with no judges.`,
      confirmText: "Activate",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket/activate`, { confirm: true });
    await adminRefreshEvents();
    const live = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/manual-bracket`);
    adminState.manualBracket = live?.draft || null;
    adminState.manualRunnerTab = "bracket";
    adminState.manualPick = null;
    const ev = adminState.events.find((item) => String(item.id) === eventId);
    if (ev?.comp_code) {
      const br = await apiGet(`/event/bracket?code=${encodeURIComponent(String(ev.comp_code).trim().toUpperCase())}`);
      adminState.bracket = br;
      adminState.bracketSig = bracketSignature(br);
      adminState.lastBracketAt = new Date().toLocaleTimeString();
    }
    adminState.page = "manual-bracket";
    toast("Simple Bracket activated.", "ok");
    renderAdmin();
  }

  async function adminOpenBracketDraft(eventId, { create = false } = {}) {
    const endpoint = `/admin/events/${encodeURIComponent(eventId)}/qualifying/${create ? "generate-bracket" : "bracket-draft"}`;
    const data = create ? await apiPost(endpoint, {}) : await apiGet(endpoint);
    const draft = data?.draft || null;
    if (!draft) throw new Error("No bracket draft exists yet.");
    adminState.selectedId = String(eventId);
    adminState.bracketDraft = draft;
    adminState.page = "bracket-draft";
    renderAdmin();
  }

  function finalResultsMarkup(results, pending) {
    if (pending) return `<div class="gl-ce-muted" style="margin-top:12px;">Official final results pending admin approval.</div>`;
    if (!Array.isArray(results) || !results.length) return "";
    return `<section class="gl-ce-finishing-order" aria-label="Official final results">
      <h3 class="gl-ce-h2" style="margin:12px 0;">Official Final Results</h3>
      <ol class="gl-ce-finishing-order-list">${results.slice().sort((a, b) => Number(a.place) - Number(b.place)).map((entry) =>
        `<li class="gl-ce-finishing-order-row exact"><strong>${esc(ordinalSuffix(Number(entry.place)))}</strong><span>${esc(entry.driver_name || "Driver")}</span><small>Seed ${entry.qualifying_seed == null ? "—" : esc(String(entry.qualifying_seed))}</small></li>`
      ).join("")}</ol>
    </section>`;
  }

  function ordinalSuffix(value) {
    const n = Number(value);
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th");
    return `${n}${suffix}`;
  }

  function renderAdminFinalResultsDraftView() {
    const draft = adminState.finalResultsDraft;
    const entries = Array.isArray(draft?.placements) ? draft.placements.slice().sort((a, b) => Number(a.place) - Number(b.place)) : [];
    const options = entries.map((entry) => `<option value="${esc(String(entry.place))}">${esc(ordinalSuffix(entry.place))}</option>`).join("");
    adminPane.innerHTML = `<div class="gl-ce-card">
      <div class="gl-ce-row gl-ce-row-between">
        <div><h2 class="gl-ce-h" style="margin:0;">Review Final Results</h2><div class="gl-ce-muted">Final places are not public until you approve them.</div></div>
        <button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-final-results-back" type="button">Back</button>
      </div>
      ${!draft ? `<div class="gl-ce-muted" style="margin-top:16px;">Loading final results…</div>` : `<div style="display:grid;gap:8px;margin-top:16px;">
        ${entries.map((entry, index) => `<div class="gl-ce-row gl-ce-row-between gl-ce-card-soft" style="padding:10px;gap:10px;">
          <div><b>${esc(entry.driver_name || "Driver")}</b><div class="gl-ce-muted" style="font-size:12px;">Qualifying seed: ${entry.qualifying_seed == null ? "—" : `#${esc(String(entry.qualifying_seed))}`} · ${entry.eliminated_round ? `Eliminated Round ${esc(String(entry.eliminated_round))}, Match ${esc(String(entry.eliminated_match_no || ""))}` : "Podium placement"}</div></div>
          <label class="gl-ce-row" style="gap:6px;">Final place <select class="gl-ce-input" data-final-result-index="${index}" style="width:auto;">${options}</select></label>
        </div>`).join("")}
      </div>
      <div class="gl-ce-row gl-ce-row-wrap" style="margin-top:16px;">
        ${draft.status === "approved" ? `<span class="gl-ce-muted">Approved and published. Saving changes updates the official results immediately.</span><button class="gl-ce-btn" data-action="admin-final-results-save" type="button">Save Changes</button>` : `<button class="gl-ce-btn gl-ce-btn-ghost" data-action="admin-final-results-save" type="button">Save Final Places</button><button class="gl-ce-btn" data-action="admin-final-results-approve" type="button">Approve &amp; Publish Results</button>`}
      </div>`}
    </div>`;
    for (const select of adminPane.querySelectorAll("[data-final-result-index]")) {
      const index = Number(select.dataset.finalResultIndex);
      select.value = String(entries[index]?.place || "");
      select.addEventListener("change", () => {
        const changed = entries[index];
        const oldPlace = changed.place;
        const other = entries.find((entry) => entry !== changed && Number(entry.place) === Number(select.value));
        changed.place = Number(select.value);
        if (other) other.place = oldPlace;
        adminState.finalResultsDraft.placements = entries;
        renderAdminFinalResultsDraftView();
      });
    }
  }

  async function adminOpenFinalResultsDraft(eventId) {
    const data = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/final-results`);
    adminState.selectedId = String(eventId);
    adminState.finalResultsDraft = data.draft;
    adminState.page = "final-results-draft";
    renderAdmin();
  }

  async function adminSaveFinalResultsDraft(approve = false) {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({ title: approve ? "Approve final results" : "Save final places", message: approve ? "This publishes the official final results to Admin, Driver, and Spectator hubs." : "This saves the reviewed final positions without publishing them.", confirmText: approve ? "Approve & Publish" : "Save", mountEl: adminPane });
    if (!confirmed) return;
    const placements = adminState.finalResultsDraft.placements.map((entry) => ({ registration_id: entry.registration_id, place: Number(entry.place) }));
    if (!approve) {
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/final-results`, { placements, confirm: true });
      adminState.finalResultsDraft = data.draft;
      renderAdmin();
      return;
    }
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/final-results`, { placements, confirm: true });
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/final-results/approve`, { confirm: true });
    adminState.finalResultsDraft = data.draft;
    toast("Final results approved and published.", "ok");
    renderAdmin();
  }

  function draftDriverIds() {
    return (adminState.bracketDraft?.preview?.slots || [])
      .filter((slot) => slot.registration_id)
      .sort((a, b) => Number(a.qualifying_seed) - Number(b.qualifying_seed))
      .map((slot) => String(slot.registration_id));
  }

  async function adminSaveBracketDraft(driverIds, summary) {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Confirm bracket change",
      message: `${summary}\n\nThis changes qualifying placement in the draft bracket. Live battles are not affected until approval.`,
      confirmText: "Confirm",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/bracket-draft`, { driver_ids: driverIds, confirm: true });
    adminState.bracketDraft = data.draft;
    renderAdmin();
  }

  async function adminApproveBracketDraft() {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Approve and activate bracket",
      message: "This activates the reviewed bracket, advances the event to Live, and sends qualifying-results emails to eligible recipients.",
      confirmText: "Approve & Activate",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/bracket-draft/approve`, { confirm: true });
    adminState.bracketDraft = null;
    await adminRefreshEvents();
    adminState.page = "manage";
    toast("Bracket approved and activated.", "ok");
    renderAdmin();
  }

  async function adminResolveBracketDraftTies() {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Confirm qualifying tie resolution",
      message: "You are confirming the displayed seed order as the manual resolution for every tied qualifying total. Qualifying edits will require this confirmation again.",
      confirmText: "Confirm Tie Order",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/bracket-draft/resolve-ties`, { confirm: true });
    const refreshed = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/qualifying/bracket-draft`);
    adminState.bracketDraft = refreshed.draft;
    renderAdmin();
  }

  async function adminRecoverActiveBracket(eventId, simple) {
    const label = simple ? "rebuild a Bracket Override draft from saved qualifying" : "return to the qualifying bracket draft";
    const confirmed = await showConfirmModal({
      title: "Recover active bracket",
      message: `This clears the active bracket and its downstream battle records, retains qualifying scores and rankings, and ${label}. This is only allowed before any battle starts.`,
      confirmText: "Recover Bracket",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const suffix = simple ? "rebuild-simple" : "reset-to-qualifying";
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/active-bracket/${suffix}`, { confirm: true });
    const refreshed = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/qualifying/bracket-draft`);
    adminState.bracketDraft = refreshed.draft;
    await adminRefreshEvents();
    adminState.page = "bracket-draft";
    toast(simple ? "Bracket Override draft rebuilt from qualifying." : "Active bracket reset to qualifying draft.", "ok");
    renderAdmin();
  }

  async function adminOpenSimpleBracket() {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Create Bracket Override",
      message: "This replaces the qualifying draft with an editable Round 1 bracket prefilled from the current qualifying results in Formula Drift seed positions.",
      confirmText: "Create Bracket Override",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/simple-bracket`, { confirm: true });
    adminState.bracketDraft = data.draft;
    renderAdmin();
  }

  async function adminRestoreQualifyingDraft() {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Restore qualifying draft",
      message: "Discard the editable Bracket Override slots and rebuild the qualifying-seeded bracket draft from the current qualifying results?",
      confirmText: "Restore Qualifying Draft",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/generate-bracket`, {});
    adminState.bracketDraft = data.draft;
    toast("Qualifying bracket draft restored.", "ok");
    renderAdmin();
  }

  async function adminRefreshBracketRecovery(eventId, eventCode) {
    const confirmed = await showConfirmModal({
      title: "Reset / Refresh Bracket",
      message: "This clears every stored bracket batch and generation lock so a qualifying or random bracket can be created again. Event status is unchanged. Qualifying scores and historical audit are preserved.",
      confirmText: "Continue",
      danger: true,
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const code = window.prompt(`Type the exact event code to reset this bracket:\n${eventCode || ""}`);
    if (code === null) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/active-bracket/refresh`, { confirm: true, event_code: code });
    adminState.bracketDraft = null;
    adminState.manualBracket = null;
    adminState.bracket = null;
    adminState.bracketSig = "";
    await adminRefreshEvents();
    toast("Bracket reset. Event status is unchanged.", "ok");
    renderAdmin();
  }

  function simpleBracketSlotsFromForm() {
    const count = Number(adminState.bracketDraft?.bracket_size || 0);
    return Array.from({ length: count }, (_, index) => String(adminPane.querySelector(`[data-simple-slot="${index}"]`)?.value || "").trim() || null);
  }

  async function adminSaveSimpleBracket() {
    const eventId = String(adminState.selectedId || "");
    const slots = simpleBracketSlotsFromForm();
    const confirmed = await showConfirmModal({
      title: "Save manual matchups",
      message: "This updates the Round 1 matchups in the Bracket Override draft.",
      confirmText: "Save Matchups",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/simple-bracket/slots`, { slots, confirm: true });
    adminState.bracketDraft = data.draft;
    renderAdmin();
  }

  async function adminActivateSimpleBracket() {
    const eventId = String(adminState.selectedId || "");
    const slots = simpleBracketSlotsFromForm();
    const confirmed = await showConfirmModal({
      title: "Activate Bracket Override",
      message: "This locks the manual Round 1 matchups and starts the event live.",
      confirmText: "Activate",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/simple-bracket/slots`, { slots, confirm: true });
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/simple-bracket/activate`, { confirm: true });
    adminState.bracketDraft = null;
    await adminRefreshEvents();
    adminState.page = "manage";
    toast("Bracket Override activated.", "ok");
    renderAdmin();
  }

  async function adminFinalizeQualifyingAndGoLive(eventId, { refreshComp } = {}) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to generate the bracket.", "err"); return; }
    const ok = window.confirm("End qualifying now? A seeded bracket draft will be created for review before anything goes live.");
    if (!ok) return;

    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/finalize`, {});
    await adminOpenBracketDraft(eventId, { create: true });
  }

  async function adminDeleteEvent(eventId) {
    const ev = adminState.events.find((e) => String(e.id) === String(eventId));
    const st = normalizeStatus(ev?.computed_status || ev?.status);
    if (st !== "complete") {
      throw new Error("Only completed events can be deleted.");
    }
    const label = ev
      ? `${eventDisplayName(ev, "Event")} @ ${ev.venue || ""} (${ev.comp_code || ""})`
      : `Event ID: ${eventId}`;

    const ok = await showConfirmModal({
      title: "Delete Event",
      message: `${label}\n\nThis permanently deletes:\n- event\n- registrations\n- bracket matches\n- judge codes + sessions\n- judgments`,
      confirmText: "Delete",
      danger: true,
      mountEl: adminPane,
    });
    if (!ok) return;

    try {
      await apiPost(`/admin/events/${encodeURIComponent(eventId)}/delete`, {});
    } catch (err) {
      const message = String(err?.message || err || "Failed to delete event.");
      console.error("[ADMIN DELETE] API delete failed.", { eventId, err });
      throw new Error(message);
    }
    toast("Event deleted.", "ok");

    adminState.selectedId = "";
    adminState.page = "home";
    adminState.bracket = null;
    adminState.bracketSig = "";
    adminState.regs = null;
    adminState.judges = null;

    await adminRefreshEvents();
    renderAdmin();
  }

  async function handleDeleteEvent(target) {
    const eventId =
      String(target?.dataset?.eventId || "").trim() ||
      String(target?.dataset?.id || "").trim() ||
      String(target?.closest("[data-event-id]")?.getAttribute("data-event-id") || "").trim() ||
      String(target?.closest("[data-id]")?.getAttribute("data-id") || "").trim();

    // Temporary log for validating delegated click wiring.
    console.log("delete clicked", { eventId });
    toast(`Delete clicked${eventId ? ` (${eventId})` : ""}.`, "ok");

    if (!eventId) {
      console.error("[ADMIN DELETE] Missing event id for delete action.", { target });
      toast("Missing event id. Please refresh and try again.", "err");
      return;
    }

    const ev = adminState.events.find((e) => String(e.id) === String(eventId));
    const st = normalizeStatus(ev?.computed_status || ev?.status);
    if (st && st !== "complete") {
      console.warn("[ADMIN DELETE] Guard prevented delete for non-complete event.", { eventId, status: st });
      toast("Only completed events can be deleted.", "err");
      return;
    }

    try {
      await adminDeleteEvent(eventId);
    } catch (err) {
      console.error("[ADMIN DELETE] Failed to delete event.", { eventId, err });
      toast(String(err?.message || err || "Failed to delete event."), "err");
    }
  }

  async function adminRegenerateBracket(eventId, shuffle = false) {
    try {
      if (!shuffle) {
        const confirmed = await showConfirmModal({
          title: "Generate Random Bracket",
          message: "Confirm to create a randomly generated bracket. This does not factor in qualifying results.",
          confirmText: "Generate Random Bracket",
          danger: true,
          mountEl: adminPane,
        });
        if (!confirmed) return;
      }
      const result = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/generate-bracket`, { shuffle: true });
      if (result && result.ok === true) {
        toast(shuffle ? "Bracket shuffled and regenerated." : "Bracket regenerated.", "ok");
        // Refresh calls are best-effort and should not mask regeneration success.
        try {
          await adminRefreshEvents();
        } catch (refreshErr) {
          console.error("[REGEN] adminRefreshEvents failed after successful generate:", refreshErr);
          toast(`Bracket regenerated, but events refresh failed: ${refreshErr?.message || String(refreshErr)}`, "err");
          return;
        }

        const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
        if (ev?.comp_code) {
          try {
            await adminLoadBracketByCode(ev.comp_code, { silent: true });
          } catch (bracketErr) {
            console.error("[REGEN] adminLoadBracketByCode failed after successful generate:", bracketErr);
            toast(`Bracket regenerated, but bracket reload failed: ${bracketErr?.message || String(bracketErr)}`, "err");
            return;
          }
          try {
            await adminLoadRegistrations(ev.id, { silent: true });
          } catch (regErr) {
            console.error("[REGEN] adminLoadRegistrations failed after successful generate:", regErr);
            toast(`Bracket regenerated, but registrations reload failed: ${regErr?.message || String(regErr)}`, "err");
            return;
          }
        }
      } else {
        // Do NOT refresh bracket data on failure - show error and keep current state
        toast(result?.error || "Failed to regenerate bracket", "err");
      }
    } catch (err) {
      // Do NOT refresh bracket data on error - show error and keep current state
      const msg = err?.message || String(err);
      if (msg.includes("JSON") || msg.includes("<!doctype")) {
        toast("Server error: Received invalid response. Please check the server logs.", "err");
      } else {
        toast(`Failed to regenerate bracket: ${msg}`, "err");
      }
      console.error("Bracket regeneration error:", err);
    }
  }

  async function adminStartQualifying(eventId) {
    if (!hasAdminPortalAccess()) { toast("Admin access required to start qualifying.", "err"); return; }
    try {
      const choice = window.prompt(
        "Choose qualifying format:\n\n1 = Score (three 0–100 scores averaged)\n2 = Line, Angle & Style (40 + 30 + 30 summed)",
        "1"
      );
      if (choice == null) return;
      const qualifying_mode = String(choice).trim() === "2" ? "las" : "score";
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/qualifying/start`, {
        qualifying_mode,
        judge_roles: qualifying_mode === "las"
          ? { 1: "line", 2: "angle", 3: "style" }
          : null,
      });
      const savedStatus = normalizeStatus(data?.status || "qualifying");
      adminApplyStatusToLocalState(eventId, savedStatus);
      toast(qualifying_mode === "las" ? "Line, Angle & Style qualifying started." : "Score qualifying started.", "ok");
      await adminRefreshEvents();
      renderAdmin();
    } catch (err) {
      const msg = String(err?.message || err);
      console.error("[adminStartQualifying] Failed", { eventId, error: msg, err });
      if (isInvalidJsonResponseError(err)) {
        toast(
          "Server error starting qualifying: the worker returned a non-JSON response. " +
          "Check that worker.js is deployed and migration 014 is applied.",
          "err"
        );
      } else {
        toast(`Failed to start qualifying: ${msg}`, "err");
      }
    }
  }

  async function adminUnlockBracket(eventId) {
    try {
      const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/unlock-bracket`, {});
      if (data?.ok === true) {
        toast("Bracket unlocked.", "ok");
        await adminRefreshEvents();
        const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
        if (ev?.comp_code) {
          await adminLoadBracketByCode(ev.comp_code, { silent: true });
        }
      } else toast(data?.error || data?.message || "Unable to unlock bracket.", "err");
    } catch (err) {
      const msg = err?.message || String(err);
      toast(`Failed to unlock bracket: ${msg}`, "err");
      console.error("Bracket unlock error:", err);
    }
  }

  async function adminForceClearRegenLock(eventId) {
    const ok = window.confirm("Recover bracket regeneration? This will clear the regen lock and delete staged rows (safe).");
    if (!ok) return;

    try {
      const data = await apiPost(
        `/admin/events/${encodeURIComponent(eventId)}/force-clear-regen-lock`,
        { reason: "admin recover" }
      );
      if (data?.ok === true) {
        toast(`Recovered. Deleted ${Number(data.deleted_staged || 0)} staged rows.`, "ok");
        await adminRefreshEvents();
        const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
        if (ev?.comp_code) {
          await adminLoadBracketByCode(ev.comp_code, { silent: true });
          await adminLoadRegistrations(ev.id, { silent: true });
        }
      } else {
        toast(data?.error || data?.message || "Failed to recover bracket.", "err");
      }
    } catch (err) {
      const msg = err?.message || String(err);
      toast(`Failed to recover bracket: ${msg}`, "err");
      console.error("Bracket recovery error:", err);
    }
  }

  async function maintenanceRepairPreview() {
    adminState.maintenance.loading = true;
    adminState.maintenance.error = null;
    renderAdmin();
    try {
      const data = await apiPost("/admin/maintenance/repair-all-next-match-ids", { apply: false, limit: 50 });
      adminState.maintenance.lastResult = data;
      toast("Preview done.", "ok");
    } catch (err) {
      adminState.maintenance.error = err?.message || String(err);
      toast(adminState.maintenance.error, "err");
    } finally {
      adminState.maintenance.loading = false;
      renderAdmin();
    }
  }

  async function maintenanceRepairApply() {
    if (adminState.maintenance.confirmText !== "REPAIR_NEXT_MATCH_ID") return;
    adminState.maintenance.loading = true;
    adminState.maintenance.error = null;
    renderAdmin();
    try {
      const data = await apiPost("/admin/maintenance/repair-all-next-match-ids", {
        apply: true,
        confirm: "REPAIR_NEXT_MATCH_ID",
        limit: 50,
      });
      adminState.maintenance.lastResult = data;
      toast(`Applied: ${data.total_updated_count} updated, ${data.total_skipped_count} skipped.`, "ok");
    } catch (err) {
      adminState.maintenance.error = err?.message || String(err);
      toast(adminState.maintenance.error, "err");
    } finally {
      adminState.maintenance.loading = false;
      renderAdmin();
    }
  }

  function maintenanceCopyReport() {
    const r = adminState.maintenance.lastResult;
    if (!r) {
      toast("No report yet", "err");
      return;
    }
    const lines = [
      `Repair next_match_id report | applied=${r.applied} | events_processed=${r.events_processed} | total_updated=${r.total_updated_count} | total_skipped=${r.total_skipped_count}`,
      "",
      ...(r.per_event || []).map((pe) => {
        const sample = (pe.unresolved_sample || []).map((u) => `  ${u.reason}`).join("\n");
        return `Event ${String(pe.event_id).slice(0, 8)}… | broken=${pe.bad_count} | fixable=${pe.updated_count} | unresolved=${pe.skipped_count}${sample ? "\n" + sample : ""}`;
      }),
    ];
    const text = lines.join("\n");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => toast("Report copied to clipboard", "ok")).catch(() => {
        copyReportFallback(text);
      });
    } else {
      copyReportFallback(text);
    }
  }

  function copyReportFallback(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      toast("Report copied to clipboard", "ok");
    } catch (e) {
      toast("Copy failed", "err");
    }
    document.body.removeChild(ta);
  }

  // Check if all matches in current round have winners and auto-advance if so
  async function checkAndAutoAdvanceRound(eventId) {
    const ev = adminState.events.find((item) => String(item.id) === String(eventId));
    const mode = String(ev?.bracket_mode || "");
    if (mode === "simple" || mode === "manual") return;
    // Prevent duplicate checks
    if (adminState.autoAdvance.checking) return;
    
    // Don't check too frequently (max once per 3 seconds)
    const now = Date.now();
    if (adminState.autoAdvance.lastCheckedAt && (now - adminState.autoAdvance.lastCheckedAt) < 3000) {
      return;
    }
    
    adminState.autoAdvance.checking = true;
    adminState.autoAdvance.lastCheckedAt = now;
    
    try {
      // Get bracket to check current round status
      const ev = adminState.events.find((x) => String(x.id) === String(eventId));
      if (!ev?.comp_code) {
        adminState.autoAdvance.checking = false;
        return;
      }
      
      const bracket = await apiGet(`/event/bracket?code=${encodeURIComponent(ev.comp_code)}`);
      if (!bracket || !bracket.matches || !Array.isArray(bracket.matches)) {
        adminState.autoAdvance.checking = false;
        return;
      }
      const nonThirdMatches = bracket.matches.filter(m => String(m.state || "").toLowerCase() !== "third");
      const maxRoundInBracket = nonThirdMatches.length
        ? Math.max(...nonThirdMatches.map(m => Number(m.round || 0)).filter(Number.isFinite))
        : 0;
      const finalRound = Number(bracket.final_round) > 0 ? Number(bracket.final_round) : maxRoundInBracket;
      
      // Find the highest round with matches
      const rounds = [...new Set(bracket.matches.map(m => Number(m.round || 0)))].sort((a, b) => b - a);
      if (rounds.length === 0) {
        adminState.autoAdvance.checking = false;
        return;
      }
      
      // Check each round from highest to lowest to find incomplete round
      for (const round of rounds) {
        const roundMatches = bracket.matches.filter(m => 
          Number(m.round) === round && 
          String(m.state || "").toLowerCase() !== "third"
        );
        
        if (roundMatches.length === 0) continue;
        
        // Check if all matches in this round have winners (excluding BYE matches)
        // Also check match status - matches with status 'complete' are done
        const incompleteMatches = roundMatches.filter(m => {
          const a = String(m.driver_a || "");
          const b = String(m.driver_b || "");
          const isBye = a === "BYE" || b === "BYE";
          const status = (m.status || "pending").toLowerCase();
          // Match is incomplete if: no winner AND not a BYE AND status is not 'complete'
          return !m.winner && !isBye && status !== "complete";
        });
        
        // If this round is complete, try to advance
        if (incompleteMatches.length === 0 && roundMatches.length > 0) {
          // Check if we already checked this round
          if (adminState.autoAdvance.lastCheckedRound === round) {
            adminState.autoAdvance.checking = false;
            return;
          }
          
          adminState.autoAdvance.lastCheckedRound = round;

          // Final battle rule: once finals are complete, stop progression and crown champion.
          if (finalRound > 0 && round >= finalRound) {
            adminState.autoAdvance.checking = false;
            return;
          }
          
          // With the pre-wired bracket skeleton, next-round rows ALWAYS exist
          // after generateBracket runs -- their driver slots stay empty
          // (TBD/null) until /advance-round invokes reseedPairsInline to pair
          // surviving winners "first vs last surviving seed". The original
          // guard ("skip if any next-round row exists") therefore prevented
          // auto-advance from ever firing for fresh brackets, leaving BYE
          // winners and judged R1 winners stuck on R1 visually until the
          // admin manually clicked Advance Round. Skip auto-advance only
          // when the next round has been GENUINELY populated already
          // (admin already advanced, or a concurrent auto-advance finished
          // the job) -- detected by ANY driver slot in next round carrying
          // a non-TBD label.
          const nextRound = round + 1;
          const nextRoundMatches = bracket.matches.filter(m =>
            Number(m.round) === nextRound &&
            String(m.state || "").toLowerCase() !== "third"
          );
          const nextRoundAlreadyPopulated = nextRoundMatches.length > 0 && nextRoundMatches.some(m => {
            const aLabel = String(m.driver_a || "").trim().toUpperCase();
            const bLabel = String(m.driver_b || "").trim().toUpperCase();
            const aFilled = aLabel && aLabel !== "TBD";
            const bFilled = bLabel && bLabel !== "TBD";
            return aFilled || bFilled;
          });
          
          if (nextRoundAlreadyPopulated) {
            // Next round slots are already filled, no work to do.
            adminState.autoAdvance.checking = false;
            return;
          }
          
          // All matches complete and next round is empty -- auto-advance.
          console.log(`[AUTO ADVANCE] Round ${round} is complete, auto-advancing to round ${nextRound}`);
          
          try {
            const result = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/advance-round`, {});
            
            if (result.complete) {
              toast(`Event completed! Winner: ${result.placements?.first || "N/A"}`, "ok");
              await adminRefreshEvents();
            } else {
              const roundName = result.round_name || displayRoundName(result.next_round || 0, null, null);
              toast(`Auto-advanced to ${roundName}`, "ok");
            }
            
            // Refresh bracket and events
            await adminRefreshEvents();
            if (ev?.comp_code) {
              await adminLoadBracketByCode(ev.comp_code, { silent: true });
            }
          } catch (err) {
            // Silently fail - manual advance button is still available
            console.log("[AUTO ADVANCE] Could not auto-advance:", err.message);
          }
          
          adminState.autoAdvance.checking = false;
          return;
        } else {
          // Round is incomplete: do NOT bail out of the whole loop. With the
          // pre-wired skeleton (Phase 4 generation), every round above the
          // active one will appear "incomplete" because its slots are TBD.
          // Keep scanning down to find the highest round that IS complete --
          // that's the round to advance from (mirrors the worker's
          // /advance-round "highest fully-decided round" selection logic).
          continue;
        }
      }
      
      adminState.autoAdvance.checking = false;
    } catch (err) {
      console.error("[AUTO ADVANCE] Error checking round:", err);
      adminState.autoAdvance.checking = false;
    }
  }

  async function adminAdvanceRound(eventId) {
    try {
      // Reset auto-advance tracking when manually advancing
      adminState.autoAdvance.lastCheckedRound = null;
      adminState.autoAdvance.lastCheckedAt = null;

      // Guardrail: do not advance after finals are complete.
      const ev = adminState.events.find((x) => String(x.id) === String(eventId));
      if (ev?.comp_code) {
        const bracket = await apiGet(`/event/bracket?code=${encodeURIComponent(ev.comp_code)}`);
        if (bracket && Array.isArray(bracket.matches) && bracket.matches.length > 0) {
          const nonThirdMatches = bracket.matches.filter(m => String(m.state || "").toLowerCase() !== "third");
          const maxRoundInBracket = nonThirdMatches.length
            ? Math.max(...nonThirdMatches.map(m => Number(m.round || 0)).filter(Number.isFinite))
            : 0;
          const finalRound = Number(bracket.final_round) > 0 ? Number(bracket.final_round) : maxRoundInBracket;
          const finalRoundMatches = nonThirdMatches.filter(m => Number(m.round || 0) === finalRound);
          const finalRoundComplete = finalRoundMatches.length > 0 && finalRoundMatches.every(m => {
            const a = String(m.driver_a || "");
            const b = String(m.driver_b || "");
            const isBye = a === "BYE" || b === "BYE";
            const status = String(m.status || "pending").toLowerCase();
            return !!m.winner || isBye || status === "complete";
          });
          const hasBeyondFinalRound = nonThirdMatches.some(m => Number(m.round || 0) > finalRound);

          if (finalRound > 0 && finalRoundComplete && !hasBeyondFinalRound) {
            toast("Final battle is complete. Winner is the event champion; no further rounds will be generated.", "ok");
            return;
          }
        }
      }
      
      const result = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/advance-round`, {});
      
      if (result.complete) {
        toast(`Event completed! Winner: ${result.placements?.first || "N/A"}`, "ok");
        await adminRefreshEvents();
      } else {
        const roundName = result.round_name || displayRoundName(result.next_round || 0, null, null);
        toast(`Advanced to ${roundName}. ${result.created || 0} match(es) created.`, "ok");
      }
      
      // Refresh bracket and events
      await adminRefreshEvents();
      const evAfter = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
      if (evAfter?.comp_code) {
        await adminLoadBracketByCode(evAfter.comp_code, { silent: true });
      }
    } catch (err) {
      console.error("Advance round error:", err);
      toast(err.message || "Failed to advance round. Make sure all matches in the current round have winners.", "err");
    }
  }

  async function adminAddRegistration(eventId) {
    const firstName = (adminPane.querySelector("#glCeAdmRegFirst")?.value || "").trim();
    const lastName = (adminPane.querySelector("#glCeAdmRegLast")?.value || "").trim();
    const email = (adminPane.querySelector("#glCeAdmRegEmail")?.value || "").trim();
    const city = (adminPane.querySelector("#glCeAdmRegCity")?.value || "").trim();
    const car = (adminPane.querySelector("#glCeAdmRegCar")?.value || "").trim();
    const hpRaw = adminPane.querySelector("#glCeAdmRegHp")?.value || "";
    const carNumber = (adminPane.querySelector("#glCeAdmRegCarNumber")?.value || "").trim();
    const hp = Number(hpRaw || 0) || null;
    if (!firstName || !lastName) throw new Error("First name and last name are required.");
    if (!email) throw new Error("Email is required.");
    if (!carNumber) throw new Error("Car number is required.");
    if (hpRaw && !Number.isFinite(hp)) throw new Error("HP must be a valid number.");
    const event = adminState.events.find((row) => String(row.id) === String(eventId));
    const cap = Number(event?.registration_cap || 0);
    if (!adminState.editingRegistration?.id && Number(event?.registration_cap_enabled || 0) === 1 && cap > 0 && registrationRows(adminState.regs).length >= cap) {
      const override = await showConfirmModal({
        title: "Registration cap reached",
        message: `The public registration cap of ${cap} has been reached. Add this driver anyway as an admin override?`,
        confirmText: "Add anyway",
        danger: false,
      });
      if (!override) return;
    }
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      car_number: carNumber,
      ...(city ? { city } : {}),
      ...(car ? { car } : {}),
      ...(hp != null ? { hp } : {}),
    };
    if (adminState.editingRegistration?.id) {
      await apiPatch(`/admin/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(adminState.editingRegistration.id)}`, payload);
      toast("Driver updated.", "ok");
    } else {
      await apiPost(`/admin/events/${encodeURIComponent(eventId)}/registrations`, payload);
      toast("Registration added.", "ok");
    }
    adminState.addDriverModalOpen = false;
    adminState.editingRegistration = null;
    await adminLoadRegistrations(eventId, { silent: true });
    await adminRefreshSelectedEventStatus({ silent: true });
    renderAdmin();
  }

  async function adminRemoveRegistration(eventId, regId) {
    const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
    if (!ev) return;
    const ok = await showConfirmModal({
      title: "Remove registration",
      message: "Are you sure you want to remove this registration?",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    await apiDelete(`/admin/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(regId)}`);
    toast("Registration removed.", "ok");
    await adminLoadRegistrations(eventId, { silent: true });
    await adminRefreshSelectedEventStatus({ silent: true });
    if (ev.comp_code) {
      await adminLoadBracketByCode(ev.comp_code, { silent: true });
    }
  }

  async function adminDownloadFlyer(eventId) {
    const url = `${API}${withStaffSessionToken(`/admin/events/${encodeURIComponent(eventId)}/flyer.pdf`)}`;
    try {
      const res = await fetch(url, { method: "GET", credentials: "same-origin" });
      if (!res.ok) {
        let msg = `Download failed (${res.status}).`;
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `event-${eventId}-flyer.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast(err?.message || "Unable to download PDF.", "err");
    }
  }

  async function adminSaveJudgeDisplayName(eventId, judgeCode, displayName) {
    await apiPatch(`/admin/events/${encodeURIComponent(eventId)}/judges/${encodeURIComponent(judgeCode)}`, {
      display_name: String(displayName || "").trim(),
    });
    if (adminState.judgeEditByCode) {
      delete adminState.judgeEditByCode[String(judgeCode || "")];
    }
    await adminLoadJudges(eventId, { silent: true });
    toast("Judge display name saved.", "ok");
  }

  async function adminBracketSaveRow(eventId, matchNo) {
    const editWrap = adminPane.querySelector("#glCeAdmBracketEdit");
    if (!editWrap) return;

    const selA = editWrap.querySelector(`select[data-brset-match="${String(matchNo)}"][data-brset-slot="a"]`);
    const selB = editWrap.querySelector(`select[data-brset-match="${String(matchNo)}"][data-brset-slot="b"]`);

    const a = String(selA?.value || "").trim() || null;
    const b = String(selB?.value || "").trim() || null;

    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/bracket/set-slot`, {
      round: 1,
      match_no: Number(matchNo),
      driver_a: a,
      driver_b: b,
    });

    toast(`Saved Match ${matchNo}.`, "ok");

    const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
    if (ev?.comp_code) await adminLoadBracketByCode(ev.comp_code, { silent: true });
  }

  function getDriverLabelFromMatch(match, slot) {
    if (slot === "a") {
      if (match.driver_a_label) return match.driver_a_label;
      if (match.a_first && match.a_last) return `${match.a_first} ${match.a_last}`;
      if (match.sa_first && match.sa_last) return `${match.sa_first} ${match.sa_last}`;
      if (match.driver_a && match.driver_a !== "BYE") return match.driver_a.substring(0, 8);
      return "TBD";
    } else {
      if (match.driver_b_label) return match.driver_b_label;
      if (match.b_first && match.b_last) return `${match.b_first} ${match.b_last}`;
      if (match.sb_first && match.sb_last) return `${match.sb_first} ${match.sb_last}`;
      if (match.driver_b && match.driver_b !== "BYE") return match.driver_b.substring(0, 8);
      return "TBD";
    }
  }

  async function adminSwapDrivers(eventId) {
    const panel = adminPane.querySelector("#glCeSwapDriversPanel");
    if (!panel) return;

    const matchSelect = panel.querySelector("#glCeSwapMatchSelect");
    const driverASelect = panel.querySelector("#glCeSwapDriverA");
    const driverBSelect = panel.querySelector("#glCeSwapDriverB");
    const reasonInput = panel.querySelector("#glCeSwapReason");
    const submitBtn = panel.querySelector("#glCeSwapSubmit");
    const statusSpan = panel.querySelector("#glCeSwapStatus");

    const matchId = String(matchSelect?.value || "").trim();
    const driverAId = String(driverASelect?.value || "").trim();
    const driverBId = String(driverBSelect?.value || "").trim();
    const reason = String(reasonInput?.value || "").trim();

    if (!matchId) {
      toast("Please select a match", "err");
      return;
    }
    if (!driverAId || !driverBId) {
      toast("Both drivers must be selected", "err");
      return;
    }
    if (!reason || reason.length < 5 || reason.length > 250) {
      toast("Reason must be between 5 and 250 characters", "err");
      return;
    }

    const ok = window.confirm("This will modify the bracket and will be logged. Continue?");
    if (!ok) return;

    // Disable inputs
    matchSelect.disabled = true;
    driverASelect.disabled = true;
    driverBSelect.disabled = true;
    reasonInput.disabled = true;
    submitBtn.disabled = true;
    statusSpan.textContent = "Saving…";
    statusSpan.style.color = "#666";

    try {
      await apiPost(`/admin/events/${encodeURIComponent(eventId)}/bracket/swap-drivers`, {
        match_id: matchId,
        driver_a_id: driverAId,
        driver_b_id: driverBId,
        reason: reason,
        confirm: true,
      });

      toast("Drivers swapped successfully", "ok");
      
      // Clear form
      matchSelect.value = "";
      driverASelect.innerHTML = '<option value="">Select match first</option>';
      driverBSelect.innerHTML = '<option value="">Select match first</option>';
      driverASelect.disabled = true;
      driverBSelect.disabled = true;
      reasonInput.value = "";
      submitBtn.disabled = true;
      statusSpan.textContent = "";

      // Refresh bracket
      const ev = adminState.events.find((x) => String(x.id) === String(adminState.selectedId));
      if (ev?.comp_code) {
        await adminLoadBracketByCode(ev.comp_code, { silent: true });
        renderAdminManageView();
      }
    } catch (err) {
      toast(err?.message || "Failed to swap drivers", "err");
      statusSpan.textContent = "";
      // Re-enable inputs on error (on success they're cleared and disabled appropriately)
      matchSelect.disabled = false;
      driverASelect.disabled = false;
      driverBSelect.disabled = false;
      reasonInput.disabled = false;
      // Trigger validation to set submit button state correctly
      if (matchSelect && driverASelect && driverBSelect && reasonInput && submitBtn) {
        const matchId = String(matchSelect.value || "").trim();
        const driverAId = String(driverASelect.value || "").trim();
        const driverBId = String(driverBSelect.value || "").trim();
        const reason = String(reasonInput.value || "").trim();
        const isValid = matchId && driverAId && driverBId && driverAId !== driverBId && reason.length >= 5 && reason.length <= 250;
        submitBtn.disabled = !isValid;
      } else {
        submitBtn.disabled = true;
      }
    } finally {
      // Re-enable inputs if not already handled in catch
      if (matchSelect.disabled) matchSelect.disabled = false;
      if (reasonInput.disabled) reasonInput.disabled = false;
    }
  }

  async function adminSimpleWinner(matchId, winnerId) {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Record manual winner",
      message: "This marks the selected driver as the winner of this battle.",
      confirmText: "Record Winner",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/bracket/manual-winner`, {
      match_id: matchId, winner_id: winnerId, confirm: true,
    });
    const ev = adminState.events.find((event) => String(event.id) === eventId);
    if (ev?.comp_code) await adminLoadBracketByCode(ev.comp_code, { silent: true });
    renderAdmin();
  }

  async function adminSimpleSwap(matchId, driverAId, driverBId) {
    const eventId = String(adminState.selectedId || "");
    const confirmed = await showConfirmModal({
      title: "Swap battle sides",
      message: "This reverses only the top and bottom drivers in this battle. It does not move either driver elsewhere.",
      confirmText: "Swap Sides",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/bracket/swap-drivers`, {
      match_id: matchId, driver_a_id: driverAId, driver_b_id: driverBId,
      reason: "Battle-local top/bottom swap", confirm: true,
    });
    const ev = adminState.events.find((event) => String(event.id) === eventId);
    if (ev?.comp_code) await adminLoadBracketByCode(ev.comp_code, { silent: true });
    renderAdmin();
  }

  async function adminSimpleAdvance(eventId) {
    const confirmed = await showConfirmModal({
      title: "Advance completed round",
      message: "This advances the winners from the completed Bracket Override round.",
      confirmText: "Advance",
      mountEl: adminPane,
    });
    if (!confirmed) return;
    await apiPost(`/admin/events/${encodeURIComponent(eventId)}/advance-round`, { confirm: true });
    const ev = adminState.events.find((event) => String(event.id) === String(eventId));
    if (ev?.comp_code) await adminLoadBracketByCode(ev.comp_code, { silent: true });
    await adminRefreshEvents();
    renderAdmin();
  }

  async function adminCompViewOpen(eventId, view = "live") {
    const nextId = String(eventId || "");
    adminStopCompPoll();
    adminState.selectedId = nextId;
    adminState.page = "comp";
    adminState.comp.view = view === "qualifying" ? "qualifying" : "live";
    adminState.editBracket = false;
    adminState.comp.payload = null;
    adminState.comp._lastSig = undefined;
    adminState.comp.lastAt = "";
    adminState.comp.watchdogRetried = false;
    adminState.comp.error = "";
    adminState.comp.loading = true;
    adminState.comp.judgeNotes = {
      openMatchId: null,
      loadingMatchId: null,
      error: "",
      byMatchId: {},
    };
    adminStartCompPoll();
    renderAdmin();
  }

  async function adminCompRefresh() {
    await adminCompPollTick({ reason: "manual", force: true });
  }

  function adminCompPopout() {
    const id = adminState.selectedId;
    if (!id) {
      toast("No event selected.", "err");
      return;
    }
    
    // Open popup window - Smaller and adaptive
    const popup = window.open(
      "",
      "liveJudging",
      "width=1000,height=700,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no"
    );
    
    if (!popup) {
      toast("Popup blocked. Please allow popups for this site.", "err");
      return;
    }

    // Initial loading state
    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Live Judging - Pop-out</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            background: #1a1a1a;
            color: #fff;
            padding: 20px;
            margin: 0;
          }
          .loading {
            text-align: center;
            padding: 40px;
            font-size: 18px;
          }
        </style>
      </head>
      <body>
        <div class="loading">Loading live judging data...</div>
      </body>
      </html>
    `);
    popup.document.close();

    // Store popup reference
    adminCompPopout._popup = popup;
    adminCompPopout._eventId = id;

    // Start polling and updating popup
    let pollInterval = null;
    let updateInFlight = false;
    const apiBase = API; // Store API base for popup
    // Get logo URL (use global LOGO_URL or fallback)
    const logoUrl = LOGO_URL || (root?.dataset?.logoUrl || root?.getAttribute?.('data-logo-url') || '').trim();
    // Reveal state: track winner + omt_count between polls so we can show a 5-second
    // overlay without relying on the backend lock window timing.
    let lastSeenOmtCount = 0;
    let lastSeenWinnerKey = ""; // matchId:winnerId
    let revealUntilMs = 0;
    
    async function updatePopup() {
      if (!popup || popup.closed) {
        clearInterval(pollInterval);
        return;
      }
      if (updateInFlight) return;
      updateInFlight = true;

      try {
        const selectedEvent = adminState.events.find((ev) => String(ev.id) === String(id));
        if (normalizeStatus(selectedEvent?.status) === "qualifying") {
          const data = await apiGet(
            `/admin/events/${encodeURIComponent(id)}/qualifying/current-run?include_locked=1`
          );
          renderQualifyingJudgingPopup(popup, data, logoUrl);
          return;
        }

        // Use fetch directly since popup doesn't have access to apiGet
        const apiUrl = `${apiBase}${withStaffSessionToken(`/admin/events/${encodeURIComponent(id)}/comp-view`)}`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Expected JSON but got ${contentType}. Response: ${text.substring(0, 100)}`);
        }
        
        const data = await response.json();
        
        if (!data || data.ok === false) {
          throw new Error(data?.error || 'Invalid response from server');
        }

        // Detect OMT or winner advance. When detected, inject a fullscreen reveal overlay
        // that persists for 5 seconds before normal rendering resumes. We inject BEFORE
        // document.write() since that wipes the popup DOM.
        const curMatch = data?.current_match || null;
        const curMatchIdStr = curMatch?.id != null ? String(curMatch.id) : "";
        const newOmtCount = Number(curMatch?.omt_count || 0);
        const curWinnerId = curMatch?.winner ? String(curMatch.winner) : "";
        const curWinnerLabel = curMatch?.winner_label || curMatch?.winner || "";
        const winnerKey = curMatchIdStr && curWinnerId ? `${curMatchIdStr}:${curWinnerId}` : "";
        const omtJustAdvanced = newOmtCount > lastSeenOmtCount && !curWinnerId;
        const winnerJustDeclared = !!winnerKey && winnerKey !== lastSeenWinnerKey;
        const nowMs = Date.now();
        let triggerReveal = null; // "omt" | "winner" | null
        if (omtJustAdvanced) {
          triggerReveal = "omt";
          lastSeenOmtCount = newOmtCount;
        } else if (winnerJustDeclared) {
          triggerReveal = "winner";
          lastSeenWinnerKey = winnerKey;
        } else {
          lastSeenOmtCount = Math.max(lastSeenOmtCount, newOmtCount);
          if (winnerKey) lastSeenWinnerKey = winnerKey;
        }

        if (triggerReveal && !popup.closed) {
          revealUntilMs = nowMs + WINNER_REVEAL_DELAY_MS;
          const isOmtReveal = triggerReveal === "omt";
          const aLabelRaw = String(curMatch?.driver_a_label || "Driver A");
          const bLabelRaw = String(curMatch?.driver_b_label || "Driver B");
          const revTally = Array.isArray(curMatch?.tally) ? curMatch.tally : [];
          const revJudges = Array.isArray(data?.current_judges) ? data.current_judges : [];
          const tallyLabelFor = (decId) => {
            if (decId === "omt") return "OMT";
            if (String(decId) === String(curMatch?.driver_a)) return aLabelRaw;
            if (String(decId) === String(curMatch?.driver_b)) return bLabelRaw;
            return String(decId || "");
          };
          const tallyItems = revTally
            .filter((t) => t && (t.count || 0) > 0)
            .map((t) => {
              const isOmtVote = t.winner === "omt";
              const bg = isOmtVote ? "rgba(255,193,7,0.18)" : "rgba(255,255,255,0.08)";
              const color = isOmtVote ? "#ffc107" : "#fff";
              return `<div style="background:${bg};border:1px solid rgba(255,255,255,0.12);border-radius:10px;padding:10px 18px;min-width:130px;text-align:center;color:${color};">
                <div style="font-size:11px;opacity:0.7;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:4px;">${esc(tallyLabelFor(t.winner))}</div>
                <div style="font-size:28px;font-weight:900;">${Number(t.count || 0)}</div>
                <div style="font-size:10px;opacity:0.6;">vote${Number(t.count || 0) === 1 ? "" : "s"}</div>
              </div>`;
            })
            .join("");
          const tallyBlock = tallyItems
            ? `<div style="display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:22px;">${tallyItems}</div>`
            : "";
          const judgeRows = revJudges
            .filter((j) => j && j.decision)
            .map((j) => {
              const name = esc(j.name || `Judge ${j.slot}`);
              const isOmtVote = j.decision === "omt";
              const pillColor = isOmtVote ? "#ffc107" : "#0a7a68";
              const pillBg = isOmtVote ? "rgba(255,193,7,0.18)" : "rgba(10,122,104,0.22)";
              return `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:8px 14px;">
                <div style="font-size:13px;font-weight:600;color:#e6e6e6;">${name}</div>
                <div style="font-size:12px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:${pillColor};background:${pillBg};padding:4px 10px;border-radius:999px;">${esc(tallyLabelFor(j.decision))}</div>
              </div>`;
            })
            .join("");
          const judgesBlock = judgeRows
            ? `<div style="display:flex;flex-direction:column;gap:6px;margin-top:18px;width:min(420px,92vw);">${judgeRows}</div>`
            : "";
          const headingColor = isOmtReveal ? "#ffc107" : "#4caf50";
          const headingGlow = isOmtReveal ? "rgba(255,193,7,0.55)" : "rgba(76,175,80,0.55)";
          const headingText = isOmtReveal ? "OMT" : "WINNER";
          const subText = isOmtReveal ? "ONE MORE TIME" : esc(curWinnerLabel);
          const subColor = isOmtReveal ? "#ffc107" : "#fff";
          try {
            let overlay = popup.document.getElementById('gl-reveal-overlay');
            if (!overlay) {
              overlay = popup.document.createElement('div');
              overlay.id = 'gl-reveal-overlay';
              popup.document.body.appendChild(overlay);
            }
            overlay.style.cssText = [
              'position:fixed;inset:0;background:rgba(0,0,0,0.95);z-index:10000;',
              'display:flex;flex-direction:column;align-items:center;justify-content:center;',
              'padding:30px;overflow:auto;animation:glRevealFadeIn 0.3s ease;',
            ].join('');
            overlay.innerHTML = [
              '<style>@keyframes glRevealFadeIn{from{opacity:0}to{opacity:1}}',
              '@keyframes glRevealPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.88;transform:scale(1.02)}}</style>',
              '<div style="text-align:center;color:#fff;max-width:960px;width:100%;">',
              `<div style="font-size:clamp(56px,9vw,88px);font-weight:900;letter-spacing:6px;color:${headingColor};`,
              `text-shadow:0 0 36px ${headingGlow};animation:glRevealPulse 1.5s ease-in-out infinite;line-height:1;">${headingText}</div>`,
              `<div style="font-size:clamp(22px,3vw,40px);font-weight:bold;letter-spacing:3px;margin-top:14px;color:${subColor};text-transform:uppercase;">${subText}</div>`,
              tallyBlock,
              `<div style="display:flex;justify-content:center;">${judgesBlock}</div>`,
              '<div style="margin-top:30px;opacity:0.55;font-size:14px;">Next battle in 5 seconds...</div>',
              '</div>',
            ].join('');
          } catch (_) {}
        }

        // During the reveal window, skip the HTML rewrite so the overlay stays visible.
        if (nowMs < revealUntilMs) {
          updateInFlight = false;
          return;
        }
        
        renderLiveJudgingPopup(popup, data, logoUrl);
        
        // Fade back in smoothly
        setTimeout(() => {
          const newMainContent = popup.document.body.querySelector('.gl-ce-comp-score');
          if (newMainContent) {
            newMainContent.style.opacity = '0.8';
            setTimeout(() => {
              newMainContent.style.opacity = '1';
            }, 10);
          }
        }, 10);
      } catch (e) {
        console.error("Pop-out poll error:", e);
        if (!popup.closed) {
          const msg = friendlyJudgeNetworkError(e, "Temporary error loading live judging data.");
          popup.document.body.innerHTML = `
            <div style="padding:40px; text-align:center;">
              <div style="color:#ff6b6b; font-size:18px; margin-bottom:10px;">Connection issue</div>
              <div style="color:#c8c8c8; font-size:14px; margin-bottom:8px;">${esc(msg)}</div>
              <div style="color:#888; font-size:12px; margin-bottom:14px;">This pop-out will keep trying automatically. You can also retry or resync now.</div>
              <div style="display:flex; justify-content:center; gap:8px; flex-wrap:wrap;">
                <button onclick="window.__glRetry && window.__glRetry()" style="padding:8px 12px; border-radius:6px; border:1px solid #4caf50; background:#1f7a56; color:#fff; cursor:pointer;">Retry</button>
                <button onclick="window.__glResync && window.__glResync()" style="padding:8px 12px; border-radius:6px; border:1px solid #666; background:#2a2a2a; color:#fff; cursor:pointer;">Resync</button>
              </div>
            </div>
          `;
        }
      } finally {
        updateInFlight = false;
      }
    }

    // Initial update
    updatePopup();
    
    // Poll every 2 seconds
    pollInterval = setInterval(() => {
      if (popup && !popup.closed) {
        updatePopup();
      } else {
        clearInterval(pollInterval);
      }
    }, 2000);

    popup.__glRetry = () => {
      updatePopup().catch(() => {});
    };
    popup.__glResync = () => {
      if (adminState.page === "comp" && adminState.selectedId) {
        adminCompPollTick({ reason: "manual", force: true }).catch(() => {});
      }
      updatePopup().catch(() => {});
    };
    popup.addEventListener("focus", () => {
      updatePopup().catch(() => {});
    });
  }

  function renderQualifyingJudgingPopup(popup, data, logoUrl) {
    if (!popup || popup.closed) return;
    const run = data?.run || null;
    const config = qualifyingDisplayConfig(data);
    const position = qualifyingPositionLabel(data);
    const rawScore1 = run ? qualifyingScoreBySlot(run, 1) : null;
    const rawScore2 = run ? qualifyingScoreBySlot(run, 2) : null;
    const rawScore3 = run ? qualifyingScoreBySlot(run, 3) : null;
    const has1 = Number.isFinite(Number(rawScore1));
    const has2 = Number.isFinite(Number(rawScore2));
    const has3 = Number.isFinite(Number(rawScore3));
    const score1 = run ? formatQualifyingScoreValue(rawScore1) : "-";
    const score2 = run ? formatQualifyingScoreValue(rawScore2) : "-";
    const score3 = run ? formatQualifyingScoreValue(rawScore3) : "-";
    const result = run ? formatQualifyingScoreValue(run.avg_score) : "-";
    const scoredCount = (has1 ? 1 : 0) + (has2 ? 1 : 0) + (has3 ? 1 : 0);
    const isLocked = normalizeStatus(run?.status) === "locked";

    const evLookup = adminState.events.find((e) => String(e.id) === String(data?.event_id));
    const resolvedEventName = eventDisplayName(evLookup, "Event");

    const runNoText = run?.run_no ? `Run ${run.run_no}` : "";
    const subtitleText = run
      ? `Qualifying${runNoText ? ` — ${runNoText}` : ""}`
      : "Qualifying";

    const statusClass = isLocked ? "decided" : "pending";
    const statusText = run
      ? (isLocked
          ? `${config.resultLabel.toUpperCase()}: ${result}${position && position !== "-" ? ` • POSITION: ${position}` : ""}`
          : `${scoredCount} of ${REQUIRED_LIVE_JUDGE_VOTES} judges scored`)
      : "Waiting for qualifying run...";

    const judgeRow = (slot, scoreText, hasScore) => `
      <div class="gl-ce-comp-judge-row ${hasScore ? "scored-row" : "waiting-row"}">
        <div class="gl-ce-comp-judge-name-col">
          <div class="gl-ce-comp-judge-name">${esc(config.roleForSlot(slot).toUpperCase())}</div>
          ${hasScore
            ? `<div class="gl-ce-comp-judge-decision scored">${esc(scoreText)}</div>`
            : `<div class="gl-ce-comp-judge-waiting">Waiting</div>`}
        </div>
      </div>
    `;

    const driverMetaParts = [];
    if (run?.driver_number != null && String(run.driver_number).trim() !== "") {
      driverMetaParts.push(`Driver #${esc(String(run.driver_number))}`);
    }
    if (run?.run_no) {
      driverMetaParts.push(`Run ${esc(String(run.run_no))}`);
    }
    const driverMetaHtml = driverMetaParts.length
      ? `<div class="gl-ce-comp-driver-meta">${driverMetaParts.join(" • ")}</div>`
      : "";

    const driverCardHtml = run
      ? `
        <div class="gl-ce-comp-driver-card ${isLocked ? "winner" : ""}">
          <h4>${esc(run.driver_name || "Driver")}</h4>
          ${driverMetaHtml}
        </div>
      `
      : `
        <div class="gl-ce-comp-driver-card">
          <h4 style="opacity:0.6;">No qualifying run available</h4>
          <div class="gl-ce-comp-driver-meta">Set a current run from Comp View to begin.</div>
        </div>
      `;

    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>${esc(resolvedEventName)} - Qualifying Judging</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: Arial, sans-serif;
            background: #1a1a1a;
            color: #fff;
            padding: 20px;
            margin: 0;
          }
          .gl-ce-comp-score {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            border-radius: 8px;
            padding: 20px;
            color: #fff;
          }
          .gl-ce-comp-score-header {
            background: linear-gradient(90deg, #076251 0%, #0a7a68 100%);
            padding: 15px 20px;
            border-radius: 6px;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .gl-ce-comp-score-header .logo {
            height: 80px;
            width: auto;
            object-fit: contain;
            max-width: 200px;
          }
          .gl-ce-comp-score-header h3 {
            margin: 0;
            font-size: 20px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .gl-ce-comp-score-header .battle-info {
            font-size: 12px;
            opacity: 0.9;
            margin-top: 4px;
          }
          .gl-ce-comp-drivers {
            display: grid;
            grid-template-columns: 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .gl-ce-comp-driver-card {
            background: rgba(255,255,255,0.05);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            padding: 15px;
            text-align: center;
            transition: all 0.3s;
          }
          .gl-ce-comp-driver-card.winner {
            border-color: #076251;
            background: rgba(7,98,81,0.2);
            box-shadow: 0 0 15px rgba(7,98,81,0.4);
          }
          .gl-ce-comp-driver-card h4 {
            margin: 0 0 5px 0;
            font-size: 24px;
            font-weight: bold;
          }
          .gl-ce-comp-driver-meta {
            font-size: 12px;
            opacity: 0.8;
            margin-top: 8px;
            line-height: 1.6;
            text-align: center;
          }
          .gl-ce-comp-judges-section {
            margin-bottom: 20px;
          }
          .gl-ce-comp-judges {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .gl-ce-comp-judge-row {
            display: grid;
            grid-template-columns: 1fr;
            align-items: center;
            padding: 12px;
            background: rgba(255,255,255,0.03);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            border-left: 3px solid rgba(255,255,255,0.2);
            gap: 15px;
          }
          .gl-ce-comp-judge-row.scored-row {
            border-left-color: #076251;
            background: rgba(7,98,81,0.1);
          }
          .gl-ce-comp-judge-row.waiting-row {
            border-left-color: rgba(200, 200, 200, 0.35);
          }
          .gl-ce-comp-judge-name-col {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            min-width: 0;
          }
          .gl-ce-comp-judge-name {
            font-weight: bold;
            font-size: 15px;
            text-align: center;
          }
          .gl-ce-comp-judge-waiting {
            font-size: 12px;
            font-weight: 600;
            color: #aaa;
            letter-spacing: 0.04em;
          }
          .gl-ce-comp-judge-decision {
            font-size: 16px;
            font-weight: bold;
            padding: 6px 14px;
            border-radius: 4px;
            min-width: 70px;
            text-align: center;
          }
          .gl-ce-comp-judge-decision.scored {
            background: rgba(7,98,81,0.3);
            color: #0a7a68;
          }
          .gl-ce-comp-status {
            text-align: center;
            padding: 12px;
            margin-top: 15px;
            border-radius: 6px;
            font-weight: bold;
          }
          .gl-ce-comp-status.pending {
            background: rgba(255,193,7,0.2);
            color: #ffc107;
          }
          .gl-ce-comp-status.decided {
            background: rgba(7,98,81,0.2);
            color: #0a7a68;
          }
        </style>
      </head>
      <body>
        <div class="gl-ce-comp-score">
          <div class="gl-ce-comp-score-header">
            <img src="https://cdn.shopify.com/s/files/1/0720/3137/1432/files/20251119175125-1f0c5705-e2a9-6f32-9f1c-22a1214d2021.png?v=1763574999" alt="Ghostline Club" class="logo" onerror="this.style.display='none';" />
            <div>
              <h3>${esc(resolvedEventName)}</h3>
              <div class="battle-info">${esc(subtitleText)}</div>
            </div>
          </div>

          <div class="gl-ce-comp-drivers">
            ${driverCardHtml}
          </div>

          ${run ? `
          <div class="gl-ce-comp-judges-section">
            <div class="gl-ce-comp-judges">
              ${judgeRow(1, score1, has1)}
              ${judgeRow(2, score2, has2)}
              ${judgeRow(3, score3, has3)}
            </div>
          </div>
          ` : ""}

          <div class="gl-ce-comp-status ${statusClass}">
            ${esc(statusText)}
          </div>
        </div>
      </body>
      </html>
    `);
    popup.document.close();
  }

  function renderLiveJudgingPopup(popup, payload, logoUrl) {
    if (!popup || popup.closed) return;
    
    if (!payload) {
      popup.document.body.innerHTML = `
        <div style="padding:40px; text-align:center; color:#999;">
          No data available. Make sure the event is LIVE and a current battle is set.
        </div>
      `;
      return;
    }
    
    const ev = payload.event || null;
    const cur = payload.current_match || null;
    const judges = payload.current_judges || [];
    const st = normalizeStatus(ev?.status);
    const isEventComplete = st === "complete" || st === "completed";
    const finalPodium = payload?.podium || null;
    const selectedEvent = adminState.selectedId
      ? adminState.events.find((e) => String(e.id) === String(adminState.selectedId))
      : null;
    const resolvedEventName = eventDisplayName(ev, "") || eventDisplayName(selectedEvent, "") || "Event";

    if (isEventComplete) {
      const standingsHtml =
        finalPodium && (finalPodium.first || finalPodium.second || finalPodium.third)
          ? podiumMarkup(finalPodium)
          : `<div style="text-align:center; color:#999;">Final standings pending...</div>`;
      popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${esc(resolvedEventName)} - Final Standings</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              background: #1a1a1a;
              color: #fff;
              padding: 20px;
              margin: 0;
            }
            .gl-ce-card {
              background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
              border-radius: 8px;
              padding: 16px;
            }
            .gl-ce-podium { margin-top: 6px; }
            .gl-ce-podium-container { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; align-items: end; }
            .gl-ce-podium-place { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 12px; text-align: center; }
            .gl-ce-podium-first { border-color: rgba(255, 215, 0, 0.5); }
            .gl-ce-podium-second { border-color: rgba(192, 192, 192, 0.5); }
            .gl-ce-podium-third { border-color: rgba(205, 127, 50, 0.5); }
            .gl-ce-podium-label { font-size: 12px; color: #c8c8c8; margin-top: 6px; }
            .gl-ce-podium-name { font-weight: 700; margin-top: 4px; }
          </style>
        </head>
        <body>
          <div class="gl-ce-card">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:10px;">
              <h3 style="margin:0;">${esc(resolvedEventName)}</h3>
              <span style="font-size:12px; color:#c8c8c8;">Event Complete</span>
            </div>
            ${standingsHtml}
          </div>
        </body>
        </html>
      `);
      popup.document.close();
      return;
    }
    
    if (!cur) {
      popup.document.body.innerHTML = `
        <div style="padding:20px; text-align:center; background:rgba(255,255,255,0.05); border-radius:8px;">
          <div style="color:#999; margin-bottom:10px;">No current battle selected.</div>
          <div style="font-size:12px; opacity:0.7;">Go to Comp View and click "Set Current" on a match to view live judging.</div>
        </div>
      `;
      return;
    }

    const aLabel = esc(cur.driver_a_label || "Driver A");
    const bLabel = esc(cur.driver_b_label || "Driver B");
    const isIrl = isIrlEventType(payload?.event || ev);
    const popupMaxRoundFromMatches = Array.isArray(payload?.bracket?.matches)
      ? Math.max(0, ...payload.bracket.matches.filter((m) => !isConsolationMatch(m)).map(m => Number(m?.round || 0)).filter(Number.isFinite))
      : 0;
    const popupFinalRound = popupMaxRoundFromMatches > 0
      ? popupMaxRoundFromMatches
      : Number(payload?.bracket?.final_round || 0);
    const popupMatchTitle = buildJudgeMatchTitle(cur, popupFinalRound || null);
    const aMetaHtml = renderJudgeDriverMetaHtml("driver_a", cur, isIrl, "gl-ce-comp-driver-meta");
    const bMetaHtml = renderJudgeDriverMetaHtml("driver_b", cur, isIrl, "gl-ce-comp-driver-meta");
    const votedCount = judges.filter((j) => j?.decision != null && String(j.decision).trim() !== "").length;
    const allJudged = judges.length >= REQUIRED_LIVE_JUDGE_VOTES && votedCount >= REQUIRED_LIVE_JUDGE_VOTES && judges.every((j) => j?.decision != null && String(j.decision).trim() !== "");
    const showFinalJudgeBreakdown = allJudged;
    const showJudgeRows = judges.length > 0;
    const judgeTotalForStatus = judges.length > 0 ? judges.length : REQUIRED_LIVE_JUDGE_VOTES;
    // When the backend is in the OMT reveal window it sends winner:"omt" which is not a real
    // driver. Treat it as null so the OMT status logic fires correctly for all vote splits.
    const isOmtReveal = !!(cur?.is_omt_reveal);
    const winner = (cur.winner && !isOmtReveal) ? esc(cur.winner_label || cur.winner) : null;

    // Get tally counts
    const tally = cur.tally || [];
    const driverAVotes = tally.find(t => t.winner === cur.driver_a)?.count || 0;
    const driverBVotes = tally.find(t => t.winner === cur.driver_b)?.count || 0;
    const omtVotes = tally.find(t => t.winner === "omt")?.count || 0;
    const allJudgedNoDecision = allJudged && !winner && omtVotes < 2;
    const liveStatusClass = winner ? "decided" : (isOmtReveal || omtVotes >= 2 || allJudgedNoDecision) ? "omt" : "pending";
    const liveStatusText = winner
      ? `WINNER: ${winner}`
      : (isOmtReveal || omtVotes >= 2)
        ? "OMT - ONE MORE TIME"
        : allJudgedNoDecision
          ? "TIE - OMT TIEBREAKER REQUIRED"
          : `${votedCount} of ${judgeTotalForStatus} judges voted`;

    // Get logo URL (use provided or fallback)
    const finalLogoUrl = logoUrl || '';

    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>${esc(resolvedEventName)} - ${esc(popupMatchTitle)}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: Arial, sans-serif;
            background: #1a1a1a;
            color: #fff;
            padding: 20px;
            margin: 0;
          }
          .gl-ce-comp-score {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            border-radius: 8px;
            padding: 20px;
            color: #fff;
          }
          .gl-ce-comp-score-header {
            background: linear-gradient(90deg, #076251 0%, #0a7a68 100%);
            padding: 15px 20px;
            border-radius: 6px;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .gl-ce-comp-score-header .logo {
            height: 80px;
            width: auto;
            object-fit: contain;
            max-width: 200px;
          }
          .gl-ce-comp-score-header h3 {
            margin: 0;
            font-size: 20px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .gl-ce-comp-score-header .battle-info {
            font-size: 12px;
            opacity: 0.9;
            margin-top: 4px;
          }
          .gl-ce-comp-drivers {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .gl-ce-comp-driver-card {
            background: rgba(255,255,255,0.05);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            padding: 15px;
            text-align: center;
            transition: all 0.3s;
          }
          .gl-ce-comp-driver-card.winner {
            border-color: #076251;
            background: rgba(7,98,81,0.2);
            box-shadow: 0 0 15px rgba(7,98,81,0.4);
          }
          .gl-ce-comp-driver-card h4 {
            margin: 0 0 5px 0;
            font-size: 24px;
            font-weight: bold;
          }
          .gl-ce-comp-driver-meta {
            font-size: 12px;
            opacity: 0.8;
            margin-top: 8px;
            line-height: 1.6;
            text-align: center;
          }
          .gl-ce-comp-judges-section {
            margin-bottom: 20px;
          }
          .gl-ce-comp-judges {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .gl-ce-comp-judge-row {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            padding: 12px;
            background: rgba(255,255,255,0.03);
            border: 2px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            border-left: 3px solid rgba(255,255,255,0.2);
            gap: 15px;
          }
          .gl-ce-comp-judge-row.winner-row {
            border-left-color: #076251;
            background: rgba(7,98,81,0.1);
          }
          .gl-ce-comp-judge-row.omt-row {
            border-left-color: #ffc107;
            background: rgba(255,193,7,0.1);
          }
          .gl-ce-comp-judge-decision-col {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }
          .gl-ce-comp-judge-decision-col.left {
            align-items: flex-start;
          }
          .gl-ce-comp-judge-decision-col.right {
            align-items: flex-end;
          }
          .gl-ce-comp-judge-name {
            font-weight: bold;
            font-size: 15px;
            text-align: center;
          }
          .gl-ce-comp-judge-name-col {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            min-width: 0;
          }
          .gl-ce-comp-judge-waiting {
            font-size: 12px;
            font-weight: 600;
            color: #aaa;
            letter-spacing: 0.04em;
          }
          .gl-ce-comp-judge-row.waiting-row {
            border-left-color: rgba(200, 200, 200, 0.35);
          }
          .gl-ce-comp-judge-decision {
            font-size: 13px;
            font-weight: bold;
            padding: 6px 12px;
            border-radius: 4px;
          }
          .gl-ce-comp-judge-decision.winner {
            background: rgba(7,98,81,0.3);
            color: #0a7a68;
          }
          .gl-ce-comp-judge-decision.omt {
            background: rgba(255,193,7,0.3);
            color: #ffc107;
          }
          .gl-ce-comp-judge-decision.pending {
            opacity: 0.5;
            color: #999;
          }
          .gl-ce-comp-judge-notes {
            font-size: 12px;
            opacity: 0.8;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid rgba(255,255,255,0.1);
          }
          .gl-ce-comp-judge-notes div {
            margin-top: 5px;
          }
          .gl-ce-comp-judge-notes div:first-child {
            margin-top: 0;
          }
          .gl-ce-comp-status {
            text-align: center;
            padding: 12px;
            margin-top: 15px;
            border-radius: 6px;
            font-weight: bold;
          }
          .gl-ce-comp-status.pending {
            background: rgba(255,193,7,0.2);
            color: #ffc107;
          }
          .gl-ce-comp-status.decided {
            background: rgba(7,98,81,0.2);
            color: #0a7a68;
          }
        .gl-ce-comp-status.omt {
          background: rgba(255,193,7,0.3);
          color: #ffc107;
        }
        .gl-ce-winner-screen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.95);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .gl-ce-winner-screen-content {
          text-align: center;
          color: #fff;
        }
        .gl-ce-winner-screen h1 {
          font-size: 72px;
          font-weight: bold;
          margin: 0 0 20px 0;
          text-transform: uppercase;
          letter-spacing: 4px;
          color: #076251;
          text-shadow: 0 0 20px rgba(7,98,81,0.5);
        }
        .gl-ce-winner-screen h2 {
          font-size: 48px;
          font-weight: bold;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        .gl-ce-winner-screen .countdown {
          font-size: 24px;
          margin-top: 40px;
          opacity: 0.8;
        }
      </style>
      </head>
      <body>
        <div class="gl-ce-comp-score">
          <div class="gl-ce-comp-score-header">
            <img src="https://cdn.shopify.com/s/files/1/0720/3137/1432/files/20251119175125-1f0c5705-e2a9-6f32-9f1c-22a1214d2021.png?v=1763574999" alt="Ghostline Club" class="logo" onerror="this.style.display='none';" />
            <div>
              <h3>${esc(resolvedEventName)}</h3>
              <div class="battle-info">${esc(popupMatchTitle)}</div>
            </div>
          </div>

          <div class="gl-ce-comp-drivers">
            <div class="gl-ce-comp-driver-card ${winner === aLabel ? 'winner' : ''}">
              <h4>${aLabel}</h4>
              ${aMetaHtml}
              ${showFinalJudgeBreakdown ? (() => {
                const driverAJudges = judges.filter(j => j.decision === cur.driver_a).map(j => j.name || `Judge ${j.slot}`).join(', ');
                return driverAJudges ? `<div style="font-size: 11px; color: #0a7a68; margin-top: 8px; font-weight: 500;">Judges: ${esc(driverAJudges)}</div>` : '';
              })() : ""}
            </div>
            <div class="gl-ce-comp-driver-card ${winner === bLabel ? 'winner' : ''}">
              <h4>${bLabel}</h4>
              ${bMetaHtml}
              ${showFinalJudgeBreakdown ? (() => {
                const driverBJudges = judges.filter(j => j.decision === cur.driver_b).map(j => j.name || `Judge ${j.slot}`).join(', ');
                return driverBJudges ? `<div style="font-size: 11px; color: #0a7a68; margin-top: 8px; font-weight: 500;">Judges: ${esc(driverBJudges)}</div>` : '';
              })() : ""}
            </div>
          </div>

          <div class="gl-ce-comp-judges-section">
            <div class="gl-ce-comp-judges">
              ${showJudgeRows ? renderLiveJudgeRowsHtml(judges, cur, aLabel, bLabel) : ""}
            </div>
          </div>

          <div class="gl-ce-comp-status ${liveStatusClass}" style="margin-top: 20px;">
            ${liveStatusText}
          </div>
        </div>
      </body>
      </html>
    `);
    popup.document.close();
  }

  async function adminSetCurrentMatch(matchId) {
    const id = adminState.selectedId;
    if (!id) return;
    
    // Save scroll positions before refresh
    // The scrolling element is .gl-ce-comp-bracket-container (not .gl-ce-bracket-inner or .gl-ce-bracket-container)
    const bracketContainer = adminPane.querySelector(".gl-ce-comp-bracket-container");
    const matchControls = adminPane.querySelector("#glCeCompMatchControls");
    const bracketScrollTop = bracketContainer?.scrollTop || 0;
    const bracketScrollLeft = bracketContainer?.scrollLeft || 0;
    const matchControlsScrollTop = matchControls?.scrollTop || 0;
    
    await apiPost(`/admin/events/${encodeURIComponent(id)}/set-current-match`, { match_id: matchId });
    toast("Current battle set.", "ok");

    // Optimistically sync local comp payload so this view updates immediately.
    if (adminState.comp?.payload && adminState.comp.payload.bracket?.matches) {
      const matches = Array.isArray(adminState.comp.payload.bracket.matches) ? adminState.comp.payload.bracket.matches : [];
      const selected = matches.find(m => String(m.id) === String(matchId)) || null;
      adminState.comp.payload.current_match_id = matchId;
      adminState.comp.payload.current_match = selected;
      applyCompViewLiveUpdate(adminState.comp.payload);
      adminState.comp._lastSig = compPayloadSig(adminState.comp.payload);
    }
    
    // Use silent refresh to preserve scroll
    await adminCompPollTick({ reason: "manual", force: true });
    
    // Restore scroll positions after render
    // The scrolling element is .gl-ce-comp-bracket-container (not .gl-ce-bracket-inner or .gl-ce-bracket-container)
    requestAnimationFrame(() => {
      // Wait for DOM to settle before restoring scroll
      setTimeout(() => {
        const newBracketContainer = adminPane.querySelector(".gl-ce-comp-bracket-container");
        const newMatchControls = adminPane.querySelector("#glCeCompMatchControls");
        if (newBracketContainer) {
          newBracketContainer.scrollTop = bracketScrollTop;
          newBracketContainer.scrollLeft = bracketScrollLeft;
        }
        if (newMatchControls && matchControlsScrollTop > 0) {
          newMatchControls.scrollTop = matchControlsScrollTop;
        }
      }, 50); // Small delay to ensure DOM is fully rendered
    });
  }

  async function adminOverrideMatch(matchId, winner, drivers = {}) {
    const eventId = String(adminState.selectedId || "");
    if (!eventId) throw new Error("No event selected.");
    const path = `/admin/events/${encodeURIComponent(eventId)}/matches/${encodeURIComponent(matchId)}/override`;
    const repairOnly = drivers.repair_only === true;
    const request = {
      ...(repairOnly ? { repair_only: true } : { winner: winner || null }),
      ...(drivers.slot_a_driver_id ? { slot_a_driver_id: drivers.slot_a_driver_id } : {}),
      ...(drivers.slot_b_driver_id ? { slot_b_driver_id: drivers.slot_b_driver_id } : {}),
    };
    const preview = await apiPost(path, { ...request, preview: true });
    const impact = preview?.impact || {};
    if (impact.requires_confirmation) {
      const affected = Number(impact.affected_match_count || 0);
      const scored = Number(impact.scored_match_count || 0);
      const confirmed = await showConfirmModal({
        title: "Clear Dependent Bracket Results?",
        message: `This change affects ${affected} downstream match${affected === 1 ? "" : "es"}, including ${scored} with scores or a result. Those dependent results and votes will be cleared; unrelated bracket branches will remain unchanged.`,
        confirmText: "Apply Override",
        danger: true,
      });
      if (!confirmed) return false;
    }
    await apiPost(path, { ...request, confirm_cascade: true });
    toast("Override applied.", "ok");
    await adminCompPollTick({ reason: "manual", force: true });
    
    // Check if round should auto-advance after override
    if (adminState.selectedId) {
      setTimeout(() => checkAndAutoAdvanceRound(adminState.selectedId), 2000);
    }
    return true;
  }

  // -----------------------------
  // Message listener for popup communication
  // -----------------------------
  // Dedupe judge-score-submit postMessages. The pop-out generates a unique requestId per
  // Submit click; if the same id arrives twice (double-click, retry, duplicate dispatch),
  // we ignore the second one so it can't create a second vote row.
  const _seenJudgeScoreRequestIds = new Set();
  window.addEventListener("message", async (event) => {
    // Security: only accept messages from same origin
    if (event.origin !== window.location.origin) return;

    if (event.data.type === "judge-score-submit") {
      const requestId = String(event.data.requestId || "");
      if (requestId && _seenJudgeScoreRequestIds.has(requestId)) return;
      if (requestId) _seenJudgeScoreRequestIds.add(requestId);
      const sourceWindow = event.source;
      try {
        const scoreResult = await judgeSubmitScore(
          event.data.matchId,
          event.data.decision,
          event.data.notes_run1,
          event.data.notes_run2,
          {
            interactiveRetry: false,
            expectedBattleNo: event.data.expectedBattleNo,
            expectedOmtCount: event.data.expectedOmtCount,
            judgeToken: event.data.judgeToken,
          }
        );
        // Send result to popup immediately (before the 5s reveal delay) so popup can show
        // its own OMT overlay and close on its own schedule.
        if (sourceWindow && typeof sourceWindow.postMessage === "function" && requestId) {
          sourceWindow.postMessage(
            {
              type: "judge-score-submit-result",
              requestId,
              ok: true,
              omt: scoreResult?.omt === true,
              tally: scoreResult?.tally || [],
              driverALabel: (judgeState.matches || []).find(m => String(m.id) === String(event.data.matchId))?.driver_a_label || "",
              driverBLabel: (judgeState.matches || []).find(m => String(m.id) === String(event.data.matchId))?.driver_b_label || "",
              voteAId: (judgeState.matches || []).find(m => String(m.id) === String(event.data.matchId))?.vote_a_id || "",
              voteBId: (judgeState.matches || []).find(m => String(m.id) === String(event.data.matchId))?.vote_b_id || "",
            },
            window.location.origin
          );
        }
        // Handle result reveal and panel refresh in the background (non-blocking for popup)
        judgeHandlePostScore(event.data.matchId, scoreResult).catch((e) => {
          console.error("[JUDGE/POST-SCORE]", e?.message || e);
        });
      } catch (err) {
        const msg = friendlyJudgeNetworkError(err, "Score submission failed.");
        toast(msg, "err");
        if (sourceWindow && typeof sourceWindow.postMessage === "function" && requestId) {
          sourceWindow.postMessage(
            { type: "judge-score-submit-result", requestId, ok: false, error: msg },
            window.location.origin
          );
        }
      }
    } else if (event.data.type === "judge-notes-draft") {
      const eventId = String(event.data.eventId || judgeState.event?.id || "").trim();
      const matchId = String(event.data.matchId || "").trim();
      const notes = String(event.data.notes || "");
      const force = !!event.data.force;
      if (!eventId || !matchId || !judgeState.token) return;
      const key = `${eventId}::${matchId}`;
      if (!force && judgeState.notesCache[key] === notes) {
        if (openJudgeScoreModal._popup && !openJudgeScoreModal._popup.closed) {
          openJudgeScoreModal._popup.postMessage(
            { type: "judge-notes-save-status", matchId, status: "saved", notes },
            window.location.origin
          );
        }
        return;
      }
      try {
        await judgeSaveMatchNotes(eventId, matchId, notes);
        judgeState.notesCache[key] = notes;
        if (openJudgeScoreModal._popup && !openJudgeScoreModal._popup.closed) {
          openJudgeScoreModal._popup.postMessage(
            { type: "judge-notes-save-status", matchId, status: "saved", notes },
            window.location.origin
          );
        }
      } catch (err) {
        if (openJudgeScoreModal._popup && !openJudgeScoreModal._popup.closed) {
          openJudgeScoreModal._popup.postMessage(
            { type: "judge-notes-save-status", matchId, status: "error", message: err?.message || "save failed" },
            window.location.origin
          );
        }
      }
    } else if (event.data.type === "judge-popup-resync-request") {
      const matchId = String(event.data.matchId || "").trim();
      const sourceWindow = event.source;
      if (!matchId || !sourceWindow || typeof sourceWindow.postMessage !== "function") return;
      try {
        await judgeRefresh();
        const freshMatch = (judgeState.matches || []).find((x) => String(x.id) === matchId) || null;
        sourceWindow.postMessage(
          {
            type: "judge-popup-resync-result",
            ok: true,
            matchId,
            current_match_id: judgeState.current_match_id || null,
            match: freshMatch,
          },
          window.location.origin
        );
      } catch (err) {
        sourceWindow.postMessage(
          {
            type: "judge-popup-resync-result",
            ok: false,
            matchId,
            error: friendlyJudgeNetworkError(err, "Resync failed."),
          },
          window.location.origin
        );
      }
    } else if (event.data.type === "judge-resync-request") {
      const sourceWindow = event.source;
      const requestedEventId = String(event.data.event_id || adminState.selectedId || "").trim();
      if (!sourceWindow || typeof sourceWindow.postMessage !== "function" || !requestedEventId) return;
      try {
        const requestedEvent = adminState.events.find((ev) => String(ev.id) === String(requestedEventId));
        if (normalizeStatus(requestedEvent?.status) === "qualifying") {
          const payload = await apiGet(
            `/admin/events/${encodeURIComponent(requestedEventId)}/qualifying/current-run?include_locked=1`
          );
          sourceWindow.postMessage(
            { type: "judge-resync-result", ok: true, event_id: requestedEventId, view: "qualifying", payload },
            window.location.origin
          );
          return;
        }

        const payload = await apiGet(
          `/admin/events/${encodeURIComponent(requestedEventId)}/comp-view?popout=1&view=judging`
        );
        sourceWindow.postMessage(
          { type: "judge-resync-result", ok: true, event_id: requestedEventId, payload },
          window.location.origin
        );
      } catch (err) {
        sourceWindow.postMessage(
          {
            type: "judge-resync-result",
            ok: false,
            event_id: requestedEventId,
            error: friendlyJudgeNetworkError(err, "Resync failed."),
          },
          window.location.origin
        );
      }
    } else if (event.data.type === "judge-score-closed") {
      if (openJudgeScoreModal._pollInterval) {
        clearInterval(openJudgeScoreModal._pollInterval);
        openJudgeScoreModal._pollInterval = null;
      }
      openJudgeScoreModal._popup = null;
      if (judgeState.token) {
        await judgeRefresh();
      }
    }
  });

  // -----------------------------
  // Click handling
  // -----------------------------
  root.addEventListener("click", async (e) => {
    const btn = e.target.closest("button");
    if (!btn) return;

    if (btn.dataset.tab) {
      if (btn.dataset.tab === "spectator") openSpectatorPortal();
      else setTab(btn.dataset.tab);
      return;
    }

    const action = btn.dataset.action;
    if (!action) return;

    // Fallback delegation for the small "Current Battle" pop-out button that
    // lives in the Bracket card header (outside #glCeCompBracket) and has no
    // data-bracket-id, so it would never reach wireBracketControls' click
    // handler. popOutCurrentBattle is module-scoped, so we can invoke it here
    // directly.
    if (action === "bracket-current-battle-popout") {
      if (typeof popOutCurrentBattle === "function") {
        try { popOutCurrentBattle(); } catch (err) { console.error("popOutCurrentBattle failed:", err); }
      }
      return;
    }
    // The admin-request-access flow (open/cancel/submit) is intentionally for
    // NON-admins requesting access, so it must be exempt from the admin guard
    // below. Its backend endpoint (/admin/request-access) does not require admin.
    const isAdminRequestAction =
      action === "admin-request-open" ||
      action === "admin-request-cancel" ||
      action === "admin-request-submit" ||
      action === "staff-session-start" ||
      action === "staff-session-revoke";

    if (!isAdminRequestAction &&
        (action.startsWith("admin-") || action.startsWith("super-admin-") || action.startsWith("maintenance-"))) {
      // Temporary log for delegated admin-action click debugging.
      console.log("[ADMIN CLICK] delegated action", { action, target: e.target, button: btn });
      // Defense in depth: any admin-prefixed action requires true admin access.
      // Admin UI is supposed to only be rendered inside the Admin pane (which is
      // already gated on isAdmin), but in case a stray admin button leaks into
      // the Driver / Judge pane (e.g. shared render helpers), this guard makes
      // sure non-admins can never trigger an admin mutation from the client.
      // The backend additionally enforces requireAdmin() on every mutation.
      if (!hasAdminPortalAccess()) {
        toast("Admin access required.", "err");
        return;
      }
    }

    try {
      // DRIVER
      if (action === "driver-lookup") {
        const code = (driverPane.querySelector("#glCeDriverCode")?.value || "").trim();
        if (!code) throw new Error("Enter a code.");
        await driverLookup(code);
        toast("Loaded.", "ok");
        return;
      }
      if (action === "driver-clear") {
        stopDriverLive();
        driverState.code = "";
        driverState.event = null;
        driverState.bracket = null;
        driverState.roster = null;
        driverState.bracketSig = "";
        driverState.rosterSig = "";
        driverState.lastBracketAt = "";
        renderDriver();
        return;
      }
      if (action === "staff-session-start") {
        const event_id = String(adminPane.querySelector("#glCeStaffEventLookup")?.value || "").trim();
        const pin = String(adminPane.querySelector("#glCeStaffPin")?.value || "").trim();
        if (!event_id || !pin) throw new Error("Enter the event ID or code and PIN.");
        const data = await apiPost("/staff/session", { event_id, pin });
        adminState.series.staffSession = { token: data.token, event_id: data.event_id, expires_at: data.expires_at };
        persistStaffSession(adminState.series.staffSession);
        toast("Event access granted.", "ok");
        await adminOpenEvent(data.event_id);
        return;
      }
      if (action === "staff-session-revoke") {
        const token = String(adminState.series.staffSession?.token || "");
        if (token) await apiPost(`/staff/session/revoke?staff_token=${encodeURIComponent(token)}`, {});
        adminState.series.staffSession = null;
        clearStoredStaffSession();
        adminState.selectedId = "";
        adminState.events = [];
        adminState.page = "home";
        renderAdmin();
        toast("Event access ended.", "ok");
        return;
      }
      if (action === "admin-qualifying-export") {
        await downloadQualifyingResults(adminState.selectedId);
        toast("Qualifying results exported.", "ok");
        return;
      }
      if (action === "driver-refresh") {
        await driverRefreshAll();
        return;
      }
      if (action === "driver-register") {
        await driverRegister();
        return;
      }
      if (action === "driver-unregister") {
        await driverUnregister();
        return;
      }
      if (action === "driver-select-event") {
        const code = btn.getAttribute("data-code") || btn.closest("[data-code]")?.getAttribute("data-code");
        if (code) {
          await driverSelectEvent(code);
          toast("Loaded.", "ok");
        }
        return;
      }
      if (action === "driver-remove-event") {
        const eventId = btn.getAttribute("data-event-id") || "";
        const eventName = btn.getAttribute("data-event-name") || "Event";
        const eventStatus = btn.getAttribute("data-event-status") || "";
        if (!eventId) throw new Error("Missing event id.");
        await driverRemoveEventById(eventId, eventName, eventStatus);
        return;
      }
      if (action === "driver-bracket-select-round") {
        const roundStr = btn.dataset.round;
        const r = normalizeBracketRoundSelection(roundStr === "null" ? null : roundStr);
        driverState.bracketActiveRound = (r === null || r === "third-place" || Number.isFinite(r))
          ? r
          : driverState.bracketActiveRound;
        updateDriverBracketMount();
        return;
      }

      // JUDGE
      if (action === "judge-start") {
        const judgeCode = (judgePane.querySelector("#glCeJudgeCode")?.value || "").trim().toUpperCase();
        if (!judgeCode) throw new Error("Enter a judge code.");
        await judgeStartSession(null, judgeCode);
        toast("Session started.", "ok");
        return;
      }
      if (action === "judge-refresh") {
        await judgeRefresh();
        toast("Refreshed.", "ok");
        return;
      }
      if (action === "judge-save-name") {
        await judgeSaveName();
        return;
      }
      if (action === "judge-clear") {
        clearJudgeSessionState();
        renderJudge();
        return;
      }
      if (action === "judge-qual-submit") {
        const runId = String(btn.dataset.runId || "");
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = "Submitting…";
        try {
          await judgeSubmitQualifyingScore(runId);
        } finally {
          if (btn.isConnected) {
            btn.disabled = false;
            btn.textContent = original;
          }
        }
        return;
      }
      if (action === "judge-open-score") {
        await openJudgeScoreModal(btn.dataset.match);
        return;
      }
      if (action === "judge-pick-score") {
        if (!judgeState.scorePanel?.matchId || judgeState.scorePanel.submitting) return;
        judgeState.scorePanel.decision = String(btn.dataset.decision || "");
        judgeState.scorePanel.notes = String(
          judgePane.querySelector('[data-role="judge-inline-notes"]')?.value ?? judgeState.scorePanel.notes ?? ""
        );
        judgeState.scorePanel.error = "";
        renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
        return;
      }
      if (action === "judge-submit-inline") {
        try {
          await judgeSubmitInlineScore();
        } catch (error) {
          toast(friendlyJudgeNetworkError(error, "Score submission failed."), "err");
        }
        return;
      }
      if (action === "judge-close-score") {
        judgeState.scorePanel = {
          matchId: null, decision: "", notes: "", submitting: false, error: "",
          expectedBattleNo: null, expectedOmtCount: null,
        };
        renderJudgeMatches(judgePane.querySelector("#glCeJudgeMatches"));
        return;
      }

      // ADMIN (non-admin)
      if (action === "admin-request-open") {
        openAdminRequestForm();
        return;
      }
      if (action === "admin-request-cancel") {
        const wrap = adminPane.querySelector("#glCeAdminRequestWrap");
        if (wrap) {
          wrap.style.display = "none";
          wrap.innerHTML = "";
        }
        return;
      }
      if (action === "admin-request-submit") {
        await submitAdminRequest();
        return;
      }
      if (action === "bracket-select-round") {
        const roundStr = btn.dataset.round;
        if (roundStr === "null") {
          adminState.bracketActiveRound = null;
          adminState.bracketExplicitFull = true;
        } else {
          adminState.bracketExplicitFull = false;
          let selected;
          if (roundStr === "third-place") selected = "third-place";
          else selected = normalizeBracketRoundSelection(roundStr);

          adminState.bracketActiveRound = (selected === null || selected === "third-place" || Number.isFinite(selected))
            ? selected
            : adminState.bracketActiveRound;
        }

        const bMount = adminPane?.querySelector("#glCeCompBracket");
        const tabsMount = adminPane?.querySelector("#glCeBracketTabs");
        if (bMount && adminState.comp.payload?.bracket) {
          const payload = adminState.comp.payload;
          const ev = adminState.selectedId
            ? adminState.events.find(e => String(e.id) === String(adminState.selectedId))
            : null;
          const currentMatchId = payload.current_match_id != null ? String(payload.current_match_id) : "";
          const maxDrivers = ev?.max_drivers || payload.bracket?.max_drivers;
          const matches = payload.bracket?.matches || [];
          const maxRoundFromMatches = matches.length ? Math.max(...matches.filter(m => !isConsolationMatch(m)).map(m => Number(m.round || 0)).filter(Number.isFinite)) : 0;
          const totalRounds = (Number(payload.bracket?.final_round) > 0) ? Number(payload.bracket.final_round) : (maxRoundFromMatches > 0 ? maxRoundFromMatches : undefined);

          renderBracketVisual(bMount, payload.bracket, {
            title: ev?.event_name || "Live",
            live: true,
            lastUpdated: adminState.comp.lastAt,
            currentMatchId,
            max_drivers: maxDrivers,
            totalRounds,
            finalRound: Number(payload.bracket?.final_round || 0) || undefined,
            activeRound: adminState.bracketExplicitFull
              ? null
              : normalizeBracketRoundSelection(adminState.bracketActiveRound),
          });
          if (tabsMount) {
            renderBracketTabs(tabsMount, payload.bracket, ev);
          }
        } else {
          renderAdmin();
        }
        return;
      }

      // ADMIN (admin)
      if (action === "admin-refresh-events") {
        try {
          await Promise.all([adminRefreshEvents(), adminRefreshSeries({ includeSelectedDetail: adminState.page === "series" })]);
          toast("Events and series refreshed.", "ok");
        } catch (err) {
          toast(err?.message || String(err), "err");
        }
        renderAdmin();
        return;
      }
      if (action === "admin-fan-vote-refresh") {
        await adminLoadFanVotePrompts(btn.dataset.eventId);
        return;
      }
      if (action === "admin-fan-vote-create") {
        openFanVotePromptModal(btn.dataset.eventId);
        return;
      }
      if (action === "admin-fan-vote-edit") {
        const eventId = String(btn.dataset.eventId || "");
        const promptId = String(btn.dataset.promptId || "");
        const data = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/fan-vote/prompts/${encodeURIComponent(promptId)}`);
        openFanVotePromptModal(eventId, data.prompt);
        return;
      }
      if (action === "admin-fan-vote-save") {
        const eventId = String(btn.dataset.eventId || "");
        const promptId = String(btn.dataset.promptId || "");
        const modal = btn.closest(".gl-ce-modal");
        const input = (name) => String(modal?.querySelector(`[name="${name}"]`)?.value || "").trim();
        const title = input("title");
        if (!title) throw new Error("A question is required.");
        const body = {
          title,
          public_note: input("public_note") || null,
          private_note: input("private_note") || null,
          public_results_enabled: !!modal?.querySelector('[name="public_results_enabled"]')?.checked,
        };
        if (promptId) await apiPost(`/admin/events/${encodeURIComponent(eventId)}/fan-vote/prompts/${encodeURIComponent(promptId)}`, body);
        else await apiPost(`/admin/events/${encodeURIComponent(eventId)}/fan-vote/prompts`, body);
        modal?.remove();
        await adminLoadFanVotePrompts(eventId, { silent: true });
        renderAdmin();
        toast(promptId ? "Fan vote updated." : "Fan vote prompt created.", "ok");
        return;
      }
      if (action === "admin-fan-vote-status") {
        const eventId = String(btn.dataset.eventId || "");
        const promptId = String(btn.dataset.promptId || "");
        const next = String(btn.dataset.status || "");
        const labels = { open: "open", close: "close", cancel: "cancel" };
        const confirmed = await showConfirmModal({
          title: `${labels[next] || "Update"} fan vote`,
          message: next === "cancel" ? "Cancel this prompt? It will no longer be publicly available." : `Are you sure you want to ${labels[next]} this fan vote?`,
          confirmText: next === "cancel" ? "Cancel Prompt" : `${labels[next] || "Update"}`.replace(/^\w/, (c) => c.toUpperCase()),
          danger: next === "cancel",
          mountEl: adminPane,
        });
        if (!confirmed) return;
        await apiPost(`/admin/events/${encodeURIComponent(eventId)}/fan-vote/prompts/${encodeURIComponent(promptId)}/${encodeURIComponent(next)}`, {});
        await adminLoadFanVotePrompts(eventId, { silent: true });
        renderAdmin();
        toast(`Fan vote ${labels[next]}d.`, "ok");
        return;
      }
      if (action === "admin-fan-vote-details" || action === "admin-fan-vote-voters-page") {
        const eventId = String(btn.dataset.eventId || "");
        const promptId = String(btn.dataset.promptId || "");
        const page = action === "admin-fan-vote-voters-page" ? Number(btn.dataset.page || 1) : 1;
        await adminLoadFanVoteDetails(eventId, promptId, { votersPage: page });
        renderAdmin();
        return;
      }
      if (action === "admin-open-create") {
        setAdminPage("create");
        return;
      }
      if (action === "admin-open-series") {
        await adminRefreshSeries({ includeSelectedDetail: true });
        adminState.page = "series";
        renderAdmin();
        return;
      }
      if (action === "admin-open-event-access") {
        adminState.page = "event-access";
        renderAdmin();
        return;
      }
      if (action === "admin-series-create") {
        const name = String(adminPane.querySelector("#glCeSeriesName")?.value || "").trim();
        const description = String(adminPane.querySelector("#glCeSeriesDescription")?.value || "").trim();
        if (!name) throw new Error("Series name is required.");
        const data = await apiPost("/admin/series", { name, description: description || null });
        adminState.series.byId[data.series.id] = normalizeSeriesForUi(data.series);
        adminState.series.selectedId = data.series.id;
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series created.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-save-details") {
        const series = selectedSeries();
        if (!series) throw new Error("Select a series.");
        const name = String(adminPane.querySelector("#glCeSeriesEditName")?.value || "").trim();
        const description = String(adminPane.querySelector("#glCeSeriesEditDescription")?.value || "").trim();
        if (!name) throw new Error("Series name is required.");
        const data = await apiPatch(`/admin/series/${encodeURIComponent(series.id)}`, { name, description: description || null });
        adminState.series.byId[series.id] = normalizeSeriesForUi({ ...series, ...data.series });
        toast("Series details saved.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-delete") {
        const series = selectedSeries();
        if (!series) throw new Error("Select a series.");
        const confirmed = await showConfirmModal({
          title: "Delete series",
          message: `Delete “${series.name}” and its reusable drivers, staff, and series admin assignments? Events already linked to this series will remain, including their results, but will no longer be linked to it.`,
          confirmText: "Delete Series",
          danger: true,
          mountEl: adminPane,
        });
        if (!confirmed) return;
        await apiDelete(`/admin/series/${encodeURIComponent(series.id)}`, { confirm: true });
        delete adminState.series.byId[series.id];
        adminState.series.selectedId = Object.keys(adminState.series.byId)[0] || "";
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series deleted. Existing events and results were preserved.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-admin-add") {
        const series = selectedSeries();
        const customer_id = String(adminPane.querySelector("#glCeSeriesAdminCustomerId")?.value || "").trim();
        if (!series || !customer_id) throw new Error("Enter a Shopify customer ID.");
        await apiPost(`/admin/series/${encodeURIComponent(series.id)}/admins`, { customer_id });
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series admin added.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-admin-remove") {
        const series = selectedSeries();
        const customerId = String(btn.dataset.customerId || "");
        if (!series || !customerId) throw new Error("Series admin is missing.");
        const confirmed = await showConfirmModal({
          title: "Remove series admin",
          message: "This person will no longer be able to manage this series or its events.",
          confirmText: "Remove Admin",
          danger: true,
        });
        if (!confirmed) return;
        await apiDelete(`/admin/series/${encodeURIComponent(series.id)}/admins/${encodeURIComponent(customerId)}`);
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series admin removed.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-driver-create") {
        const series = selectedSeries();
        if (!series) throw new Error("Select a series.");
        const value = (id) => String(adminPane.querySelector(id)?.value || "").trim();
        const body = {
          first_name: value("#glCeSeriesDriverFirst"), last_name: value("#glCeSeriesDriverLast"),
          email: value("#glCeSeriesDriverEmail") || null, driver_number: value("#glCeSeriesDriverNumber") || null,
          city: value("#glCeSeriesDriverCity") || null, car: value("#glCeSeriesDriverCar") || null,
          hp: value("#glCeSeriesDriverHp") || null, image_url: value("#glCeSeriesDriverImage") || null,
        };
        if (!body.first_name || !body.last_name) throw new Error("Driver first and last name are required.");
        const data = await apiPost(`/admin/series/${encodeURIComponent(series.id)}/drivers`, body);
        const imageFile = adminPane.querySelector("#glCeSeriesDriverImageFile")?.files?.[0];
        const driver = imageFile
          ? await uploadSeriesDriverImage(series.id, data.driver.id, imageFile)
          : data.driver;
        series.drivers.push(driver);
        toast("Series driver added.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-driver-edit") {
        const series = selectedSeries();
        const driverId = String(btn.dataset.driverId || "");
        const driver = series?.drivers?.find((item) => String(item.id) === driverId);
        if (!series || !driver) throw new Error("Select a series driver.");
        const body = await showSeriesDriverEditModal(driver);
        if (!body) return;
        const imageFile = body.imageFile;
        delete body.imageFile;
        await apiPatch(`/admin/series/${encodeURIComponent(series.id)}/drivers/${encodeURIComponent(driver.id)}`, body);
        if (imageFile && Number(imageFile.size) > 0) {
          await uploadSeriesDriverImage(series.id, driver.id, imageFile);
        }
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series driver updated.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-driver-remove") {
        const series = selectedSeries();
        const driverId = String(btn.dataset.driverId || "");
        const driver = series?.drivers?.find((item) => String(item.id) === driverId);
        if (!series || !driver) throw new Error("Select a series driver.");
        const confirmed = await showConfirmModal({
          title: "Remove series driver",
          message: `Permanently remove ${driver.first_name || ""} ${driver.last_name || ""} from this series roster? This cannot be undone.`,
          confirmText: "Remove Driver",
          danger: true,
        });
        if (!confirmed) return;
        await apiDelete(`/admin/series/${encodeURIComponent(series.id)}/drivers/${encodeURIComponent(driver.id)}`);
        adminState.series.recap = null;
        adminState.series.recapError = "";
        await adminRefreshSeries({ includeSelectedDetail: true });
        toast("Series driver removed.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-series-driver-recap") {
        const series = selectedSeries();
        const driverId = String(btn.dataset.driverId || "");
        if (!series || !driverId) throw new Error("Select a series driver.");
        adminState.series.recapLoading = true;
        adminState.series.recapError = "";
        adminState.series.recap = null;
        renderAdmin();
        try {
          adminState.series.recap = await apiGet(
            `/admin/series/${encodeURIComponent(series.id)}/drivers/${encodeURIComponent(driverId)}/recap`
          );
        } catch (err) {
          adminState.series.recapError = err?.message || "Unable to load recap.";
        } finally {
          adminState.series.recapLoading = false;
          renderAdmin();
        }
        return;
      }
      if (action === "admin-series-recap-close") {
        adminState.series.recap = null;
        adminState.series.recapError = "";
        renderAdmin();
        return;
      }
      if (action === "admin-pin-open") {
        adminState.adminPinPanelOpen = true;
        renderAdmin();
        const eventId = String(adminState.selectedId || "");
        if (eventId) {
          try {
            const data = await apiGet(`/admin/events/${encodeURIComponent(eventId)}/staff/pin`);
            adminState.series.pinByEvent[eventId] = data.pin || (data.active ? "Rotate required" : "Not generated");
          } catch (error) {
            adminState.series.pinByEvent[eventId] = "Unavailable";
            toast(error?.message || "Unable to load the active Admin PIN.", "err");
          }
          renderAdmin();
        }
        return;
      }
      if (action === "admin-pin-close") {
        adminState.adminPinPanelOpen = false;
        renderAdmin();
        return;
      }
      if (action === "admin-pin-generate" || action === "admin-pin-rotate" || action === "admin-pin-revoke") {
        const eventId = String(btn.dataset.eventId || "");
        if (!eventId) throw new Error("Event not found.");
        const operation = action === "admin-pin-generate" ? "generate" : action === "admin-pin-rotate" ? "rotate" : "revoke";
        const confirmation = operation === "revoke"
          ? await showConfirmModal({ title: "Revoke Admin PIN", message: "This immediately ends active PIN sessions for this event.", confirmText: "Revoke Pin", danger: true })
          : operation === "rotate"
            ? await showConfirmModal({ title: "Rotate Admin PIN", message: "This immediately ends active PIN sessions. Share the replacement only with people who should administer this event.", confirmText: "Rotate Pin", danger: false })
            : true;
        if (!confirmation) return;
        const suffix = operation === "generate" ? "" : `/${operation}`;
        const data = await apiPost(`/admin/events/${encodeURIComponent(eventId)}/staff/pin${suffix}`, {});
        adminState.series.pinByEvent[eventId] = data.pin || "Revoked";
        toast(data.pin ? `Admin Pin ${operation === "rotate" ? "rotated" : "generated"}.` : "Admin Pin revoked.", "ok");
        renderAdmin();
        return;
      }
      if (action === "admin-back-home") {
        adminStopCompPoll();
        try {
          await Promise.all([adminRefreshEvents(), adminRefreshSeries({ includeSelectedDetail: false })]);
        } catch (err) {
          toast(err?.message || "Unable to refresh admin data.", "err");
        }
        adminState.page = "home";
        adminState.editBracket = false;
        renderAdmin();
        return;
      }
      if (action === "admin-back-manage") {
        adminStopCompPoll();
        adminState.editBracket = false;
        const selId = String(adminState.selectedId || "");
        if (selId) {
          await adminOpenEvent(selId);
        } else {
          adminState.page = "home";
          renderAdmin();
        }
        return;
      }
      if (action === "admin-create-submit") {
        await adminCreateEventSubmit();
        return;
      }
      if (action === "admin-open") {
        await adminOpenEvent(btn.dataset.id);
        return;
      }
      if (action === "admin-edit-details") {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.dataset.id;
        if (!id) return;
        adminState.edit.eventId = id;
        adminState.page = "edit";
        renderAdmin();
        return;
      }
      if (action === "admin-edit-cancel") {
        adminState.edit.eventId = null;
        adminState.page = "manage";
        renderAdmin();
        return;
      }
      if (action === "admin-edit-save") {
        await handleAdminEditSave();
        return;
      }
      if (action === "admin-open-driver") {
        const code = String(btn.dataset.code || "").trim();
        if (!code) return;
        setTab("driver");
        await driverLookup(code);
        toast("Opened in driver view.", "ok");
        return;
      }
      if (action === "admin-download-flyer") {
        await adminDownloadFlyer(btn.dataset.id || adminState.selectedId);
        return;
      }
      if (action === "admin-open-add-driver") {
        adminOpenAddDriverModal();
        return;
      }
      if (action === "admin-close-add-driver") {
        adminCloseAddDriverModal();
        return;
      }
      if (action === "admin-set-status") {
        await adminSetStatus(btn.dataset.id, btn.dataset.status);
        return;
      }
      if (action === "admin-open-final-results") {
        await adminOpenFinalResultsDraft(btn.dataset.id || adminState.selectedId);
        return;
      }
      if (action === "admin-delete-event") {
        await handleDeleteEvent(btn);
        return;
      }
      if (action === "admin-load-judges") {
        await adminLoadJudges(btn.dataset.id);
        await adminRefreshSelectedEventStatus({ silent: true });
        toast("Judges refreshed.", "ok");
        return;
      }
      if (action === "admin-load-regs") {
        await adminLoadRegistrations(btn.dataset.eventId || adminState.selectedId);
        await adminRefreshSelectedEventStatus({ silent: true });
        toast("Registrations refreshed.", "ok");
        return;
      }
      if (action === "admin-add-registration") {
        await adminAddRegistration(btn.dataset.eventId || adminState.selectedId);
        return;
      }
      if (action === "admin-edit-registration") {
        const row = registrationRows(adminState.regs).find((r) => String(r.id) === String(btn.dataset.regid));
        if (!row) throw new Error("Registration not found.");
        adminState.editingRegistration = row;
        adminState.addDriverModalOpen = true;
        renderAdmin();
        return;
      }
      if (action === "admin-remove-judge") {
        const ok = await showConfirmModal({ title: "Remove judge", message: "Remove this judge from the event? This can be undone by assigning a new judge.", confirmText: "Remove", danger: true });
        if (!ok) return;
        await apiDelete(`/admin/events/${encodeURIComponent(btn.dataset.id || adminState.selectedId)}/judges/${encodeURIComponent(btn.dataset.judgeCode)}`);
        await adminLoadJudges(btn.dataset.id || adminState.selectedId, { silent: true });
        toast("Judge removed.", "ok");
        return;
      }
      if (action === "admin-refresh-bracket") {
        await adminLoadBracketByCode(btn.dataset.code);
        await adminRefreshSelectedEventStatus({ silent: true });
        return;
      }
      if (action === "admin-bracket-recovery-refresh") {
        await adminRefreshBracketRecovery(btn.dataset.id, btn.dataset.code);
        return;
      }
      if (action === "admin-judge-name-edit") {
        const code = String(btn.dataset.judgeCode || "");
        if (!code) return;
        adminState.judgeEditByCode[code] = true;
        renderAdmin();
        return;
      }
      if (action === "admin-judge-name-cancel") {
        const code = String(btn.dataset.judgeCode || "");
        if (code && adminState.judgeEditByCode[code]) {
          delete adminState.judgeEditByCode[code];
        }
        renderAdmin();
        return;
      }
      if (action === "admin-judge-name-save") {
        const code = String(btn.dataset.judgeCode || "");
        const eventId = String(btn.dataset.id || adminState.selectedId || "");
        const row = btn.closest("[data-judge-row]");
        const input = row ? row.querySelector("input[data-role='judge-display-name']") : null;
        const displayName = (input?.value || "").trim();
        await adminSaveJudgeDisplayName(eventId, code, displayName);
        return;
      }
      if (action === "admin-load-bracket") {
        const code = String(btn.dataset.code || "").trim();
        if (code) {
          await adminLoadBracketByCode(code);
          renderAdminManageView();
        }
        return;
      }
      if (action === "admin-swap-submit") {
        const eventId = btn.dataset.eventid;
        if (eventId) adminSwapDrivers(eventId);
        return;
      }
      if (action === "admin-simple-winner") {
        await adminSimpleWinner(btn.dataset.matchid, btn.dataset.winnerid);
        return;
      }
      if (action === "admin-simple-swap") {
        await adminSimpleSwap(btn.dataset.matchid, btn.dataset.a, btn.dataset.b);
        return;
      }
      if (action === "admin-simple-advance") {
        await adminSimpleAdvance(btn.dataset.eventid);
        return;
      }
      if (action === "admin-regenerate-bracket") {
        const shuffle = String(btn.dataset.shuffle || "false") === "true";
        await adminRegenerateBracket(btn.dataset.id, shuffle);
        return;
      }
      if (action === "admin-generate-qualifying-bracket") {
        await adminFinalizeQualifyingAndGoLive(btn.dataset.id);
        return;
      }
      if (action === "admin-open-manual-bracket") {
        await adminOpenManualBracket(btn.dataset.id);
        return;
      }
      if (action === "admin-manual-bracket-back") {
        adminState.page = "manage";
        adminState.manualBracket = null;
        renderAdmin();
        return;
      }
      if (action === "admin-manual-bracket-tab") {
        adminState.manualBracketRound = Number(btn.dataset.round || 1);
        renderAdmin();
        return;
      }
      if (action === "admin-manual-runner-tab") {
        adminState.manualRunnerTab = String(btn.dataset.tab || "bracket");
        renderAdmin();
        return;
      }
      if (action === "admin-manual-bracket-save") {
        await adminSaveManualBracketRound();
        return;
      }
      if (action === "admin-manual-bracket-activate") {
        await adminActivateManualBracket();
        return;
      }
      if (action === "admin-bracket-draft-back") {
        adminState.page = "manage";
        adminState.bracketDraft = null;
        renderAdmin();
        return;
      }
      if (action === "admin-final-results-back") {
        adminState.page = "manage";
        adminState.finalResultsDraft = null;
        renderAdmin();
        return;
      }
      if (action === "admin-final-results-save") {
        await adminSaveFinalResultsDraft(false);
        return;
      }
      if (action === "admin-final-results-approve") {
        await adminSaveFinalResultsDraft(true);
        return;
      }
      if (action === "admin-simple-bracket-restore-qualifying") {
        await adminRestoreQualifyingDraft();
        return;
      }
      if (action === "admin-bracket-draft-qualifying") {
        adminState.comp.view = "qualifying";
        adminState.page = "comp";
        await adminCompRefresh();
        return;
      }
      if (action === "admin-bracket-draft-move") {
        const ids = draftDriverIds();
        const index = Number(btn.dataset.index);
        const target = index + Number(btn.dataset.direction);
        if (index >= 0 && target >= 0 && target < ids.length) {
          [ids[index], ids[target]] = [ids[target], ids[index]];
          await adminSaveBracketDraft(ids, `Seed #${index + 1} will swap with seed #${target + 1}.`);
        }
        return;
      }
      if (action === "admin-bracket-draft-remove") {
        const ids = draftDriverIds();
        const index = Number(btn.dataset.index);
        const removed = ids[index];
        if (removed) {
          ids.splice(index, 1);
          await adminSaveBracketDraft(ids, `This driver will be removed from the draft bracket and later seeds will move up.`);
        }
        return;
      }
      if (action === "admin-bracket-draft-add") {
        const ids = draftDriverIds();
        const registrationId = String(btn.dataset.regid || "");
        if (registrationId && !ids.includes(registrationId)) {
          ids.push(registrationId);
          await adminSaveBracketDraft(ids, "This eligible driver will be added at the bottom of the draft bracket.");
        }
        return;
      }
      if (action === "admin-bracket-draft-approve") {
        await adminApproveBracketDraft();
        return;
      }
      if (action === "admin-bracket-draft-resolve-ties") {
        await adminResolveBracketDraftTies();
        return;
      }
      if (action === "admin-active-bracket-reset") {
        await adminRecoverActiveBracket(btn.dataset.id, false);
        return;
      }
      if (action === "admin-active-bracket-rebuild-simple") {
        await adminRecoverActiveBracket(btn.dataset.id, true);
        return;
      }
      if (action === "admin-open-simple-bracket") {
        await adminOpenSimpleBracket();
        return;
      }
      if (action === "admin-simple-bracket-save") {
        await adminSaveSimpleBracket();
        return;
      }
      if (action === "admin-simple-bracket-activate") {
        await adminActivateSimpleBracket();
        return;
      }
      if (action === "admin-start-qualifying") {
        await adminStartQualifying(btn.dataset.id);
        return;
      }
      if (action === "admin-unlock-bracket") {
        await adminUnlockBracket(btn.dataset.id);
        return;
      }
      if (action === "admin-force-clear-regen-lock") {
        await adminForceClearRegenLock(btn.dataset.id);
        return;
      }
      if (action === "admin-advance-round") {
        const ok = window.confirm("Advance to the next round? Make sure all matches in the current round have winners.");
        if (!ok) return;
        await adminAdvanceRound(btn.dataset.id);
        return;
      }
      if (action === "admin-remove-driver") {
        await adminRemoveRegistration(adminState.selectedId, btn.dataset.regid);
        return;
      }
      if (action === "admin-remove-registration") {
        await adminRemoveRegistration(btn.dataset.eventId || adminState.selectedId, btn.dataset.regid);
        return;
      }
      if (action === "admin-bracket-save-row") {
        await adminBracketSaveRow(btn.dataset.eventid, btn.dataset.matchno);
        return;
      }
      if (action === "admin-check-super-admin") {
        setAdminPage("super-admin");
        return;
      }
      if (action === "maintenance-preview") {
        await maintenanceRepairPreview();
        return;
      }
      if (action === "maintenance-apply") {
        await maintenanceRepairApply();
        return;
      }
      if (action === "maintenance-copy-report") {
        maintenanceCopyReport();
        return;
      }
      if (action === "super-admin-load-users") {
        await superAdminLoadUsers();
        return;
      }
      if (action === "super-admin-load-organizers") {
        await superAdminLoadOrganizers();
        return;
      }
      if (action === "super-admin-load-admins") {
        await superAdminLoadAdmins();
        return;
      }
      if (action === "super-admin-view-organizer-events") {
        await superAdminViewOrganizerEvents(btn.dataset.organizer);
        return;
      }
      if (action === "super-admin-grant-access") {
        await superAdminGrantAccess();
        return;
      }
      if (action === "super-admin-revoke-access") {
        await superAdminRevokeAccess(btn.dataset.customerId);
        return;
      }

      // ADMIN comp view
      if (action === "admin-comp-view") {
        await adminCompViewOpen(btn.dataset.id);
        return;
      }
      if (action === "admin-convert-simple-bracket") {
        await adminConvertToSimpleBracket(btn.dataset.id || adminState.selectedId);
        return;
      }
      if (action === "admin-qualifying-view") {
        await adminCompViewOpen(btn.dataset.id, "qualifying");
        return;
      }
      if (action === "admin-comp-refresh") {
        await adminCompRefresh();
        return;
      }
      if (action === "admin-qualifying-toggle-edit") {
        const regId = String(btn.dataset.regId || "");
        const current = String(adminState.comp.qualifyingEditing?.registrationId || "");
        if (current === regId) {
          adminState.comp.qualifyingEditing = { registrationId: null, drafts: {} };
        } else {
          adminState.comp.qualifyingEditing = { registrationId: regId, drafts: {} };
        }
        renderAdmin();
        return;
      }
      if (action === "admin-qualifying-save-scores") {
        await adminQualifyingSaveScores(String(btn.dataset.runId || ""));
        return;
      }
      if (action === "admin-qualifying-lock") {
        await adminQualifyingLockRun(String(btn.dataset.runId || ""));
        return;
      }
      if (action === "admin-qualifying-dnf") {
        await adminQualifyingDnfRun(String(btn.dataset.runId || ""));
        return;
      }
      if (action === "admin-qualifying-set-current") {
        await adminQualifyingSetCurrentRun(String(btn.dataset.runId || ""));
        return;
      }
      if (action === "admin-qualifying-move-seed") {
        const direction = String(btn.dataset.direction || "");
        const regId = String(btn.dataset.regId || "");
        const ok = await showConfirmModal({ title: "Override qualifying seed", message: `Move this driver one seed ${direction}? This manual seed order is saved and used for qualifying bracket drafts.`, confirmText: "Override seed", danger: false });
        if (!ok) return;
        await apiPost(`/admin/events/${encodeURIComponent(adminState.selectedId)}/qualifying/rank/${encodeURIComponent(regId)}/${encodeURIComponent(direction)}`, { confirm: true });
        adminState.comp._lastSig = undefined;
        await adminCompPollTick({ reason: "qualifying-seed-override" });
        return;
      }
      if (action === "admin-comp-retry") {
        await adminCompPollTick({ reason: "manual", force: true });
        return;
      }
      // PHASE 4.4: Quick actions
      if (action === "admin-advance-round-quick") {
        const eventId = btn.dataset.eventId;
        if (!eventId) return;
        await adminAdvanceRound(eventId);
        await adminCompRefresh();
        return;
      }
      if (action === "admin-set-status-quick") {
        const eventId = btn.dataset.eventId;
        const status = btn.dataset.status;
        if (!eventId || !status) return;
        try {
          await adminSetStatus(eventId, status, { refreshComp: true });
        } catch (err) {
          toast(err?.message || "Failed to set status.", "err");
        }
        return;
      }
      if (action === "admin-comp-popout") {
        adminCompPopout();
        return;
      }
      if (action === "admin-set-current") {
        await adminSetCurrentMatch(btn.dataset.matchid);
        return;
      }
      if (action === "admin-override-open") {
        openOverrideModal(btn.dataset.matchid);
        return;
      }
      if (action === "admin-judge-notes-toggle") {
        await adminToggleJudgeNotes(btn.dataset.matchid);
        return;
      }
      if (action === "admin-override-close") {
        const modal = adminPane.querySelector("#glCeOverrideModal");
        if (modal) {
          modal.style.display = "none";
          modal.innerHTML = "";
        }
        // Resume auto-refresh when override modal closes
        adminStartCompPoll();
        return;
      }
      if (action === "admin-override-set" || action === "admin-override-apply-drivers") {
        const modal = adminPane.querySelector("#glCeOverrideModal");
        const slotA = String(modal?.querySelector('[data-role="override-driver-a"]')?.value || "");
        const slotB = String(modal?.querySelector('[data-role="override-driver-b"]')?.value || "");
        const winnerSlot = String(btn.dataset.winnerSlot || "");
        const winner = action === "admin-override-apply-drivers"
          ? ""
          : (winnerSlot === "a" ? slotA : winnerSlot === "b" ? slotB : String(btn.dataset.winner || ""));
        const applied = await adminOverrideMatch(btn.dataset.matchid, winner, {
          slot_a_driver_id: slotA,
          slot_b_driver_id: slotB,
          repair_only: action === "admin-override-apply-drivers",
        });
        if (applied === false) return;
        if (modal) {
          modal.style.display = "none";
          modal.innerHTML = "";
        }
        // Resume auto-refresh when override is applied and modal closes
        adminStartCompPoll();
        return;
      }
    } catch (err) {
      toast(err?.message || String(err), "err");
    }
  });

  // -----------------------------
  // Initial renders + initial loads
  // -----------------------------
  renderDriver();
  renderJudge();
  renderAdmin();
  (async () => {
    let saved = null;
    try { saved = JSON.parse(window.localStorage.getItem(EVENT_ACCESS_SESSION_STORAGE_KEY) || "null"); } catch (_) {}
    if (!saved?.token || !saved?.event_id) return;
    if (saved.expires_at && Date.parse(String(saved.expires_at)) <= Date.now()) {
      clearStoredStaffSession();
      return;
    }
    adminState.series.staffSession = {
      token: String(saved.token),
      event_id: String(saved.event_id),
      expires_at: saved.expires_at || null,
    };
    try {
      const session = await apiGet(`/staff/session?staff_token=${encodeURIComponent(saved.token)}`);
      adminState.series.staffSession.expires_at = session.expires_at || saved.expires_at || null;
      persistStaffSession(adminState.series.staffSession);
      const detail = await apiGet(`/admin/events/${encodeURIComponent(saved.event_id)}`);
      adminState.events = detail.event ? [detail.event] : [];
      adminState.selectedId = String(saved.event_id);
      adminState.page = "manage";
      renderAdmin();
    } catch (_) {
      adminState.series.staffSession = null;
      clearStoredStaffSession();
      renderAdmin();
    }
  })();
  (async () => {
    let saved = null;
    try { saved = JSON.parse(window.localStorage.getItem(JUDGE_SESSION_STORAGE_KEY) || "null"); } catch (_) {}
    if (!saved?.token || !saved?.judge_code) return;
    judgeState.token = String(saved.token);
    judgeState.judge_code = String(saved.judge_code);
    try {
      await judgeRefresh();
    } catch (error) {
      if (!isInvalidJudgeSessionError(error)) console.warn("Unable to restore Judge Hub session:", error);
    }
  })();

  // Recompute registration open/closed status every 30s so the driver and
  // admin views flip from open to closed at the UTC close instant without a
  // manual refresh. We re-render rather than re-fetch — the underlying time
  // math is local and cheap. Real status is still source-of-truth on the
  // server; this just narrows the visual lag to <=30s.
  //
  // Important: these re-renders blow away `innerHTML`, which would wipe an
  // admin/driver who is typing into a form. We skip the tick whenever:
  //   - the admin is on a form-only page (create/edit), OR
  //   - any input/textarea/select inside the relevant pane is focused.
  const REGISTRATION_STATUS_REFRESH_MS = 30 * 1000;
  const paneHasActiveInput = (pane) => {
    if (!pane) return false;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (!active || active === document.body) return false;
    if (!pane.contains(active)) return false;
    const tag = String(active.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (active.isContentEditable) return true;
    return false;
  };
  setInterval(() => {
    try {
      if (driverState && driverState.event && !paneHasActiveInput(driverPane)) {
        renderDriver();
      }
    } catch (_) {
      // ignore — interval must not crash the SPA
    }
    try {
      if (!isAdmin || !Array.isArray(adminState?.events) || !adminState.events.length) return;
      // Never re-render while an admin is filling out the create or edit form;
      // those views are 100% inputs and we'd lose every keystroke.
      const page = String(adminState?.page || "");
      if (page === "create" || page === "edit") return;
      if (paneHasActiveInput(adminPane)) return;
      renderAdmin();
    } catch (_) {
      // ignore
    }
  }, REGISTRATION_STATUS_REFRESH_MS);

  (async () => {
    if (isLoggedIn) {
      // Load driver's registered events
      try {
        await driverLoadMyEvents();
      } catch {
        // ignore
      }
    }
    if (isAdmin && isLoggedIn) {
      try {
        await Promise.all([adminRefreshEvents(), adminRefreshSeries()]);
      } catch {
        // ignore
      }
      renderAdmin();
    }
    // Fire-and-forget: refresh admin status from backend so UI reflects DB-backed isAdmin
    if (isLoggedIn) {
      refreshAdminStatus();
    }
  })();
})();
